import React, { useState, useEffect, useRef, useCallback } from "react";
// 토큰 기록·삭제는 전부 이 래퍼를 지난다(설계 v7 §4-3). ESLint 가 직접 호출을 막는다.
import { setToken, clearToken } from "./audit/authState";
import {
  Settings,
  Database,
  RefreshCw,
  X,
  FileText,
  Shield,
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  Eye,
  Upload,
  Trash2,
  Plus,
  Minus,
  RotateCcw,
  ChevronUp,
  ChevronDown,
  File as FileIcon,
  Play,
  Clock,
  Download,
  LayoutTemplate,
  Scan,
  Sparkles,
  PanelLeftClose,
  PanelLeft,
  HelpCircle,
  MessageSquare,
  FileSpreadsheet,
  FileImage,
  Image as ImageIcon,
  Server,
  FlaskConical,
  ExternalLink,
} from "lucide-react";
import HarnessDashboard from "./components/HarnessDashboard";
import HarnessWorkflowGuide from "./components/HarnessWorkflowGuide";
import TemplateManagement from "./components/TemplateManagement";
import TemplateSampleViewer from "./components/TemplateSampleViewer"; // 🚀 Import new viewer
import OCRLabelingViewer from "./components/OCRLabelingViewer"; // 🚀 OCR Visualization
import ExcelViewer from "./components/ExcelViewer"; // 📊 fortune-sheet 기반 Excel 뷰어 (오픈소스)
import * as XLSX from "xlsx";
import PromptManagementModal from "./components/PromptManagementModal";
import PromptPlayground from "./components/PromptPlayground";
import VehicleMasterManagement from "./components/VehicleMasterManagement";
import VehicleMatchingPanel from "./components/VehicleMatchingPanel";
import TaxpayerMatchingPanel from "./components/TaxpayerMatchingPanel";
import HSClassifierPanel from "./components/HSClassifierPanel";
import HSClassifierAuditPanel from "./components/HSClassifierAuditPanel";
import TrainingVerifyPanel from "./components/TrainingVerifyPanel";
import TrainingVerifyExportPanel from "./components/TrainingVerifyExportPanel";
import DonutLearningDataPanel from "./components/DonutLearningDataPanel";
import GTVerifyPanel from "./components/GTVerifyPanel";
import FedexVerifyPanel from "./components/FedexVerifyPanel"; // 📦 FedEx 추출 검증 (TW 400건)
import PDFViewer from "./components/PDFViewer";
import CanvasPDFViewer from "./components/CanvasPDFViewer";
import ReactPDFViewer from "./components/ReactPDFViewer"; // 📘 @react-pdf-viewer 기반 뷰어
import NativePDFViewer from "./components/NativePDFViewer";
import UpdateNotification from "./components/UpdateNotification";
import FineTuningManagement from "./components/FineTuningManagement";
import BatchValidationGrid from "./components/BatchValidationGrid";
import {
  saveCache as saveExtractionCacheDB,
  loadCache as loadExtractionCacheDB,
  clearCache as clearExtractionCacheDB,
  migrateFromLocalStorage,
} from "./utils/extractionCacheDB";
import BoundingBoxOverlay from "./components/BoundingBoxOverlay";
import FieldHighlightOverlay from "./components/FieldHighlightOverlay";
import GroundingHighlightOverlay from "./components/GroundingHighlightOverlay"; // ⭐ [Grounding] LangExtract 필드 위치 하이라이트
import Login from "./components/Login";
import SignupPage from "./components/SignupPage";
import LinkKeyPage from "./components/LinkKeyPage";
import LoginSuccessPage from "./components/LoginSuccessPage";
import InstallAppPage from "./components/InstallAppPage";
import MobileTabBar from "./components/MobileTabBar";
import HsCopilotPanel from "./components/HsCopilotPanel";
import InquiryBoardPage from "./components/InquiryBoardPage";
import PricingPage, { PaymentResultPage } from "./components/PricingPage";
import AdminLogin from "./components/admin/AdminLogin";
import AdminPage from "./components/admin/AdminPage";
import AccountSettingsPage from "./components/AccountSettingsPage";
import LandingPage from "./components/LandingPage";
import LandingPageLoginHS from "./components/LandingPageLoginHS";
import ApiDocsPage from "./components/ApiDocsPage";
import UserGuidePage from "./components/UserGuidePage";
import CourierFreightPage from "./components/CourierFreightPage";
import TradeCodesPage from "./components/TradeCodesPage";
import ReqAgencyDirectoryPage from "./components/ReqAgencyDirectoryPage";
import ExchangeRatePage from "./components/ExchangeRatePage";
import FormsPage from "./components/FormsPage";
import NewsPage from "./components/NewsPage";
import UsagePage from "./audit/UsagePage";
import CompanyAccountPage from "./audit/CompanyAccountPage";
import { submitPendingDisclosureConsent } from "./audit/disclosureConsent";
import CaseStudiesPage from "./components/CaseStudiesPage";
import TaxCalcPage from "./components/TaxCalcPage";
import ApiKeysPage from "./components/ApiKeysPage";
import SnsSignupPage from "./components/SnsSignupPage";
import ContactPage from "./components/ContactPage";
import { isLoginHSProduct } from "./config/product";
import {
  PUBLIC_PAGE_ROUTES,
  navigatePublicPage,
} from "./navigation/publicPageNavigation";
import DBSettings from "./components/DBSettings";
import VLLMServerPanel from "./components/VLLMServerPanel";
import PageBasedGridPanel from "./components/PageBasedGridPanel";
import VllmSettingsModal from "./components/VllmSettingsModal";
import EvalLabelingModal from "./components/EvalLabelingModal";
import CompareResultPanel from "./components/CompareResultPanel";
import RightPanelTabs from "./components/RightPanelTabs";
import ChunkDebugModal from "./components/ChunkDebugModal";
import DocumentWizard from "./components/DocumentWizard";
import ExcelImageExtractor from "./components/ExcelImageExtractor"; // 📊 마이그레이션 도구
import LegacyCustomerRegistration from "./components/LegacyCustomerRegistration"; // 🏛️ 레거시 거래처 등록
import Sidebar from "./components/Sidebar"; // 🧭 사이드바
import CenterSearchBar from "./audit/CenterSearchBar"; // 🔎 검색뷰 상단 검색바
import { shouldShowHeaderSearch } from "./audit/searchBarPlacement"; // LoginHS 업무 화면 전역 검색
import CollapsedSidebarTopNav from "./audit/CollapsedSidebarTopNav"; // 접힘 전용 상단 nav
import FontSizeModal from "./components/FontSizeModal"; // 🔤 글자 크기 선택
import {
  AuditDemo,
  PrecedentsPage,
  MainPage,
  QuickRefPage,
  HsComparePage,
  ResultsPage,
  SynonymsPage,
  HsHubPage,
  TradePartnersPage,
  LegacyProveRedirect,
  FeedbackDashboardPage,
  HeadingDetailPage,
  HomeDashboard,
  InvoiceUploadPanel,
  OntologyReviewPage,
  MyReportsPage,
  ReqGuidePage,
  ExemptionPage,
  SimplifiedRatePage,
  InstallmentPage,
  EndUseRatePage,
  CustomsGuideHubPage,
  BulkClassifyPanel,
} from "./audit"; // 🧾 Audit UI (P5/P6) — query-param 라우트
import MasterHsPanel from "./audit/MasterHsPanel"; // 📋 마스터 세번관리
import MappingTableViewer from "./components/MappingTableViewer"; // 📋 데이터 변환표(DTO) 시각화
import AIChatPanel from "./components/AIChatPanel"; // 🤖 AI 채팅 패널
import FedexAssistantPanel from "./components/FedexAssistantPanel"; // 🤖 FedEx 검수 전용 AI 어시스턴트
import {
  parseBoundingBoxes,
  scaleBoundingBoxes,
} from "./utils/boundingBoxParser";
import { getModelNickname, getAllModelNicknames } from "./utils/modelNickname";
import {
  GEMINI_FLASH_MODEL,
  normalizeGeminiGenerationModel,
  normalizeGeminiGenerationModels,
} from "./utils/geminiModel";
import { mergePageResults, applyMergeRules } from "./utils/mergeUtils"; // 📄 멀티 문서 머지
import {
  saveUploadToDB,
  saveExtractionToDB,
  restoreDocuments,
  refreshDocuments,
  getPdfDownloadUrl,
  fetchRestoredPdf,
  deleteDocumentsFromDB,
} from "./utils/documentPersist"; // 📄 문서 PostgreSQL + GCS 영구 저장
import * as pdfjsLib from "pdfjs-dist"; // 📄 PDF 페이지 수 감지용

// PDF.js worker 설정
const PDFJS_VERSION = pdfjsLib.version || "3.11.174";
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.js`;

// loginHS 제품 감지(도메인 loginhs.* / ?product=loginhs) — config/product.js SSoT. 모듈 1회 평가.
const IS_LOGINHS_PRODUCT = isLoginHSProduct();

// 탭 제목 — 도메인별 브랜드(index.html 기본값 "DocuAI2" 대체). 랜딩은 자체 useEffect 로 별도 설정.
if (typeof document !== "undefined") {
  document.title = IS_LOGINHS_PRODUCT
    ? "LoginHS — HS 품목분류 세번 조회"
    : "DocuAI2";
}

function App() {
  // API 베이스 URL 설정 (localhost일 때만 3002 포트 사용, 프로덕션은 상대 경로 사용)
  const API_BASE_URL =
    process.env.REACT_APP_API_URL ||
    (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
      ? `http://${window.location.hostname}:3002`
      : "");

  // 문서인식(추출) 호출용 인증 헤더 — 프리미엄 게이트(하루 10p 무료) 사용자 식별.
  //   토큰 없으면 빈 객체(게이트 fail-open, 기존 흐름 무해).
  const ocrAuthHeader = () => {
    const t = localStorage.getItem("token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };
  // 추출 응답이 402(무료한도 초과)면 결제 안내로 — true 반환 시 호출부는 중단.
  const handleOcrQuota = async (res) => {
    if (res.status !== 402) return false;
    try {
      const d = await res.clone().json();
      if (d.upgrade) {
        if (
          window.confirm(
            (d.error || "무료 체험 한도를 모두 사용했습니다.") +
              "\n\n구독 페이지로 이동할까요?",
          )
        ) {
          window.history.pushState({}, "", "/?page=pricing");
          window.dispatchEvent(new PopStateEvent("popstate"));
        }
        return true;
      }
    } catch (_e) {
      /* ignore */
    }
    return true;
  };

  // ⚠️ 중요: 모든 hooks는 조건부 return 전에 선언되어야 합니다 (React Hooks 규칙)

  // popstate 리스너 — audit sub-page 들의 pushState 기반 navigation 이
  // App.js re-render 를 트리거하도록 강제 갱신. (?audit=...) 라우팅은
  // _auditRoute 를 매 render 시 URL 에서 읽으므로 forceUpdate 만으로 충분.
  const [, _forceRouteUpdate] = useState(0);
  useEffect(() => {
    const onPop = () => {
      // ?page= 기반 전체 페이지(요금제·결제·설치 등)는 currentPage 동기화
      const pg = new URLSearchParams(window.location.search).get("page");
      if (pg && PUBLIC_PAGE_ROUTES.includes(pg)) setCurrentPage(pg);
      else if (!pg && window.location.hash !== "#admin") {
        // ?page 없음 → 랜딩/메인 복귀 (audit 라우트는 _auditRoute 가 별도 처리)
        setCurrentPage((prev) =>
          PUBLIC_PAGE_ROUTES.includes(prev) ? "landing" : prev,
        );
      }
      _forceRouteUpdate((x) => x + 1);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 로그인 상태 관리
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    // localStorage에서 로그인 상태 확인 (토큰 유효성은 mount 후 verify)
    const savedToken = localStorage.getItem("token");
    const savedGroupCode =
      localStorage.getItem("groupCode") || sessionStorage.getItem("groupCode");
    const savedUserId =
      localStorage.getItem("userId") || sessionStorage.getItem("userId");
    return !!(savedToken && savedGroupCode && savedUserId);
  });
  const [currentGroupCode, setCurrentGroupCode] = useState(() => {
    return (
      localStorage.getItem("groupCode") ||
      sessionStorage.getItem("groupCode") ||
      ""
    );
  });
  const [currentUserId, setCurrentUserId] = useState(() => {
    return (
      localStorage.getItem("userId") || sessionStorage.getItem("userId") || ""
    );
  });

  // 🧭 메뉴 권한 (백엔드 D_DOCUAI_MENU_VISIBILITY)
  //   null = 아직 fetch 전 → Sidebar 는 menuConfig.hiddenByDefault 사용
  //   object = { menuKey: boolean } → Sidebar 가 visibility 적용
  const [menuVisibility, setMenuVisibility] = useState(null);
  const [isManager, setIsManager] = useState(false);
  // embedded audit 페이지 keep-alive — 한 번 방문하면 unmount 안 하고 display 로
  // show/hide (관세청 품목분류 사례 58K 재로딩 방지, 결과/스크롤 state 보존)
  const [mountedAudit, setMountedAudit] = useState({});

  // Landing Page / Login / Signup 페이지 토글
  // URL hash로 #admin 접근 시 관리자 페이지로 전환
  const [currentPage, setCurrentPage] = useState(() => {
    const hash = window.location.hash;
    if (hash === "#admin") return "admin-login";
    // OAuth2 콜백 복귀 — 백엔드가 /?page=login-success (성공) / /?error=... (실패) 로 보냄
    const params = new URLSearchParams(window.location.search);
    const requestedPage = params.get("page");
    if (PUBLIC_PAGE_ROUTES.includes(requestedPage)) return requestedPage;
    if (params.get("error")) return "login"; // 로그인 화면이 ?error= 한국어 안내 표시
    return "landing";
  }); // 'landing' | 'login' | 'signup' | 'admin-login' | 'admin'

  const handlePublicNavigate = (page) => {
    navigatePublicPage(page, setCurrentPage);
  };

  // DB 연결 상태 관리
  const [dbStatus, setDbStatus] = useState({
    connected: false,
    server: null,
    database: null,
    loading: true,
  });

  // 상태 관리 (모든 hooks를 early return 전에 선언)
  const [fileList, setFileList] = useState([]); // { id, name, file, preview, status, extractedData, checked }
  const [selectedFileIndex, setSelectedFileIndex] = useState(null);
  const [gridSelectedFile, setGridSelectedFile] = useState(null); // 그리드에서 선택된 파일 (PDF 뷰어용)
  const [selectedPageNumber, setSelectedPageNumber] = useState(1); // PDF 페이지 선택
  const [pageJumpToken, setPageJumpToken] = useState(0); // 📄 페이지 점프 강제 트리거 토큰
  const [checkedPages, setCheckedPages] = useState([]); // 체크된 페이지 번호들
  const [validatedPages, setValidatedPages] = useState({}); // 검증 완료된 페이지 { fileId: { pageNumber: true } }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processingCount, setProcessingCount] = useState(0); // 동시 처리 중인 파일 수
  const [activeTab, setActiveTab] = useState("viewer"); // 'viewer', 'grid', 'mapping', 'finetuning', 'dbsettings'

  // 템플릿 관리 상태
  const [showTemplateManagement, setShowTemplateManagement] = useState(false);
  const [templateManagementMode, setTemplateManagementMode] = useState("list"); // 'list', 'create', 'edit'
  const [templateManagementTargetId, setTemplateManagementTargetId] =
    useState(null); // ID of template to edit/pre-select
  const [templateManagementActiveTab, setTemplateManagementActiveTab] =
    useState("fields"); // 🔧 템플릿관리 초기 탭 ('fields' | 'prompt' | 'mergeRules')

  // 레거시 파일 히스토리
  const [legacyFiles, setLegacyFiles] = useState([]);
  const [legacyFilesTotal, setLegacyFilesTotal] = useState(0);
  const [legacyFilesLoading, setLegacyFilesLoading] = useState(false);

  // 템플릿 관리 열기 핸들러
  const handleOpenTemplateManager = (mode = "list", templateId = null) => {
    setTemplateManagementMode(mode);
    setTemplateManagementTargetId(templateId);
    setShowTemplateManagement(true);
  };
  const [templates, setTemplates] = useState({});
  const [selectedOcrTemplate, setSelectedOcrTemplate] = useState(
    () => localStorage.getItem("docuai_default_template") || "",
  ); // 🔧 수동 템플릿 선택 (문서인식)
  const [ocrProcessingMode, setOcrProcessingMode] = useState("speed"); // 🚀 처리 모드: 'speed'(PDF 네이티브) | 'coordinate'(PNG 청크+Bbox)
  const [useExtendAI, setUseExtendAI] = useState(
    () => localStorage.getItem("useExtendAI") === "true",
  ); // 🔗 Extend.ai 비교 테스트 ON/OFF
  const [extendLightMode, setExtendLightMode] = useState(
    () => localStorage.getItem("extendLightMode") === "true",
  ); // 🔗 Extend.ai Light Mode (extraction_light)
  const [showChunkDebug, setShowChunkDebug] = useState(true); // 🔍 청크 디버그 팝업 표시 여부
  const [chunkDebugData, setChunkDebugData] = useState(null); // 청크 디버그 데이터 (chunkDetails, referenceExample)
  const [showChunkDebugModal, setShowChunkDebugModal] = useState(false); // 모달 표시 상태
  const [showDocumentWizard, setShowDocumentWizard] = useState(false); // 🧙 문서 마법사
  const [wizardFirstPage, setWizardFirstPage] = useState(null); // 마법사용 첫 페이지 이미지
  const [centerViewTab, setCenterViewTab] = useState("document"); // 🎯 가운데 패널 탭: 'template' | 'document'
  const [schedulerEnabled, setSchedulerEnabled] = useState(false); // 🔄 배치 스케줄러 ON/OFF
  const [isBatchExtracting, setIsBatchExtracting] = useState(false); // 🔄 배치 추출 진행 중
  const batchAbortRef = useRef(false); // 배치 추출 취소 플래그

  // 🔄 파일명-템플릿 자동매핑 규칙 (localStorage 저장)
  const [templateMappingRules, setTemplateMappingRules] = useState(() => {
    try {
      const saved = localStorage.getItem("templateMappingRules");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [showMappingRulesEditor, setShowMappingRulesEditor] = useState(false);
  const [showMappingTableViewer, setShowMappingTableViewer] = useState(false); // 📋 데이터 변환표(DTO) 뷰어

  // 템플릿 로드
  // 템플릿 로드 함수 (컴포넌트 스코프로 이동)
  const fetchTemplates = async () => {
    try {
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_BASE_URL}/api/templates`, { headers });
      const data = await res.json();
      // 배열을 객체로 변환 (id 기준)
      const templateMap = {};
      data.forEach((tpl) => {
        templateMap[tpl.id] = tpl;
      });
      setTemplates(templateMap);

      // 🔧 기본 템플릿 자동 선택: localStorage 저장값 → isDefault → 첫 번째
      const savedDefault = localStorage.getItem("docuai_default_template");
      const defaultTemplate = data.find((tpl) => tpl.isDefault === true);

      if (!selectedOcrTemplate) {
        if (savedDefault && data.find((tpl) => tpl.name === savedDefault)) {
          setSelectedOcrTemplate(savedDefault);
        } else if (defaultTemplate) {
          setSelectedOcrTemplate(defaultTemplate.name);
        } else if (data.length > 0) {
          setSelectedOcrTemplate(data[0].name);
        }
      }
    } catch (error) {
      console.error("템플릿 로드 실패:", error);
    }
  };

  // 🔄 파일명-템플릿 자동매칭 함수
  const autoMatchTemplate = (filename) => {
    const lowerName = filename.toLowerCase().replace(/\.[^.]+$/, ""); // 확장자 제거

    // 1단계: 매핑 규칙 검사 (keywords → templateName)
    for (const rule of templateMappingRules) {
      const keywords = (rule.keywords || "")
        .split(",")
        .map((k) => k.trim().toLowerCase())
        .filter((k) => k);
      if (keywords.some((kw) => kw && lowerName.includes(kw))) {
        console.log(
          `📋 [AutoMatch] "${filename}" → "${rule.templateName}" (규칙: ${rule.keywords})`,
        );
        return rule.templateName;
      }
    }

    // 2단계: 템플릿명 토큰과 파일명 유사도 검사
    const templateList = Object.values(templates);
    for (const tpl of templateList) {
      if (tpl.name === "표준인보이스" || tpl.isDefault) continue; // 기본 템플릿은 폴백용이므로 스킵
      const tplTokens = tpl.name
        .split(/[\s\-_\/\\()]+/)
        .filter((k) => k.length >= 2);
      if (tplTokens.some((token) => lowerName.includes(token.toLowerCase()))) {
        console.log(`📋 [AutoMatch] "${filename}" → "${tpl.name}" (유사도)`);
        return tpl.name;
      }
    }

    // 3단계: 매칭 없음 → null (기본 템플릿 사용)
    return null;
  };

  // 매핑 규칙 저장
  const saveTemplateMappingRules = (rules) => {
    setTemplateMappingRules(rules);
    localStorage.setItem("templateMappingRules", JSON.stringify(rules));
  };

  // #admin 해시 변경 감지 — 이미 로드된 상태에서도 URL 변경으로 어드민 진입 가능
  useEffect(() => {
    const onHashChange = () => {
      if (
        window.location.hash === "#admin" &&
        currentPage !== "admin" &&
        currentPage !== "admin-login"
      ) {
        setCurrentPage("admin-login");
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [currentPage]);

  // 🛡️ AUIGrid 내부 ResizeObserver 가 throw 하는 self-triggered "parent id is not correct" 에러 swallow
  //    (라이브러리 자체 fail-safe 부재 — fortune-sheet/다른 컴포넌트 mount 시 일시적 reflow 동안 발생)
  //    React DEV overlay 만 막으면 됨. console.warn 으로 남겨 사일런트 안 함.
  useEffect(() => {
    const onErr = (e) => {
      const msg = e?.message || e?.error?.message || "";
      if (
        typeof msg === "string" &&
        msg.includes("parent id is not correct for AUIGrid")
      ) {
        console.warn("[AUIGrid] resize race condition (suppressed):", msg);
        e.preventDefault();
        e.stopImmediatePropagation();
        return false;
      }
    };
    window.addEventListener("error", onErr);
    return () => window.removeEventListener("error", onErr);
  }, []);

  // 템플릿 로드 Effect (TemplateManagement는 자체 fetch 사용)
  useEffect(() => {
    fetchTemplates();
  }, [API_BASE_URL, isLoggedIn]);

  // 🚀 [UX] 템플릿 관리 열릴 때 우측 패널 자동 확장 (Wide Mode) & 왼쪽 패널 축소 & 샘플 탭 선택
  // ⭐ 이전 상태 저장을 위한 ref (닫을 때 복원용)
  const prevPanelStateRef = useRef(null);

  useEffect(() => {
    if (showTemplateManagement) {
      // ⭐ 열기: checkedPages 저장 (인라인 토글이므로 패널 상태 변경 불필요)
      prevPanelStateRef.current = { checkedPages, selectedPageNumber };
      // 오른쪽 패널이 접혀있으면 자동 확장
      if (isRightPanelCollapsed) setIsRightPanelCollapsed(false);
    } else {
      // ⭐ 닫기: checkedPages 복원
      if (prevPanelStateRef.current) {
        if (
          prevPanelStateRef.current.checkedPages &&
          prevPanelStateRef.current.checkedPages.length > 0
        ) {
          const savedPages = prevPanelStateRef.current.checkedPages;
          setCheckedPages(savedPages);
          pendingCheckedPagesRef.current = [...savedPages];
        }
        if (prevPanelStateRef.current.selectedPageNumber) {
          setSelectedPageNumber(prevPanelStateRef.current.selectedPageNumber);
        }
        prevPanelStateRef.current = null;
      }
    }
  }, [showTemplateManagement]);

  // 🎯 [UX] 템플릿 선택 시 템플릿 탭 자동 전환
  useEffect(() => {
    if (selectedOcrTemplate) {
      console.log(
        "🎯 템플릿 선택됨 → 템플릿 탭으로 전환:",
        selectedOcrTemplate,
      );
      setCenterViewTab("document"); // 템플릿 뷰어 탭 제거됨
      // 오른쪽 패널도 자동 열기
      if (isRightPanelCollapsed) setIsRightPanelCollapsed(false);
    }
  }, [selectedOcrTemplate]);

  // 🎯 [UX] 파일 선택/업로드 시 탭 자동 전환
  useEffect(() => {
    if (selectedFileIndex !== null && fileList[selectedFileIndex]) {
      if (showTemplateManagement) {
        // 템플릿 관리가 열려 있을 때는 템플릿 탭으로 전환 (업로드된 파일 = 템플릿 샘플)
        console.log("📄 템플릿 관리 + 파일 업로드 → 템플릿 탭으로 전환");
        setCenterViewTab("document"); // 템플릿 뷰어 탭 제거됨
      } else {
        // 일반 모드에서는 문서 탭으로 전환
        console.log(
          "📄 파일 선택됨 → 문서 탭으로 전환:",
          fileList[selectedFileIndex]?.name,
        );
        setCenterViewTab("document");
      }
    }
  }, [selectedFileIndex, fileList, showTemplateManagement]);

  const [showBatchValidationGrid, setShowBatchValidationGrid] = useState(false); // 전체검증 그리드 표시 여부
  const [showMasterSettingsModal, setShowMasterSettingsModal] = useState(false); // 기초설정 모달 표시 여부
  const [showMigrationModal, setShowMigrationModal] = useState(false); // 📊 마이그레이션 도구 모달
  const [isDragging, setIsDragging] = useState(false);

  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(() => {
    try {
      return localStorage.getItem("leftPanelCollapsed") === "true";
    } catch {
      return false;
    }
  }); // 왼쪽 패널 접기 상태 (localStorage 유지)
  useEffect(() => {
    try {
      localStorage.setItem(
        "leftPanelCollapsed",
        isLeftPanelCollapsed.toString(),
      );
    } catch {
      /* ignore */
    }
  }, [isLeftPanelCollapsed]);
  // 📌 Claude-style 사이드바 열림/닫힘 (localStorage 유지)
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    try {
      return localStorage.getItem("sidebarOpen") !== "false";
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("sidebarOpen", isSidebarOpen.toString());
    } catch {
      /* ignore */
    }
  }, [isSidebarOpen]);
  // ↔ 사이드바 폭 드래그 리사이즈 (좌/중앙 세로선, localStorage 유지) — 검색 패널이
  //   사이드바로 이전(2026-07-07)된 뒤 좌측 폭 조절 요구를 사이드바 폭으로 승계.
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem("sidebarWidth"), 10);
      return Number.isFinite(v) ? Math.min(560, Math.max(200, v)) : 240;
    } catch {
      return 240;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("sidebarWidth", String(sidebarWidth));
    } catch {
      /* ignore */
    }
  }, [sidebarWidth]);
  const handleSidebarResizeStart = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = sidebarWidth;
    const onMove = (ev) => {
      setSidebarWidth(
        Math.min(560, Math.max(200, startW + (ev.clientX - startX))),
      );
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  // 📱 loginHS 모바일 셸 — 좌측 세로 레일 대신 하단 탭바 + 슬림 헤더(로고 + AI 어시스턴트만).
  //   데스크톱 웹은 문서인식 포함 전체 기능 유지 (모바일에서만 본연 기능 노출).
  const [viewportW, setViewportW] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1440,
  );
  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobileShell = IS_LOGINHS_PRODUCT && viewportW < 768;
  const [autoCollapseOnExtract, setAutoCollapseOnExtract] = useState(() => {
    const saved = localStorage.getItem("autoCollapseOnExtract");
    return saved !== null ? saved === "true" : true; // 기본값: ON (시인성 향상)
  }); // ⭐ 문서 추출 시 왼쪽 패널 자동 닫힘
  const [processingProgress, setProcessingProgress] = useState({
    currentPage: 0, // 현재 처리 중인 페이지
    totalPages: 0, // 전체 페이지 수
    step: "",
    fileName: "",
    percentage: 0,
    startTime: null, // 처리 시작 시간
    estimatedSeconds: 0, // 예상 시간 (초)
    // ⚡ 빠른 비교: 모델별 개별 진행상태
    model1: null, // { name, status: 'running'|'done', startTime, elapsed, percentage }
    model2: null, // { name, status: 'running'|'done', startTime, elapsed, percentage }
  }); // ⭐ 진행률 표시
  const [lastCompletionLog, setLastCompletionLog] = useState(null); // ⭐ 완료 메시지 (하단 바에 표시)
  const [isCenterPanelCollapsed, setIsCenterPanelCollapsed] = useState(false); // 가운데 패널 접기 상태
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false); // 오른쪽 패널 접기 상태 (초기: 열림)
  const [
    isVehicleRegistrationPanelCollapsed,
    setIsVehicleRegistrationPanelCollapsed,
  ] = useState(true); // 차량등록 패널 접기 상태 (기본: 접힘)
  const [layoutMode, setLayoutMode] = useState(() => {
    const saved = localStorage.getItem("layoutMode");
    return saved || "vertical"; // 'vertical' (기본) | 'horizontal' (상단 그리드 + 하단 PDF)
  }); // 🔧 레이아웃 모드
  // 🔤 글자 크기(접근성) — 앱이 px 인라인 스타일을 광범위하게 써서 rem 스케일 불가 →
  //   로그인 후 앱 루트에 CSS zoom 으로 화면 전체 배율 조정. 0.9/1.0(기본)/1.15.
  const [fontScale, setFontScale] = useState(() => {
    const v = parseFloat(localStorage.getItem("appFontScale"));
    return v === 0.9 || v === 1.15 ? v : 1.0; // 화이트리스트(보통=기본)
  });
  const [showFontSizeModal, setShowFontSizeModal] = useState(false);
  const applyFontScale = (scale) => {
    setFontScale(scale);
    try {
      localStorage.setItem("appFontScale", String(scale));
    } catch (_e) {}
  };
  const [leftPanelWidth, setLeftPanelWidth] = useState(230); // 왼쪽 패널 너비 (px)
  const [rightPanelWidth, setRightPanelWidth] = useState(() => {
    // 4:6 비율 (PDF 40% : 오른쪽 패널 60%)
    const available = window.innerWidth - 280; // 왼쪽 패널(~230) + 여백
    return Math.max(400, Math.round(available * 0.6));
  });

  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [isPromptPlaygroundOpen, setIsPromptPlaygroundOpen] = useState(true); // 기본적으로 열림 (사용자 요청)
  const [playgroundProcessing, setPlaygroundProcessing] = useState(false);
  const [isVehicleMasterModalOpen, setIsVehicleMasterModalOpen] =
    useState(false);
  const [highlightedBboxes, setHighlightedBboxes] = useState(null); // ⭐ 필드 클릭 시 하이라이트할 bbox 좌표

  // 🤖 AI 채팅 패널 상태 (PageBasedGridPanel 인라인 사이드바로 대체 — 툴바 버튼 토글용)
  const [showAIPanel, setShowAIPanel] = useState(false);
  // 🤖 PageBasedGridPanel에서 AI 패널 상태 변경 시 툴바 버튼 동기화
  useEffect(() => {
    const handler = (e) => setShowAIPanel(e.detail?.open ?? false);
    window.addEventListener("aiPanelStateChange", handler);
    return () => window.removeEventListener("aiPanelStateChange", handler);
  }, []);
  // ⭐ 리사이징 성능 최적화를 위한 ref
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const resizeAnimationFrameRef = useRef(null);

  // ⭐ [Drag & Drop] 전역 상태 및 핸들러 (Hook 규칙 준수를 위해 최상단 선언)
  const dragCounter = useRef(0);
  const addFilesRef = useRef(null);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      e.dataTransfer &&
      e.dataTransfer.types &&
      e.dataTransfer.types.includes("Files")
    ) {
      dragCounter.current++;
      if (dragCounter.current > 0) setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    const files = Array.from(e.dataTransfer.files);
    // 이미지, PDF, TIF/TIFF 파일 필터링 (TIF는 MIME이 빈 경우도 있어 확장자로도 체크)
    const imageExts = [
      ".tif",
      ".tiff",
      ".jpg",
      ".jpeg",
      ".png",
      ".bmp",
      ".gif",
      ".webp",
    ];
    const validFiles = files.filter((file) => {
      if (file.type.startsWith("image/") || file.type === "application/pdf")
        return true;
      const ext = ("." + file.name.split(".").pop()).toLowerCase();
      return imageExts.includes(ext);
    });

    if (validFiles.length > 0) {
      if (addFilesRef.current) addFilesRef.current(validFiles);
    } else {
      if (files.length > 0) {
        alert("이미지(JPG, PNG, TIF) 또는 PDF 파일만 업로드 가능합니다.");
      }
    }
  }, []);

  // ⭐ 전역 드래그 이벤트 리스너 (최상단 Effect)
  useEffect(() => {
    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("drop", handleDrop);
    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("drop", handleDrop);
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);
  const promptTextareaRef = useRef(null); // 프롬프트 textarea ref
  const scrollAnimationFrameRef = useRef(null); // 스크롤 애니메이션 프레임 ref
  const [promptEditorHeight, setPromptEditorHeight] = useState(200); // 프롬프트 편집기 높이 (px)
  const [isPromptEditorCollapsed, setIsPromptEditorCollapsed] = useState(true); // 프롬프트 편집기 접기 상태 (초기: 접힘)
  const [isPromptEditing, setIsPromptEditing] = useState(false); // 프롬프트 편집 모드
  const [editablePrompt, setEditablePrompt] = useState(""); // 편집 중인 프롬프트
  const [customPromptEnabled, setCustomPromptEnabled] = useState(false); // 커스텀 프롬프트 사용 여부
  const [defaultPromptLoaded, setDefaultPromptLoaded] = useState(false); // 기본 프롬프트 로드 여부
  const [promptType, setPromptType] = useState("ultra-light"); // 'ultra-light', 'classification', or 'unified'
  const [selectedModel, setSelectedModel] = useState(GEMINI_FLASH_MODEL); // 선택된 모델
  const [executionTime, setExecutionTime] = useState(null); // 실행 시간 (ms)
  const [tokenCount, setTokenCount] = useState(null); // 토큰 카운트

  // Resize state
  const [gridHeight, setGridHeight] = useState(50); // percentage
  const [isResizing, setIsResizing] = useState(false);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleMouseMove = (e) => {
    if (!isResizing) return;

    // Calculate percentage based on container height
    const containerHeight = e.currentTarget.clientHeight;
    const newHeight =
      ((e.clientY - e.currentTarget.getBoundingClientRect().top) /
        containerHeight) *
      100;

    // Limit between 20% and 80%
    if (newHeight >= 20 && newHeight <= 80) {
      setGridHeight(newHeight);
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  const [showSettings, setShowSettings] = useState(false); // 설정 모달 표시
  const [showPromptManager, setShowPromptManager] = useState(false); // 프롬프트 관리 모달 표시

  const [settingsModalTab, setSettingsModalTab] = useState("ocr"); // 설정 모달 탭: 'ocr', 'basic', 'db', 'finetuning'
  const [selectedDocType, setSelectedDocType] = useState(""); // ⭐ 프롬프트 편집용 문서 타입 선택

  // localStorage에서 설정 불러오기 (hooks 선언 전에 실행)
  const loadSettings = () => {
    try {
      const savedClassificationModel = localStorage.getItem(
        "classificationFlashModel",
      );
      const savedTemplateExtractionModel = localStorage.getItem(
        "templateExtractionModel",
      ); // ⭐ 템플릿 추출 모델
      const savedValidationModels = localStorage.getItem(
        "selectedValidationModels",
      );
      const savedOcrEngine = localStorage.getItem("ocrEngine");
      const savedDeepseekMode = localStorage.getItem("deepseekMode");
      const savedGrounding = localStorage.getItem("grounding");
      const savedDatalabBoundingBox =
        localStorage.getItem("datalabBoundingBox");
      const savedVerifyEnabled = localStorage.getItem("verifyEnabled");
      const savedVerificationMode = localStorage.getItem("verificationMode");
      const savedOcrEnabled = localStorage.getItem("ocrEnabled");

      const savedThinkingBudget = localStorage.getItem("thinkingBudget");
      const savedThinkingLevel = localStorage.getItem("thinkingLevel");

      // ⭐ 마이그레이션: 기존 설정이 있으면 파싱하되, 구형 모델은 신형으로 교체
      const parsedValidationModels = normalizeGeminiGenerationModels(
        savedValidationModels ? JSON.parse(savedValidationModels) : [],
      );

      return {
        classificationFlashModel: savedClassificationModel || "2.0",
        templateExtractionModel: normalizeGeminiGenerationModel(
          savedTemplateExtractionModel,
        ),
        selectedValidationModels: parsedValidationModels,
        ocrEngine: savedOcrEngine || "gemini",
        deepseekMode: savedDeepseekMode || "optimized",
        grounding: savedGrounding !== null ? savedGrounding === "true" : true,
        datalabBoundingBox:
          savedDatalabBoundingBox !== null
            ? savedDatalabBoundingBox === "true"
            : true,
        thinkingBudget:
          savedThinkingBudget !== null ? parseInt(savedThinkingBudget) : -1,
        thinkingLevel: savedThinkingLevel || "HIGH",
        verifyEnabled:
          savedVerifyEnabled !== null ? savedVerifyEnabled === "true" : false,
        verificationMode: savedVerificationMode || "parallel",
        ocrEnabled:
          savedOcrEnabled !== null ? savedOcrEnabled === "true" : false,
      };
    } catch (error) {
      console.error("설정 불러오기 실패:", error);
      return {
        classificationFlashModel: "2.0",
        templateExtractionModel: GEMINI_FLASH_MODEL,
        selectedValidationModels: [GEMINI_FLASH_MODEL],
        ocrEngine: "gemini",
        deepseekMode: "optimized",
        grounding: true,
        datalabBoundingBox: true,
        thinkingBudget: -1,
        thinkingLevel: "HIGH",
        verifyEnabled: false,
        verificationMode: "parallel",
        ocrEnabled: false,
      };
    }
  };

  const initialSettings = loadSettings();

  // 💾 서버 설정 저장/로드 헬퍼 (DB 영구 저장 — 사용자 계정에 연동)
  const savePreferencesToServer = async (prefs) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return; // 비로그인 상태면 localStorage만 사용
      await fetch(`${API_BASE_URL}/api/user/preferences`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ preferences: prefs }),
      });
    } catch (err) {
      console.warn("⚠️ 서버 설정 저장 실패 (오프라인):", err.message);
    }
  };

  const loadPreferencesFromServer = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const res = await fetch(`${API_BASE_URL}/api/user/preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success && data.preferences) {
        console.log("📋 [ServerPrefs] 서버에서 설정 로드 완료");
        return data.preferences;
      }
    } catch (err) {
      console.warn("⚠️ 서버 설정 로드 실패 (오프라인):", err.message);
    }
    return null;
  };

  // 검증 모델 설정 (새로운 구조 - 최대 2개 선택 가능)
  const [selectedValidationModels, setSelectedValidationModels] = useState(
    initialSettings.selectedValidationModels,
  );

  // 🔄 검증 모드 토글: 체크 → 교차검증(이중 모델), 해제 → 단일 모드
  const [verifyEnabled, setVerifyEnabled] = useState(
    initialSettings.verifyEnabled,
  );
  // 🔍 검증 방식: 'parallel' (빠른 비교) / 'sequential' (정밀 검증)
  const [verificationMode, setVerificationMode] = useState(
    initialSettings.verificationMode,
  );
  // 🆕 OCR 모드 토글: 체크 → PaddleOCR 1차 추출 결과 추가 표시
  const [ocrEnabled, setOcrEnabled] = useState(initialSettings.ocrEnabled);
  // 🆕 PaddleOCR 결과 저장 ({fileName: {doc_type, fields, ocr_ms, overall_confidence, ...}})
  const [paddleOcrResults, setPaddleOcrResults] = useState({});
  // 🆕 Donut 모드 토글: 체크 → Donut 1차 모델 추출 결과 추가 표시 (PaddleOCR과 별개, 성능 비교용)
  const [donutEnabled, setDonutEnabled] = useState(false);
  // 🆕 Donut 결과 저장 ({fileName: {doc_type, fields, elapsed_ms, model: "donut-v1", ...}})
  const [donutResults, setDonutResults] = useState({});
  // 🆕 vLLM max_tokens 설정 모달
  const [vllmSettingsOpen, setVllmSettingsOpen] = useState(false);
  // 🆕 평가셋 라벨링 모달
  const [evalLabelingOpen, setEvalLabelingOpen] = useState(false);
  // ⭐ 실제 추출 시 전송할 모델 리스트 (검증 모드에 따라 결정)
  const effectiveValidationModels = verifyEnabled
    ? selectedValidationModels.length >= 2
      ? selectedValidationModels.slice(0, 2) // 병렬: 사용자가 선택한 2개 모델
      : [...selectedValidationModels, GEMINI_FLASH_MODEL].slice(0, 2) // 1개만 선택 시 Flash 보조
    : [selectedValidationModels[0] || GEMINI_FLASH_MODEL]; // 단일: 첫 번째 선택 모델

  // 레거시 호환용 (기존 코드와 호환)
  const [validationModel, setValidationModel] = useState(GEMINI_FLASH_MODEL);
  const [useClaudeForValidation, setUseClaudeForValidation] = useState(false);
  const [speedMode, setSpeedMode] = useState(true);

  const [classificationFlashModel, setClassificationFlashModel] = useState(
    initialSettings.classificationFlashModel,
  );
  const [templateExtractionModel, setTemplateExtractionModel] = useState(
    initialSettings.templateExtractionModel,
  ); // ⭐ 템플릿 추출 모델
  const [ocrEngine, setOcrEngine] = useState(initialSettings.ocrEngine);
  const [deepseekMode, setDeepseekMode] = useState(
    initialSettings.deepseekMode || "optimized",
  );
  const [deepseekServerUrl, setDeepseekServerUrl] = useState(
    localStorage.getItem("deepseekServerUrl") || "http://localhost:8001",
  );
  const [grounding, setGrounding] = useState(initialSettings.grounding);
  const [datalabBoundingBox, setDatalabBoundingBox] = useState(
    initialSettings.datalabBoundingBox,
  );
  // ⭐ OCR 설정 (Hybrid Mode)
  const [enableHybridOCR, setEnableHybridOCR] = useState(() => {
    const saved = localStorage.getItem("enableHybridOCR");
    return saved !== null ? saved === "true" : true; // Default true
  });
  // ⭐ 문서 분류 활성화 설정
  const [enableClassification, setEnableClassification] = useState(() => {
    const saved = localStorage.getItem("enableClassification");
    return saved !== null ? saved === "true" : true; // Default true (분류 사용)
  });
  // ⭐ ThinkingConfig 설정 (Gemini 2.5/3.0)
  const [thinkingBudget, setThinkingBudget] = useState(
    initialSettings.thinkingBudget,
  ); // Gemini 2.5: -1(동적), 0(비활성화), 1024~32768
  const [thinkingLevel, setThinkingLevel] = useState(
    initialSettings.thinkingLevel,
  ); // Gemini 3.7: 'HIGH', 'LOW'
  // ⭐ AI 어시스턴트 설정
  const [aiAssistantModel, setAiAssistantModel] = useState(() =>
    normalizeGeminiGenerationModel(localStorage.getItem("aiAssistantModel")),
  );
  const DEFAULT_AI_QUICK_COMMANDS = [
    {
      category: "무역·관세",
      items: [
        "인코텀즈 추출",
        "HS코드 10자리 생성",
        "HS코드 검증",
        "원산지 추출",
      ],
    },
    {
      category: "데이터 정리",
      items: ["대소문자 변환", "날짜 형식 통일", "열 분리", "빈 셀 채우기"],
    },
    {
      category: "필터·삭제",
      items: ["조건부 행 삭제", "중복 제거", "특정 값만 유지"],
    },
  ];
  const [aiQuickCommands, setAiQuickCommands] = useState(() => {
    try {
      const saved = localStorage.getItem("aiQuickCommands");
      return saved ? JSON.parse(saved) : DEFAULT_AI_QUICK_COMMANDS;
    } catch {
      return DEFAULT_AI_QUICK_COMMANDS;
    }
  });
  const [showBoundingBoxes, setShowBoundingBoxes] = useState(true);
  const [showDetailMode, setShowDetailMode] = useState(false);
  const [highlightedField, setHighlightedField] = useState(null);
  const [highlightedFieldValue, setHighlightedFieldValue] = useState(null);
  const [clickedField, setClickedField] = useState(null);
  const [groundingHighlight, setGroundingHighlight] = useState(null); // ⭐ [Grounding] LangExtract 필드 위치 { fieldKey, page, coordinates, confidence }
  const [useCanvasPDF, setUseCanvasPDF] = useState(true);
  const [useGridMode, setUseGridMode] = useState(true);
  const [processingLogs, setProcessingLogs] = useState([]);

  const [showLogs, setShowLogs] = useState(false);

  // 로그 추가 헬퍼 함수 (moved up)
  const addLog = (message, type = "info") => {
    const timestamp = new Date().toLocaleTimeString();
    setProcessingLogs((prev) => [...prev, { message, type, timestamp }]);
  };

  // 이미지 파일 여부 판별 (TIF/TIFF/JPG/PNG 등)
  const isImageFile = (fileName) => {
    const ext = fileName.toLowerCase().split(".").pop();
    return ["tif", "tiff", "jpg", "jpeg", "png", "bmp", "gif", "webp"].includes(
      ext,
    );
  };

  // 이미지 → PDF 변환 (백엔드 호출)
  const convertImageToPdf = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(`${API_BASE_URL}/api/convert-to-pdf`, {
      method: "POST",
      body: formData,
    });
    if (!response.ok) {
      const err = await response
        .json()
        .catch(() => ({ error: "Unknown error" }));
      throw new Error(err.error || `HTTP ${response.status}`);
    }
    const pdfBlob = await response.blob();
    const pdfName = file.name.replace(/\.[^.]+$/, ".pdf");
    return new File([pdfBlob], pdfName, { type: "application/pdf" });
  };

  // 파일 추가 공통 함수 (moved up)
  const addFiles = async (files) => {
    console.log(
      "🚀 [addFiles] 호출됨, files:",
      files.length,
      files.map((f) => f.name),
    );

    // 📁 파일명 중복 방지: Windows 스타일 "파일 (1).pdf", "파일 (2).pdf"
    const existingNames = new Set(fileList.map((f) => f.name));
    const batchNames = new Set(); // 같은 배치 내 중복도 처리

    const deduplicateName = (originalName) => {
      if (!existingNames.has(originalName) && !batchNames.has(originalName)) {
        batchNames.add(originalName);
        return originalName;
      }
      const dotIdx = originalName.lastIndexOf(".");
      const baseName =
        dotIdx > 0 ? originalName.substring(0, dotIdx) : originalName;
      const ext = dotIdx > 0 ? originalName.substring(dotIdx) : "";
      // 기존 "(N)" 패턴 제거 후 카운트
      const cleanBase = baseName.replace(/\s*\(\d+\)$/, "");
      let counter = 1;
      let newName;
      do {
        newName = `${cleanBase} (${counter})${ext}`;
        counter++;
      } while (existingNames.has(newName) || batchNames.has(newName));
      batchNames.add(newName);
      return newName;
    };

    const newFiles = files.map((file, index) => {
      const isPDF = file.name.toLowerCase().endsWith(".pdf");
      const isImg = isImageFile(file.name);
      const uniqueName = deduplicateName(file.name);
      const matched = autoMatchTemplate(file.name);
      if (uniqueName !== file.name) {
        console.log(
          `📁 [addFiles] 파일명 중복 → "${file.name}" → "${uniqueName}"`,
        );
      }
      return {
        id: Date.now() + index,
        name: uniqueName,
        originalName: file.name,
        file: file,
        preview: URL.createObjectURL(file),
        status: "pending", // pending, processing, completed, error
        extractedData: null,
        error: null,
        checked: true, // ⭐ 업로드 시 자동 선택
        isPDF: isPDF,
        isConverting: isImg && !isPDF, // 이미지→PDF 변환 중 플래그
        totalPages: null, // 아래에서 PDF 페이지 수 감지 후 업데이트
        pageResults: null, // SSE로 받아서 업데이트
        matchedTemplate: matched || "표준인보이스", // 🔄 자동매칭 or 기본 '표준인보이스'
      };
    });

    // ⭐ 기존 파일 전체 체크 해제 후 새 파일만 체크된 상태로 추가
    setFileList((prev) => [
      ...prev.map((f) => ({ ...f, checked: false })),
      ...newFiles,
    ]);

    // ⭐ 새로 추가된 첫 번째 파일 자동 선택 & 뷰잉
    const newFirstIndex = fileList.length; // 기존 파일 개수 = 새 파일의 시작 인덱스
    setSelectedFileIndex(newFirstIndex);
    addLog(`📌 ${newFiles[0].name} 자동 선택됨`);

    // 📊 Excel/Word 파일 → 미리보기용 사전 업로드 (SpreadJS 뷰어 즉시 표시용)
    newFiles.forEach(async (fileObj) => {
      const lname = (fileObj.name || "").toLowerCase();
      const isOfficeFile = /\.(xlsx|xlsm|xls|docx|doc)$/i.test(lname);
      if (!isOfficeFile) return;
      try {
        setFileList((prev) =>
          prev.map((f) =>
            f.id === fileObj.id ? { ...f, previewUploading: true } : f,
          ),
        );
        const fd = new FormData();
        fd.append("file", fileObj.file);
        const resp = await fetch(`${API_BASE_URL}/api/files/upload-temp`, {
          method: "POST",
          body: fd,
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        if (json?.fileId) {
          setFileList((prev) =>
            prev.map((f) =>
              f.id === fileObj.id
                ? { ...f, uploadedFileId: json.fileId, previewUploading: false }
                : f,
            ),
          );
          addLog(`📊 ${fileObj.name} 미리보기 준비 완료`);
        }
      } catch (e) {
        console.warn(`⚠️ [pre-upload] ${fileObj.name} 실패:`, e.message);
        setFileList((prev) =>
          prev.map((f) =>
            f.id === fileObj.id ? { ...f, previewUploading: false } : f,
          ),
        );
      }
    });

    // 🖼️ 이미지 파일 → PDF 변환 (비동기, 백엔드)
    newFiles.forEach(async (fileObj) => {
      console.log(
        `🔍 [addFiles] ${fileObj.name} isConverting=${fileObj.isConverting} isPDF=${fileObj.isPDF} type=${fileObj.file.type}`,
      );
      if (fileObj.isConverting) {
        try {
          console.log(`🔄 [convert] ${fileObj.name} → PDF 변환 시작...`);
          addLog(`🔄 ${fileObj.name} → PDF 변환 중...`);
          const pdfFile = await convertImageToPdf(fileObj.file);
          const pdfUrl = URL.createObjectURL(pdfFile);

          // PDF 페이지 수 감지
          let numPages = 1;
          try {
            const ab = await pdfFile.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: ab }).promise;
            numPages = pdf.numPages;
          } catch (e) {
            /* fallback: 1 page */
          }

          // 파일 객체를 변환된 PDF로 교체
          setFileList((prev) =>
            prev.map((f) =>
              f.id === fileObj.id
                ? {
                    ...f,
                    file: pdfFile,
                    preview: pdfUrl,
                    isPDF: true,
                    isConverting: false,
                    totalPages: numPages,
                  }
                : f,
            ),
          );
          addLog(`✅ ${fileObj.name} → PDF 변환 완료 (${numPages}페이지)`);

          // 📄 변환 완료 후 DB 저장
          try {
            const savedDoc = await saveUploadToDB({
              fileName: fileObj.name,
              fileType: "pdf",
              pageCount: numPages,
              templateType: fileObj.matchedTemplate,
              file: pdfFile,
            });
            if (savedDoc?.id) {
              setFileList((prev) =>
                prev.map((f) =>
                  f.id === fileObj.id ? { ...f, _documentId: savedDoc.id } : f,
                ),
              );
            }
          } catch (dbErr) {
            console.warn(
              `⚠️ [convert DB] ${fileObj.name} 저장 실패:`,
              dbErr.message,
            );
          }
        } catch (error) {
          console.error(`❌ 이미지→PDF 변환 실패: ${fileObj.name}`, error);
          addLog(`❌ ${fileObj.name} PDF 변환 실패: ${error.message}`);
          setFileList((prev) =>
            prev.map((f) =>
              f.id === fileObj.id
                ? { ...f, isConverting: false, conversionError: error.message }
                : f,
            ),
          );
        }
      }
    });

    // 📄 PDF 파일의 페이지 수 감지 (비동기)
    newFiles.forEach(async (fileObj) => {
      if (fileObj.isPDF) {
        try {
          const arrayBuffer = await fileObj.file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          const numPages = pdf.numPages;
          console.log(
            `📄 [PDF 페이지 감지] ${fileObj.name}: ${numPages}페이지`,
          );

          // 파일 리스트 업데이트
          setFileList((prev) =>
            prev.map((f) =>
              f.id === fileObj.id ? { ...f, totalPages: numPages } : f,
            ),
          );
        } catch (error) {
          console.error(`❌ PDF 페이지 수 감지 실패: ${fileObj.name}`, error);
        }
      }
    });

    // 📄 업로드 즉시 DB 저장 (비동기, 실패해도 로컬 동작에 영향 없음)
    newFiles.forEach(async (fileObj) => {
      // 이미지→PDF 변환 중인 파일은 변환 완료 후 저장 (위의 convert 로직에서 처리)
      if (fileObj.isConverting) return;
      try {
        // 파일 타입을 짧은 키로 정규화 (DB 컬럼 길이 제약 회피)
        const _lname = (fileObj.name || "").toLowerCase();
        let _fileTypeShort = "unknown";
        if (fileObj.isPDF || _lname.endsWith(".pdf")) _fileTypeShort = "pdf";
        else if (/\.(xlsx|xlsm)$/i.test(_lname)) _fileTypeShort = "xlsx";
        else if (/\.xls$/i.test(_lname)) _fileTypeShort = "xls";
        else if (/\.docx$/i.test(_lname)) _fileTypeShort = "docx";
        else if (/\.doc$/i.test(_lname)) _fileTypeShort = "doc";
        else if (/\.(png|jpe?g|tiff?|bmp|webp|gif)$/i.test(_lname))
          _fileTypeShort = "image";
        const savedDoc = await saveUploadToDB({
          fileName: fileObj.name,
          fileType: _fileTypeShort,
          pageCount: fileObj.totalPages || 1,
          templateType: fileObj.matchedTemplate,
          file: fileObj.file,
        });
        if (savedDoc?.id) {
          setFileList((prev) =>
            prev.map((f) =>
              f.id === fileObj.id ? { ...f, _documentId: savedDoc.id } : f,
            ),
          );
        }
      } catch (err) {
        console.warn(
          `⚠️ [addFiles DB] ${fileObj.name} 저장 실패:`,
          err.message,
        );
      }
    });

    // Auto-start trigger
    if (isAutoExtractionEnabled) {
      setShouldAutoStart(true);
    }
  };

  // ⭐ 전역 드래그 핸들러와 addFiles 함수 연결 (addFiles 정의 후 실행)
  useEffect(() => {
    addFilesRef.current = addFiles;
  }, [addFiles]);

  // 🔥 SpreadJS iframe prefetch (페이지 진입 시 라이브러리 캐시 워밍업)
  //    첫 xlsx 업로드 시 ~5MB 다운로드 지연 회피
  useEffect(() => {
    if (typeof window === "undefined") return;
    const _id = "spreadjs-prefetch-iframe";
    if (document.getElementById(_id)) return;
    const _ifr = document.createElement("iframe");
    _ifr.id = _id;
    _ifr.src =
      "https://lcsv2.tlogin.net/docuai/excludeExcelViewer.do?doc_path=tmp";
    _ifr.style.cssText =
      "position:absolute;width:0;height:0;border:0;visibility:hidden;left:-9999px;top:-9999px;";
    _ifr.tabIndex = -1;
    _ifr.setAttribute("aria-hidden", "true");
    _ifr.setAttribute("sandbox", "allow-scripts allow-same-origin");
    document.body.appendChild(_ifr);
    return () => {
      try {
        _ifr.remove();
      } catch (_e) {}
    };
  }, []);

  // 📊 xlsx/docx 선택 시 lazy 사전 업로드 (history 에서 선택된 파일이 uploadedFileId 없을 때)
  useEffect(() => {
    if (selectedFileIndex === null) return;
    const sel = fileList[selectedFileIndex];
    if (!sel) return;
    const lname = (
      sel.file?.name ||
      sel.originalName ||
      sel.name ||
      ""
    ).toLowerCase();
    const lmt = (sel.file?.type || "").toLowerCase();
    const isOffice =
      /\.(xlsx|xlsm|xls|docx|doc)$/i.test(lname) ||
      lmt.includes("spreadsheetml") ||
      lmt.includes("ms-excel") ||
      lmt.includes("wordprocessingml") ||
      lmt === "application/msword";
    if (!isOffice) return;
    if (sel.uploadedFileId) return; // 이미 있음
    if (sel.previewUploading) return; // 이미 진행 중
    if (!sel.file) return; // history 복원 파일은 file 객체 없음 → 재업로드 안내만
    // file 객체가 있으면 사전 업로드 트리거
    (async () => {
      try {
        setFileList((prev) =>
          prev.map((f) =>
            f.id === sel.id ? { ...f, previewUploading: true } : f,
          ),
        );
        const fd = new FormData();
        fd.append("file", sel.file);
        const resp = await fetch(`${API_BASE_URL}/api/files/upload-temp`, {
          method: "POST",
          body: fd,
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        if (json?.fileId) {
          setFileList((prev) =>
            prev.map((f) =>
              f.id === sel.id
                ? { ...f, uploadedFileId: json.fileId, previewUploading: false }
                : f,
            ),
          );
        }
      } catch (e) {
        console.warn(`⚠️ [lazy pre-upload] ${sel.name} 실패:`, e.message);
        setFileList((prev) =>
          prev.map((f) =>
            f.id === sel.id ? { ...f, previewUploading: false } : f,
          ),
        );
      }
    })();
  }, [selectedFileIndex, fileList, API_BASE_URL]);

  // Auto-start Extraction State
  // Auto-start Extraction State
  const [isAutoExtractionEnabled, setIsAutoExtractionEnabled] = useState(() => {
    return localStorage.getItem("isAutoExtractionEnabled") === "true";
  });
  const [shouldAutoStart, setShouldAutoStart] = useState(false);

  // 🚀 처리 취소 기능 (Google AI Studio 스타일)
  const [isCancelled, setIsCancelled] = useState(false);
  const abortControllerRef = useRef(null);

  // 🎯 OCR 파이프라인 상태 (메인 화면 bbox 시각화)
  const [ocrLabels, setOcrLabels] = useState([]); // PaddleOCR bbox + 필드 매핑
  const [correctedImageUrl, setCorrectedImageUrl] = useState(null); // 보정된 이미지 URL
  const [ocrPipelineStep, setOcrPipelineStep] = useState(""); // 현재 단계 표시
  const [ocrImageSize, setOcrImageSize] = useState({ width: 0, height: 0 }); // OCR 이미지 크기

  // 📄 멀티페이지 PDF 상태
  const [pdfPageResults, setPdfPageResults] = useState([]); // 모든 페이지 OCR 결과
  const [currentPdfPage, setCurrentPdfPage] = useState(1); // 현재 표시 중인 페이지 (1-indexed)
  const [totalPdfPages, setTotalPdfPages] = useState(1); // 총 페이지 수

  const logsEndRef = useRef(null);
  const lastClickedPageRef = useRef(null);
  const prevSelectedFileIndexRef = useRef(null);
  const pendingCheckedPagesRef = useRef(null); // 🔧 추출 완료 후 checkedPages 복원용
  // 📦 증분 추출 캐시: 파일별 추출 결과를 누적 저장 (React state와 독립)
  // 구조: { [fileName]: { extractionBatches: {...}, groundedFields: {...}, pageDimensions: {...} } }
  // IndexedDB에 저장하여 새로고침 후에도 유지 (용량 제한 수백 MB)
  const extractionCacheRef = useRef({});
  const saveExtractionCache = useCallback(() => {
    saveExtractionCacheDB(extractionCacheRef.current);
  }, []);
  // 📦 IndexedDB에서 캐시 비동기 복원 (컴포넌트 마운트 시)
  useEffect(() => {
    (async () => {
      // localStorage → IndexedDB 마이그레이션 (기존 사용자)
      const migrated = await migrateFromLocalStorage();
      if (migrated) {
        extractionCacheRef.current = migrated;
        return;
      }
      // IndexedDB에서 로드
      const cached = await loadExtractionCacheDB();
      if (cached && Object.keys(cached).length > 0) {
        extractionCacheRef.current = cached;
      }
    })();
  }, []);

  // ⚠️ 모든 useEffect hooks도 조건부 return 전에 선언되어야 합니다
  // validationModel 변경 시 useClaudeForValidation 자동 업데이트
  useEffect(() => {
    setUseClaudeForValidation(validationModel.startsWith("claude"));
  }, [validationModel]);

  // 로그가 추가될 때마다 자동 스크롤
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [processingLogs]);

  // 페이지 번호 변경 추적 (디버깅용)
  useEffect(() => {
    console.log(`🔄 [State] selectedPageNumber 변경됨: ${selectedPageNumber}`);
  }, [selectedPageNumber]);

  // 파일 선택 시 자동으로 프롬프트 로드
  useEffect(() => {
    const loadDefaultPrompt = async () => {
      if (selectedFileIndex !== null && !editablePrompt) {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/prompts?type=${promptType}`,
          );
          const data = await response.json();
          if (data.success) {
            setEditablePrompt(data.prompt);
            setDefaultPromptLoaded(true);
          }
        } catch (err) {
          console.error("프롬프트 로드 실패:", err);
        }
      }
    };
    loadDefaultPrompt();
  }, [selectedFileIndex, promptType, editablePrompt, API_BASE_URL]);

  // Auto-start Extraction Effect
  useEffect(() => {
    if (shouldAutoStart && !loading && fileList.length > 0) {
      console.log("🚀 자동 추출 시작 트리거됨");
      setShouldAutoStart(false);
      handleBatchFullProcess();
    }
  }, [shouldAutoStart, loading, fileList]);

  // Save Auto-start setting
  useEffect(() => {
    localStorage.setItem("isAutoExtractionEnabled", isAutoExtractionEnabled);
  }, [isAutoExtractionEnabled]);

  // ⭐ 모든 설정 자동 저장 (Auto-persistence)
  useEffect(() => {
    localStorage.setItem("classificationFlashModel", classificationFlashModel);
  }, [classificationFlashModel]);

  // 💾 서버 자동 저장용 ref (초기 로드 시 skip)
  const prefsInitializedRef = useRef(false);
  const prefsSaveTimerRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(
      "selectedValidationModels",
      JSON.stringify(selectedValidationModels),
    );
    // 💾 디바운스 서버 저장 (초기 로드 skip, 1초 대기)
    if (prefsInitializedRef.current) {
      clearTimeout(prefsSaveTimerRef.current);
      prefsSaveTimerRef.current = setTimeout(() => {
        savePreferencesToServer({
          selectedValidationModels,
          classificationFlashModel,
          templateExtractionModel,
          ocrEngine,
          thinkingBudget,
          thinkingLevel,
          verifyEnabled,
        });
      }, 1000);
    }
  }, [selectedValidationModels]);

  // 초기 서버 로드 완료 후 자동 저장 활성화
  useEffect(() => {
    const timer = setTimeout(() => {
      prefsInitializedRef.current = true;
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  // 🤖 AI 어시스턴트 모델 → 서버 동기화 (마운트 시)
  useEffect(() => {
    const m = normalizeGeminiGenerationModel(
      localStorage.getItem("aiAssistantModel"),
    );
    localStorage.setItem("aiAssistantModel", m);
    fetch(`${API_BASE_URL}/api/ai/set-model`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ model: m }),
    }).catch(() => {});
  }, [API_BASE_URL]);

  useEffect(() => {
    localStorage.setItem("ocrEngine", ocrEngine);
  }, [ocrEngine]);

  useEffect(() => {
    localStorage.setItem("deepseekMode", deepseekMode);
  }, [deepseekMode]);

  useEffect(() => {
    localStorage.setItem("deepseekServerUrl", deepseekServerUrl);
  }, [deepseekServerUrl]);

  useEffect(() => {
    localStorage.setItem("grounding", grounding.toString());
  }, [grounding]);

  useEffect(() => {
    localStorage.setItem("datalabBoundingBox", datalabBoundingBox.toString());
  }, [datalabBoundingBox]);

  useEffect(() => {
    localStorage.setItem("thinkingBudget", thinkingBudget.toString());
  }, [thinkingBudget]);

  useEffect(() => {
    localStorage.setItem("thinkingLevel", thinkingLevel);
  }, [thinkingLevel]);

  // DB 연결 상태 확인 (로그인 후)
  useEffect(() => {
    if (!isLoggedIn) return;

    const fetchDbStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/db/status`);
        const data = await response.json();

        setDbStatus({
          connected: data.connected,
          server: data.server,
          database: data.database,
          loading: false,
        });
      } catch (error) {
        console.error("DB 상태 확인 실패:", error);
        setDbStatus({
          connected: false,
          server: null,
          database: null,
          loading: false,
        });
      }
    };

    // 초기 로드
    fetchDbStatus();

    // 30초마다 DB 상태 갱신 (사용자 요청으로 비활성화 - 2025.12.19)
    // const intervalId = setInterval(fetchDbStatus, 30000);
    // return () => clearInterval(intervalId);
    return () => {};
  }, [isLoggedIn, API_BASE_URL]);

  // 🚀 외부(모달)에서 파일 선택 시 뷰어 업데이트
  const handleExternalFileSelect = (file) => {
    if (!file) return;

    // 🔧 File 객체인지 wrapper 객체인지 구분 (duck typing 사용)
    const hasFileProperties =
      file &&
      typeof file === "object" &&
      (file.name || file.size) &&
      (typeof file.arrayBuffer === "function" ||
        typeof file.slice === "function");

    const actualFile = hasFileProperties ? file : file?.file;
    const fileName = file.name || actualFile?.name;
    const fileSize = file.size || actualFile?.size;

    if (!actualFile && !hasFileProperties) {
      console.warn("⚠️ handleExternalFileSelect: Invalid file object", file);
      return;
    }

    // 이미 리스트에 있는지 확인 (이름과 크기로 대략적 중복 체크)
    const existingIndex = fileList.findIndex(
      (f) => f.name === fileName && (f.file?.size || f.size) === fileSize,
    );

    if (existingIndex >= 0) {
      setSelectedFileIndex(existingIndex);
      return;
    }

    // 새 파일 추가
    const objectUrl = URL.createObjectURL(actualFile);
    const newFile = {
      id: Date.now(),
      name: fileName,
      file: actualFile,
      preview: objectUrl,
      type: actualFile.type,
      status: "ready",
      checked: true,
      extractedData: null,
    };

    setFileList((prev) => [...prev, newFile]);
    setSelectedFileIndex(fileList.length); // 마지막 인덱스 (현재 길이 = 새 인덱스)
  };

  // 파일 선택이 바뀔 때 페이지 체크 상태 초기화
  // 🔧 FIX: fileList 변경(추출 결과 업데이트)으로 인한 재실행 시에는 checkedPages를 리셋하지 않음
  useEffect(() => {
    if (selectedFileIndex !== null) {
      const selectedFile = fileList[selectedFileIndex];
      const isNewFileSelection =
        prevSelectedFileIndexRef.current !== selectedFileIndex;

      // 🔧 FIX: 새 파일 전환 시 totalPdfPages를 파일 자체 정보로 즉시 리셋 (이전 파일 잔여값 방지)
      // React setState는 비동기이므로, 클로저 내에서는 로컬 변수 safeTotalPages를 사용
      const safeTotalPages = isNewFileSelection
        ? selectedFile?.totalPages || 1
        : totalPdfPages;
      if (isNewFileSelection) {
        setTotalPdfPages(safeTotalPages);
        console.log(
          "📄 [파일 전환] totalPdfPages 리셋:",
          safeTotalPages,
          "(이전값:",
          totalPdfPages,
          "제거)",
        );
      }

      if (selectedFile?.pageResults && selectedFile.pageResults.length > 0) {
        if (isNewFileSelection) {
          // 🔄 새 파일 선택: 전체 페이지 체크 초기화
          // 🔧 FIX: 실제 PDF 페이지 수(totalPages) 우선 사용 — Gemini phantom 페이지 방어
          // totalPages가 확정되면 pageResults.length/maxPageNumber보다 우선 (phantom 방지)
          const resolvedTotalPages = selectedFile.totalPages
            ? selectedFile.totalPages
            : Math.max(
                ...selectedFile.pageResults.map((p) => p.pageNumber || 0),
                0,
                selectedFile.pageResults.length,
              );
          const allPageNumbers = Array.from(
            { length: resolvedTotalPages },
            (_, i) => i + 1,
          );
          console.log("📄 [새 파일] 전체 페이지 체크 초기화:", {
            selectedFileIndex,
            fileName: selectedFile.name,
            resolvedTotalPages,
            allPageNumbers,
          });
          setCheckedPages(allPageNumbers);
          setSelectedPageNumber(allPageNumbers[0] || 1);
          lastClickedPageRef.current = allPageNumbers[0] || 1;
        } else {
          // ✅ 같은 파일 — fileList 업데이트(추출 완료 등)로 인한 재실행
          // 🔧 FIX: 3단계 복원: pendingRef → selectedFile.selectedPages → 유지
          if (
            pendingCheckedPagesRef.current &&
            pendingCheckedPagesRef.current.length > 0
          ) {
            const pendingPages = pendingCheckedPagesRef.current;
            pendingCheckedPagesRef.current = null; // 한 번만 복원
            console.log(
              "📄 [같은 파일] checkedPages 복원 (pendingRef):",
              pendingPages,
            );
            setCheckedPages([...pendingPages]);
          } else if (
            selectedFile.selectedPages &&
            selectedFile.selectedPages.length > 0
          ) {
            // 🔧 FIX: pendingRef가 소비된 후에도, selectedPages가 있고 현재 checkedPages가
            // selectedPages보다 적으면 복원 (useEffect 재실행으로 인한 리셋 방지)
            const savedPages = selectedFile.selectedPages;
            const currentSorted = JSON.stringify(
              [...checkedPages].sort((a, b) => a - b),
            );
            const savedSorted = JSON.stringify(
              [...savedPages].sort((a, b) => a - b),
            );
            if (
              checkedPages.length < savedPages.length &&
              currentSorted !== savedSorted
            ) {
              console.log(
                "📄 [같은 파일] checkedPages 복원 (selectedPages 폴백):",
                {
                  saved: savedPages,
                  current: checkedPages,
                },
              );
              setCheckedPages([...savedPages]);
            } else {
              console.log("📄 [같은 파일] checkedPages 유지:", checkedPages);
            }
          } else {
            console.log(
              "📄 [같은 파일] checkedPages 유지 (폴백 없음):",
              checkedPages,
            );
          }
        }
      } else if (
        selectedFile?.isPDF &&
        (selectedFile?.totalPages > 0 || safeTotalPages > 1)
      ) {
        // PDF 파일인데 아직 pageResults가 없는 경우 (처리 대기 중)
        if (isNewFileSelection) {
          // 새 파일일 때만 초기화 — 같은 파일의 fileList/safeTotalPages 업데이트 시에는 유지
          const pageCount = Math.max(
            selectedFile.totalPages || 0,
            safeTotalPages || 0,
            1,
          );
          const expectedPages = Array.from(
            { length: pageCount },
            (_, i) => i + 1,
          );
          console.log("📄 [새 파일] PDF 처리 대기 중, 예상 페이지 선택:", {
            selectedFileIndex,
            fileName: selectedFile.name,
            totalPages: selectedFile.totalPages,
            safeTotalPages,
            pageCount,
            expectedPages,
          });
          setCheckedPages(expectedPages);
          setSelectedPageNumber(1);
          lastClickedPageRef.current = 1;
        } else {
          // 같은 파일 — safeTotalPages가 나중에 확인된 경우 (PDF 뷰어 로드 등)
          // pageResults 없는 상태 = 아직 추출 전이므로 전체 페이지로 확장해도 안전
          const pageCount = Math.max(
            selectedFile.totalPages || 0,
            safeTotalPages || 0,
            1,
          );
          if (checkedPages.length < pageCount) {
            const expectedPages = Array.from(
              { length: pageCount },
              (_, i) => i + 1,
            );
            console.log(
              "📄 [같은 파일] totalPdfPages 확인 → 전체 페이지 확장:",
              { from: checkedPages, to: expectedPages },
            );
            setCheckedPages(expectedPages);
          } else {
            console.log(
              "📄 [같은 파일] pageResults 없지만 checkedPages 유지:",
              checkedPages,
            );
          }
        }
      } else if (selectedFile?.isPDF) {
        // PDF 파일이지만 아직 totalPages도 없는 경우 (업로드 직후)
        if (isNewFileSelection) {
          console.log("📄 [새 파일] PDF 업로드 직후, 페이지 1 선택:", {
            selectedFileIndex,
            fileName: selectedFile.name,
          });
          setCheckedPages([1]);
          setSelectedPageNumber(1);
          lastClickedPageRef.current = 1;
        } else {
          console.log("📄 [같은 파일] PDF checkedPages 유지:", checkedPages);
        }
      } else if (selectedFile) {
        // 단일 이미지 파일인 경우
        if (isNewFileSelection) {
          console.log("🖼️ [새 파일] 단일 이미지 파일 선택:", selectedFile.name);
          setCheckedPages([]);
          setSelectedPageNumber(1);
          lastClickedPageRef.current = null;
        }
      }

      prevSelectedFileIndexRef.current = selectedFileIndex;
    } else {
      setCheckedPages([]);
      prevSelectedFileIndexRef.current = null;
    }
  }, [selectedFileIndex, fileList, totalPdfPages]);

  // 📄 [GCS] DB에서 복원된 문서 선택 시 PDF를 GCS에서 lazy 다운로드
  useEffect(() => {
    if (selectedFileIndex === null) return;
    const file = fileList[selectedFileIndex];
    if (!file) return;
    // 복원된 문서이고, PDF가 있지만 아직 로컬 file이 없는 경우에만 다운로드
    if (!file._restoredFromDB || !file._hasPdf || file.file) return;

    let cancelled = false;
    (async () => {
      const pdfFile = await fetchRestoredPdf(file._documentId, file.name);
      if (cancelled) return;
      if (!pdfFile) {
        // 다운로드 fail — spinner 무한 방지 위해 _pdfFetchFailed flag
        setFileList((prev) =>
          prev.map((f, idx) =>
            idx === selectedFileIndex && f._documentId === file._documentId
              ? { ...f, _pdfFetchFailed: true }
              : f,
          ),
        );
        return;
      }
      const preview = URL.createObjectURL(pdfFile);
      setFileList((prev) =>
        prev.map((f, idx) =>
          idx === selectedFileIndex && f._documentId === file._documentId
            ? {
                ...f,
                file: pdfFile,
                preview,
                isPDF: true,
                _pdfFetchFailed: false,
              }
            : f,
        ),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedFileIndex, fileList]);

  // 🔍 [DEBUG] checkedPages 변경 추적
  useEffect(() => {
    console.log(
      "🔍 [TRACE] checkedPages changed:",
      checkedPages,
      "selectedFileIndex:",
      selectedFileIndex,
    );
    // 🔧 [1]로 리셋되는 원인 추적
    if (checkedPages.length === 1 && checkedPages[0] === 1) {
      console.warn("⚠️ [TRACE] checkedPages가 [1]로 리셋됨! 호출 스택:");
      console.trace("checkedPages=[1] trace");
    }
  }, [checkedPages]);

  // 🔐 마운트 시 JWT 자동 검증 (Ctrl+F5 시 재로그인 방지)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    fetch(`${API_BASE_URL}/api/auth/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.user) {
          console.log(
            "🔐 [Auto-Login] 토큰 유효 → 자동 로그인:",
            data.user.userId,
          );
          setCurrentGroupCode(data.user.groupCode);
          setCurrentUserId(data.user.userId);
          setIsLoggedIn(true);
          // groupCode/userId도 localStorage에 동기화
          localStorage.setItem("groupCode", data.user.groupCode);
          localStorage.setItem("userId", data.user.userId);
          // 🔄 슬라이딩 30일: 활성 사용자는 방문할 때마다 세션 만료를 30일 뒤로 연장
          //    (서버 touchExpiry + 새 JWT). 비활성 30일 경과 시에만 재로그인 → 보안+편의 균형.
          fetch(`${API_BASE_URL}/api/auth/refresh`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
          })
            .then((r) => r.json())
            .then((rf) => {
              if (rf && rf.success && rf.token) {
                setToken(rf.token, "local");
              }
            })
            .catch(() => {});
          // 💾 서버에서 사용자 설정 로드 (DB 영구 저장된 모델 선택 등)
          loadPreferencesFromServer().then((prefs) => {
            if (prefs) {
              if (prefs.selectedValidationModels) {
                const normalizedModels = normalizeGeminiGenerationModels(
                  prefs.selectedValidationModels,
                );
                setSelectedValidationModels(normalizedModels);
                localStorage.setItem(
                  "selectedValidationModels",
                  JSON.stringify(normalizedModels),
                );
              }
              if (prefs.classificationFlashModel) {
                setClassificationFlashModel(prefs.classificationFlashModel);
                localStorage.setItem(
                  "classificationFlashModel",
                  prefs.classificationFlashModel,
                );
              }
              if (prefs.templateExtractionModel) {
                const normalizedTemplate = normalizeGeminiGenerationModel(
                  prefs.templateExtractionModel,
                );
                setTemplateExtractionModel(normalizedTemplate);
                localStorage.setItem(
                  "templateExtractionModel",
                  normalizedTemplate,
                );
              }
              if (prefs.ocrEngine) {
                setOcrEngine(prefs.ocrEngine);
                localStorage.setItem("ocrEngine", prefs.ocrEngine);
              }
              if (prefs.thinkingBudget !== undefined) {
                setThinkingBudget(prefs.thinkingBudget);
                localStorage.setItem(
                  "thinkingBudget",
                  prefs.thinkingBudget.toString(),
                );
              }
              if (prefs.thinkingLevel) {
                setThinkingLevel(prefs.thinkingLevel);
                localStorage.setItem("thinkingLevel", prefs.thinkingLevel);
              }
              if (prefs.verifyEnabled !== undefined) {
                setVerifyEnabled(prefs.verifyEnabled);
                localStorage.setItem(
                  "verifyEnabled",
                  prefs.verifyEnabled.toString(),
                );
              }
              console.log(
                "💾 [ServerPrefs] 서버 설정 → localStorage 동기화 완료",
              );
            }
          });

          // 📄 PostgreSQL+GCS에서 최근 문서 복원 (비동기, 로컬과 병합)
          restoreDocuments().then((restoredFiles) => {
            if (restoredFiles && restoredFiles.length > 0) {
              setFileList((prev) => {
                if (prev.length === 0) {
                  console.log(
                    `📄 [Auto-Login Restore] ${restoredFiles.length}건 문서를 fileList에 복원`,
                  );
                  return restoredFiles;
                }
                // 로컬 파일과 DB 문서를 병합 (로컬 우선, DB에만 있는 문서 추가)
                const localIds = new Set(
                  prev.map((f) => f._documentId).filter(Boolean),
                );
                const localNames = new Set(prev.map((f) => f.name));
                const newFromDB = restoredFiles.filter(
                  (rf) =>
                    !localIds.has(rf._documentId) && !localNames.has(rf.name),
                );
                if (newFromDB.length > 0) {
                  console.log(
                    `📄 [Auto-Login Restore] 로컬 ${prev.length}건 + DB ${newFromDB.length}건 병합`,
                  );
                  return [...prev, ...newFromDB];
                }
                return prev;
              });
            }
          });
        } else {
          console.log("🔐 [Auto-Login] 토큰 만료 → 로그아웃");
          clearToken();
          localStorage.removeItem("groupCode");
          localStorage.removeItem("userId");
          setIsLoggedIn(false);
        }
      })
      .catch((err) => {
        console.error("🔐 [Auto-Login] 서버 연결 실패 (오프라인 모드):", err);
        // 서버 연결 실패 시에도 localStorage 기반으로 유지
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 로그인 성공 핸들러
  const handleLoginSuccess = (userInfo, token) => {
    setCurrentGroupCode(userInfo.groupCode);
    setCurrentUserId(userInfo.userId);
    setIsLoggedIn(true);
    // 63차: 가입 화면에서 체크한 신고분포 정보공개 동의는 토큰이 없어 보류됐다 → 첫 로그인 뒤 인증된 호출로 적재(실패는 다음 로그인에 재시도)
    if (token) submitPendingDisclosureConsent(token).catch(() => {});
    // localStorage에 저장 (새로고침 시 자동 복원용)
    if (token) setToken(token, "local");
    // API 키 발급 의도(발급 페이지→로그인 경로) — 로그인 후 키 발급 페이지로 복귀 (pay_after_login 미러)
    if (sessionStorage.getItem("apikeys_after_login")) {
      sessionStorage.removeItem("apikeys_after_login");
      setTimeout(() => {
        setCurrentPage("api-keys");
        window.history.replaceState({}, "", "/?page=api-keys");
      }, 0);
    }
    // 결제 의도가 있으면(구독하기→로그인 경로) 로그인 후 요금제로 복귀
    if (sessionStorage.getItem("pay_after_login")) {
      sessionStorage.removeItem("pay_after_login");
      setTimeout(() => {
        setCurrentPage("pricing");
        window.history.replaceState({}, "", "/?page=pricing");
      }, 0);
    }
    localStorage.setItem("groupCode", userInfo.groupCode);
    localStorage.setItem("userId", userInfo.userId);
    if (userInfo.userName) localStorage.setItem("userName", userInfo.userName);
    // 💾 서버에서 사용자 설정 로드 (로그인 직후)
    setTimeout(() => {
      loadPreferencesFromServer().then((prefs) => {
        if (prefs) {
          if (prefs.selectedValidationModels) {
            const normalizedModels = normalizeGeminiGenerationModels(
              prefs.selectedValidationModels,
            );
            setSelectedValidationModels(normalizedModels);
            localStorage.setItem(
              "selectedValidationModels",
              JSON.stringify(normalizedModels),
            );
          }
          if (prefs.classificationFlashModel) {
            setClassificationFlashModel(prefs.classificationFlashModel);
            localStorage.setItem(
              "classificationFlashModel",
              prefs.classificationFlashModel,
            );
          }
          if (prefs.templateExtractionModel) {
            const normalizedTemplate = normalizeGeminiGenerationModel(
              prefs.templateExtractionModel,
            );
            setTemplateExtractionModel(normalizedTemplate);
            localStorage.setItem(
              "templateExtractionModel",
              normalizedTemplate,
            );
          }
          if (prefs.ocrEngine) {
            setOcrEngine(prefs.ocrEngine);
            localStorage.setItem("ocrEngine", prefs.ocrEngine);
          }
          if (prefs.thinkingBudget !== undefined) {
            setThinkingBudget(prefs.thinkingBudget);
            localStorage.setItem(
              "thinkingBudget",
              prefs.thinkingBudget.toString(),
            );
          }
          if (prefs.thinkingLevel) {
            setThinkingLevel(prefs.thinkingLevel);
            localStorage.setItem("thinkingLevel", prefs.thinkingLevel);
          }
          if (prefs.verifyEnabled !== undefined) {
            setVerifyEnabled(prefs.verifyEnabled);
            localStorage.setItem(
              "verifyEnabled",
              prefs.verifyEnabled.toString(),
            );
          }
          console.log("💾 [Login] 서버 설정 → state/localStorage 동기화 완료");
        }
      });

      // 📄 PostgreSQL+GCS에서 최근 문서 복원 (비동기, 로컬과 병합)
      restoreDocuments().then((restoredFiles) => {
        if (restoredFiles && restoredFiles.length > 0) {
          setFileList((prev) => {
            if (prev.length === 0) {
              console.log(
                `📄 [DB Restore] ${restoredFiles.length}건 문서를 fileList에 복원`,
              );
              return restoredFiles;
            }
            // 로컬 파일과 DB 문서를 병합 (로컬 우선, DB에만 있는 문서 추가)
            const localIds = new Set(
              prev.map((f) => f._documentId).filter(Boolean),
            );
            const localNames = new Set(prev.map((f) => f.name));
            const newFromDB = restoredFiles.filter(
              (rf) => !localIds.has(rf._documentId) && !localNames.has(rf.name),
            );
            if (newFromDB.length > 0) {
              console.log(
                `📄 [DB Restore] 로컬 ${prev.length}건 + DB ${newFromDB.length}건 병합`,
              );
              return [...prev, ...newFromDB];
            }
            return prev;
          });
        }
      });
    }, 100);
  };

  // 📄 탭 포커스 시 DB에서 최신 문서 목록 새로고침 (크로스 브라우저 동기화)
  useEffect(() => {
    if (!isLoggedIn) return;
    let lastRefresh = 0;
    const THROTTLE_MS = 30000; // 30초 쓰로틀

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastRefresh < THROTTLE_MS) return;
      lastRefresh = now;

      try {
        const dbFiles = await refreshDocuments();
        if (!dbFiles || dbFiles.length === 0) return;

        setFileList((prev) => {
          // 로컬에 있는 문서 ID/이름 집합
          const localIds = new Set(
            prev.map((f) => f._documentId).filter(Boolean),
          );
          const localNames = new Set(prev.map((f) => f.name));

          // DB에만 있는 새 문서 추가
          const newFromDB = dbFiles.filter(
            (rf) => !localIds.has(rf._documentId) && !localNames.has(rf.name),
          );

          // 로컬에 있지만 DB에서 추출 완료된 문서 → 추출 결과 업데이트
          const updatedPrev = prev.map((f) => {
            if (!f._documentId) return f;
            const dbMatch = dbFiles.find(
              (df) => df._documentId === f._documentId,
            );
            if (
              dbMatch &&
              dbMatch.status === "completed" &&
              f.status !== "completed" &&
              dbMatch.extractedData
            ) {
              return {
                ...f,
                status: "completed",
                extractedData: dbMatch.extractedData,
                pageResults: dbMatch.pageResults,
              };
            }
            return f;
          });

          if (newFromDB.length > 0) {
            console.log(
              `🔄 [Tab Focus] DB에서 ${newFromDB.length}건 새 문서 추가`,
            );
            return [...updatedPrev, ...newFromDB];
          }
          if (updatedPrev !== prev) return updatedPrev;
          return prev;
        });
      } catch (err) {
        console.warn("⚠️ [Tab Focus] 새로고침 실패:", err.message);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isLoggedIn]);

  // 레거시 파일 히스토리 로드
  useEffect(() => {
    if (!isLoggedIn || !currentGroupCode) return;
    const loadLegacyFiles = async () => {
      setLegacyFilesLoading(true);
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/files/history?groupCode=${currentGroupCode}&limit=200`,
        );
        const data = await res.json();
        if (data.success) {
          setLegacyFiles(data.data || []);
          setLegacyFilesTotal(data.total || 0);
        }
      } catch (e) {
        console.error("Legacy files load error:", e);
      } finally {
        setLegacyFilesLoading(false);
      }
    };
    loadLegacyFiles();
  }, [isLoggedIn, currentGroupCode, currentUserId]);

  // 로그아웃 핸들러
  const handleLogout = () => {
    localStorage.removeItem("groupCode");
    localStorage.removeItem("userId");
    clearToken();
    localStorage.removeItem("password");
    localStorage.removeItem("userName");
    // deviceTrustToken은 유지 (같은 기기에서 OTP 재입력 방지)
    // 명시적 로그아웃 시에도 기기 신뢰는 보존

    sessionStorage.removeItem("groupCode");
    sessionStorage.removeItem("userId");
    // clearToken() 이 두 저장소를 모두 비웠다

    // URL 에 ?audit= 딥링크가 남아 있으면 로그아웃 후 랜딩 대신
    // 비로그인 공개(standalone) 화면이 떠버림 — 루트로 정리
    if (window.location.search || window.location.hash) {
      window.history.replaceState({}, "", "/");
    }

    setCurrentGroupCode("");
    setCurrentUserId("");
    setIsLoggedIn(false);
    setCurrentPage("landing");
  };

  // 🧭 메뉴 가시성 fetch (로그인 시 + 토큰 변경 시)
  useEffect(() => {
    if (!isLoggedIn) {
      setMenuVisibility(null);
      setIsManager(false);
      return;
    }
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch(`${API_BASE_URL}/api/auth/menu-visibility`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data && data.success) {
          setMenuVisibility(data.visibility || {});
          setIsManager(!!data.isManager);
        }
      })
      .catch((e) => console.warn("menu-visibility fetch failed:", e.message));
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔐 401 fetch interceptor — 폐기된/만료된 세션 감지 시 자동 로그아웃
  useEffect(() => {
    if (!isLoggedIn) return;
    const originalFetch = window.fetch;
    let alerted = false;
    window.fetch = async function patchedFetch(...args) {
      const response = await originalFetch.apply(this, args);
      if (response.status === 401) {
        try {
          const cloned = response.clone();
          const data = await cloned.json();
          const reason = data && data.reason;
          if (
            reason === "session_revoked" ||
            reason === "legacy_token_expired_migrate" ||
            reason === "session_expired" ||
            reason === "session_not_found"
          ) {
            if (!alerted) {
              alerted = true;
              setTimeout(() => {
                alert(
                  data.message || "세션이 만료되었습니다. 다시 로그인해주세요.",
                );
                handleLogout();
              }, 0);
            }
          }
        } catch (_) {
          /* not JSON, ignore */
        }
      }
      return response;
    };
    return () => {
      window.fetch = originalFetch;
    };
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Audit UI quick route (P5/P6) ─────────────────────────────────
  // 정식 라우팅 통합 전까지 query-param fallback.
  //   /?audit=demo        → AuditDemo (분류 + Evidence Cards)
  //   /?audit=precedents  → PrecedentsPage (AUI Grid 사례 검색)
  // 로그인/landing 분기보다 먼저 처리해서 외부 리뷰어가 바로 접근 가능.
  const _rawAuditRoute =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("audit")
      : null;
  // 🏠 loginHS 로그인 사용자는 audit/page 없으면 기본 홈 대시보드 — URL 리다이렉트에
  //   의존하지 않고 매 렌더 결정(무엇이 URL을 /로 되돌려도 홈 유지, DocuAI 뷰어 노출 방지).
  const _loginHsHome =
    typeof window !== "undefined" &&
    IS_LOGINHS_PRODUCT &&
    isLoggedIn &&
    !_rawAuditRoute &&
    !new URLSearchParams(window.location.search).get("page") &&
    !window.location.hash;
  const _auditRoute = _loginHsHome ? "home" : _rawAuditRoute;
  const _auditTool =
    new URLSearchParams(window.location.search).get("tool") || "exchange";
  // 🏠 loginHS 디폴트 랜딩 — audit/page 파라미터 없이 진입하면 문서인식(viewer)이
  //   기본이라 "왼쪽에서 파일을 선택하세요" 빈 화면이 얼굴이 됨. loginHS 제품은
  //   AI HS 분류 초기 화면(중앙 검색바)으로 정규화. (DocuAI 는 기존 viewer 유지)
  useEffect(() => {
    // ⚠ 로그인 상태에서만 — 비로그인은 마케팅 랜딩(LandingPageLoginHS)이 얼굴
    //   (audit=onto-detail 을 붙이면 비로그인 풀페이지 분기로 랜딩이 사라짐).
    if (!IS_LOGINHS_PRODUCT || !isLoggedIn) return;
    const p = new URLSearchParams(window.location.search);
    if (!p.get("audit") && !p.get("page") && !window.location.hash) {
      p.set("audit", "home"); // 검색 히어로 + 최근검색·내품목·새소식 홈 대시보드
      window.history.replaceState(
        {},
        "",
        window.location.pathname + "?" + p.toString(),
      );
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // 메인 레이아웃 embedded 라우트 → activeTab 동기화 + keep-alive 등록
  useEffect(() => {
    if (_auditRoute === "home") setActiveTab("hs-home");
    else if (_auditRoute === "onto-detail") setActiveTab("hs-ai-classify");
    else if (_auditRoute === "precedents")
      setActiveTab("hs-precedents-explorer");
    else if (_auditRoute === "onto-review") setActiveTab("hs-ontology-review");
    else if (_auditRoute === "inquiry") setActiveTab("inquiry-board");
    else if (_auditRoute === "quickref")
      // 관세율표(noextra=1)와 속견표(일반)가 같은 quickref 렌더를 공유 — 사이드바 하이라이트만 구분.
      setActiveTab(
        new URLSearchParams(window.location.search).get("noextra") === "1"
          ? "hs-ai-classify"
          : "hs-unified-search",
      );
    else if (_auditRoute === "hs-compare") setActiveTab("hs-compare");
    else if (_auditRoute === "my-reports") setActiveTab("hs-my-reports");
    else if (_auditRoute === "req-guide") setActiveTab("hs-req-guide");
    else if (_auditRoute === "exemption") setActiveTab("hs-exemption");
    else if (_auditRoute === "simplified-rate")
      setActiveTab("hs-simplified-rate");
    else if (_auditRoute === "installment") setActiveTab("hs-installment");
    else if (_auditRoute === "enduse-rate") setActiveTab("hs-enduse-rate");
    else if (_auditRoute === "customs-guide") setActiveTab("hs-customs-guide");
    else if (_auditRoute === "news") setActiveTab("hs-news");
    else if (_auditRoute === "tools") setActiveTab("hs-tools");
    else if (_auditRoute === "usage") setActiveTab("hs-usage");
    else if (_auditRoute === "company") setActiveTab("hs-company");
    else if (_auditRoute === "bulk-classify") setActiveTab("hs-bulk-classify");
    else if (_auditRoute === "master-hs") setActiveTab("hs-master");
    if (
      _auditRoute === "home" ||
      _auditRoute === "onto-detail" ||
      _auditRoute === "precedents" ||
      _auditRoute === "onto-review" ||
      _auditRoute === "inquiry" ||
      _auditRoute === "quickref" ||
      _auditRoute === "hs-compare" ||
      _auditRoute === "my-reports" ||
      _auditRoute === "req-guide" ||
      _auditRoute === "exemption" ||
      _auditRoute === "simplified-rate" ||
      _auditRoute === "installment" ||
      _auditRoute === "enduse-rate" ||
      _auditRoute === "customs-guide" ||
      _auditRoute === "news" ||
      _auditRoute === "tools" ||
      _auditRoute === "usage" ||
      _auditRoute === "company" ||
      _auditRoute === "bulk-classify" ||
      _auditRoute === "master-hs"
    ) {
      setMountedAudit((prev) =>
        prev[_auditRoute] ? prev : { ...prev, [_auditRoute]: true },
      );
    }
  }, [_auditRoute]);
  if (_auditRoute === "demo") {
    return <AuditDemo />;
  }
  if (_auditRoute === "precedents" && !isLoggedIn) {
    // 비로그인(외부 리뷰어)만 전체 페이지. 로그인 사용자는 메인 레이아웃에
    // embedded 로 렌더 (메인 Sidebar 고정 + 오른쪽 전환).
    return <PrecedentsPage />;
  }
  if (_auditRoute === "main") {
    return <MainPage />;
  }
  if (_auditRoute === "results") {
    return <ResultsPage />;
  }
  if (_auditRoute === "chapter") {
    return <ResultsPage />;
  }
  if (_auditRoute === "synonyms") {
    return <SynonymsPage />;
  }
  if (_auditRoute === "hs-hub") {
    return <HsHubPage />;
  }
  if (_auditRoute === "trade-partners") {
    return <TradePartnersPage />;
  }
  if (_auditRoute === "onto-search") return <LegacyProveRedirect />;
  if (_auditRoute === "onto-detail" && !isLoggedIn) {
    return <HeadingDetailPage />;
  }
  if (_auditRoute === "onto-overview" || _auditRoute === "onto-master") {
    return <LegacyProveRedirect />;
  }
  if (_auditRoute === "onto-review" && !isLoggedIn) {
    // 비로그인(외부 리뷰어)만 전체 페이지. 로그인 사용자는 메인 레이아웃에
    // embedded (AI HS 분류와 동일 패턴 — 사이드바 고정 + 같은 맥락 비교).
    return <OntologyReviewPage />;
  }
  if (_auditRoute === "feedback-dashboard") {
    return <FeedbackDashboardPage />;
  }

  // ── 내 계정 설정 (로그인 사용자만) ──
  if (currentPage === "account-settings" && isLoggedIn) {
    return (
      <AccountSettingsPage
        API_BASE_URL={API_BASE_URL}
        currentUserId={currentUserId}
        currentGroupCode={currentGroupCode}
        onLogout={handleLogout}
        onClose={() => setCurrentPage("main")}
      />
    );
  }

  // ── 관리자 페이지 (로그인 상태와 무관하게 접근 가능) ──
  if (currentPage === "admin-login") {
    return (
      <AdminLogin
        API_BASE_URL={API_BASE_URL}
        onAdminLoginSuccess={() => setCurrentPage("admin")}
      />
    );
  }
  if (currentPage === "admin") {
    return (
      <AdminPage
        API_BASE_URL={API_BASE_URL}
        onLogout={() => {
          localStorage.removeItem("admin_token");
          setCurrentPage("landing");
        }}
        onSuperLoginImpersonate={(data) => {
          // 슈퍼 로그인 → 해당 사용자로 메인 앱 진입
          setCurrentGroupCode(data.user.groupCode);
          setCurrentUserId(data.user.userId);
          setIsLoggedIn(true);
          setCurrentPage("main"); // admin 페이지에서 나옴
        }}
      />
    );
  }

  // PWA 설치 안내 — 공유용 링크 /?page=install (로그인 여부 무관)
  if (currentPage === "install") {
    return <InstallAppPage />;
  }
  // 공개 문의하기 — 푸터/랜딩 '문의하기' (로그인 여부 무관)
  if (currentPage === "contact") {
    return (
      <ContactPage
        API_BASE_URL={API_BASE_URL}
        onNavigate={handlePublicNavigate}
      />
    );
  }
  // 공개 API 문서 — 랜딩 'API' 메뉴 (로그인 여부 무관)
  if (currentPage === "api-docs") {
    return <ApiDocsPage onNavigate={handlePublicNavigate} />;
  }
  // 사용자 가이드 — 기능 소개 매뉴얼 (로그인 여부 무관)
  if (currentPage === "guide") {
    return <UserGuidePage onNavigate={handlePublicNavigate} />;
  }
  // 특송운임 계산기 — 공개 도구 (로그인 여부 무관)
  if (currentPage === "freight") {
    return <CourierFreightPage onNavigate={handlePublicNavigate} />;
  }
  // 관세환율 — 공개 도구 (로그인 여부 무관)
  if (currentPage === "exchange") {
    return <ExchangeRatePage onNavigate={handlePublicNavigate} />;
  }
  // 서식자료실 — 공개 (로그인 여부 무관)
  if (currentPage === "forms") {
    return <FormsPage onNavigate={handlePublicNavigate} />;
  }
  // 새소식(관세청 공지) — 공개 (로그인 여부 무관)
  if (currentPage === "news") {
    return <NewsPage onNavigate={handlePublicNavigate} />;
  }
  // 분류 사례연구("이 세번, 왜?") — 공개 (로그인 여부 무관)
  if (currentPage === "cases") {
    return <CaseStudiesPage onNavigate={handlePublicNavigate} />;
  }
  // 수입 세금 계산기 — 공개 (로그인 여부 무관)
  if (currentPage === "taxcalc") {
    return <TaxCalcPage onNavigate={handlePublicNavigate} />;
  }
  // 무역통계부호 조회 — 공개 도구 (로그인 여부 무관)
  if (currentPage === "trade-codes") {
    return <TradeCodesPage onNavigate={handlePublicNavigate} />;
  }
  // 요건확인기관 연락처 — 공개 도구 (로그인 여부 무관)
  if (currentPage === "req-agencies") {
    return (
      <ReqAgencyDirectoryPage onNavigate={handlePublicNavigate} />
    );
  }
  // API 키 자가발급 — 비로그인 시 페이지 내부에서 로그인 유도
  if (currentPage === "api-keys") {
    return (
      <ApiKeysPage
        API_BASE_URL={API_BASE_URL}
        onNavigate={handlePublicNavigate}
      />
    );
  }
  if (currentPage === "pricing") {
    return (
      <PricingPage
        onClose={() => {
          window.history.pushState({}, "", "/");
          window.dispatchEvent(new PopStateEvent("popstate"));
        }}
        onLoginRequired={() => {
          window.history.replaceState({}, "", "/");
          setCurrentPage("login");
        }}
      />
    );
  }
  if (currentPage === "payment-success") {
    return <PaymentResultPage kind="success" />;
  }
  if (currentPage === "payment-fail") {
    return <PaymentResultPage kind="fail" />;
  }

  // 로그인되지 않은 경우 LandingPage / Login / Signup 표시
  if (!isLoggedIn) {
    if (currentPage === "login") {
      return (
        <Login
          API_BASE_URL={API_BASE_URL}
          onLoginSuccess={handleLoginSuccess}
          onNavigate={handlePublicNavigate}
        />
      );
    }
    if (currentPage === "signup") {
      return (
        <SignupPage
          API_BASE_URL={API_BASE_URL}
          onNavigate={handlePublicNavigate}
        />
      );
    }
    if (currentPage === "linkKey") {
      return (
        <LinkKeyPage
          API_BASE_URL={API_BASE_URL}
          onNavigate={handlePublicNavigate}
        />
      );
    }
    if (currentPage === "login-success") {
      return (
        <LoginSuccessPage
          API_BASE_URL={API_BASE_URL}
          onLoginSuccess={handleLoginSuccess}
        />
      );
    }
    if (currentPage === "sns-signup") {
      return (
        <SnsSignupPage
          API_BASE_URL={API_BASE_URL}
          onLoginSuccess={handleLoginSuccess}
          onNavigate={handlePublicNavigate}
        />
      );
    }
    return IS_LOGINHS_PRODUCT ? (
      <LandingPageLoginHS onLoginClick={() => handlePublicNavigate("login")} />
    ) : (
      <LandingPage onLoginClick={() => handlePublicNavigate("login")} />
    );
  }

  // 문서 타입 코드 매핑
  const DOC_TYPE_MAP = {
    경락확인서: "N",
    자동차매매계약서: "O",
    자동차등록원부: "L",
    자동차등록증: "R",
    말소등록증: "B",
    사업자등록증: "A",
    신분증: "H",
    폐차증: "D",
    세금계산서: "T",
    기타: "U",
  };

  // ⭐ 필드 호버 핸들러
  const handleFieldHover = (fieldKey, fieldValue) => {
    console.log(`🎯 필드 호버: ${fieldKey} = ${fieldValue}`);
    setHighlightedField(fieldKey);
    setHighlightedFieldValue(fieldValue);
    setClickedField(null); // 호버 시 클릭 상태 해제
  };

  // ⭐ 필드 클릭 핸들러 (자동 스크롤 + bbox 하이라이트)
  // 🎯 [Grounding] fieldInfo 객체 형식: { fieldKey, fieldValue, page, coordinates, confidence, hasGrounding }
  const handleFieldClick = (fieldInfo) => {
    // 하위 호환: 기존 (fieldKey, fieldValue) 형식도 지원
    if (typeof fieldInfo === "string") {
      const fieldKey = fieldInfo;
      const fieldValue = arguments[1];
      console.log(`🖱️ 필드 클릭: ${fieldKey} = ${fieldValue}`);
      setHighlightedField(fieldKey);
      setHighlightedFieldValue(fieldValue);
      setClickedField(fieldKey);
      setHighlightedBboxes(fieldKey);
      setTimeout(() => {
        setClickedField(null);
        setHighlightedBboxes(null);
      }, 3000);
      return;
    }

    // 🎯 [Grounding] 새 형식: 객체로 전달됨
    const {
      fieldKey,
      fieldValue,
      page,
      coordinates,
      confidence,
      hasGrounding,
    } = fieldInfo;
    console.log(
      `🖱️ 필드 클릭: ${fieldKey} = ${fieldValue}`,
      hasGrounding ? `📍 Page ${page}` : "(no grounding)",
    );

    setHighlightedField(fieldKey);
    setHighlightedFieldValue(fieldValue);
    setClickedField(fieldKey);
    setHighlightedBboxes(fieldKey);

    // 🎯 [Grounding] 위치 정보가 있으면 해당 페이지로 이동 + 하이라이트 표시
    // 🔧 FIX: page가 0일 때도 처리되도록 !== null 사용 (0은 falsy이므로)
    if (hasGrounding && page !== null && page !== undefined) {
      console.log(
        `🎯 [Grounding] PDF 페이지 이동: ${page}, 좌표:`,
        coordinates,
      );

      // 1. 해당 페이지로 이동
      setSelectedPageNumber(page);
      handlePdfPageChange(page);

      // 2. 문서 탭으로 전환 (grounding 확인을 위해)
      setCenterViewTab("document");

      // 3. Grounding 하이라이트 좌표 저장 (PDFViewer에서 사용)
      setGroundingHighlight({
        fieldKey,
        fieldValue, // 🔍 텍스트 검색 하이라이트용
        page,
        coordinates, // [x1, y1, x2, y2]
        confidence,
      });
    } else {
      // 🔍 [Text Search Fallback] bbox가 없어도 텍스트 검색으로 하이라이트
      // 📄 페이지 정보가 있으면 해당 페이지로 이동
      if (page !== null && page !== undefined) {
        setSelectedPageNumber(page);
        handlePdfPageChange(page);
        setCenterViewTab("document");
      }
      setGroundingHighlight({
        fieldKey,
        fieldValue, // 텍스트 값만 저장 (좌표 없음)
        page: page || null,
        coordinates: null,
        confidence: null,
      });
    }

    // 3초 후 일반 클릭 상태 해제
    setTimeout(() => {
      setClickedField(null);
      setHighlightedBboxes(null);
    }, 3000);
  };

  // 법인 여부 판별 함수 (개인사업자 vs 법인)
  const isCompanyType = (data) => {
    if (!data) return false;

    // 사업자등록증이 있는 경우
    if (data.documentType === "사업자등록증" && data.businessName) {
      // 상호에 "(주)" 또는 "주식회사"가 포함되어야 법인
      const name = data.businessName;
      return (
        name.includes("(주)") ||
        name.includes("주식회사") ||
        name.includes("㈜")
      );
    }

    // 차량 서류에서도 확인
    if (data.ownerName) {
      const name = data.ownerName;
      return (
        name.includes("(주)") ||
        name.includes("주식회사") ||
        name.includes("㈜")
      );
    }

    return false;
  };

  // 문서 타입별 필드 정의 (라벨 및 레이아웃)
  const DOCUMENT_FIELDS_MAP = {
    말소등록증: {
      ownerName: { label: "성명", width: "full" },
      residentNumber: { label: "주민등록번호", width: "full" },
      plateNumber: { label: "차량등록번호", width: "full" },
      vin: { label: "차대번호 (VIN)", width: "full" },
      model: { label: "차명 (모델)", width: "full" },
      modelYear: { label: "연식", width: "half" },
      productionDate: { label: "제작연월일", width: "half" }, // 생년월일/법인등록번호? 문맥에 따라 다름
      mileage: { label: "주행거리", width: "half" },
      deregistrationDate: { label: "말소등록일", width: "full" },
    },
    자동차등록증: {
      ownerName: { label: "소유자명", width: "full" },
      residentNumber: { label: "주민등록번호", width: "full" },
      address: { label: "사용본거지", width: "full" },
      plateNumber: { label: "차량번호", width: "full" },
      vin: { label: "차대번호", width: "full" },
      model: { label: "차명", width: "full" },
      modelYear: { label: "연식", width: "half" },
      firstRegDate: { label: "최초등록일", width: "half" },
      totalWeight: { label: "총중량", width: "half" },
      displacement: { label: "배기량", width: "half" },
      length: { label: "길이", width: "third" },
      width: { label: "너비", width: "third" },
      height: { label: "높이", width: "third" },
      inspectionDate: { label: "검사유효기간", width: "full" },
    },
    자동차등록원부: {
      ownerName: { label: "소유자명", width: "full" },
      residentNumber: { label: "주민부호", width: "full" },
      address: { label: "사용본거지", width: "full" },
      plateNumber: { label: "차량번호", width: "full" },
      vin: { label: "차대번호", width: "full" },
      model: { label: "차명", width: "full" },
      modelYear: { label: "연식", width: "full" },
    },
    경락확인서: {
      ownerName: { label: "소유자명", width: "full" },
      residentNumber: { label: "주민등록번호", width: "full" },
      address: { label: "주소", width: "full" },
      plateNumber: { label: "차량번호", width: "full" },
      vin: { label: "차대번호", width: "full" },
      model: { label: "차명", width: "full" },
      displacement: { label: "배기량", width: "half" },
      mileage: { label: "주행거리", width: "half" },
      totalAmount: { label: "낙찰금액", width: "full" },
    },
    자동차매매계약서: {
      ownerName: { label: "매수인 성명", width: "full" },
      residentNumber: { label: "주민등록번호", width: "full" },
      address: { label: "주소", width: "full" },
      plateNumber: { label: "차량번호", width: "full" },
      vin: { label: "차대번호", width: "full" },
      model: { label: "차명", width: "full" },
      totalAmount: { label: "매매금액", width: "full" },
      tradeDate: { label: "매매일", width: "full" },
    },
    폐차증: {
      ownerName: { label: "소유자 성명", width: "full" },
      residentNumber: { label: "주민등록번호", width: "full" },
      address: { label: "주소", width: "full" },
      plateNumber: { label: "차량번호", width: "full" },
      vin: { label: "차대번호", width: "full" },
      model: { label: "차명", width: "full" },
      modelYear: { label: "연식", width: "full" },
      deregistrationDate: { label: "폐차(인수)일자", width: "full" },
    },
    신분증: {
      name: { label: "이름", width: "full" },
      residentNumber: { label: "주민등록번호", width: "full" },
      licenseNumber: { label: "면허번호", width: "full" },
      issueDate: { label: "발급일", width: "full" },
      address: { label: "주소", width: "full" },
    },
    사업자등록증: {
      businessNumber: { label: "사업자등록번호", width: "full" },
      businessName: { label: "상호", width: "full" },
      ownerName: { label: "대표자", width: "full" },
      address: { label: "사업장소재지", width: "full" },
      openDate: { label: "개업년월일", width: "full" },
    },
    세금계산서: {
      businessNumber: { label: "사업자등록번호", width: "full" },
      businessName: { label: "상호", width: "full" },
      ownerName: { label: "대표자", width: "full" },
      address: { label: "사업장소재지", width: "full" },
      totalAmount: { label: "합계금액", width: "full" },
      issueDate: { label: "작성일자", width: "full" },
      plateNumber: { label: "차량번호", width: "half" },
      vin: { label: "차대번호", width: "half" },
      model: { label: "차명", width: "full" },
    },
  };

  // 필드 존재 여부 확인 함수
  const isFieldApplicable = (documentType, fieldName) => {
    const fieldsMap = DOCUMENT_FIELDS_MAP[documentType];
    if (!fieldsMap) return true; // 알 수 없는 문서 타입은 모든 필드 표시
    return fieldsMap[fieldName] !== false;
  };

  // 셀 렌더링 함수
  const renderTableCell = (data, fieldName, value) => {
    const documentType = data?.documentType;
    const fieldApplicable = isFieldApplicable(documentType, fieldName);

    // 이 문서 타입에 해당 필드가 프롬프트에 정의되지 않은 경우 (회색, 작게)
    if (!fieldApplicable) {
      return <span className="text-gray-300 text-xs">-</span>;
    }

    // 🔧 객체인 경우 처리 (React는 객체를 직접 렌더링할 수 없음)
    if (typeof value === "object" && value !== null) {
      // JSON 형태로 문자열 변환 시도
      try {
        const stringValue = JSON.stringify(value);
        if (stringValue && stringValue !== "{}" && stringValue !== "[]") {
          return <span className="text-xs text-gray-600">{stringValue}</span>;
        }
      } catch (e) {
        console.warn("Failed to stringify value:", value);
      }
      return <span className="text-gray-400 text-xs">-</span>;
    }

    // ✅ 미인식 판단 로직 개선:
    // Flash와 Pro 모두 인식 결과가 없는 경우 → 공백 처리 (미인식 아님)
    // Flash나 Pro 중 하나라도 값이 있었는데 최종 결과 없음 → 미인식
    if (!value || value === "-") {
      const comparisonDetail =
        data?.metadata?.matchDetails?.comparisonDetails?.[fieldName];
      const flashValue = comparisonDetail?.flash;
      const proValue = comparisonDetail?.pro || comparisonDetail?.final;
      const hadAnyValue = flashValue || proValue;

      // Flash나 Pro에서 값이 있었는데 최종 결과가 없는 경우만 미인식
      if (hadAnyValue) {
        return (
          <span className="text-orange-400 text-xs font-semibold">미인식</span>
        );
      }

      // Flash와 Pro 모두 없는 경우 → 공백
      return <span className="text-gray-400 text-xs">-</span>;
    }

    // 정상적으로 추출된 경우
    return <span>{String(value)}</span>;
  };

  // 통합 API: 1차 추출 + 2차 검증 + 일치율 계산 (한 번에)
  // 🔗 단건/다중 공통 추출 — 적응형 엔드포인트(gemini-extract-pdf-native)로 통일.
  //    건별·다중건이 반드시 동일한 추출 로직(템플릿 DB / few-shot / 정규화)을 쓰도록 보장.
  //    → 같은 파일이면 단건과 다중건 결과가 일치한다 (불변식).
  //    반환: { data(raw 응답), result(매핑된 추출객체), cv(교차검증), receivedGrounding }
  const callAdaptiveExtract = async (fileBlob, mode = "adaptive") => {
    // 🖼️ TIFF/이미지 등 비-PDF 입력은 추출 직전 PDF 로 변환 (단건/다중 공통).
    //    업로드 시 변환이 끝나기 전(레이스)이거나 변환 실패해도 추출이 자가치유된다.
    let blob = fileBlob;
    const bName = (blob?.name || "").toLowerCase();
    const isPdfBlob =
      blob?.type === "application/pdf" || bName.endsWith(".pdf");
    if (!isPdfBlob && isImageFile(bName)) {
      addLog(`🔄 ${blob?.name || "이미지"} → PDF 변환 후 추출`);
      blob = await convertImageToPdf(blob);
    }

    const globalTemplateObj = selectedOcrTemplate
      ? Object.values(templates).find((t) => t.name === selectedOcrTemplate)
      : null;
    const fileTemplateId = globalTemplateObj?.id || "";

    const formData = new FormData();
    formData.append("file", blob);
    formData.append("templateId", fileTemplateId);
    // 🚗 차량 마스터 enricher 의 doc_type 추정용 — template 명을 docType 으로 전달
    if (globalTemplateObj?.name)
      formData.append("docType", globalTemplateObj.name);
    if (editablePrompt) formData.append("customPrompt", editablePrompt); // 📋 UI 프롬프트
    formData.append(
      "selectedValidationModels",
      JSON.stringify(effectiveValidationModels),
    );
    if (verifyEnabled) formData.append("verificationMode", verificationMode);
    if (verifyEnabled && verificationMode === "sequential")
      formData.append("enableJudge", "true");

    const res = await fetch(
      `${API_BASE_URL}/api/ocr/gemini-extract-pdf-native`,
      { method: "POST", headers: { ...ocrAuthHeader() }, body: formData },
    );
    if (await handleOcrQuota(res)) throw new Error("FREE_QUOTA_EXCEEDED");
    const data = await res.json();
    if (!(data.success && data.data)) {
      throw new Error(data.error || "추출 실패");
    }

    const result = {
      documentType: globalTemplateObj?.name || "미분류",
      ...data.data,
      metadata: { mode, elapsed: data.metadata?.elapsed },
    };
    const cv = data.metadata?.crossValidation;
    const receivedGrounding = data.data.groundedFields || {};
    return { data, result, cv, receivedGrounding };
  };

  // 1차 추출만 수행
  const extractOnly = async (file) => {
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("enableHybridOCR", enableHybridOCR); // ⭐ Hybrid Mode Flag

      const response = await fetch(`${API_BASE_URL}/api/ocr/extract-only`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "서버 오류");
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "처리 실패");
      }

      return result.data;
    } catch (err) {
      throw new Error("1차 추출 실패: " + err.message);
    }
  };

  // 2차 검증만 수행
  const validateOnly = async (file, extractedData) => {
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("extractedData", JSON.stringify(extractedData));
      // ⭐ 선택된 검증 모델 전송 (동적 모델 선택)
      if (effectiveValidationModels && effectiveValidationModels.length > 0) {
        formData.append(
          "selectedValidationModels",
          JSON.stringify(effectiveValidationModels),
        );
      }

      const response = await fetch(`${API_BASE_URL}/api/ocr/validate`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "서버 오류");
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "처리 실패");
      }

      return result.data;
    } catch (err) {
      throw new Error("2차 검증 실패: " + err.message);
    }
  };

  // ====== 관리자 모드 전용 API ======

  // 관리자: 분류만 실행
  const adminClassify = async (file) => {
    try {
      const formData = new FormData();
      // ⭐ 중요: 파일보다 필드를 먼저 append 해야 백엔드(Multer)에서 정상 인식 가능성 높음
      formData.append("flashModelVersion", classificationFlashModel);
      formData.append("image", file);

      if (ocrEngine === "deepseek") {
        formData.append("model", "deepseek");
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/classify`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("분류 실패");
      }

      const result = await response.json();
      return result;
    } catch (err) {
      throw new Error("분류 실패: " + err.message);
    }
  };

  // 관리자: Flash 추출만
  const adminExtractFlash = async (file, documentType = null) => {
    try {
      const formData = new FormData();
      formData.append("image", file);
      if (documentType) {
        formData.append("documentType", documentType);
      }
      if (ocrEngine === "deepseek") {
        formData.append("model", "deepseek");
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/extract-flash`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Flash 추출 실패");
      }

      const result = await response.json();
      return result;
    } catch (err) {
      throw new Error("Flash 추출 실패: " + err.message);
    }
  };

  // 관리자: Pro 추출만
  const adminExtractPro = async (file, documentType = null) => {
    try {
      const formData = new FormData();
      formData.append("image", file);
      if (documentType) {
        formData.append("documentType", documentType);
      }
      if (ocrEngine === "deepseek") {
        formData.append("model", "deepseek");
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/extract-pro`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Pro 추출 실패");
      }

      const result = await response.json();
      return result;
    } catch (err) {
      throw new Error("Pro 추출 실패: " + err.message);
    }
  };

  // ====== 관리자 모드 핸들러 ======
  const handleAdminClassifyWrapper = async () => {
    if (selectedFileIndex === null) return;

    const selectedFile = fileList[selectedFileIndex];
    setLoading(true);
    setError(null);

    try {
      const result = await adminClassify(selectedFile.file);

      console.log("✅ 분류 완료:", result.data.documentType);

      setFileList((prev) =>
        prev.map((f, idx) =>
          idx === selectedFileIndex
            ? {
                ...f,
                classifyResult: result,
                status: "classified",
              }
            : f,
        ),
      );

      alert(
        `✅ 분류 완료\n문서 타입: ${result.data.documentType}\n소요 시간: ${result.duration}`,
      );
    } catch (err) {
      setError(err.message);
      console.error("❌ 분류 실패:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminExtractFlashWrapper = async () => {
    if (selectedFileIndex === null) return;

    const selectedFile = fileList[selectedFileIndex];
    setLoading(true);
    setError(null);

    try {
      const documentType =
        selectedFile.classifyResult?.data?.documentType || null;
      const result = await adminExtractFlash(selectedFile.file, documentType);

      console.log("✅ Flash 추출 완료:", result.data);

      setFileList((prev) =>
        prev.map((f, idx) =>
          idx === selectedFileIndex
            ? {
                ...f,
                flashResult: result,
                status: "flash-extracted",
              }
            : f,
        ),
      );

      alert(`✅ Flash 추출 완료\n소요 시간: ${result.duration}`);
    } catch (err) {
      setError(err.message);
      console.error("❌ Flash 추출 실패:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminExtractProWrapper = async () => {
    if (selectedFileIndex === null) return;

    const selectedFile = fileList[selectedFileIndex];
    setLoading(true);
    setError(null);

    try {
      const documentType =
        selectedFile.classifyResult?.data?.documentType || null;
      const result = await adminExtractPro(selectedFile.file, documentType);

      console.log("✅ Pro 추출 완료:", result.data);

      setFileList((prev) =>
        prev.map((f, idx) =>
          idx === selectedFileIndex
            ? {
                ...f,
                proResult: result,
                status: "pro-extracted",
              }
            : f,
        ),
      );

      alert(`✅ Pro 추출 완료\n소요 시간: ${result.duration}`);
    } catch (err) {
      setError(err.message);
      console.error("❌ Pro 추출 실패:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminValidateWrapper = async () => {
    if (selectedFileIndex === null) return;

    const selectedFile = fileList[selectedFileIndex];

    if (!selectedFile.flashResult || !selectedFile.proResult) {
      alert("먼저 Flash와 Pro 추출을 완료해주세요");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const flashData = selectedFile.flashResult.data;
      const proData = selectedFile.proResult.data;

      // 간단한 비교 로직
      const differences = {};
      const allFields = [
        "plateNumber",
        "vin",
        "model",
        "ownerName",
        "modelYear",
        "productionDate",
        "mileage",
        "totalWeight",
        "displacement",
        "width",
        "length",
        "height",
        "cbm",
        "deregistrationDate",
      ];

      let matchCount = 0;
      allFields.forEach((field) => {
        const flashValue = flashData[field];
        const proValue = proData[field];

        if (String(flashValue || "") === String(proValue || "")) {
          matchCount++;
        } else {
          differences[field] = {
            flash: flashValue,
            pro: proValue,
            different: true,
          };
        }
      });

      const matchRate = Math.round((matchCount / allFields.length) * 100);

      // 최종 데이터 구성
      const finalData = {
        ...flashData,
        alternatives: proData,
        differences: differences,
        metadata: {
          validated: true,
          matchRate: matchRate,
          needsReview: matchRate < 100,
          flashExtraction: flashData,
          proExtraction: proData,
        },
      };

      setFileList((prev) =>
        prev.map((f, idx) =>
          idx === selectedFileIndex
            ? {
                ...f,
                extractedData: finalData,
                status: "completed",
              }
            : f,
        ),
      );

      alert(
        `✅ 검증 완료\n\n` +
          `일치율: ${matchRate}%\n` +
          `일치 필드: ${matchCount}/${allFields.length}\n` +
          `차이 필드: ${Object.keys(differences).length}개\n\n` +
          `${matchRate === 100 ? "✅ 완벽히 일치합니다!" : "⚠️ 검토가 필요합니다."}`,
      );
    } catch (err) {
      setError(err.message);
      console.error("❌ 검증 실패:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleMultipleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    addFiles(files);
  };

  // 체크박스 토글
  const toggleFileCheck = (index) => {
    setFileList((prev) =>
      prev.map((f, idx) => (idx === index ? { ...f, checked: !f.checked } : f)),
    );
  };

  // 전체 선택/해제
  const toggleAllChecks = () => {
    const allChecked = fileList.every((f) => f.checked);
    setFileList((prev) => prev.map((f) => ({ ...f, checked: !allChecked })));
  };

  // 선택된 파일 개수
  const checkedCount = fileList.filter((f) => f.checked).length;

  // 🗑️ 체크된 파일 일괄 삭제 (React state + PostgreSQL DB)
  const handleRemoveCheckedFiles = async () => {
    const checkedIndexes = fileList
      .map((f, i) => (f.checked ? i : -1))
      .filter((i) => i !== -1);
    if (checkedIndexes.length === 0) return;
    if (
      !window.confirm(
        `선택된 ${checkedIndexes.length}개 파일을 삭제하시겠습니까?`,
      )
    )
      return;

    // DB에서도 삭제: ID + 파일명 모두 전송 (belt-and-suspenders)
    const checkedFiles = fileList.filter((f, i) => checkedIndexes.includes(i));
    const dbDocIds = checkedFiles
      .filter((f) => f._documentId)
      .map((f) => f._documentId);
    const dbFileNames = checkedFiles.filter((f) => f.name).map((f) => f.name);
    if (dbDocIds.length > 0 || dbFileNames.length > 0) {
      try {
        await deleteDocumentsFromDB(dbDocIds, dbFileNames);
      } catch (err) {
        console.warn("⚠️ DB 삭제 실패:", err.message);
      }
    }

    setFileList((prev) => prev.filter((_, i) => !checkedIndexes.includes(i)));
    setSelectedFileIndex(null);
  };

  // 🚀 처리 취소 핸들러 (Google AI Studio 스타일)
  const handleCancelProcessing = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsCancelled(true);
    setLoading(false);
    setProcessingCount(0);
    addLog("⛔ 사용자가 처리를 취소했습니다.", "warning");
  };

  // 📄 멀티페이지 PDF 페이지 변경 핸들러
  const handlePdfPageChange = (newPage) => {
    console.log("📄 [handlePdfPageChange] 호출됨:", {
      newPage,
      totalPdfPages,
      pdfPageResultsLength: pdfPageResults.length,
      selectedFilePageResults: selectedFile?.pageResults?.length,
    });

    // 🔧 수정: selectedFile.totalPages를 우선 사용 (pdfjs로 미리 감지된 페이지 수)
    const hasPageResults =
      selectedFile?.pageResults?.length > 0 ||
      pdfPageResults.length > 0 ||
      selectedFile?.totalPages > 0;
    const maxPages =
      selectedFile?.totalPages ||
      selectedFile?.pageResults?.length ||
      totalPdfPages ||
      0;

    console.log("📄 [handlePdfPageChange] maxPages 계산:", {
      selectedFileTotalPages: selectedFile?.totalPages,
      selectedFilePageResults: selectedFile?.pageResults?.length,
      totalPdfPages,
      maxPages,
      hasPageResults,
    });

    if (newPage < 1 || newPage > maxPages || !hasPageResults) {
      console.log("⚠️ [handlePdfPageChange] 조건 불충족으로 return:", {
        newPage,
        maxPages,
        hasPageResults,
      });
      return;
    }

    setCurrentPdfPage(newPage);
    setSelectedPageNumber(newPage); // 📄 OCR Viewer용 페이지 번호도 동기화
    setPageJumpToken((prev) => prev + 1); // 📄 jumpToPage 강제 트리거
    console.log("✅ [handlePdfPageChange] 페이지 변경 완료:", newPage);

    // 해당 페이지의 데이터 적용
    const pageData = pdfPageResults[newPage - 1];
    if (pageData) {
      // 보정된 이미지 URL 업데이트
      setCorrectedImageUrl(pageData.correctedImageUrl);

      // 이미지 크기 업데이트
      if (pageData.ocrImageSize) {
        setOcrImageSize(pageData.ocrImageSize);
      }

      // 해당 페이지의 labels만 필터링하여 표시
      const pageLabels = ocrLabels.filter(
        (label) => label.pageNumber === newPage,
      );
      // Note: ocrLabels 전체는 유지하고, 화면에는 현재 페이지만 표시하도록 처리됨

      console.log(
        `📄 [Page Change] Page ${newPage}/${totalPdfPages}, Labels: ${pageLabels.length}`,
      );
    }
  };

  // 🎯 OCR 파이프라인: PaddleOCR → vLLM → Gemini (메인 화면용)
  // returnResult: true이면 setFileList 호출하지 않고 결과 객체 반환 (배치 처리용)
  const handleOcrPipeline = async (fileItem, returnResult = false) => {
    if (!fileItem?.file) {
      console.warn("[OCR Pipeline] No file provided");
      return returnResult ? null : undefined;
    }

    const file = fileItem.file;
    const startTime = Date.now();
    const isPDF =
      file.name.toLowerCase().endsWith(".pdf") ||
      file.type === "application/pdf";

    // 템플릿 정보 가져오기
    const templateObj = selectedOcrTemplate
      ? Object.values(templates).find((t) => t.name === selectedOcrTemplate)
      : null;
    const templateFields =
      templateObj?.singleFields || templateObj?.fields || [];
    const templateId = templateObj?.id || "";

    console.log("🚀 [OCR Pipeline] 시작:", {
      fileName: file.name,
      template: templateObj?.name || "None",
      fieldCount: templateFields.length,
      processingMode: ocrProcessingMode, // 🚀 처리 모드 로깅
      isPDF: isPDF,
    });

    setLoading(true);
    setOcrLabels([]);
    setCorrectedImageUrl(null);
    addLog(
      `🔄 OCR 파이프라인 시작: ${file.name} (${ocrProcessingMode === "speed" ? "⚡속도" : "📍좌표"} 모드)`,
    );

    // 🚀 속도 기반 모드 + PDF 파일 → 안정적인 기존 엔드포인트 사용
    if (ocrProcessingMode === "speed" && isPDF) {
      try {
        addLog("  ⚡ [속도 모드] PDF 네이티브 처리 중...");

        const formData = new FormData();
        formData.append("file", file);
        formData.append("templateId", templateId);
        // 🚗 차량 마스터 enricher 의 doc_type 추정용 — template 명을 docType 으로 전달
        if (templateObj?.name) formData.append("docType", templateObj.name);
        if (editablePrompt) formData.append("customPrompt", editablePrompt); // 📋 UI 프롬프트 전달
        // 📘 검증 모드에 따라 모델 전달 (verifyEnabled: 병렬, 아니면: 단일)
        formData.append(
          "selectedValidationModels",
          JSON.stringify(effectiveValidationModels),
        );
        if (verifyEnabled)
          formData.append("verificationMode", verificationMode);
        if (verifyEnabled && verificationMode === "sequential")
          formData.append("enableJudge", "true");
        addLog(
          `  📘 추출 모델: ${effectiveValidationModels.join(" + ")} (${verifyEnabled ? (verificationMode === "sequential" ? "🔍정밀 검증(Judge)" : "⚡빠른 비교") : "⚡단일"})`,
        );
        // 📄 선택된 페이지만 추출 (체크박스 기반)
        if (checkedPages && checkedPages.length > 0) {
          formData.append("selectedPages", JSON.stringify(checkedPages));
          addLog(
            `  📄 선택 페이지: ${checkedPages.join(", ")} (${checkedPages.length}페이지)`,
          );
        }

        // 🎯 안정적인 기존 엔드포인트 사용 (SSE는 추출 오류로 임시 비활성화)
        const sseResponse = await fetch(
          `${API_BASE_URL}/api/ocr/gemini-extract-pdf-native`,
          {
            method: "POST",
            headers: { ...ocrAuthHeader() },
            body: formData,
          },
        );

        if (await handleOcrQuota(sseResponse))
          throw new Error("FREE_QUOTA_EXCEEDED");
        if (!sseResponse.ok) {
          throw new Error(`API 호출 실패: ${sseResponse.status}`);
        }

        const pdfNativeData = await sseResponse.json();

        if (pdfNativeData.success && pdfNativeData.data) {
          const elapsed = Date.now() - startTime;
          addLog(
            `  ✅ [속도 모드] 완료: ${pdfNativeData.data.pageResults?.length || 0}페이지 (${(elapsed / 1000).toFixed(1)}초)`,
          );

          const finalResult = {
            documentType: templateObj?.name || "미분류",
            ...pdfNativeData.data,
            metadata: {
              ...pdfNativeData.metadata, // 🔧 교차 검증 등 서버 메타데이터 보존
              mode: "pdf-native",
              elapsed: elapsed,
              apiDuration: pdfNativeData.metadata?.apiDuration,
            },
          };

          if (!returnResult) {
            // 🔍 Debug: grounded 데이터 확인
            const receivedGrounding = pdfNativeData.data.groundedFields || {};
            console.log("📘 [속도 모드] groundedFields:", {
              totalFields: Object.keys(receivedGrounding).length,
              fieldsWithBbox: Object.values(receivedGrounding).filter(
                (v) => v?.bbox?.coordinates,
              ).length,
            });

            setFileList((prev) =>
              prev.map((f, idx) => {
                if (idx !== selectedFileIndex) return f;

                // ========== 📦 증분 추출: checkedPages 기반 캐시 ==========
                // 서버는 항상 pageNumber:1로 통합 응답 → checkedPages로 실제 페이지 식별
                const cacheKey = f.name || `file_${idx}`;

                // 캐시 초기화 또는 기존 캐시 가져오기
                if (!extractionCacheRef.current[cacheKey]) {
                  // 첫 접근: 기존 파일 상태에서 시드
                  extractionCacheRef.current[cacheKey] = {
                    extractionBatches: {}, // { "1,2,3": { extractedData, items, ... }, "4": { ... } }
                    groundedFields: { ...(f.groundedFields || {}) },
                    pageDimensions: { ...(f.pageDimensions || {}) },
                  };
                }
                const cache = extractionCacheRef.current[cacheKey];

                // 현재 추출된 페이지 번호 (checkedPages 또는 서버 메타데이터에서)
                const extractedPages = pdfNativeData.metadata?.selectedPages ||
                  checkedPages || [1];
                const batchKey = extractedPages.sort((a, b) => a - b).join(",");

                console.error("📦 [증분 캐시] 배치 저장:", {
                  cacheKey,
                  batchKey,
                  extractedPages,
                });

                // 이번 추출 결과를 배치로 저장
                const serverPageResult =
                  (pdfNativeData.data.pageResults || [])[0] || {};
                const serverItems =
                  (pdfNativeData.data.items?.length > 0
                    ? pdfNativeData.data.items
                    : null) ||
                  serverPageResult.items ||
                  finalResult.items ||
                  [];

                // 테이블 필드 추출 (배열 타입 필드들)
                const tableFields = {};
                const sourceData = { ...serverPageResult, ...finalResult };
                Object.keys(sourceData).forEach((key) => {
                  if (
                    Array.isArray(sourceData[key]) &&
                    key !== "pageResults" &&
                    key !== "items" &&
                    key !== "metadata" &&
                    key !== "boundingBoxes" &&
                    key !== "markdown"
                  ) {
                    tableFields[key] = sourceData[key];
                  }
                });

                cache.extractionBatches[batchKey] = {
                  pages: [...extractedPages],
                  extractedData: { ...finalResult },
                  pageResult: { ...serverPageResult },
                  items: [...serverItems],
                  tableFields: tableFields,
                  timestamp: Date.now(),
                };

                // groundedFields, pageDimensions 누적
                cache.groundedFields = {
                  ...cache.groundedFields,
                  ...receivedGrounding,
                };
                cache.pageDimensions = {
                  ...cache.pageDimensions,
                  ...(pdfNativeData.data.pageDimensions || {}),
                };

                // ========== 📦 캐시에서 통합 데이터 재구성 ==========
                // 모든 배치의 items를 합치기 (같은 페이지가 여러 배치에 있으면 최신 배치 우선)
                const allBatches = Object.entries(cache.extractionBatches).sort(
                  (a, b) => (a[1].timestamp || 0) - (b[1].timestamp || 0),
                ); // 시간순

                // 페이지별 최신 배치 결정
                const pageToLatestBatch = {};
                allBatches.forEach(([bKey, batch]) => {
                  batch.pages.forEach((p) => {
                    pageToLatestBatch[p] = bKey;
                  });
                });

                // 모든 고유 페이지 수집
                const allExtractedPages = [
                  ...new Set(allBatches.flatMap(([_, batch]) => batch.pages)),
                ].sort((a, b) => a - b);

                // 통합 items: 각 페이지의 최신 배치에서 해당 페이지 items 수집
                const mergedItems = [];
                allExtractedPages.forEach((page) => {
                  const latestBatchKey = pageToLatestBatch[page];
                  const batch = cache.extractionBatches[latestBatchKey];
                  if (batch) {
                    // 이 배치의 items에서 해당 페이지 items 추출
                    const pageItems = batch.items.filter((item) => {
                      const itemPage = Number(
                        item.pageNumber ?? item.P ?? item.p ?? 0,
                      );
                      return itemPage === page;
                    });
                    if (pageItems.length > 0) {
                      mergedItems.push(...pageItems);
                    } else if (
                      batch.pages.length === 1 &&
                      batch.pages[0] === page
                    ) {
                      // 단일 페이지 배치이면 모든 items가 이 페이지의 것
                      mergedItems.push(...batch.items);
                    } else if (
                      batch.items.length > 0 &&
                      allExtractedPages.length === batch.pages.length
                    ) {
                      // 모든 페이지를 한번에 추출한 경우 — items에 pageNumber가 없을 수 있음
                      // 이 경우 items를 페이지 수로 나눌 수 없으므로 전체 포함 (중복 방지)
                      if (page === batch.pages[0]) {
                        mergedItems.push(...batch.items);
                      }
                    }
                  }
                });

                // 통합 pageResults: 각 추출 페이지마다 pageResult 엔트리 생성
                const mergedPageResults = allExtractedPages.map((page) => {
                  const latestBatchKey = pageToLatestBatch[page];
                  const batch = cache.extractionBatches[latestBatchKey];
                  return {
                    pageNumber: page,
                    ...(batch?.pageResult || {}),
                    pageNumber: page, // 덮어쓰기: 실제 페이지 번호
                  };
                });

                // 통합 extractedData: 모든 배치의 테이블 필드 합치기
                const mergedTableFields = {};
                allBatches.forEach(([bKey, batch]) => {
                  Object.entries(batch.tableFields || {}).forEach(
                    ([key, data]) => {
                      if (!mergedTableFields[key]) {
                        mergedTableFields[key] = [];
                      }
                      // 이 배치의 페이지 중 최신인 것만 포함
                      const batchPages = batch.pages;
                      const isLatestForAnyPage = batchPages.some(
                        (p) => pageToLatestBatch[p] === bKey,
                      );
                      if (isLatestForAnyPage) {
                        // 기존 데이터에서 이 배치 페이지 제거 후 추가
                        mergedTableFields[key] = mergedTableFields[key].filter(
                          (item) => {
                            const itemPage = Number(
                              item.pageNumber ?? item.P ?? item.p ?? 0,
                            );
                            return !batchPages.includes(itemPage);
                          },
                        );
                        mergedTableFields[key].push(...data);
                      }
                    },
                  );
                });

                // 최종 extractedData 구성
                const latestBatch = allBatches[allBatches.length - 1]?.[1];
                // Option 2.5: propagate _raw/_enrichment from server pageResults[0]
                //   so FieldSourceBadge can render OCR vs DB badges on the grid.
                const _serverPR0 =
                  (pdfNativeData.data.pageResults || [])[0] || {};
                const mergedExtractedData = {
                  ...(latestBatch?.extractedData || {}),
                  items: mergedItems,
                  pageResults: mergedPageResults,
                  ...mergedTableFields,
                  _source: "gemini-pdf-native",
                  _raw: _serverPR0._raw || null,
                  _enrichment: _serverPR0._enrichment || null,
                };

                console.error("📦 [증분 캐시] 통합 완료:", {
                  cacheKey,
                  batches: Object.keys(cache.extractionBatches),
                  allExtractedPages,
                  mergedItemsCount: mergedItems.length,
                  mergedPageResultsCount: mergedPageResults.length,
                  tableFieldKeys: Object.keys(mergedTableFields),
                });
                saveExtractionCache(); // 💾 localStorage 저장

                return {
                  ...f,
                  status: "completed",
                  extractedData: mergedExtractedData,
                  flashData: mergedExtractedData,
                  proData: mergedExtractedData,
                  pageResults: mergedPageResults,
                  items: mergedItems,
                  totalPages:
                    f.totalPages ||
                    pdfNativeData.metadata?.originalTotalPages ||
                    pdfNativeData.metadata?.totalPages ||
                    mergedPageResults.length ||
                    1,
                  groundedFields: { ...cache.groundedFields },
                  pageDimensions: { ...cache.pageDimensions },
                  // 🔧 교차 검증 데이터 (Side-by-Side 비교 모달용)
                  crossValidation: (() => {
                    const cv = pdfNativeData.metadata?.crossValidation || null;
                    console.error("🔧 [교차 검증 데이터]", {
                      hasCrossValidation: !!cv,
                      hasMetadata: !!pdfNativeData.metadata,
                      metadataKeys: Object.keys(pdfNativeData.metadata || {}),
                      crossItemsCount: cv?.crossItems?.length,
                      matchRate: cv?.matchRate,
                    });
                    return cv
                      ? {
                          ...cv,
                          verificationMode:
                            pdfNativeData.metadata?.verificationMode ||
                            "parallel",
                        }
                      : null;
                  })(),
                };
              }),
            );
            // 🔧 OCR 완료 후 grid 탭 자동 전환 비활성화 (사용자 요청: 1.문서인식에서 추출결과 확인)
            // setActiveTab('grid');

            // ✅ OCR 완료: 사용자의 원래 페이지 선택 유지 (체크/미체크 상태 보존)
            // setCheckedPages를 호출하지 않음 — 추출 전 사용자가 선택한 페이지만 체크 유지
            const extractedPages =
              pdfNativeData.metadata?.selectedPages || checkedPages;
            console.log(
              `📄 [OCR 완료] 사용자 선택 유지 — 체크 페이지: [${checkedPages.join(", ")}], 추출 페이지: [${extractedPages.join(", ")}]`,
            );

            // 🔍 청크 디버그 팝업 (토글 ON일 때 자동 표시)
            if (showChunkDebug && pdfNativeData.chunkDetails) {
              setChunkDebugData({
                chunkDetails: pdfNativeData.chunkDetails,
                referenceExample: pdfNativeData.referenceExample,
                totalPages: pdfNativeData.metadata?.totalPages || 0,
              });
              setShowChunkDebugModal(true);
            }
          }

          setLoading(false);
          addLog(`✅ OCR 파이프라인 완료 (${(elapsed / 1000).toFixed(1)}초)`);
          // setShowBatchValidationGrid(true); // 자동 표시 비활성화 — 이미지뷰어 가림 방지

          return returnResult
            ? {
                fileId: fileItem.id,
                isPDF: true,
                finalResult,
                mode: "pdf-native",
                pageResults: pdfNativeData.data.pageResults || [],
                paddlePageResults: pdfNativeData.data.pageResults || [],
                totalPages:
                  pdfNativeData.metadata?.totalPages ||
                  pdfNativeData.data.pageResults?.length ||
                  1,
                multiPage: (pdfNativeData.data.pageResults?.length || 0) > 1,
                groundedFields: pdfNativeData.data.groundedFields || {}, // ⭐ [Grounding]
                pageDimensions: pdfNativeData.data.pageDimensions || {}, // ⭐ [Grounding]
                selectedPages:
                  pdfNativeData.metadata?.selectedPages || checkedPages || null, // 📦 증분 캐시용
                crossValidation: pdfNativeData.metadata?.crossValidation
                  ? {
                      ...pdfNativeData.metadata.crossValidation,
                      verificationMode:
                        pdfNativeData.metadata?.verificationMode || "parallel",
                    }
                  : null, // 🔧 교차 검증 데이터 + 검증 모드
              }
            : undefined;
        } else {
          throw new Error(pdfNativeData.error || "PDF 네이티브 처리 실패");
        }
      } catch (pdfError) {
        // 🚨 폴백 제거: 명확한 에러 표시 (사용자가 문제를 인지할 수 있도록)
        console.error("❌ [속도 모드] 실패:", pdfError);
        addLog(`  ❌ [속도 모드] 실패: ${pdfError.message}`, "error");

        // 🔔 사용자에게 에러 알림 (토스트)
        if (window.toast) {
          window.toast.error(`PDF 추출 실패: ${pdfError.message}`);
        } else {
          alert(
            `PDF 추출 실패: ${pdfError.message}\n\n좌표 모드로 다시 시도하려면 처리모드를 변경하세요.`,
          );
        }

        setLoading(false);
        setFileList((prev) =>
          prev.map((f, idx) =>
            idx === selectedFileIndex
              ? { ...f, status: "error", error: pdfError.message }
              : f,
          ),
        );

        return returnResult
          ? { fileId: fileItem.id, error: pdfError.message }
          : undefined;
      }
    }

    try {
      // ===== 1단계: PaddleOCR =====
      setOcrPipelineStep("1/3 PaddleOCR...");
      addLog("  📷 [1/3] PaddleOCR 텍스트 감지 중...");

      const formData = new FormData();
      formData.append("file", file);

      const paddleRes = await fetch(`${API_BASE_URL}/api/ocr/paddle-only`, {
        method: "POST",
        body: formData,
      });
      const paddleData = await paddleRes.json();

      // 🔍 DEBUG: 백엔드 응답 확인
      console.log(
        "🔍 [DEBUG] paddleData 응답:",
        JSON.stringify({
          success: paddleData.success,
          multiPage: paddleData.multiPage,
          totalPages: paddleData.totalPages,
          hasPageResults: !!paddleData.pageResults,
          pageResultsLength: paddleData.pageResults?.length,
          hasLabels: !!paddleData.labels,
        }),
      );

      if (!paddleData.success) {
        throw new Error(paddleData.error || "PaddleOCR 실패");
      }

      // ⭐ 멀티페이지 PDF 처리
      let allLabels = [];
      let correctedUrl = null;
      let pageOcrImageSize = null;

      if (paddleData.multiPage && paddleData.pageResults) {
        console.log(
          `📄 [OCR Pipeline] 멀티페이지 PDF: ${paddleData.totalPages}페이지`,
        );
        addLog(`  📄 멀티페이지 PDF 감지: ${paddleData.totalPages}페이지`);

        // 모든 페이지의 labels 합치기
        paddleData.pageResults.forEach((pageResult, idx) => {
          const pageLabels = (pageResult.labels || []).map(
            (label, labelIdx) => ({
              id: label.id || `ocr-page${pageResult.pageNumber}-${labelIdx}`,
              text: label.text || "",
              bbox: label.bbox,
              confidence: label.confidence,
              fieldKey: label.fieldKey || null,
              pageNumber: pageResult.pageNumber,
              vllmText: null,
              geminiText: null,
            }),
          );
          allLabels = [...allLabels, ...pageLabels];

          console.log(
            `  📄 Page ${pageResult.pageNumber}: ${pageLabels.length} labels`,
          );
        });

        // 첫 페이지의 corrected image 사용 (현재 뷰어용)
        const firstPage = paddleData.pageResults[0];
        correctedUrl = firstPage?.correctedImageUrl
          ? `${API_BASE_URL}${firstPage.correctedImageUrl}?t=${Date.now()}`
          : null;
        pageOcrImageSize = firstPage?.ocrImageSize;

        addLog(
          `  ✅ 총 ${allLabels.length}개 텍스트 감지 (${paddleData.totalPages}페이지)`,
        );
      } else {
        // 단일 페이지 처리 (기존 로직)
        correctedUrl = paddleData.correctedImageUrl
          ? `${API_BASE_URL}${paddleData.correctedImageUrl}?t=${Date.now()}`
          : null;
        pageOcrImageSize = paddleData.ocrImageSize;

        allLabels = (paddleData.labels || []).map((label, idx) => ({
          id: label.id || `ocr-${idx}`,
          text: label.text || "",
          bbox: label.bbox,
          confidence: label.confidence,
          fieldKey: label.fieldKey || null,
          vllmText: null,
          geminiText: null,
        }));

        addLog(
          `  ✅ PaddleOCR: ${allLabels.length}개 텍스트 감지${paddleData.detectedAngle ? ` (${paddleData.detectedAngle}° 보정)` : ""}`,
        );
      }

      // 보정된 이미지 URL 설정
      setCorrectedImageUrl(correctedUrl);

      // 📄 멀티페이지 상태 저장
      if (paddleData.multiPage && paddleData.pageResults) {
        console.log(
          "📄 [Frontend] 멀티페이지 감지:",
          paddleData.totalPages,
          "페이지",
        );
        console.log(
          "📄 [Frontend] pageResults:",
          paddleData.pageResults.length,
          "개",
        );
        setPdfPageResults(
          paddleData.pageResults.map((pr) => ({
            ...pr,
            correctedImageUrl: pr.correctedImageUrl
              ? `${API_BASE_URL}${pr.correctedImageUrl}?t=${Date.now()}`
              : null,
          })),
        );
        setCurrentPdfPage(1);
        setSelectedPageNumber(1); // 📄 OCR Viewer용 페이지 번호도 초기화
        setTotalPdfPages(paddleData.totalPages);
        console.log(
          "📄 [Frontend] setTotalPdfPages 호출:",
          paddleData.totalPages,
        );
      } else {
        // 단일 페이지 - 멀티페이지 상태 초기화
        setPdfPageResults([]);
        setCurrentPdfPage(1);
        setSelectedPageNumber(1);
        setTotalPdfPages(1);
      }

      // 이미지 크기 저장
      if (pageOcrImageSize) {
        setOcrImageSize(pageOcrImageSize);
      }

      // 🔍 디버그: labels과 bbox 데이터 확인
      console.log("🔍 [OCR Debug] allLabels:", allLabels.length, "items");
      console.log("🔍 [OCR Debug] First label bbox:", allLabels[0]?.bbox);
      console.log("🔍 [OCR Debug] ocrImageSize:", pageOcrImageSize);

      setOcrLabels(allLabels);

      // initialLabels를 allLabels로 대체
      const initialLabels = allLabels;

      // ===== 2단계: vLLM (병렬 시작) =====
      setOcrPipelineStep("2/3 AI 병합...");
      addLog("  🤖 [2/3] vLLM 필드 병합 중...");

      // 📄 pageNumber 포함하여 백엔드에서 페이지별 분리 처리 가능하게 함
      const ocrBoxes = initialLabels.map((l) => ({
        text: l.text,
        bbox: l.bbox,
        pageNumber: l.pageNumber || 1,
      }));

      // 📄 멀티페이지 PDF면 단일 Gemini 호출 스킵 (나중에 병렬 추출)
      const isMultiPage =
        paddleData.multiPage && paddleData.pageResults?.length > 1;

      // 🚀 vLLM 타임아웃: 30초 (페이지별 병렬 처리 시간 확보)
      const vllmController = new AbortController();
      const vllmTimeout = setTimeout(() => vllmController.abort(), 30000);

      // vLLM + Gemini 병렬 실행
      const [mergeResult, geminiResult] = await Promise.allSettled([
        // vLLM Merge (2초 타임아웃)
        fetch(`${API_BASE_URL}/api/ocr/merge-with-llm`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ocrBoxes: ocrBoxes,
            templateFields: templateFields,
          }),
          signal: vllmController.signal,
        })
          .then((res) => res.json())
          .finally(() => clearTimeout(vllmTimeout)),

        // Gemini Extract (멀티페이지면 스킵!)
        isMultiPage
          ? Promise.resolve({ success: true, data: {}, skipped: true })
          : (async () => {
              const geminiFormData = new FormData();
              // correctedUrl 또는 원본 파일 사용
              if (correctedUrl) {
                const imgRes = await fetch(correctedUrl);
                const imgBlob = await imgRes.blob();
                geminiFormData.append("file", imgBlob, "image.png");
              } else {
                geminiFormData.append("file", file);
              }
              geminiFormData.append("templateId", templateId);
              geminiFormData.append(
                "templateFields",
                JSON.stringify(templateFields),
              );
              geminiFormData.append("skipPaddleOCR", "true"); // 🚀 중복 PaddleOCR 방지
              if (editablePrompt)
                geminiFormData.append("customPrompt", editablePrompt); // 📋 UI 프롬프트 전달
              return fetch(`${API_BASE_URL}/api/ocr/gemini-extract`, {
                method: "POST",
                body: geminiFormData,
              }).then((res) => res.json());
            })(),
      ]);

      // ===== vLLM 결과 처리 =====
      let updatedLabels = [...initialLabels];
      if (mergeResult.status === "fulfilled" && mergeResult.value.success) {
        const mergedData = mergeResult.value.data;
        addLog(`  ✅ vLLM: ${Object.keys(mergedData).length}개 필드 추출`);

        // 역매핑: 추출값으로 bbox에 fieldKey 할당
        for (const [fieldKey, extractedValue] of Object.entries(mergedData)) {
          if (!extractedValue || typeof extractedValue !== "string") continue;

          const normalizedValue = extractedValue
            .replace(/[\s\-.,·()]/g, "")
            .toLowerCase();

          for (let i = 0; i < updatedLabels.length; i++) {
            const label = updatedLabels[i];
            if (label.fieldKey) continue;
            if (!label.text) continue;

            const normalizedText = label.text
              .replace(/[\s\-.,·()]/g, "")
              .toLowerCase();
            const isMatch =
              normalizedValue.includes(normalizedText) ||
              normalizedText.includes(normalizedValue);

            if (isMatch && normalizedText.length >= 2) {
              updatedLabels[i] = {
                ...label,
                fieldKey: fieldKey,
                vllmText: extractedValue,
                source: "vllm",
              };
            }
          }
        }
      } else {
        addLog("  ⚠️ vLLM 병합 실패", "warning");
      }

      // ===== 3단계: Gemini 결과 처리 =====
      setOcrPipelineStep("3/3 Gemini 추출...");

      let extractedData = {};
      if (geminiResult.status === "fulfilled" && geminiResult.value.success) {
        extractedData = geminiResult.value.data || {};
        addLog(`  ✅ Gemini: ${Object.keys(extractedData).length}개 필드 추출`);

        // Gemini 결과를 labels에 geminiText로 추가
        for (const [fieldKey, value] of Object.entries(extractedData)) {
          // 해당 fieldKey를 가진 labels 찾기
          const matchingIndices = updatedLabels
            .map((l, i) => (l.fieldKey === fieldKey ? i : -1))
            .filter((i) => i >= 0);

          matchingIndices.forEach((idx) => {
            updatedLabels[idx] = {
              ...updatedLabels[idx],
              geminiText: value,
            };
          });
        }
      } else {
        addLog("  ⚠️ Gemini 추출 실패", "warning");
      }

      setOcrLabels(updatedLabels);

      // ===== 결과를 fileList에 저장 =====
      const elapsed = Date.now() - startTime;
      const finalResult = {
        documentType: templateObj?.name || "미분류",
        ...extractedData,
        metadata: {
          ocrLabels: updatedLabels,
          correctedImageUrl: correctedUrl,
          ocrImageSize: paddleData.ocrImageSize,
          elapsed: elapsed,
        },
      };

      // 📄 멀티페이지 PDF: 각 페이지에 대해 LLM 추출 실행
      let allPageExtractedData = [];
      if (
        paddleData.multiPage &&
        paddleData.pageResults &&
        paddleData.pageResults.length > 1
      ) {
        addLog(
          `📄 멀티페이지 PDF: ${paddleData.totalPages}개 페이지 LLM 추출 중...`,
        );

        // 🚀 30페이지 청크 기반 배치 API 사용 (문맥 유지 + 효율성)
        addLog(
          `  🚀 ${paddleData.totalPages}페이지 배치 Gemini 추출 시작 (30페이지/청크)...`,
        );

        try {
          // 페이지 이미지 정보 수집
          const pageImages = paddleData.pageResults
            .map((pageResult, i) => {
              const pageNum = pageResult.pageNumber || i + 1;
              const pageImageUrl = pageResult.correctedImageUrl
                ? pageResult.correctedImageUrl.startsWith("http")
                  ? pageResult.correctedImageUrl
                  : `${API_BASE_URL}${pageResult.correctedImageUrl}`
                : null;

              return {
                imageUrl: pageImageUrl,
                pageNumber: pageNum,
              };
            })
            .filter((p) => p.imageUrl);

          console.log(`📄 [Batch] 유효 페이지 이미지: ${pageImages.length}개`);

          // 배치 API 호출
          const batchRes = await fetch(
            `${API_BASE_URL}/api/ocr/gemini-extract-batch`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                pageImages: pageImages,
                templateId: templateId,
                customPrompt: editablePrompt || undefined, // 📋 UI 프롬프트 전달
              }),
            },
          );
          const batchData = await batchRes.json();

          if (batchData.success && batchData.data) {
            addLog(
              `  ✅ 배치 추출 완료: ${batchData.metadata?.chunksSuccessful || 1}/${batchData.metadata?.chunksProcessed || 1} 청크 성공 (${(batchData.metadata?.elapsed / 1000).toFixed(1)}초)`,
            );

            // 배치 결과를 페이지별 데이터 형태로 변환
            // 실제 전송된 pageImages의 pageNumber를 사용 (첫 번째 페이지에 전체 데이터 할당)
            const validPageNumbers = pageImages.map((p) => p.pageNumber);
            const firstValidPage = validPageNumbers[0] || 1;

            allPageExtractedData = [
              {
                pageNumber: firstValidPage, // 실제 첫 번째 유효 페이지 번호 사용
                data: {
                  documentType: templateObj?.name || "미분류",
                  ...batchData.data,
                },
              },
            ];

            // 나머지 유효 페이지는 빈 데이터 (테이블 행은 첫 번째에 병합됨)
            for (let i = 1; i < validPageNumbers.length; i++) {
              allPageExtractedData.push({
                pageNumber: validPageNumbers[i],
                data: { documentType: templateObj?.name || "미분류" },
              });
            }

            console.log(
              `📄 [Batch] 페이지 매핑: ${validPageNumbers.join(", ")}`,
            );
          } else {
            // 배치 실패 시 기존 방식으로 폴백
            console.warn(
              "⚠️ 배치 API 실패, 기존 방식으로 폴백:",
              batchData.error,
            );
            addLog(`  ⚠️ 배치 실패, 개별 추출로 전환...`, "warning");

            // 기존 개별 페이지 처리 로직
            const extractionPromises = paddleData.pageResults.map(
              async (pageResult, i) => {
                const pageNum = pageResult.pageNumber || i + 1;
                const pageImageUrl = pageResult.correctedImageUrl
                  ? pageResult.correctedImageUrl.startsWith("http")
                    ? pageResult.correctedImageUrl
                    : `${API_BASE_URL}${pageResult.correctedImageUrl}?t=${Date.now()}`
                  : null;

                if (!pageImageUrl) return { pageNumber: pageNum, data: null };

                try {
                  const geminiFormData = new FormData();
                  const imgRes = await fetch(pageImageUrl);
                  const imgBlob = await imgRes.blob();
                  geminiFormData.append("file", imgBlob, `page${pageNum}.png`);
                  geminiFormData.append("templateId", templateId);
                  geminiFormData.append(
                    "templateFields",
                    JSON.stringify(templateFields),
                  );
                  geminiFormData.append("skipPaddleOCR", "true");
                  if (editablePrompt)
                    geminiFormData.append("customPrompt", editablePrompt); // 📋 UI 프롬프트 전달

                  const geminiRes = await fetch(
                    `${API_BASE_URL}/api/ocr/gemini-extract`,
                    {
                      method: "POST",
                      body: geminiFormData,
                    },
                  );
                  const geminiJson = await geminiRes.json();

                  if (geminiJson.success && geminiJson.data) {
                    return {
                      pageNumber: pageNum,
                      data: {
                        documentType: templateObj?.name || "미분류",
                        ...geminiJson.data,
                      },
                    };
                  }
                  return { pageNumber: pageNum, data: null };
                } catch (err) {
                  console.error(
                    `❌ [페이지 ${pageNum}] Gemini 추출 오류:`,
                    err,
                  );
                  return { pageNumber: pageNum, data: null };
                }
              },
            );

            allPageExtractedData = await Promise.all(extractionPromises);
          }
        } catch (batchError) {
          console.error("❌ 배치 API 오류:", batchError);
          addLog(`  ❌ 배치 오류: ${batchError.message}`, "error");
        }

        allPageExtractedData.sort((a, b) => a.pageNumber - b.pageNumber);
        addLog(
          `✅ 멀티페이지 LLM 추출 완료: ${allPageExtractedData.filter((p) => p.data).length}/${paddleData.totalPages} 성공`,
        );
      }

      // 📄 멀티페이지 머지 그룹 생성
      let mergedGroups = null;
      if (paddleData.multiPage && allPageExtractedData.length > 0) {
        // allPageExtractedData를 pageResults 형태로 변환
        let pageResultsForMerge = allPageExtractedData.map((p) => ({
          pageNumber: p.pageNumber,
          extractedData: p.data,
          documentType: p.data?.documentType,
        }));

        // 📄 템플릿에서 mergeRules 수집 및 적용
        const allMergeRules = [];
        Object.values(templates || {}).forEach((tpl) => {
          if (tpl.mergeRules && Array.isArray(tpl.mergeRules)) {
            allMergeRules.push(...tpl.mergeRules);
          }
        });

        if (allMergeRules.length > 0) {
          pageResultsForMerge = applyMergeRules(
            pageResultsForMerge,
            allMergeRules,
          );
          addLog(`🔄 머지 규칙 적용: ${allMergeRules.length}개`);
        }

        mergedGroups = mergePageResults(pageResultsForMerge, templates, {
          defaultMatchKey: "성명",
          enableMerge: true,
        });

        addLog(
          `📦 머지 완료: ${pageResultsForMerge.length}페이지 → ${mergedGroups.length}그룹`,
        );
      }

      // 🎯 결과 객체 생성 (배치 모드/단일 모드 공통)
      const ocrResult = {
        fileId: fileItem.id,
        isPDF: fileItem.isPDF,
        pageNumber: selectedPageNumber || 1,
        finalResult,
        updatedLabels,
        ocrImageSize: paddleData.ocrImageSize,
        correctedUrl,
        // 📄 멀티페이지 PDF 데이터 추가
        multiPage: paddleData.multiPage || false,
        totalPages: paddleData.totalPages || 1,
        paddlePageResults: paddleData.pageResults || null, // 전체 페이지 OCR 결과
        allPageExtractedData:
          allPageExtractedData.length > 0 ? allPageExtractedData : null, // 📄 각 페이지 LLM 결과
        mergedGroups, // 📄 머지된 그룹
      };

      // 🔥 배치 모드: 결과만 반환 (setFileList는 호출하지 않음)
      if (returnResult) {
        addLog(`✅ OCR 파이프라인 완료 (${(elapsed / 1000).toFixed(1)}초)`);
        return ocrResult;
      }

      // 🎯 단일 모드: 직접 setFileList 호출
      setFileList((prev) =>
        prev.map((f, idx) => {
          if (idx !== selectedFileIndex) return f;

          if (f.isPDF) {
            // 🎯 PDF 파일: pageResults 업데이트

            // ⭐ 멀티페이지 PDF 처리: paddleData.pageResults 사용
            console.log("🔍 [FileList Update] PDF 조건 확인:", {
              multiPage: paddleData.multiPage,
              hasPageResults: !!paddleData.pageResults,
              pageResultsLength: paddleData.pageResults?.length,
              totalPages: paddleData.totalPages,
            });
            if (
              paddleData.multiPage &&
              paddleData.pageResults &&
              paddleData.pageResults.length > 1
            ) {
              console.log(
                "📄 [FileList Update] 멀티페이지 PDF:",
                paddleData.totalPages,
                "페이지",
              );

              // 모든 페이지 결과를 pageResults로 저장
              const allPageResults = paddleData.pageResults.map(
                (pr, prIdx) => ({
                  pageNumber: pr.pageNumber || prIdx + 1,
                  ocrImageSize: pr.ocrImageSize,
                  boundingBoxes: (pr.labels || []).map((label, labelIdx) => ({
                    id: label.id || `ocr-page${pr.pageNumber}-${labelIdx}`,
                    text: label.text || "",
                    bbox: label.bbox,
                    confidence: label.confidence,
                    fieldKey: label.fieldKey || null,
                    pageNumber: pr.pageNumber,
                  })),
                  correctedImageUrl: pr.correctedImageUrl
                    ? pr.correctedImageUrl.startsWith("http")
                      ? pr.correctedImageUrl
                      : `${API_BASE_URL}${pr.correctedImageUrl}?t=${Date.now()}`
                    : null,
                  pngImage: pr.correctedImageUrl
                    ? pr.correctedImageUrl.startsWith("http")
                      ? pr.correctedImageUrl
                      : `${API_BASE_URL}${pr.correctedImageUrl}?t=${Date.now()}`
                    : null,
                  flashData: null, // LLM 추출 결과는 아직 없음
                  proData: null,
                  extractedData: null,
                }),
              );

              console.log(
                "📄 [FileList Update] allPageResults:",
                allPageResults.length,
                "페이지",
              );

              return {
                ...f,
                status: "completed",
                pageResults: allPageResults,
                totalPages: paddleData.totalPages, // ⭐ totalPages 설정!
              };
            }

            // 단일 페이지 PDF 처리 (기존 로직)
            const pageNum = selectedPageNumber || 1;
            const existingPageResults = f.pageResults || [];

            // 해당 페이지 결과 업데이트 또는 추가
            let pageResultUpdated = false;
            const updatedPageResults = existingPageResults.map((pr) => {
              if (pr.pageNumber === pageNum) {
                pageResultUpdated = true;
                return {
                  ...pr,
                  flashData: finalResult, // PageBasedGridPanel이 인식
                  proData: finalResult, // 둘 다 같은 값으로 설정
                  extractedData: finalResult,
                  boundingBoxes: updatedLabels,
                  ocrImageSize: paddleData.ocrImageSize,
                  pngImage: correctedUrl || pr.pngImage, // correctedUrl은 이미 전체 URL
                };
              }
              return pr;
            });

            // 페이지가 없으면 추가
            if (!pageResultUpdated) {
              updatedPageResults.push({
                pageNumber: pageNum,
                flashData: finalResult,
                proData: finalResult,
                extractedData: finalResult,
                boundingBoxes: updatedLabels,
                ocrImageSize: paddleData.ocrImageSize,
                pngImage: correctedUrl || null, // correctedUrl은 이미 전체 URL
              });
            }

            return {
              ...f,
              status: "completed",
              pageResults: updatedPageResults,
              totalPages: f.totalPages || updatedPageResults.length, // ⭐ totalPages 설정
            };
          } else {
            // 🎯 이미지 파일: extractedData + flashData/proData 업데이트
            return {
              ...f,
              status: "completed",
              extractedData: finalResult,
              flashData: finalResult, // PageBasedGridPanel 호환
              proData: finalResult, // PageBasedGridPanel 호환
              boundingBoxes: updatedLabels,
              ocrImageSize: paddleData.ocrImageSize,
            };
          }
        }),
      );

      setOcrPipelineStep("");
      addLog(`✅ OCR 파이프라인 완료 (${(elapsed / 1000).toFixed(1)}초)`);
      // setShowBatchValidationGrid(true); // 자동 표시 비활성화 — 이미지뷰어 가림 방지
    } catch (error) {
      console.error("[OCR Pipeline Error]", error);
      setOcrPipelineStep("");
      addLog(`❌ OCR 파이프라인 오류: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  // 🔄 배치 스케줄러: 체크된 파일을 순차적으로 추출 (파일별 상태 추적)
  const handleSchedulerBatchExtract = async (action) => {
    // 취소 처리
    if (action === "cancel") {
      batchAbortRef.current = true;
      setIsBatchExtracting(false);
      addLog("⏹ 배치 추출 중지 요청됨");
      // 남은 queued 파일 상태 초기화
      setFileList((prev) =>
        prev.map((f) =>
          f.batchStatus === "queued" ? { ...f, batchStatus: null } : f,
        ),
      );
      // progressbar 리셋
      setProcessingProgress({
        currentPage: 0,
        totalPages: 0,
        step: "",
        fileName: "",
        percentage: 0,
        startTime: null,
        estimatedSeconds: 0,
        model1: null,
        model2: null,
      });
      return;
    }

    const checkedFiles = fileList
      .map((f, idx) => ({ ...f, index: idx }))
      .filter((f) => f.checked);
    if (checkedFiles.length === 0) {
      alert("처리할 파일을 선택해주세요");
      return;
    }

    // 🔄 글로벌 템플릿 1개로 일괄 추출
    const globalTemplateObj = selectedOcrTemplate
      ? Object.values(templates).find((t) => t.name === selectedOcrTemplate)
      : null;

    if (!globalTemplateObj) {
      alert("템플릿을 먼저 선택해주세요");
      return;
    }

    console.log(
      "🔄 [배치 추출] 시작:",
      checkedFiles.length,
      "파일, 글로벌 템플릿:",
      globalTemplateObj.name,
    );
    setShowLogs(true);
    setProcessingLogs([]);
    addLog(
      `🔄 배치 추출 시작: ${checkedFiles.length}개 파일 (📋 ${globalTemplateObj.name})`,
    );

    // 모든 체크 파일을 'queued'로 설정
    setFileList((prev) =>
      prev.map((f, idx) => ({
        ...f,
        batchStatus: f.checked ? "queued" : f.batchStatus,
      })),
    );

    setIsBatchExtracting(true);
    batchAbortRef.current = false;

    // ⭐ 진행률 초기화
    const progressStartTime = Date.now();
    const estimatedTime = checkedFiles.length * 15; // 파일당 ~15초 추정
    setProcessingProgress({
      currentPage: 0,
      totalPages: checkedFiles.length,
      step: "배치 추출 중...",
      fileName: checkedFiles[0]?.name || "",
      percentage: 0,
      startTime: progressStartTime,
      estimatedSeconds: estimatedTime,
    });

    let successCount = 0;
    let errorCount = 0;
    let completedCount = 0;
    let firstCompletedIdx = null;

    // 🔧 단일 파일 추출 워커 함수
    const extractOneFile = async (fileItem, orderIdx) => {
      if (batchAbortRef.current) return;

      const fileIdx = fileItem.index;

      // 'extracting' 상태 표시
      setFileList((prev) =>
        prev.map((f, idx) =>
          idx === fileIdx ? { ...f, batchStatus: "extracting" } : f,
        ),
      );
      addLog(
        `  🔄 [${orderIdx + 1}/${checkedFiles.length}] ${fileItem.name} 추출 중...`,
      );

      try {
        // 📥 복원 문서: file이 null이면 GCS에서 다운로드
        let fileBlob = fileItem.file;
        if (!fileBlob && fileItem._restoredFromDB && fileItem._hasPdf) {
          addLog(
            `  📥 [${orderIdx + 1}/${checkedFiles.length}] ${fileItem.name} GCS에서 다운로드 중...`,
          );
          fileBlob = await fetchRestoredPdf(
            fileItem._documentId,
            fileItem.name,
          );
          if (!fileBlob) throw new Error("PDF 다운로드 실패 (GCS)");
          setFileList((prev) =>
            prev.map((f, idx) =>
              idx === fileIdx
                ? {
                    ...f,
                    file: fileBlob,
                    preview: URL.createObjectURL(fileBlob),
                    isPDF: true,
                  }
                : f,
            ),
          );
        }
        if (!fileBlob)
          throw new Error("파일이 없습니다. 원본 PDF를 다시 업로드해주세요.");

        // ✅ 단건/다중 공통 추출 헬퍼 사용 (건별=다중건 결과 일치 보장)
        const { data, result, cv, receivedGrounding } =
          await callAdaptiveExtract(fileBlob, "batch-scheduler");

        if (batchAbortRef.current) return;

        if (data.success && data.data) {
          setFileList((prev) =>
            prev.map((f, idx) => {
              if (idx !== fileIdx) return f;
              return {
                ...f,
                batchStatus: "completed",
                status: "completed",
                extractedData: result,
                flashData: result,
                proData: cv?.secondData
                  ? { ...result, ...cv.secondData }
                  : result,
                pageResults: result.pageResults || [],
                items: result.items || [],
                totalPages: f.totalPages || data.data.pageResults?.length || 1,
                groundedFields: {
                  ...(f.groundedFields || {}),
                  ...receivedGrounding,
                },
                pageDimensions: {
                  ...(f.pageDimensions || {}),
                  ...(data.data.pageDimensions || {}),
                },
                crossValidation: cv
                  ? {
                      ...cv,
                      verificationMode:
                        data.metadata?.verificationMode || "parallel",
                    }
                  : null,
              };
            }),
          );

          // 📄 현재 선택된 파일의 추출이 완료되면 → checkedPages 즉시 업데이트
          if (fileIdx === selectedFileIndex) {
            // 🔧 FIX: 실제 PDF 페이지 수 사용 (pageResults.length는 Gemini phantom 페이지로 부풀려질 수 있음)
            const actualTotalPages =
              fileList[fileIdx]?.totalPages ||
              data.metadata?.totalPages ||
              data.data.pageResults?.length ||
              1;
            const pageCount = Math.min(
              actualTotalPages,
              data.data.pageResults?.length || 1,
            );
            const allPageNumbers = Array.from(
              { length: pageCount },
              (_, k) => k + 1,
            );
            setCheckedPages(allPageNumbers);
            setSelectedPageNumber(1);
          }

          if (firstCompletedIdx === null) firstCompletedIdx = fileIdx;
          successCount++;
          addLog(
            `  ✅ [${orderIdx + 1}/${checkedFiles.length}] ${fileItem.name} 완료 (${((data.metadata?.elapsed || 0) / 1000).toFixed(1)}초)`,
          );
        } else {
          throw new Error(data.error || "추출 실패");
        }
      } catch (err) {
        errorCount++;
        console.error(`❌ [배치] ${fileItem.name}:`, err.message);
        setFileList((prev) =>
          prev.map((f, idx) =>
            idx === fileIdx ? { ...f, batchStatus: "error" } : f,
          ),
        );
        addLog(
          `  ❌ [${orderIdx + 1}/${checkedFiles.length}] ${fileItem.name} 실패: ${err.message}`,
        );
      } finally {
        completedCount++;
        const pct = Math.round((completedCount / checkedFiles.length) * 100);
        setProcessingProgress((prev) => ({
          ...prev,
          currentPage: completedCount,
          percentage: pct,
          step: `완료 ${completedCount}/${checkedFiles.length}`,
          fileName: fileItem.name,
        }));
      }
    };

    // 🚀 제한 병렬 처리 — 다중파일 배치 속도 최적화
    // 2→5: 10건 기준 5 wave→2 wave (≈70s→≈15-18s). 작은 인보이스는 문서당 ~6-7s(inline, 업로드 없음).
    // 백엔드 callGeminiAPIWithRetry(429/503 backoff) + GEMINI_API_KEY 4개 round-robin 이라 5 동시(키당 ~1-2)는 안전.
    // 10 전체 병렬은 멀티페이지 문서에서 GEMINI_CONCURRENCY(4p) 와 곱해져 429 위험 → 5가 안전 절충.
    const CONCURRENCY = 5;
    addLog(`  ⚡ 병렬 처리 모드: ${CONCURRENCY}건씩 동시 실행`);

    for (let i = 0; i < checkedFiles.length; i += CONCURRENCY) {
      if (batchAbortRef.current) {
        addLog(
          `⏹ 배치 추출 중지됨 (${completedCount}/${checkedFiles.length} 처리됨)`,
        );
        break;
      }

      const batch = checkedFiles.slice(i, i + CONCURRENCY);
      const batchNames = batch.map((f) => f.name).join(", ");
      console.log(
        `🚀 [배치 병렬] Wave ${Math.floor(i / CONCURRENCY) + 1}: ${batchNames}`,
      );

      // 진행률 업데이트: 현재 Wave 표시
      setProcessingProgress((prev) => ({
        ...prev,
        step: `[${i + 1}~${Math.min(i + CONCURRENCY, checkedFiles.length)}/${checkedFiles.length}] 처리 중...`,
        fileName: batchNames,
      }));

      await Promise.allSettled(
        batch.map((fileItem, batchIdx) =>
          extractOneFile(fileItem, i + batchIdx),
        ),
      );
    }

    setIsBatchExtracting(false);
    const completionMsg = `✅ 배치 추출 완료: 성공 ${successCount}, 실패 ${errorCount} / 총 ${checkedFiles.length}`;
    addLog(completionMsg);
    setLastCompletionLog(completionMsg);

    // 🔧 배치 완료 후: 첫 번째 완료 파일로 전환 + checkedPages 설정
    if (firstCompletedIdx !== null) {
      setSelectedFileIndex(firstCompletedIdx);
      // fileList에서 해당 파일의 페이지 수 계산
      setFileList((prev) => {
        const completedFile = prev[firstCompletedIdx];
        const pageCount =
          completedFile?.pageResults?.length || completedFile?.totalPages || 1;
        const allPageNumbers = Array.from(
          { length: pageCount },
          (_, i) => i + 1,
        );
        setCheckedPages(allPageNumbers);
        setSelectedPageNumber(1);
        return prev; // fileList 자체는 변경 없음
      });
    }

    // // 추출 완료 시 전체검증 그리드 자동 표시 — 비활성화 (이미지뷰어 가림 방지)
    // if (successCount > 0) setShowBatchValidationGrid(true);

    setProcessingProgress((prev) => ({
      ...prev,
      percentage: 100,
      currentPage: checkedFiles.length,
      model1: null,
      model2: null,
    }));
    setTimeout(() => {
      setProcessingProgress({
        currentPage: 0,
        totalPages: 0,
        step: "",
        fileName: "",
        percentage: 0,
        startTime: null,
        estimatedSeconds: 0,
        model1: null,
        model2: null,
      });
    }, 1000);
    setTimeout(() => {
      setLastCompletionLog(null);
    }, 5000);
  };

  // 옵션5: 일괄 병렬 처리 (분류 + Flash + Pro 병렬)
  const handleBatchFullProcess = async () => {
    if (loading) {
      console.warn("[일괄 처리] 이미 실행 중입니다. 중복 클릭 무시.");
      return;
    }

    const checkedFiles = fileList
      .map((f, idx) => ({ ...f, index: idx }))
      .filter((f) => f.checked);

    if (checkedFiles.length === 0) {
      alert("처리할 파일을 선택해주세요");
      return;
    }

    // 🚀 취소 상태 초기화 및 AbortController 생성
    setIsCancelled(false);
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setProcessingCount(checkedFiles.length);
    setShowLogs(true); // 로그 영역 표시
    setProcessingLogs([]); // 이전 로그 초기화
    addLog(
      `일괄 처리 시작... (${ocrProcessingMode === "speed" ? "⚡속도" : "📍좌표"} 모드)`,
    );

    // 🆕 한국 차종 한↔영 dictionary (자주 쓰이는 차종)
    const CAR_KO_EN = {
      // Hyundai
      아반떼: "AVANTE",
      아반테: "AVANTE",
      쏘나타: "SONATA",
      소나타: "SONATA",
      그랜저: "GRANDEUR",
      그렌져: "GRANDEUR",
      베르나: "VERNA",
      엘란트라: "ELANTRA",
      산타페: "SANTAFE",
      싼타페: "SANTAFE",
      투싼: "TUCSON",
      코나: "KONA",
      베뉴: "VENUE",
      팰리세이드: "PALISADE",
      아이오닉: "IONIQ",
      캐스퍼: "CASPER",
      제네시스: "GENESIS",
      포터: "PORTER",
      포터2: "PORTER 2",
      포터II: "PORTER II",
      스타렉스: "STAREX",
      그랜드스타렉스: "GRAND STAREX",
      스타리아: "STARIA",
      마이티: "MIGHTY",
      엑센트: "ACCENT",
      // Kia
      모닝: "MORNING",
      레이: "RAY",
      프라이드: "PRIDE",
      리오: "RIO",
      쏘렌토: "SORENTO",
      소렌토: "SORENTO",
      스포티지: "SPORTAGE",
      셀토스: "SELTOS",
      니로: "NIRO",
      카니발: "CARNIVAL",
      봉고: "BONGO",
      봉고3: "BONGO3",
      포르테: "FORTE",
      쎄라토: "CERATO",
      세라토: "CERATO",
      로체: "LOTZE",
      오피러스: "OPIRUS",
      모하비: "MOHAVE",
      k3: "K3",
      k5: "K5",
      k7: "K7",
      k8: "K8",
      k9: "K9",
      // GM/Renault Samsung
      스파크: "SPARK",
      아베오: "AVEO",
      크루즈: "CRUZE",
      말리부: "MALIBU",
      트레일블레이저: "TRAILBLAZER",
      qm3: "QM3",
      qm5: "QM5",
      qm6: "QM6",
      sm3: "SM3",
      sm5: "SM5",
      sm6: "SM6",
      sm7: "SM7",
      xm3: "XM3",
      // SsangYong
      코란도: "KORANDO",
      렉스턴: "REXTON",
      티볼리: "TIVOLI",
      액티언: "ACTYON",
      체어맨: "CHAIRMAN",
    };

    // 🆕 후처리: 차량명 영문만 추출 (괄호 영문 → 영문 토큰 → 한↔영 dict)
    const postprocessCarName = (raw) => {
      if (!raw) return raw;
      const s = String(raw).trim();
      // 1) 괄호 안 영문 우선 ("아반떼(AVANTE)" → AVANTE)
      const paren = s.match(/[(](.*?)[)]/);
      if (paren) {
        const inside = paren[1].trim();
        if (/^[A-Z][A-Z0-9 .]{1,}$/i.test(inside)) return inside.toUpperCase();
      }
      // 2) "AVANTE 1.6" 같이 영문 + 숫자/공백
      const en = s.match(/[A-Z][A-Z0-9 .]{1,}/i);
      if (en) {
        const trimmed = en[0].trim();
        if (trimmed.length >= 2) return trimmed.toUpperCase();
      }
      // 3) 한글만 있는 경우 dict lookup
      // 양식 표기 변형 처리: "포르테", "포 르 테" 같이 공백 포함도 허용
      const noSpace = s.replace(/\s+/g, "").toLowerCase();
      if (CAR_KO_EN[noSpace]) return CAR_KO_EN[noSpace];
      // partial match (가장 긴 매칭)
      let best = null;
      for (const [ko, en] of Object.entries(CAR_KO_EN)) {
        if (
          noSpace.includes(ko) &&
          (best === null || ko.length > best[0].length)
        ) {
          best = [ko, en];
        }
      }
      if (best) return best[1];
      return s;
    };

    // 🆕 PaddleOCR fields → grid 컬럼명 매핑 (ucar.tlogin.net 14개 필드 기준)
    // 최초등록일(registrationDate) 제외. 14개 필드만.
    const mapPaddleToGrid = (fields) => {
      const mapped = {
        // ucar 표준 영문 키 (vehicleRegistrationNumber/vehicleName 같은 더 길고 명시적인 키와
        // 짧은 별칭 plateNumber/model 둘 다 set 해서 grid 어떤 키로 lookup 해도 매칭되게)
        vehicleRegistrationNumber: fields.plate_no || null,
        plateNumber: fields.plate_no || null,
        vehicleName: postprocessCarName(fields.car_name),
        model: postprocessCarName(fields.car_name),
        vin: fields.vin || null,
        modelYear: fields.year || null,
        ownerName: fields.owner_name || null,
        name: fields.owner_name || null,
        ownerIdNumber: fields.owner_id_no || null,
        birthDate: fields.owner_id_no || null,
        mileage: fields.mileage_km || null,
        totalWeight: fields.vehicle_weight_kg || null,
        grossWeight: fields.vehicle_weight_kg || null,
        displacement: fields.displacement_cc || null,
        length: fields.length_mm || null,
        width: fields.width_mm || null,
        height: fields.height_mm || null,
        baseAddress: fields.owner_address || null,
        address: fields.owner_address || null,
        registeredAddress: fields.owner_address || null,
        deregistrationDate: fields.canceled_date || null,
        cancellationDate: fields.canceled_date || null,
      };
      // CBM 자동 계산 (길이 × 폭 × 높이 / 1e9, mm → m³)
      const L = parseFloat(mapped.length);
      const W = parseFloat(mapped.width);
      const H = parseFloat(mapped.height);
      if (
        Number.isFinite(L) &&
        Number.isFinite(W) &&
        Number.isFinite(H) &&
        L > 0 &&
        W > 0 &&
        H > 0
      ) {
        mapped.cbm = ((L * W * H) / 1e9).toFixed(2);
      }
      return mapped;
    };

    // 🆕 OCR/Donut/vLLM 동시 추출 — fire-and-forget (await 제거)
    //   PaddleOCR이 timeout(60s) 나도 vLLM/Donut 결과는 빠르게 표시
    let paddleOcrMap = {};
    let paddleOcrPromise = null;
    if (ocrEnabled) {
      addLog(
        `🟢 PaddleOCR 1차 추출 시작 (${checkedFiles.length}개 파일, 병렬, vLLM과 동시)...`,
      );
      const ocrT0 = Date.now();
      paddleOcrPromise = Promise.all(
        checkedFiles.map(async (cf) => {
          try {
            const fd = new FormData();
            // cf.file 은 browser File 또는 Blob 객체 (fileList의 파일 참조)
            const fileObj = cf.file || cf.blob || cf;
            if (!fileObj || typeof fileObj.size !== "number") {
              addLog(`  ⚠️ ${cf.name || cf.fileName}: 파일 객체 없음, skip`);
              return;
            }
            fd.append("file", fileObj, cf.name || cf.fileName || "upload.bin");
            const r = await fetch("/api/extract/paddleocr", {
              method: "POST",
              body: fd,
              signal: abortControllerRef.current?.signal,
            });
            if (!r.ok) {
              const errText = await r.text();
              addLog(
                `  ❌ ${cf.name || cf.fileName}: HTTP ${r.status} ${errText.slice(0, 80)}`,
              );
              return;
            }
            const data = await r.json();
            // 🆕 응답의 car_name 을 영문 변환된 값으로 덮어쓰기 (OCR 배너 + grid 공통)
            if (data.fields && data.fields.car_name) {
              data.fields.car_name = postprocessCarName(data.fields.car_name);
            }
            paddleOcrMap[cf.name || cf.fileName] = data;
            const fields = data.fields || {};
            const filledCount = Object.values(fields).filter((v) => v).length;
            addLog(
              `  ✅ ${cf.name || cf.fileName}: ${data.doc_type} conf=${data.overall_confidence} (${filledCount}필드, ${data.ocr_ms?.toFixed?.(0) ?? "?"}ms)`,
            );

            // 🆕 OCR 결과를 즉시 grid extractedData 에 매핑 (사용자가 OCR 결과 바로 보도록)
            const gridFields = mapPaddleToGrid(fields);
            setFileList((prev) =>
              prev.map((f) => {
                if ((f.name || f.fileName) === (cf.name || cf.fileName)) {
                  return {
                    ...f,
                    extractedData: {
                      ...(f.extractedData || {}),
                      ...gridFields,
                      _source: "paddleocr",
                      // Phase 1.5b G2: server metadata for FieldSourceBadge
                      _raw: data._raw || null,
                      _enrichment: data._enrichment || null,
                    },
                    paddleOcrFields: fields,
                  };
                }
                return f;
              }),
            );
          } catch (e) {
            addLog(`  ❌ ${cf.name || cf.fileName}: ${e.message}`);
          }
        }),
      );
      // fire-and-forget: 결과 도착하면 setState (vLLM 메인 흐름 안 막음)
      paddleOcrPromise
        .then(() => {
          setPaddleOcrResults((prev) => ({ ...prev, ...paddleOcrMap }));
          addLog(
            `🟢 PaddleOCR 완료 (${Object.keys(paddleOcrMap).length}/${checkedFiles.length}, ${Date.now() - ocrT0}ms)`,
          );
        })
        .catch((err) => addLog(`❌ PaddleOCR 전체 실패: ${err.message}`));
    }

    // 🆕 Donut 체크박스 활성화: Donut 1차 모델 추출 (vLLM과 동시, fire-and-forget)
    let donutMap = {};
    let donutPromise = null;
    if (donutEnabled) {
      addLog(
        `🟣 Donut 1차 모델 추출 시작 (${checkedFiles.length}개 파일, 병렬, vLLM과 동시)...`,
      );
      const donutT0 = Date.now();
      donutPromise = Promise.all(
        checkedFiles.map(async (cf) => {
          try {
            const fd = new FormData();
            const fileObj = cf.file || cf.blob || cf;
            if (!fileObj || typeof fileObj.size !== "number") return;
            fd.append("file", fileObj, cf.name || cf.fileName || "upload.bin");
            const r = await fetch("/api/extract/donut", {
              method: "POST",
              body: fd,
              signal: abortControllerRef.current?.signal,
            });
            if (!r.ok) {
              const errText = await r.text();
              addLog(
                `  ❌ Donut ${cf.name || cf.fileName}: HTTP ${r.status} ${errText.slice(0, 80)}`,
              );
              return;
            }
            const data = await r.json();
            if (data.fields && data.fields.car_name) {
              data.fields.car_name = postprocessCarName(data.fields.car_name);
            }
            donutMap[cf.name || cf.fileName] = data;
            const fields = data.fields || {};
            const filledCount = Object.values(fields).filter((v) => v).length;
            addLog(
              `  ✅ Donut ${cf.name || cf.fileName}: ${data.doc_type} (${filledCount}필드, ${data.elapsed_ms?.toFixed?.(0) ?? "?"}ms)`,
            );

            // Phase 1.5b G2: surface server metadata (_raw + _enrichment) to grid
            // so FieldSourceBadge can render OCR vs DB badges from Donut too.
            // PaddleOCR may have already populated extractedData; only overlay
            // metadata if not present (preserve PaddleOCR mapping if it ran).
            const donutGridFields = mapPaddleToGrid(fields);
            setFileList((prev) =>
              prev.map((f) => {
                if ((f.name || f.fileName) === (cf.name || cf.fileName)) {
                  const prevED = f.extractedData || {};
                  return {
                    ...f,
                    extractedData: {
                      ...prevED,
                      // donut fields only fill gaps (don't clobber paddle/vLLM mapping)
                      ...Object.fromEntries(
                        Object.entries(donutGridFields).filter(
                          ([k, v]) => v != null && !prevED[k],
                        ),
                      ),
                      _source: prevED._source || "donut",
                      // metadata: prefer existing (paddleocr) when present,
                      // otherwise adopt donut server response
                      _raw: prevED._raw || data._raw || null,
                      _enrichment:
                        prevED._enrichment || data._enrichment || null,
                    },
                  };
                }
                return f;
              }),
            );
          } catch (e) {
            addLog(`  ❌ Donut ${cf.name || cf.fileName}: ${e.message}`);
          }
        }),
      );
      // fire-and-forget: 결과 도착하면 setState (vLLM 메인 흐름 안 막음)
      donutPromise
        .then(() => {
          setDonutResults((prev) => ({ ...prev, ...donutMap }));
          addLog(
            `🟣 Donut 완료 (${Object.keys(donutMap).length}/${checkedFiles.length}, ${Date.now() - donutT0}ms)`,
          );
        })
        .catch((err) => addLog(`❌ Donut 전체 실패: ${err.message}`));
    }

    // ⭐ 진행률 초기화
    const calculateTotalPages = () => {
      return checkedFiles.reduce((sum, file) => {
        if (file.isPDF) {
          return sum + (checkedPages?.length || file.totalPages || 1);
        }
        return sum + 1; // 이미지는 1페이지
      }, 0);
    };
    const totalPagesCount = calculateTotalPages();
    const estimatedTime = totalPagesCount * 6; // 페이지당 ~6초
    const progressStartTime = Date.now();

    // ⚡ 빠른 비교 모드: 모델별 개별 진행상태 초기화
    const isParallelMode =
      verifyEnabled &&
      verificationMode === "parallel" &&
      effectiveValidationModels.length >= 2;
    const model1Info = isParallelMode
      ? {
          name: getModelNickname(effectiveValidationModels[0]) || "1차 모델",
          status: "running",
          startTime: progressStartTime,
          elapsed: 0,
          percentage: 0,
        }
      : null;
    const model2Info = isParallelMode
      ? {
          name: getModelNickname(effectiveValidationModels[1]) || "2차 모델",
          status: "running",
          startTime: progressStartTime,
          elapsed: 0,
          percentage: 0,
        }
      : null;

    setProcessingProgress({
      currentPage: 0,
      totalPages: totalPagesCount,
      step: isParallelMode ? "⚡ 빠른 비교 추출 중..." : "OCR 처리 중...",
      fileName: checkedFiles[0]?.name || "",
      percentage: 0,
      startTime: progressStartTime,
      estimatedSeconds: estimatedTime,
      model1: model1Info,
      model2: model2Info,
    });

    // ⭐ 시간 기반 점진적 진행률 업데이트 (0% → 90%)
    const progressInterval = setInterval(() => {
      const elapsed = (Date.now() - progressStartTime) / 1000;
      const progressPercent = Math.min(
        90,
        Math.round((elapsed / estimatedTime) * 100),
      );
      const currentPage = Math.min(
        totalPagesCount,
        Math.ceil((progressPercent / 100) * totalPagesCount),
      );
      setProcessingProgress((prev) => ({
        ...prev,
        currentPage: currentPage,
        percentage: progressPercent,
        // ⚡ 빠른 비교: 모델별 진행률도 업데이트 (완료된 모델은 건드리지 않음)
        model1:
          prev.model1 && prev.model1.status !== "done"
            ? {
                ...prev.model1,
                elapsed: Math.round(elapsed),
                percentage: progressPercent,
              }
            : prev.model1,
        model2:
          prev.model2 && prev.model2.status !== "done"
            ? {
                ...prev.model2,
                elapsed: Math.round(elapsed),
                percentage: progressPercent,
              }
            : prev.model2,
      }));
    }, 500);

    // 🚀 속도 모드: PDF 파일은 네이티브 처리
    if (ocrProcessingMode === "speed") {
      try {
        const isPdfFile = (f) =>
          f.isPDF ||
          f.name.toLowerCase().endsWith(".pdf") ||
          f.file?.type === "application/pdf";
        const pdfFiles = checkedFiles.filter(isPdfFile);
        const imageFiles = checkedFiles.filter((f) => !isPdfFile(f));

        addLog(
          `⚡ [속도 모드] PDF: ${pdfFiles.length}개, 이미지: ${imageFiles.length}개`,
        );

        // 템플릿 정보 가져오기
        const templateObj = selectedOcrTemplate
          ? Object.values(templates).find((t) => t.name === selectedOcrTemplate)
          : null;
        const templateId = templateObj?.id || "";
        console.log(
          "🔍 [딥러닝] templateId:",
          templateId,
          "selectedOcrTemplate:",
          selectedOcrTemplate,
          "templateObj:",
          templateObj?.name || "null",
        );

        // PDF 파일들 병렬 처리
        if (pdfFiles.length > 0) {
          const pdfResults = await Promise.allSettled(
            pdfFiles.map(async (fileItem, i) => {
              addLog(
                `  ⚡ [${i + 1}/${pdfFiles.length}] ${fileItem.name} PDF 네이티브 처리 중...`,
              );

              // 📥 복원 문서: file이 null이면 GCS에서 다운로드
              let fileBlob = fileItem.file;
              if (!fileBlob && fileItem._restoredFromDB && fileItem._hasPdf) {
                addLog(
                  `  📥 [${i + 1}/${pdfFiles.length}] ${fileItem.name} GCS에서 다운로드 중...`,
                );
                fileBlob = await fetchRestoredPdf(
                  fileItem._documentId,
                  fileItem.name,
                );
                if (!fileBlob) throw new Error("PDF 다운로드 실패 (GCS)");
                // fileList에도 반영 (재추출 시 다시 다운로드 방지)
                setFileList((prev) =>
                  prev.map((f, idx) =>
                    idx === fileItem.index
                      ? {
                          ...f,
                          file: fileBlob,
                          preview: URL.createObjectURL(fileBlob),
                          isPDF: true,
                        }
                      : f,
                  ),
                );
              }
              if (!fileBlob)
                throw new Error(
                  "파일이 없습니다. 원본 PDF를 다시 업로드해주세요.",
                );

              const formData = new FormData();
              formData.append("file", fileBlob);
              formData.append("templateId", templateId);
              if (editablePrompt)
                formData.append("customPrompt", editablePrompt); // 📋 UI 프롬프트 전달
              // 📘 검증 모드에 따라 모델 전달
              formData.append(
                "selectedValidationModels",
                JSON.stringify(effectiveValidationModels),
              );
              if (verifyEnabled)
                formData.append("verificationMode", verificationMode);
              if (verifyEnabled && verificationMode === "sequential")
                formData.append("enableJudge", "true");
              // 📘 선택된 페이지 전달 (현재 파일은 checkedPages 우선, 배치 내 다른 파일은 저장값 사용)
              const pageSelection =
                fileItem.index === selectedFileIndex
                  ? checkedPages && checkedPages.length > 0
                    ? checkedPages
                    : fileItem.selectedPages
                  : fileItem.selectedPages || null;
              if (pageSelection && pageSelection.length > 0) {
                formData.append("selectedPages", JSON.stringify(pageSelection));
              }

              const res = await fetch(
                `${API_BASE_URL}/api/ocr/gemini-extract-pdf-native`,
                {
                  method: "POST",
                  headers: { ...ocrAuthHeader() },
                  body: formData,
                  signal: abortControllerRef.current?.signal,
                },
              );

              if (await handleOcrQuota(res))
                throw new Error("FREE_QUOTA_EXCEEDED");
              // ⚡ 빠른비교 SSE 스트리밍: 모델별 결과를 즉시 화면에 출력
              let data;
              if (
                isParallelMode &&
                res.headers.get("content-type")?.includes("text/event-stream")
              ) {
                const reader = res.body.getReader();
                const decoder = new TextDecoder();
                let sseBuffer = "";
                let completeData = null;

                while (true) {
                  const { value, done } = await reader.read();
                  if (done) break;

                  sseBuffer += decoder.decode(value, { stream: true });
                  const messages = sseBuffer.split("\n\n");
                  sseBuffer = messages.pop();

                  for (const msg of messages) {
                    if (!msg.trim()) continue;
                    // SSE 메시지 파싱: 각 라인별 처리 (대용량 JSON 안전)
                    let event = "",
                      dataStr = "";
                    for (const line of msg.split("\n")) {
                      if (line.startsWith("event: ")) event = line.slice(7);
                      else if (line.startsWith("data: "))
                        dataStr = line.slice(6);
                    }
                    if (!event || !dataStr) continue;
                    let eventData;
                    try {
                      eventData = JSON.parse(dataStr);
                    } catch (e) {
                      continue;
                    }

                    if (event === "model-result") {
                      const {
                        modelIndex,
                        modelName,
                        data: modelData,
                        elapsed: modelElapsed,
                        _error: modelError,
                      } = eventData;

                      // ❌ 에러 감지: 모델 추출 실패 시 명확히 표시
                      if (modelError) {
                        const modelKey = modelIndex === 1 ? "model1" : "model2";
                        setProcessingProgress((prev) => ({
                          ...prev,
                          [modelKey]: prev[modelKey]
                            ? {
                                ...prev[modelKey],
                                status: "error",
                                elapsed: Math.round(modelElapsed / 1000),
                                percentage: 100,
                              }
                            : prev[modelKey],
                        }));
                        addLog(
                          `   ❌ ${modelIndex === 1 ? "[1]" : "[2]"} ${modelName} 추출 실패: ${modelError}`,
                          "error",
                        );
                        // 에러가 있어도 빈 결과로 계속 진행 (다른 모델 결과는 유지)
                      }

                      // ⚡ 프로그레스바: 해당 모델 완료 표시
                      if (!modelError) {
                        const modelKey = modelIndex === 1 ? "model1" : "model2";
                        setProcessingProgress((prev) => ({
                          ...prev,
                          [modelKey]: prev[modelKey]
                            ? {
                                ...prev[modelKey],
                                status: "done",
                                elapsed: Math.round(modelElapsed / 1000),
                                percentage: 100,
                              }
                            : prev[modelKey],
                        }));
                      }

                      addLog(
                        `   ⚡ ${modelIndex === 1 ? "[1]" : "[2]"} ${modelName} ${modelError ? "실패" : "추출 완료"} (${(modelElapsed / 1000).toFixed(1)}초)`,
                        modelError ? "error" : "success",
                      );

                      // ⚡ 모델별 결과 즉시 화면 출력 (먼저 완료된 모델부터 표시)
                      setFileList((prev) =>
                        prev.map((f, idx) => {
                          if (idx !== fileItem.index) return f;
                          if (modelIndex === 1) {
                            // 1차 모델 결과: flashData 즉시 표시
                            // 2차 모델이 먼저 완료된 경우 기존 proData 병합
                            const existingProData = f.proData;
                            return {
                              ...f,
                              status: "extracting",
                              flashData: {
                                documentType: templateObj?.name || "미분류",
                                ...modelData,
                              },
                              extractedData: {
                                documentType: templateObj?.name || "미분류",
                                ...modelData,
                              },
                              pageResults: (modelData.pageResults || []).map(
                                (pr) => ({
                                  ...pr,
                                  flashData: pr,
                                  proData: existingProData
                                    ? (existingProData.pageResults || []).find(
                                        (p) => p.pageNumber === pr.pageNumber,
                                      ) || null
                                    : null,
                                  documentType:
                                    pr.documentType ||
                                    templateObj?.name ||
                                    "미분류",
                                }),
                              ),
                              items: modelData.items || [],
                            };
                          } else {
                            // 2차 모델 결과: proData 표시
                            const existingPR = f.pageResults || [];
                            if (existingPR.length > 0) {
                              // 1차 모델 결과가 이미 있으면 병합
                              return {
                                ...f,
                                proData: {
                                  documentType: templateObj?.name || "미분류",
                                  ...modelData,
                                },
                                pageResults: existingPR.map((pr) => {
                                  const proPage = (
                                    modelData.pageResults || []
                                  ).find((p) => p.pageNumber === pr.pageNumber);
                                  return { ...pr, proData: proPage || null };
                                }),
                              };
                            } else {
                              // 2차 모델이 먼저 완료 → proData만 우선 저장 (1차 도착 시 병합)
                              return {
                                ...f,
                                status: "extracting",
                                proData: {
                                  documentType: templateObj?.name || "미분류",
                                  ...modelData,
                                },
                              };
                            }
                          }
                        }),
                      );
                    } else if (event === "complete") {
                      completeData = eventData;
                    } else if (event === "error") {
                      console.error(
                        `❌ [빠른비교 SSE] Error:`,
                        eventData.message,
                      );
                    }
                  }
                }

                data = completeData || {
                  success: false,
                  error: "No complete event received",
                };
              } else {
                data = await res.json();
              }

              if (data.success && data.data) {
                // ⭐ Few-Shot 적용 결과 표시
                const fsi = data.fewShotInfo || data.metadata?.fewShotInfo;
                if (fsi && fsi.applied) {
                  addLog(
                    `  🧠 [Few-Shot] ${fsi.sampleCount}개 학습 샘플 적용 (거래처: ${fsi.vendorName}${fsi.correctionCount > 0 ? `, 교정: ${fsi.correctionCount}건 [${(fsi.correctionFields || []).join(", ")}]` : ""})`,
                    "success",
                  );
                }

                const result = {
                  documentType: templateObj?.name || "미분류",
                  ...data.data,
                  metadata: {
                    mode: "pdf-native",
                    elapsed: data.metadata?.elapsed,
                  },
                };

                // ⭐ 교차 검증 결과 처리
                const cv = data.metadata?.crossValidation;

                // ⚡ 빠른 비교: 속도모드 PDF 결과 도착 → 모델별 실제 소요시간 반영
                setProcessingProgress((prev) => ({
                  ...prev,
                  model1: prev.model1
                    ? {
                        ...prev.model1,
                        status: "done",
                        elapsed: cv?.model1Elapsed
                          ? Math.round(cv.model1Elapsed / 1000)
                          : Math.round(
                              (Date.now() - prev.model1.startTime) / 1000,
                            ),
                        percentage: 100,
                      }
                    : prev.model1,
                  model2: prev.model2
                    ? {
                        ...prev.model2,
                        status: "done",
                        elapsed: cv?.model2Elapsed
                          ? Math.round(cv.model2Elapsed / 1000)
                          : Math.round(
                              (Date.now() - prev.model2.startTime) / 1000,
                            ),
                        percentage: 100,
                      }
                    : prev.model2,
                }));
                let crossValidationDiffs = null;
                if (cv && !cv.error && cv.differences) {
                  crossValidationDiffs = cv.differences;
                  // pageResults 각 항목에 differences 주입
                  if (result.pageResults && Array.isArray(result.pageResults)) {
                    result.pageResults.forEach((pr) => {
                      pr.differences = cv.differences;
                    });
                  }
                  // matchRate 로그 출력
                  const s = cv.summary || {};
                  addLog(
                    `  ⚠️ [교차 검증] ${fileItem.name}: 일치율 ${cv.matchRate}% (필드: ${s.matchedFields || 0}/${s.totalFields || 0}, 셀: ${s.matchedCells || 0}/${s.totalCells || 0}, 차이 행: ${s.diffItems || 0})`,
                    cv.matchRate < 100 ? "warning" : "success",
                  );
                }

                // 🔍 [DEBUG] API 응답에서 groundedFields 확인
                const receivedGrounding = data.data.groundedFields || {};
                const groundedCount = Object.keys(receivedGrounding).length;
                const withBbox = Object.values(receivedGrounding).filter(
                  (v) => v?.bbox?.coordinates,
                ).length;
                console.log("📘 [API Response] groundedFields:", {
                  totalFields: groundedCount,
                  fieldsWithBbox: withBbox,
                  hasGrounding: data.metadata?.hasGrounding,
                  sampleKeys: Object.keys(receivedGrounding).slice(0, 5),
                });

                // 🔍 Debug: 파일 인덱스 확인
                console.log(
                  "📦 [setFileList] Saving groundedFields to fileItem.index:",
                  {
                    fileItemIndex: fileItem.index,
                    fileName: fileItem.name,
                    fileListLength: pdfFiles.length,
                  },
                );

                // 🔧 추출에 사용된 페이지를 ref에 저장 (useEffect에서 checkedPages 복원용)
                if (pageSelection && pageSelection.length > 0) {
                  pendingCheckedPagesRef.current = [...pageSelection];
                  console.log(
                    "📄 [추출 완료] pendingCheckedPages 저장:",
                    pageSelection,
                  );
                }

                // ========== 📦 증분 추출: IndexedDB 캐시 통합 ==========
                setFileList((prev) =>
                  prev.map((f, idx) => {
                    if (idx !== fileItem.index) return f;

                    const cacheKey = f.name || `file_${idx}`;

                    // 캐시 초기화 또는 기존 캐시 가져오기
                    if (!extractionCacheRef.current[cacheKey]) {
                      extractionCacheRef.current[cacheKey] = {
                        extractionBatches: {},
                        groundedFields: { ...(f.groundedFields || {}) },
                        pageDimensions: { ...(f.pageDimensions || {}) },
                      };
                    }
                    const cache = extractionCacheRef.current[cacheKey];

                    // 현재 추출된 페이지 번호
                    const extractedPages = data.data.metadata?.selectedPages ||
                      pageSelection || [1];
                    const batchKey = [...extractedPages]
                      .sort((a, b) => a - b)
                      .join(",");

                    console.log(
                      "📦 [handleBatchFullProcess 증분 캐시] 배치 저장:",
                      { cacheKey, batchKey, extractedPages },
                    );

                    // 이번 추출 결과를 배치로 저장
                    const serverPageResults = result.pageResults || [];
                    const serverItems = result.items || [];

                    // 테이블 필드 추출 (배열 타입 필드들)
                    const tableFields = {};
                    Object.keys(result).forEach((key) => {
                      if (
                        Array.isArray(result[key]) &&
                        key !== "pageResults" &&
                        key !== "items" &&
                        key !== "metadata" &&
                        key !== "boundingBoxes" &&
                        key !== "markdown"
                      ) {
                        tableFields[key] = result[key];
                      }
                    });

                    cache.extractionBatches[batchKey] = {
                      pages: [...extractedPages],
                      extractedData: { ...result },
                      pageResults: [...serverPageResults],
                      items: [...serverItems],
                      tableFields: tableFields,
                      timestamp: Date.now(),
                    };

                    // groundedFields, pageDimensions 누적
                    cache.groundedFields = {
                      ...cache.groundedFields,
                      ...receivedGrounding,
                    };
                    cache.pageDimensions = {
                      ...cache.pageDimensions,
                      ...(data.data.pageDimensions || {}),
                    };

                    // ========== 📦 캐시에서 통합 데이터 재구성 ==========
                    const allBatches = Object.entries(
                      cache.extractionBatches,
                    ).sort(
                      (a, b) => (a[1].timestamp || 0) - (b[1].timestamp || 0),
                    );

                    // 페이지별 최신 배치 결정
                    const pageToLatestBatch = {};
                    allBatches.forEach(([bKey, batch]) => {
                      batch.pages.forEach((p) => {
                        pageToLatestBatch[p] = bKey;
                      });
                    });

                    // 모든 고유 페이지 수집
                    const allExtractedPages = [
                      ...new Set(
                        allBatches.flatMap(([_, batch]) => batch.pages),
                      ),
                    ].sort((a, b) => a - b);

                    // 통합 items
                    const mergedItems = [];
                    allExtractedPages.forEach((page) => {
                      const latestBatchKey = pageToLatestBatch[page];
                      const batch = cache.extractionBatches[latestBatchKey];
                      if (batch) {
                        const pageItems = batch.items.filter((item) => {
                          const itemPage = Number(
                            item.pageNumber ?? item.P ?? item.p ?? 0,
                          );
                          return itemPage === page;
                        });
                        if (pageItems.length > 0) {
                          mergedItems.push(...pageItems);
                        } else if (
                          batch.pages.length === 1 &&
                          batch.pages[0] === page
                        ) {
                          mergedItems.push(...batch.items);
                        } else if (
                          batch.items.length > 0 &&
                          allExtractedPages.length === batch.pages.length
                        ) {
                          if (page === batch.pages[0]) {
                            mergedItems.push(...batch.items);
                          }
                        }
                      }
                    });

                    // 통합 pageResults
                    const mergedPageResults = allExtractedPages.map((page) => {
                      const latestBatchKey = pageToLatestBatch[page];
                      const batch = cache.extractionBatches[latestBatchKey];
                      const batchPageResult = (batch?.pageResults || []).find(
                        (pr) => Number(pr.pageNumber) === page,
                      );
                      return {
                        ...(batchPageResult || batch?.pageResults?.[0] || {}),
                        pageNumber: page,
                      };
                    });

                    // 통합 테이블 필드
                    const mergedTableFields = {};
                    allBatches.forEach(([bKey, batch]) => {
                      Object.entries(batch.tableFields || {}).forEach(
                        ([key, batchData]) => {
                          if (!mergedTableFields[key])
                            mergedTableFields[key] = [];
                          const batchPages = batch.pages;
                          const isLatestForAnyPage = batchPages.some(
                            (p) => pageToLatestBatch[p] === bKey,
                          );
                          if (isLatestForAnyPage) {
                            mergedTableFields[key] = mergedTableFields[
                              key
                            ].filter((item) => {
                              const itemPage = Number(
                                item.pageNumber ?? item.P ?? item.p ?? 0,
                              );
                              return !batchPages.includes(itemPage);
                            });
                            mergedTableFields[key].push(...batchData);
                          }
                        },
                      );
                    });

                    // 최종 통합 extractedData
                    const latestBatch = allBatches[allBatches.length - 1]?.[1];
                    // Option 2.5: propagate _raw/_enrichment from server pageResults[0]
                    //   so FieldSourceBadge can render OCR vs DB badges on the grid.
                    const _serverPR0 = (result.pageResults || [])[0] || {};
                    const mergedExtractedData = {
                      ...(latestBatch?.extractedData || {}),
                      items: mergedItems,
                      pageResults: mergedPageResults,
                      ...mergedTableFields,
                      _source: "gemini-pdf-native",
                      _raw: _serverPR0._raw || null,
                      _enrichment: _serverPR0._enrichment || null,
                    };

                    console.log(
                      "📦 [handleBatchFullProcess 증분 캐시] 통합 완료:",
                      {
                        cacheKey,
                        batches: Object.keys(cache.extractionBatches),
                        allExtractedPages,
                        mergedItemsCount: mergedItems.length,
                        mergedPageResultsCount: mergedPageResults.length,
                      },
                    );

                    saveExtractionCache(); // 💾 IndexedDB 저장

                    // ⚡ 빠른비교: SSE model-result에서 설정한 proData 보존 + crossItems 활용
                    const hasSSEProData =
                      isParallelMode && f.proData && cv?.crossItems;
                    const finalProData = hasSSEProData
                      ? { ...f.proData, items: cv.crossItems }
                      : cv?.crossItems
                        ? { ...mergedExtractedData, items: cv.crossItems }
                        : mergedExtractedData;

                    // ⚡ pageResults에 per-page flashData/proData 포함
                    const finalPageResults = mergedPageResults.map((pr) => ({
                      ...pr,
                      flashData: pr,
                      proData: hasSSEProData
                        ? (f.proData?.pageResults || []).find(
                            (p) => p.pageNumber === pr.pageNumber,
                          ) || null
                        : null,
                    }));

                    return {
                      ...f,
                      status: "completed",
                      extractedData: mergedExtractedData,
                      flashData: mergedExtractedData,
                      proData: finalProData,
                      pageResults: finalPageResults,
                      items: mergedItems,
                      totalPages:
                        f.totalPages || data.data.pageResults?.length || 1,
                      groundedFields: cache.groundedFields,
                      pageDimensions: cache.pageDimensions,
                      crossValidation: cv
                        ? {
                            ...cv,
                            verificationMode:
                              data.metadata?.verificationMode || "parallel",
                          }
                        : null,
                      selectedPages: pageSelection || [],
                    };
                  }),
                );

                // 🔧 FIX: checkedPages를 직접 복원 (useEffect 의존 대신 즉시 설정)
                // React 18 배칭으로 setFileList와 함께 처리되므로 안전
                if (fileItem.index === selectedFileIndex) {
                  const extractedPageNumbers = (data.data.pageResults || [])
                    .map((p) => Number(p.pageNumber))
                    .filter((n) => Number.isFinite(n) && n >= 1);
                  const finalPages =
                    extractedPageNumbers.length > 0
                      ? [...new Set(extractedPageNumbers)].sort((a, b) => a - b)
                      : pageSelection && pageSelection.length > 0
                        ? [...pageSelection]
                        : [1];
                  console.log(
                    "📄 [추출 완료] checkedPages 직접 설정:",
                    finalPages,
                  );
                  setCheckedPages(finalPages);
                }

                addLog(
                  `  ✅ [${i + 1}/${pdfFiles.length}] ${fileItem.name} 완료 (${(data.metadata?.elapsed / 1000).toFixed(1)}초)`,
                );

                // 📄 [PostgreSQL+GCS] 추출 결과 영구 저장 → _documentId 즉시 반영
                try {
                  const savedDoc = await saveExtractionToDB({
                    name: fileItem.name,
                    isPDF: true,
                    totalPages:
                      fileItem.totalPages ||
                      data.data?.pageResults?.length ||
                      1,
                    matchedTemplate: fileItem.matchedTemplate,
                    pageResults: data.data?.pageResults || [],
                    extractedData: data.data || {},
                    file: fileItem.file, // PDF 원본 (GCS 업로드용)
                  });
                  if (savedDoc?.id) {
                    setFileList((prev) =>
                      prev.map((f) =>
                        f.id === fileItem.id
                          ? { ...f, _documentId: savedDoc.id }
                          : f,
                      ),
                    );
                  }
                } catch (dbErr) {
                  console.warn("⚠️ [DB Save] 저장 실패 (무시):", dbErr.message);
                }

                return { fileItem, result };
              } else {
                throw new Error(data.error || "PDF 처리 실패");
              }
            }),
          );

          const successCount = pdfResults.filter(
            (r) => r.status === "fulfilled",
          ).length;
          addLog(
            `✅ [속도 모드] PDF 처리 완료: ${successCount}/${pdfFiles.length} 성공`,
          );
        }

        // 이미지 파일이 없으면 종료
        if (imageFiles.length === 0) {
          clearInterval(progressInterval);
          setProcessingProgress((prev) => ({
            ...prev,
            currentPage: prev.totalPages,
            step: "완료!",
            percentage: 100,
          }));
          const completionMsg = `✅ 전체 처리 완료!`;
          setLastCompletionLog(completionMsg);
          setTimeout(() => {
            setProcessingProgress({
              currentPage: 0,
              totalPages: 0,
              step: "",
              fileName: "",
              percentage: 0,
              startTime: null,
              estimatedSeconds: 0,
              model1: null,
              model2: null,
            });
          }, 1000);
          setTimeout(() => {
            setLastCompletionLog(null);
          }, 5000);
          setLoading(false);
          setProcessingCount(0);
          addLog(completionMsg);

          // // 추출 완료 시 전체검증 그리드 자동 표시 — 비활성화 (이미지뷰어 가림 방지)
          // setShowBatchValidationGrid(true);
          return;
        }

        // 이미지 파일들은 기존 방식으로 계속 진행
        addLog(`📍 이미지 파일 ${imageFiles.length}개는 좌표 모드로 처리...`);
        // checkedFiles를 imageFiles로 교체하여 아래 기존 로직 실행
        // (아래 Promise.allSettled에서 imageFiles 사용)
      } catch (speedModeError) {
        console.error("❌ [속도 모드] 오류:", speedModeError);
        addLog(`⚠️ 속도 모드 오류, 기존 방식으로 전환...`, "warning");
        // 폴백: 기존 방식으로 계속 진행
      }
    }

    try {
      // ✨ 전체 파일을 한 번에 처리 (분류 + Flash + Pro 병렬)
      addLog(`${checkedFiles.length}개 파일 전체 추출 시작...`);

      const results = await Promise.allSettled(
        checkedFiles.map(async (fileItem, i) => {
          const fileStartTime = Date.now();

          return new Promise(async (resolve, reject) => {
            try {
              addLog(
                `  🔄 [${i + 1}/${checkedFiles.length}] ${fileItem.name} 처리 중...`,
              );

              const formData = new FormData();
              formData.append("image", fileItem.file);
              formData.append("useClaudeForValidation", useClaudeForValidation);
              formData.append("validationModel", validationModel); // 레거시 호환용
              formData.append(
                "classificationFlashModel",
                classificationFlashModel,
              ); // 분류 모델 전달
              formData.append("flashModelVersion", classificationFlashModel); // ⭐ 분류 모델 버전/타입 전송 (Gemini vs Upstage)
              formData.append("speedMode", speedMode); // 레거시 호환용
              formData.append(
                "selectedValidationModels",
                JSON.stringify(effectiveValidationModels),
              ); // 검증 모드 기반 모델 배열
              formData.append(
                "templateExtractionModel",
                templateExtractionModel,
              ); // ⭐ 템플릿 추출 모델 (병렬 추출과 별개)

              // ⭐ ThinkingConfig 전달 (Gemini 2.5/3.0)
              formData.append("thinkingBudget", thinkingBudget.toString());
              formData.append("thinkingLevel", thinkingLevel);

              // ⭐ DeepSeek 설정 (selectedValidationModels에 포함되어 있으면 전송)
              if (selectedValidationModels.includes("deepseek")) {
                formData.append("deepseekMode", deepseekMode);
                formData.append("grounding", grounding.toString()); // ⭐ 명시적 문자열 변환
              }

              // ⭐ Datalab 설정 (selectedValidationModels에 포함되어 있으면 전송)
              if (selectedValidationModels.includes("datalab-marker")) {
                formData.append(
                  "datalabBoundingBox",
                  datalabBoundingBox.toString(),
                );
              }

              // 🔧 수동 템플릿 선택 - 분류 건너뛰고 해당 템플릿으로 추출
              console.log("🔍 [Debug] Template selection check:", {
                selectedOcrTemplate: selectedOcrTemplate || "EMPTY",
                templatesCount: Object.keys(templates).length,
                templateNames: Object.values(templates)
                  .map((t) => t.name)
                  .slice(0, 5),
              });

              if (selectedOcrTemplate) {
                formData.append("templateName", selectedOcrTemplate);
                // ⭐ 템플릿 전체 객체도 전송 (Structured Output용)
                const templateObj = Object.values(templates).find(
                  (t) => t.name === selectedOcrTemplate,
                );
                if (templateObj) {
                  formData.append("template", JSON.stringify(templateObj));
                  console.log(
                    "✅ [Template] Structured Output 템플릿 전송:",
                    templateObj.name,
                  );
                  console.log(
                    "   └── singleFields:",
                    templateObj.singleFields?.length || 0,
                  );
                  console.log(
                    "   └── tableFields:",
                    templateObj.tableFields?.length || 0,
                  );
                  console.log(
                    "   └── fields:",
                    templateObj.fields?.length || 0,
                  );
                } else {
                  console.warn(
                    "⚠️ [Template] 템플릿을 찾을 수 없음:",
                    selectedOcrTemplate,
                  );
                }
                addLog(`  📋 템플릿 지정: ${selectedOcrTemplate}`);
              } else {
                console.warn(
                  "⚠️ [Template] selectedOcrTemplate가 비어있음 - 자동 분류 모드",
                );
              }

              // ⭐ 레거시 호환: ocrEngine === 'deepseek'이고 selectedValidationModels가 비어있으면 model 전송
              if (
                ocrEngine === "deepseek" &&
                selectedValidationModels.length === 0
              ) {
                formData.append("model", "deepseek");
              }

              formData.append("enableHybridOCR", enableHybridOCR); // ⭐ Hybrid Mode Flag 추가
              formData.append("enableClassification", enableClassification); // ⭐ 문서 분류 활성화 플래그
              formData.append("extendLightMode", extendLightMode.toString()); // 🔗 Extend.ai Light Mode

              let classifyResult = null;
              let flashResult = null;
              let proResult = null;
              let totalPages = 1;
              let pageResults = [];

              // Fetch with SSE response stream
              const response = await fetch(
                `${API_BASE_URL}/api/ocr/extract-stream`,
                {
                  method: "POST",
                  headers:
                    ocrEngine === "deepseek"
                      ? { "X-DeepSeek-Server-URL": deepseekServerUrl }
                      : {},
                  body: formData,
                  signal: abortControllerRef.current?.signal, // 🚀 취소 가능하도록 signal 추가
                },
              );

              if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
              }

              const reader = response.body.getReader();
              const decoder = new TextDecoder();
              let buffer = "";

              while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n\n");
                buffer = lines.pop(); // 마지막 불완전한 줄은 버퍼에 유지

                for (const line of lines) {
                  if (!line.trim()) continue;

                  const eventMatch = line.match(/^event: (.+)\ndata: (.+)$/s);
                  if (!eventMatch) continue;

                  const [, event, dataStr] = eventMatch;
                  const data = JSON.parse(dataStr);

                  if (event === "progress") {
                    addLog(
                      `    📊 [${i + 1}/${checkedFiles.length}] ${data.message || data.step}`,
                    );
                  } else if (event === "pages") {
                    // PDF 페이지 개수 감지 - 실시간 업데이트
                    totalPages = data.totalPages;
                    addLog(
                      `    📄 [${i + 1}/${checkedFiles.length}] ${data.totalPages}개 페이지 감지`,
                    );

                    setFileList((prev) => {
                      const updated = [...prev];
                      const idx = updated.findIndex(
                        (f) => f.id === fileItem.id,
                      );
                      if (idx !== -1) {
                        updated[idx] = {
                          ...updated[idx],
                          totalPages: data.totalPages,
                          status: "processing",
                        };
                      }
                      return updated;
                    });
                  } else if (event === "page-classify") {
                    // 페이지별 분류 결과 - 실시간 업데이트
                    addLog(
                      `    ✅ [${i + 1}/${checkedFiles.length}] 페이지 ${data.pageNumber} 분류: ${data.documentType} 🔥`,
                      "success",
                    );

                    setFileList((prev) => {
                      const updated = [...prev];
                      const idx = updated.findIndex(
                        (f) => f.id === fileItem.id,
                      );
                      if (idx !== -1) {
                        const currentPageResults =
                          updated[idx].pageResults || [];
                        const pageIdx = currentPageResults.findIndex(
                          (p) => p.pageNumber === data.pageNumber,
                        );

                        if (pageIdx !== -1) {
                          // 기존 페이지 업데이트
                          currentPageResults[pageIdx] = {
                            ...currentPageResults[pageIdx],
                            documentType: data.documentType,
                          };
                        } else {
                          // 새 페이지 추가
                          currentPageResults.push({
                            pageNumber: data.pageNumber,
                            documentType: data.documentType,
                            flashData: null,
                            proData: null,
                          });
                        }

                        updated[idx] = {
                          ...updated[idx],
                          pageResults: currentPageResults.sort(
                            (a, b) => a.pageNumber - b.pageNumber,
                          ),
                          status: "processing",
                        };

                        // 🔧 첫 페이지 분류 완료 시 grid 탭 전환 비활성화
                        // if (data.pageNumber === 1 && activeTab === 'viewer') {
                        //   setActiveTab('grid');
                        // }
                      }
                      return updated;
                    });
                  } else if (event === "page-extract") {
                    // 페이지별 추출 결과 - 실시간 업데이트
                    // ⭐ 디버그: SSE 파싱 직후 pngImage 확인
                    console.log(
                      `🔍 [SSE Parse] page-extract data.pngImage: ${data.pngImage ? data.pngImage.substring(0, 50) + "..." : "null/undefined"}, data.ocrImageSize:`,
                      data.ocrImageSize,
                    );
                    const isPartial = data.partial === true; // 1차 모델만 완료된 부분 결과

                    // ⚡ 빠른 비교: 모델별 완료 상태 업데이트
                    if (isPartial) {
                      setProcessingProgress((prev) =>
                        prev.model1
                          ? {
                              ...prev,
                              model1: {
                                ...prev.model1,
                                status: "done",
                                elapsed: Math.round(
                                  (Date.now() - prev.model1.startTime) / 1000,
                                ),
                                percentage: 100,
                              },
                            }
                          : prev,
                      );
                    } else {
                      setProcessingProgress((prev) => ({
                        ...prev,
                        model1: prev.model1
                          ? { ...prev.model1, status: "done", percentage: 100 }
                          : prev.model1,
                        model2: prev.model2
                          ? {
                              ...prev.model2,
                              status: "done",
                              elapsed: Math.round(
                                (Date.now() - prev.model2.startTime) / 1000,
                              ),
                              percentage: 100,
                            }
                          : prev.model2,
                      }));
                    }

                    // ⭐ 실제 사용된 모델명 표시 (동적)
                    let modelNames = selectedValidationModels
                      .map((m) => getModelNickname(m))
                      .join(" + ");
                    if (!modelNames)
                      modelNames = getModelNickname(validationModel); // 레거시
                    if (data.documentType === "기타") modelNames += " (실패)";

                    const logMessage = isPartial
                      ? `    ⚡ [${i + 1}/${checkedFiles.length}] 페이지 ${data.pageNumber} 1차 추출 완료 (${modelNames} 처리 중...)`
                      : `    ✅ [${i + 1}/${checkedFiles.length}] 페이지 ${data.pageNumber} 추출 완료 (${modelNames})`;
                    addLog(logMessage, "success");

                    // ⭐ 교차 검증 일치율 알림
                    if (
                      data.matchRate !== undefined &&
                      data.matchRate < 100 &&
                      !isPartial
                    ) {
                      const diffs = data.differences || {};
                      const headerDiffCount = Object.keys(diffs).filter(
                        (k) => !k.startsWith("_") && diffs[k]?.match === false,
                      ).length;
                      const itemDiffCount = diffs._itemDiffs?.length || 0;
                      const itemSummary = diffs._itemSummary;
                      let diffDetail = "";
                      if (headerDiffCount > 0)
                        diffDetail += `헤더 ${headerDiffCount}건`;
                      if (itemDiffCount > 0)
                        diffDetail += `${diffDetail ? ", " : ""}아이템 ${itemDiffCount}행`;
                      if (itemSummary)
                        diffDetail += ` (셀 일치율: ${itemSummary.totalCells > 0 ? Math.round((itemSummary.matchedCells / itemSummary.totalCells) * 100) : 100}%)`;
                      addLog(
                        `    ⚠️ 페이지 ${data.pageNumber} 교차 검증: 일치율 ${data.matchRate}% — 차이점: ${diffDetail || "확인 필요"}`,
                        "warning",
                      );
                    }

                    setFileList((prev) => {
                      const updated = [...prev];
                      const idx = updated.findIndex(
                        (f) => f.id === fileItem.id,
                      );
                      if (idx !== -1) {
                        const currentPageResults =
                          updated[idx].pageResults || [];
                        const pageIdx = currentPageResults.findIndex(
                          (p) => p.pageNumber === data.pageNumber,
                        );

                        const pageData = {
                          pageNumber: data.pageNumber,
                          documentType: data.documentType,
                          flashData: data.flashData,
                          proData: data.proData || data.secondData,
                          partial: isPartial,
                          boundingBoxes: data.boundingBoxes || [],
                          paddleRaw: data.paddleRaw,
                          pngImage: data.pngImage,
                          differences: data.differences,
                          ocrImageSize: data.ocrImageSize,
                        };

                        if (pageIdx !== -1) {
                          // 기존 페이지 업데이트
                          currentPageResults[pageIdx] = {
                            ...currentPageResults[pageIdx],
                            ...pageData,
                            proData:
                              data.proData ||
                              data.secondData ||
                              currentPageResults[pageIdx].proData,
                            documentType:
                              data.documentType ||
                              currentPageResults[pageIdx].documentType,
                            boundingBoxes:
                              data.boundingBoxes ||
                              currentPageResults[pageIdx].boundingBoxes ||
                              [],
                            paddleRaw:
                              data.paddleRaw ||
                              currentPageResults[pageIdx].paddleRaw,
                            pngImage:
                              data.pngImage ||
                              currentPageResults[pageIdx].pngImage,
                            differences:
                              data.differences ||
                              currentPageResults[pageIdx].differences,
                            ocrImageSize:
                              data.ocrImageSize ||
                              currentPageResults[pageIdx].ocrImageSize,
                          };
                        } else {
                          // 새 페이지 추가
                          currentPageResults.push(pageData);
                          // ⭐ 디버그: pngImage 확인
                          console.log(
                            "🔍 [SSE Received] pngImage:",
                            data.pngImage
                              ? data.pngImage.substring(0, 50) + "..."
                              : "null",
                            "ocrImageSize:",
                            data.ocrImageSize,
                          );
                        }

                        // ⭐ SELLER 데이터 로그 확인 (사용자 요청)
                        const sellerBox = pageData.boundingBoxes?.find(
                          (b) => b.key === "SELLER",
                        );
                        if (sellerBox) {
                          console.log(
                            "📡 [SSE:page-extract] Received SELLER:",
                            JSON.stringify(sellerBox),
                          );
                        }

                        // 기존 로직: 페이지 결과 임시 저장
                        // ⭐ 핵심 수정: 지역 변수 pageResults에도 저장 (complete 이벤트에서 참조용)
                        const localPageIdx = pageResults.findIndex(
                          (p) => p.pageNumber === data.pageNumber,
                        );
                        if (localPageIdx !== -1) {
                          pageResults[localPageIdx] = pageData;
                        } else {
                          pageResults.push(pageData);
                        }

                        updated[idx] = {
                          ...updated[idx],
                          pageResults: currentPageResults.sort(
                            (a, b) => a.pageNumber - b.pageNumber,
                          ),
                          status: "extracting",
                        };

                        // 🔥 실시간 화면 업데이트: 첫 번째 페이지 결과가 도착하면 자동으로 선택
                        if (
                          currentPageResults.length === 1 &&
                          data.pageNumber === 1
                        ) {
                          setSelectedFileIndex(idx);
                          setSelectedPageNumber(1);
                        }
                      }
                      return updated;
                    });

                    // ⭐ 100% 일치 시 자동 검증 처리 (Human-in-the-Loop Bypass)
                    if (data.matchRate === 100) {
                      setValidatedPages((prev) => ({
                        ...prev,
                        [fileItem.id]: {
                          ...(prev[fileItem.id] || {}),
                          [data.pageNumber]: true,
                        },
                      }));
                      console.log(
                        `✅ [Auto-Validate] 페이지 ${data.pageNumber} 100% 일치로 자동 검증 완료`,
                      );
                    }
                  } else if (event === "classify") {
                    classifyResult = {
                      data: {
                        documentType: data.documentType,
                        documentCode: DOC_TYPE_MAP[data.documentType] || "U",
                      },
                    };
                    addLog(
                      `    ✅ [${i + 1}/${checkedFiles.length}] 분류 완료: ${data.documentType} (${(data.duration / 1000).toFixed(1)}초)`,
                    );

                    // 실시간 업데이트: 분류 결과 반영
                    setFileList((prev) => {
                      const updated = [...prev];
                      const idx = updated.findIndex(
                        (f) => f.id === fileItem.id,
                      );
                      if (idx !== -1) {
                        updated[idx] = {
                          ...updated[idx],
                          classifyResult,
                          status: "processing",
                        };
                      }
                      return updated;
                    });
                  } else if (event === "flash") {
                    flashResult = { data: data.data };
                    console.log(
                      `[Flash] documentType: ${data.data?.documentType}`,
                      data.data,
                    );
                    addLog(
                      `    ⚡ [${i + 1}/${checkedFiles.length}] Flash 추출 완료: ${data.data?.documentType || "타입 없음"}`,
                    );

                    // 실시간 업데이트: Flash 결과 반영
                    setFileList((prev) => {
                      const updated = [...prev];
                      const idx = updated.findIndex(
                        (f) => f.id === fileItem.id,
                      );
                      if (idx !== -1) {
                        updated[idx] = {
                          ...updated[idx],
                          flashResult,
                          classifyResult,
                          status: "extracting",
                        };
                      }
                      return updated;
                    });
                  } else if (event === "pro") {
                    proResult = { data: data.data };
                    console.log(
                      `[Pro] documentType: ${data.data?.documentType}`,
                      data.data,
                    );
                    addLog(
                      `    [${i + 1}/${checkedFiles.length}] Pro/Claude 추출 완료: ${data.data?.documentType || "타입 없음"}`,
                    );

                    // 실시간 업데이트: Pro 결과 반영
                    setFileList((prev) => {
                      const updated = [...prev];
                      const idx = updated.findIndex(
                        (f) => f.id === fileItem.id,
                      );
                      if (idx !== -1) {
                        updated[idx] = {
                          ...updated[idx],
                          proResult,
                          flashResult,
                          classifyResult,
                          status: "completed",
                        };
                      }
                      return updated;
                    });
                  } else if (event === "complete") {
                    const fileDuration = Date.now() - fileStartTime;
                    const durationSec = (fileDuration / 1000).toFixed(1);

                    addLog(
                      `  ✅ [${i + 1}/${checkedFiles.length}] ${fileItem.name} 완료 (${durationSec}초)`,
                    );

                    // PDF 페이지별 결과 or 이미지 결과
                    if (data.pageResults) {
                      // PDF 페이지별 결과
                      // ⭐ 기존 page-extract에서 저장한 pngImage/ocrImageSize 병합
                      // ⭐ 수정: 지역 변수 pageResults 사용 (React 클로저 문제 해결)
                      const existingPageResults = pageResults || [];
                      console.log(
                        "🔍 [Complete] Local pageResults count:",
                        existingPageResults.length,
                        "first pngImage:",
                        existingPageResults[0]?.pngImage ? "exists" : "null",
                      );

                      const mergedPageResults = data.pageResults.map(
                        (newPage) => {
                          const existingPage = existingPageResults.find(
                            (p) => p.pageNumber === newPage.pageNumber,
                          );
                          return {
                            ...newPage,
                            // ⭐ 기존에 저장된 pngImage/ocrImageSize 유지 (complete 이벤트는 이 데이터를 포함하지 않음)
                            pngImage:
                              newPage.pngImage || existingPage?.pngImage,
                            ocrImageSize:
                              newPage.ocrImageSize ||
                              existingPage?.ocrImageSize,
                            boundingBoxes:
                              newPage.boundingBoxes ||
                              existingPage?.boundingBoxes ||
                              [],
                            paddleRaw:
                              newPage.paddleRaw || existingPage?.paddleRaw,
                          };
                        },
                      );
                      pageResults = mergedPageResults;
                      console.log(
                        "🔍 [Complete] Merged pngImage:",
                        mergedPageResults[0]?.pngImage
                          ? mergedPageResults[0].pngImage.substring(0, 50) +
                              "..."
                          : "null",
                      );
                      resolve({
                        fileId: fileItem.id,
                        duration: fileDuration,
                        totalPages: data.totalPages,
                        pageResults: mergedPageResults,
                        uploadedFilePath: data.uploadedFilePath || "", // ⭐ 파일 경로 저장
                        uploadedFileId: data.uploadedFileId || "", // ⭐ SpreadJS 뷰어용 (xlsx)
                        serverOriginalFileName: data.originalFileName || "", // ⭐ 서버에서 받은 원본 파일명
                      });
                    } else {
                      // 이미지 단일 결과
                      resolve({
                        fileId: fileItem.id,
                        duration: fileDuration,
                        classifyResult: classifyResult || {
                          data: {
                            documentType: data.documentType,
                            documentCode: data.documentCode,
                          },
                        },
                        flashResult: flashResult || { data: data.flashData },
                        proResult: proResult || { data: data.secondData },
                        uploadedFilePath: data.uploadedFilePath || "", // ⭐ 파일 경로 저장
                        uploadedFileId: data.uploadedFileId || "", // ⭐ SpreadJS 뷰어용 (xlsx)
                        serverOriginalFileName: data.originalFileName || "", // ⭐ 서버에서 받은 원본 파일명
                      });
                    }
                    return;
                  } else if (event === "error") {
                    throw new Error(data.error || "Processing failed");
                  }
                }
              }
            } catch (error) {
              const fileDuration = Date.now() - fileStartTime;
              const durationSec = (fileDuration / 1000).toFixed(1);
              addLog(
                `  ❌ [${i + 1}/${checkedFiles.length}] ${fileItem.name} 실패 (${durationSec}초): ${error.message}`,
              );
              reject(error);
            }
          });
        }),
      );

      // 결과 업데이트
      const resultMap = new Map();
      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value) {
          resultMap.set(result.value.fileId, result.value);
        }
      });

      // 첫 번째 완료된 파일의 ID 찾기
      const firstCompletedFileId = checkedFiles.find((fileItem) =>
        resultMap.has(fileItem.id),
      )?.id;
      console.log("🔍 첫 번째 완료 파일 ID:", firstCompletedFileId);
      console.log("🔍 결과 맵:", Array.from(resultMap.entries()));

      setFileList((prev) => {
        const updated = [...prev];

        checkedFiles.forEach((fileItem) => {
          const idx = updated.findIndex((f) => f.id === fileItem.id);
          const resultData = resultMap.get(fileItem.id);
          if (idx !== -1) {
            if (resultData) {
              // PDF 페이지별 결과가 있는 경우
              if (resultData.pageResults) {
                console.log(`📦 PDF 결과 업데이트: ${fileItem.name}`, {
                  totalPages: resultData.totalPages,
                  pageResultsLength: resultData.pageResults?.length,
                  uploadedFilePath: resultData.uploadedFilePath, // ⭐ 디버깅용 로그
                  serverOriginalFileName: resultData.serverOriginalFileName, // ⭐ 서버 원본 파일명
                });
                updated[idx] = {
                  ...updated[idx],
                  totalPages: resultData.totalPages,
                  pageResults: resultData.pageResults,
                  uploadedFilePath: resultData.uploadedFilePath || "", // ⭐ 파일 경로 저장
                  uploadedFileId: resultData.uploadedFileId || "", // ⭐ SpreadJS 뷰어용 (xlsx)
                  serverOriginalFileName:
                    resultData.serverOriginalFileName || "", // ⭐ 서버 원본 파일명 저장
                  status: "completed",
                };
              } else {
                // 이미지 단일 결과
                // Flash와 Pro 데이터 비교하여 일치율 계산
                const flashData = resultData.flashResult?.data || {};
                const proData = resultData.proResult?.data || {};

                // 공통 필드 찾기
                const allKeys = new Set([
                  ...Object.keys(flashData),
                  ...Object.keys(proData),
                ]);
                let matchedFields = 0;
                let totalFields = 0;

                allKeys.forEach((key) => {
                  if (
                    key === "metadata" ||
                    key === "timestamp" ||
                    key === "source"
                  )
                    return;
                  totalFields++;
                  if (flashData[key] === proData[key]) {
                    matchedFields++;
                  }
                });

                const matchRate =
                  totalFields > 0
                    ? Math.round((matchedFields / totalFields) * 100)
                    : 100;

                // Pro 결과를 기본 extractedData로 설정 (더 정확함)
                const extractedData = {
                  ...proData,
                  metadata: {
                    ...proData.metadata,
                    matchRate: matchRate,
                    flashData: flashData,
                    proData: proData,
                    autoValidated: true,
                    validatedAt: new Date().toISOString(),
                  },
                };

                // 📊 xlsx/docx/이미지 단일 결과를 PageBasedGrid 가 표시할 수 있도록
                //    pageResults 가짜 단일 페이지 형식으로 변환 (isPDF 체크 통과용)
                const _singlePageResult = {
                  pageNumber: 1,
                  extractedData: extractedData,
                  flashData: flashData,
                  proData: proData,
                };
                updated[idx] = {
                  ...updated[idx],
                  classifyResult: resultData.classifyResult,
                  flashResult: resultData.flashResult,
                  proResult: resultData.proResult,
                  extractedData: extractedData, // 자동으로 extractedData 설정
                  pageResults: [_singlePageResult], // ⭐ 그리드 패널 호환 (isPDF=true 효과)
                  totalPages: 1,
                  uploadedFilePath: resultData.uploadedFilePath || "", // ⭐ 파일 경로 저장
                  uploadedFileId: resultData.uploadedFileId || "", // ⭐ SpreadJS 뷰어용 (xlsx)
                  serverOriginalFileName:
                    resultData.serverOriginalFileName || "", // ⭐ 서버 원본 파일명 저장
                  status: "completed",
                };

                // ⭐ 100% 일치 시 자동 검증 처리 (Human-in-the-Loop Bypass)
                if (matchRate === 100) {
                  setValidatedPages((prev) => ({
                    ...prev,
                    [fileItem.id]: {
                      ...(prev[fileItem.id] || {}),
                      1: true, // 단일 이미지는 1페이지로 간주
                    },
                  }));
                  console.log(
                    `✅ [Auto-Validate] 단일 파일 100% 일치로 자동 검증 완료`,
                  );
                }
              }
            } else {
              // 처리 실패한 파일은 에러 상태로 설정
              updated[idx] = {
                ...updated[idx],
                status: "error",
              };
            }
          }
        });

        // 🔧 첫 번째 완료된 파일을 자동 선택 (업데이트된 리스트 사용)
        if (firstCompletedFileId) {
          console.log(
            "🎯 자동 선택 로직 시작, firstCompletedFileId:",
            firstCompletedFileId,
          );
          setTimeout(() => {
            const idx = updated.findIndex((f) => f.id === firstCompletedFileId);
            console.log("🔍 찾은 인덱스:", idx);
            if (idx !== -1) {
              console.log(
                `📌 첫 번째 파일 자동 선택: index=${idx}, id=${firstCompletedFileId}, name=${updated[idx].name}`,
              );
              console.log(`📋 선택된 파일 데이터:`, {
                classifyResult: updated[idx].classifyResult,
                flashResult: updated[idx].flashResult,
                proResult: updated[idx].proResult,
                pageResults: updated[idx].pageResults,
                totalPages: updated[idx].totalPages,
                hasPageResults: !!updated[idx].pageResults,
                pageResultsLength: updated[idx].pageResults?.length,
              });

              setSelectedFileIndex(idx);

              // PDF 파일인 경우 페이지 체크 상태 명시적으로 초기화
              console.log("🔍 PDF 체크 조건:", {
                hasPageResults: !!updated[idx].pageResults,
                pageResultsLength: updated[idx].pageResults?.length,
                willInitialize: !!(
                  updated[idx].pageResults &&
                  updated[idx].pageResults.length > 0
                ),
              });

              if (
                updated[idx].pageResults &&
                updated[idx].pageResults.length > 0
              ) {
                // 🔧 FIX: 실제 PDF 페이지 수 사용 (pageResults.length는 Gemini phantom 페이지로 부풀려질 수 있음)
                const tp =
                  updated[idx].totalPages || updated[idx].pageResults.length;
                const safetp = Math.min(tp, updated[idx].totalPages || tp);
                const allPageNumbers = Array.from(
                  { length: safetp },
                  (_, i) => i + 1,
                );
                console.log(
                  "📄 [배치 자동선택] 전체 페이지 체크:",
                  allPageNumbers,
                  "totalPages:",
                  tp,
                );
                setCheckedPages(allPageNumbers);
                setSelectedPageNumber(allPageNumbers[0] || 1);
                lastClickedPageRef.current = allPageNumbers[0] || 1;
              } else {
                console.warn(
                  "⚠️ pageResults가 없거나 비어있어서 페이지 초기화 스킵",
                );
              }

              addLog(`📌 첫 번째 파일 자동 선택됨 (${updated[idx].name})`);
            } else {
              console.warn(
                "⚠️ 파일을 찾을 수 없음, firstCompletedFileId:",
                firstCompletedFileId,
              );
            }
          }, 100);
        } else {
          console.warn("⚠️ firstCompletedFileId가 없어서 자동 선택 스킵");
        }

        return updated;
      });

      const successCount = results.filter(
        (r) => r.status === "fulfilled",
      ).length;

      // 처리 시간 통계
      const successResults = results.filter(
        (r) => r.status === "fulfilled" && r.value,
      );
      const durations = successResults
        .map((r) => r.value.duration)
        .filter((d) => d);
      const totalDuration = durations.reduce((sum, d) => sum + d, 0);
      const avgDuration =
        durations.length > 0 ? totalDuration / durations.length : 0;
      const avgSec = (avgDuration / 1000).toFixed(1);

      const completionMessage = `전체 처리 완료! (${successCount}/${checkedFiles.length}) - 평균 ${avgSec}초/건`;
      addLog(completionMessage);
      console.log("[일괄 처리] 전체 완료!");

      // ⭐ 진행률 완료 상태로 업데이트
      clearInterval(progressInterval);
      setProcessingProgress((prev) => ({
        ...prev,
        currentPage: prev.totalPages,
        step: "완료!",
        percentage: 100,
      }));

      // ⭐ 완료 메시지를 하단 바에 표시
      setLastCompletionLog(completionMessage);

      // 1초 후 진행률 리셋
      setTimeout(() => {
        setProcessingProgress({
          currentPage: 0,
          totalPages: 0,
          step: "",
          fileName: "",
          percentage: 0,
          startTime: null,
          estimatedSeconds: 0,
          model1: null,
          model2: null,
        });
      }, 1000);

      // 5초 후 완료 메시지 숨김
      setTimeout(() => {
        setLastCompletionLog(null);
      }, 5000);

      // // 추출 완료 시 전체검증 그리드 자동 표시 — 비활성화 (이미지뷰어 가림 방지)
      // if (successCount > 0) setShowBatchValidationGrid(true);
    } catch (error) {
      // 🚀 취소에 의한 AbortError는 정상 흐름이므로 오류 처리하지 않음
      if (error.name === "AbortError") {
        console.log("[일괄 처리] 사용자에 의해 취소됨");
        return;
      }
      console.error("[일괄 처리] 오류 발생:", error);
      addLog(`오류 발생: ${error.message}`);
      alert("일괄 처리 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
      setProcessingCount(0);
      clearInterval(progressInterval); // ⭐ 에러 시에도 인터벌 정리
      abortControllerRef.current = null; // 🚀 AbortController 정리
    }
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 관리자 모드 핸들러 함수들
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  // 관리자: Step 1 - 분류
  const handleAdminClassify = async () => {
    if (selectedFileIndex === null) return;

    const file = fileList[selectedFileIndex];
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("flashModelVersion", classificationFlashModel);
      formData.append("image", file.file);

      const response = await fetch(`${API_BASE_URL}/api/admin/classify`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("분류 실패");
      }

      const result = await response.json();

      // 파일 목록 업데이트 - classifyResult 저장
      setFileList((prev) =>
        prev.map((f, idx) =>
          idx === selectedFileIndex
            ? { ...f, classifyResult: result, status: "분류 완료" }
            : f,
        ),
      );
    } catch (err) {
      console.error("분류 에러:", err);
      setError(err.message);
      setFileList((prev) =>
        prev.map((f, idx) =>
          idx === selectedFileIndex
            ? { ...f, status: "error", error: err.message }
            : f,
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  // 관리자: Step 2-1 - Flash 추출
  const handleAdminExtractFlash = async () => {
    if (selectedFileIndex === null) return;

    const file = fileList[selectedFileIndex];
    if (!file || !file.classifyResult) {
      setError("먼저 분류를 실행해주세요");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("image", file.file);
      formData.append("documentType", file.classifyResult.data.docCode);

      const response = await fetch(`${API_BASE_URL}/api/admin/extract-flash`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Flash 추출 실패");
      }

      const result = await response.json();

      // 파일 목록 업데이트 - flashResult 저장
      setFileList((prev) =>
        prev.map((f, idx) =>
          idx === selectedFileIndex
            ? { ...f, flashResult: result, status: "Flash 추출 완료" }
            : f,
        ),
      );
    } catch (err) {
      console.error("Flash 추출 에러:", err);
      setError(err.message);
      setFileList((prev) =>
        prev.map((f, idx) =>
          idx === selectedFileIndex
            ? { ...f, status: "error", error: err.message }
            : f,
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  // 관리자: Step 2-2 - Pro 추출
  const handleAdminExtractPro = async () => {
    if (selectedFileIndex === null) return;

    const file = fileList[selectedFileIndex];
    if (!file || !file.classifyResult) {
      setError("먼저 분류를 실행해주세요");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("image", file.file);
      formData.append("documentType", file.classifyResult.data.docCode);

      const response = await fetch(`${API_BASE_URL}/api/admin/extract-pro`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Pro 추출 실패");
      }

      const result = await response.json();

      // 파일 목록 업데이트 - proResult 저장
      setFileList((prev) =>
        prev.map((f, idx) =>
          idx === selectedFileIndex
            ? { ...f, proResult: result, status: "Pro 추출 완료" }
            : f,
        ),
      );
    } catch (err) {
      console.error("Pro 추출 에러:", err);
      setError(err.message);
      setFileList((prev) =>
        prev.map((f, idx) =>
          idx === selectedFileIndex
            ? { ...f, status: "error", error: err.message }
            : f,
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  // 관리자: Step 3 - 검증 (Flash vs Pro 비교)
  const handleAdminValidate = async () => {
    if (selectedFileIndex === null) return;

    const file = fileList[selectedFileIndex];
    if (!file || !file.flashResult || !file.proResult) {
      setError("먼저 Flash와 Pro 추출을 완료해주세요");
      return;
    }

    // 클라이언트에서 간단한 비교 수행
    const flashData = file.flashResult.data;
    const proData = file.proResult.data;

    const fields = Object.keys(flashData);
    let matchCount = 0;
    const differences = {};

    fields.forEach((field) => {
      const flashValue = String(flashData[field] || "").trim();
      const proValue = String(proData[field] || "").trim();

      if (flashValue === proValue) {
        matchCount++;
      } else {
        differences[field] = {
          flash: flashValue,
          pro: proValue,
        };
      }
    });

    const matchRate = Math.round((matchCount / fields.length) * 100);

    const validatedData = {
      ...proData,
      metadata: {
        validated: true,
        matchRate,
        matchDetails: {
          matchCount,
          totalFields: fields.length,
        },
      },
      differences,
    };

    // extractedData 업데이트
    setFileList((prev) =>
      prev.map((f, idx) =>
        idx === selectedFileIndex
          ? {
              ...f,
              extractedData: validatedData,
              status: matchRate === 100 ? "completed" : "validated",
            }
          : f,
      ),
    );
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Prompt Playground 실행 핸들러
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const handleReExtract = async (customPrompt, model) => {
    if (selectedFileIndex === null) return;
    const file = fileList[selectedFileIndex];
    if (!file) return;

    setPlaygroundProcessing(true);

    try {
      const formData = new FormData();
      // ⭐ 필드 먼저 전송
      formData.append("classificationFlashModel", classificationFlashModel);
      formData.append("validationModel", validationModel);
      formData.append("image", file.file); // 'deepseek' or 'gemini-...'

      // 모델에 따라 파라미터 설정
      if (model === "deepseek") {
        formData.append("deepseekMode", deepseekMode);
      } else {
        // Gemini 모델인 경우 selectedValidationModels에 추가하여 전달
        formData.append("selectedValidationModels", JSON.stringify([model]));
      }

      formData.append("grounding", grounding);
      formData.append("datalabBoundingBox", datalabBoundingBox);
      formData.append("enableHybridOCR", enableHybridOCR); // ⭐ Hybrid Mode Flag

      // SSE 연결
      const response = await fetch(`${API_BASE_URL}/api/ocr/extract-stream`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error("Extraction failed");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");

        // 마지막 청크가 불완전할 수 있으므로 buffer에 남김
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("event: ")) {
            const eventType = line.substring(7).trim();
            // data: 부분 찾기 (줄바꿈으로 분리된 다음 줄)
            const dataLineIndex = line.indexOf("\ndata: ");
            let dataLine = "";

            // event: ... \n data: ... 형식인 경우
            if (dataLineIndex !== -1) {
              dataLine = line.substring(dataLineIndex + 1);
            } else {
              // 다음 줄에 data: 가 있는 경우 (이 루프에서는 처리하기 어려움, 위 split 로직상 한 덩어리로 들어옴)
              // split('\n\n')을 했으므로 event: ...\ndata: ... 가 하나의 line임
              const parts = line.split("\ndata: ");
              if (parts.length >= 2) {
                dataLine = "data: " + parts[1];
              }
            }

            if (dataLine && dataLine.startsWith("data: ")) {
              try {
                const data = JSON.parse(dataLine.substring(6));

                if (eventType === "page-extract") {
                  // 결과 업데이트
                  setFileList((prev) =>
                    prev.map((f, idx) => {
                      if (idx !== selectedFileIndex) return f;

                      // 페이지 결과 업데이트
                      const newPageResults = [...(f.pageResults || [])];
                      const pageIndex = data.pageNumber - 1;

                      // 배열 크기 확보
                      while (newPageResults.length <= pageIndex) {
                        newPageResults.push({});
                      }

                      // Option 2.5: propagate _raw/_enrichment from server
                      //   so FieldSourceBadge can render OCR vs DB badges.
                      //   Backend page-extract may carry them either on the
                      //   top-level event or nested in flashData/proData.
                      const _evRaw =
                        data._raw ||
                        data.flashData?._raw ||
                        data.proData?._raw ||
                        null;
                      const _evEnrichment =
                        data._enrichment ||
                        data.flashData?._enrichment ||
                        data.proData?._enrichment ||
                        null;
                      const _pageED = {
                        ...(data.flashData || {}),
                        _source: "playground-stream",
                        _raw: _evRaw,
                        _enrichment: _evEnrichment,
                      };

                      newPageResults[pageIndex] = {
                        ...(newPageResults[pageIndex] || {}),
                        pageNumber: data.pageNumber,
                        documentType: data.documentType,
                        flashData: data.flashData,
                        proData: data.proData,
                        extractedData: _pageED, // Playground 결과는 즉시 반영
                        boundingBoxes: data.boundingBoxes,
                        pngImage:
                          data.pngImage || newPageResults[pageIndex]?.pngImage,
                      };

                      return {
                        ...f,
                        pageResults: newPageResults,
                        // 단일 페이지인 경우 메인 결과도 업데이트
                        flashResult: { data: data.flashData },
                        proResult: { data: data.proData },
                        extractedData: _pageED,
                        status: "processing",
                      };
                    }),
                  );
                } else if (eventType === "complete") {
                  setFileList((prev) =>
                    prev.map((f, idx) =>
                      idx === selectedFileIndex
                        ? { ...f, status: "completed" }
                        : f,
                    ),
                  );
                }
              } catch (e) {
                console.error("SSE Parse Error:", e);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Re-extraction failed:", error);
      alert(`실행 실패: ${error.message}`);
    } finally {
      setPlaygroundProcessing(false);
    }
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  // 1차 추출 실행 (옵션5: 분류 + Flash + Pro 병렬)
  // 차량 정보 데이터베이스 저장
  // 검증 관련 함수들
  const handleValidatePage = async (fileId, pageNumber, editedData = null) => {
    // ⭐ editedData가 있으면 수정된 데이터로 업데이트
    if (editedData) {
      console.log(`📝 페이지 ${pageNumber} 수정된 데이터로 검증:`, editedData);

      // 파일 리스트에서 해당 파일 찾기
      const fileIndex = fileList.findIndex((f) => f.id === fileId);
      if (fileIndex !== -1) {
        const file = fileList[fileIndex];

        // 페이지별 결과 업데이트
        if (file.pageResults && file.pageResults.length > 0) {
          const pageIndex = pageNumber - 1;
          if (file.pageResults[pageIndex]) {
            const pageResult = file.pageResults[pageIndex];
            // ⭐ 기존 데이터 우선순위: extractedData > proData(추출2) > flashData(추출1)
            // extractedData가 없으면 추출2 모델(proData)을 기본값으로 사용
            const baseData =
              pageResult.extractedData ||
              pageResult.proData ||
              pageResult.flashData ||
              {};

            // 수정된 데이터를 extractedData에 반영 (검증 후 최종 데이터)
            // ⭐ 사용자가 수정한 데이터가 최우선, 없으면 추출2 모델 값 사용
            let finalData = {
              ...baseData,
              ...editedData, // ⭐ 사용자가 수정한 데이터가 최우선
            };

            // ⭐ 1. 주소 검증 가중치 저장
            if (finalData.address) {
              const ocrAddress =
                pageResult.flashData?.address ||
                pageResult.proData?.address ||
                finalData.address;
              const verifiedAddress = finalData.address;

              // OCR 인식 주소와 검증 주소가 다른 경우에만 저장
              if (ocrAddress !== verifiedAddress && ocrAddress) {
                try {
                  await fetch(`${API_BASE_URL}/api/address/validate`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      ocrRecognized: ocrAddress,
                      verifiedAddress: verifiedAddress,
                      userId: "system", // TODO: 실제 사용자 ID로 변경
                    }),
                  });
                  console.log(
                    `✅ 주소 검증 가중치 저장: ${ocrAddress} → ${verifiedAddress}`,
                  );
                } catch (err) {
                  console.error("❌ 주소 검증 가중치 저장 실패:", err);
                }
              }
            }

            // ⭐ 2. 차량 기초자료 조회 및 필드 채우기 (차량명이 있으면 항상 조회)
            if (finalData.model) {
              try {
                const carName = finalData.model; // 예: '쏘나타', '쏘나타(sonata)'
                const response = await fetch(
                  `${API_BASE_URL}/api/vehicle-master/search/${encodeURIComponent(carName)}`,
                );

                if (response.ok) {
                  const result = await response.json();

                  if (result.success && result.data) {
                    const masterData = result.data;
                    const filledFields = [];

                    // ⭐ 차량명을 항상 영문명으로 변경 (값이 다 채워져 있어도) - DB 컬럼명 사용
                    if (masterData.BASE_CAR_NM_ENG) {
                      finalData.model =
                        masterData.BASE_CAR_NM_ENG.toUpperCase(); // 예: 'SONATA'
                      filledFields.push("차량명");
                    }

                    // 누락된 필드만 채우기 (중량이 이미 있으면 패스) - DB 컬럼명 사용
                    if (!finalData.brand && masterData.BASE_CAR_MAKER) {
                      finalData.brand = masterData.BASE_CAR_MAKER;
                      filledFields.push("브랜드");
                    }

                    if (!finalData.modelYear && masterData.BASE_CAR_YEAR) {
                      finalData.modelYear = masterData.BASE_CAR_YEAR;
                      filledFields.push("연식");
                    }

                    if (!finalData.displacement && masterData.BASE_CAR_CC) {
                      finalData.displacement =
                        masterData.BASE_CAR_CC.toString();
                      filledFields.push("배기량");
                    }

                    // 중량이 없을 때만 채우기
                    if (!finalData.totalWeight && masterData.BASE_CAR_WEIGHT) {
                      finalData.totalWeight =
                        masterData.BASE_CAR_WEIGHT.toString();
                      filledFields.push("중량");
                    }

                    if (!finalData.cbm && masterData.BASE_CAR_CBM) {
                      finalData.cbm = masterData.BASE_CAR_CBM.toString();
                      filledFields.push("CBM");
                    }

                    // ⭐ 값이 다 채워져 있어도 기초자료에 추가할 수 있도록 메타데이터 추가
                    finalData.metadata = finalData.metadata || {};
                    if (filledFields.length > 0) {
                      console.log(
                        `✅ 기초자료에서 가져온 필드: ${filledFields.join(", ")}`,
                      );
                      finalData.metadata.filledFromMaster = filledFields;
                    }
                    // 기초자료에 있는 차량이므로 추가 옵션 제공
                    finalData.metadata.masterDataFound = true;
                    finalData.metadata.masterData = masterData; // 기초자료 데이터 저장
                  } else {
                    // 차량명이 기초자료에 없으면 사용자에게 추가 옵션 제공
                    console.log(`⚠️ 차량 기초자료에 없음: ${carName}`);
                    finalData.metadata = finalData.metadata || {};
                    finalData.metadata.masterDataNotFound = true;
                    finalData.metadata.carName = carName;
                  }
                }
              } catch (err) {
                console.error("❌ 차량 기초자료 조회 실패:", err);
              }
            }

            const updatedPageResults = [...file.pageResults];
            updatedPageResults[pageIndex] = {
              ...pageResult,
              extractedData: finalData,
            };

            console.log(`📝 검증 데이터 업데이트:`, {
              pageNumber,
              baseData: baseData.plateNumber,
              editedData: editedData.plateNumber,
              finalData: finalData.plateNumber,
            });

            // ⭐ 검증 완료된 final_data를 DB에 저장 (UCAR_OCR_RESULT 테이블)
            try {
              const ocrResultData = {
                fileName: file.name,
                fileType: file.isPDF ? "PDF" : "IMAGE",
                documentType: finalData.documentType || pageResult.documentType,
                flashData: pageResult.flashData || {},
                flashModel: GEMINI_FLASH_MODEL,
                flashProcessingTime:
                  pageResult.flashData?.metadata?.processingTime || null,
                proData: pageResult.proData || {},
                proModel:
                  pageResult.proData?.metadata?.model || GEMINI_FLASH_MODEL,
                proProcessingTime:
                  pageResult.proData?.metadata?.processingTime || null,
                finalData: finalData, // ⭐ 최종 승인 데이터
                hasDifferences: false, // 검증 완료 시 차이 없음
                differenceCount: 0,
                differenceFields: [],
                userId: "system", // TODO: 실제 사용자 ID로 변경
                groupId: "0001", // TODO: 실제 그룹 ID로 변경
              };

              const saveResponse = await fetch(
                `${API_BASE_URL}/api/ocr-result/save`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(ocrResultData),
                },
              );

              if (saveResponse.ok) {
                const saveResult = await saveResponse.json();
                console.log(
                  `✅ 검증 결과 DB 저장 완료: ocrId=${saveResult.ocrId}`,
                );

                // 저장된 ocrId를 pageResult에 추가
                updatedPageResults[pageIndex].ocrId = saveResult.ocrId;
              } else {
                console.warn(
                  "⚠️ 검증 결과 DB 저장 실패 (계속 진행):",
                  await saveResponse.text(),
                );
              }
            } catch (saveError) {
              console.error(
                "❌ 검증 결과 DB 저장 오류 (계속 진행):",
                saveError,
              );
              // DB 저장 실패해도 프론트엔드 state는 업데이트
            }

            setFileList((prev) =>
              prev.map((f, idx) =>
                idx === fileIndex
                  ? { ...f, pageResults: updatedPageResults }
                  : f,
              ),
            );
          }
        } else if (file.extractedData) {
          // 단일 이미지인 경우
          let finalData = { ...file.extractedData, ...editedData };

          // ⭐ 1. 주소 검증 가중치 저장
          if (finalData.address) {
            const ocrAddress =
              file.flashResult?.data?.address ||
              file.proResult?.data?.address ||
              finalData.address;
            const verifiedAddress = finalData.address;

            // OCR 인식 주소와 검증 주소가 다른 경우에만 저장
            if (ocrAddress !== verifiedAddress && ocrAddress) {
              try {
                await fetch(`${API_BASE_URL}/api/address/validate`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    ocrRecognized: ocrAddress,
                    verifiedAddress: verifiedAddress,
                    userId: "system", // TODO: 실제 사용자 ID로 변경
                  }),
                });
                console.log(
                  `✅ 주소 검증 가중치 저장: ${ocrAddress} → ${verifiedAddress}`,
                );
              } catch (err) {
                console.error("❌ 주소 검증 가중치 저장 실패:", err);
              }
            }
          }

          // ⭐ 2. 차량 기초자료 조회 및 필드 채우기 (차량명이 있으면 항상 조회)
          if (finalData.model) {
            try {
              const carName = finalData.model; // 예: '쏘나타', '쏘나타(sonata)'
              const response = await fetch(
                `${API_BASE_URL}/api/vehicle-master/search/${encodeURIComponent(carName)}`,
              );

              if (response.ok) {
                const result = await response.json();

                if (result.success && result.data) {
                  const masterData = result.data;
                  const filledFields = [];

                  // ⭐ 차량명을 항상 영문명으로 변경 (값이 다 채워져 있어도) - DB 컬럼명 사용
                  if (masterData.BASE_CAR_NM_ENG) {
                    finalData.model = masterData.BASE_CAR_NM_ENG.toUpperCase(); // 예: 'SONATA'
                    filledFields.push("차량명");
                  }

                  // 누락된 필드만 채우기 (중량이 이미 있으면 패스) - DB 컬럼명 사용
                  if (!finalData.brand && masterData.BASE_CAR_MAKER) {
                    finalData.brand = masterData.BASE_CAR_MAKER;
                    filledFields.push("브랜드");
                  }

                  if (!finalData.modelYear && masterData.BASE_CAR_YEAR) {
                    finalData.modelYear = masterData.BASE_CAR_YEAR;
                    filledFields.push("연식");
                  }

                  if (!finalData.displacement && masterData.BASE_CAR_CC) {
                    finalData.displacement = masterData.BASE_CAR_CC.toString();
                    filledFields.push("배기량");
                  }

                  // 중량이 없을 때만 채우기
                  if (!finalData.totalWeight && masterData.BASE_CAR_WEIGHT) {
                    finalData.totalWeight =
                      masterData.BASE_CAR_WEIGHT.toString();
                    filledFields.push("중량");
                  }

                  if (!finalData.cbm && masterData.BASE_CAR_CBM) {
                    finalData.cbm = masterData.BASE_CAR_CBM.toString();
                    filledFields.push("CBM");
                  }

                  // ⭐ 값이 다 채워져 있어도 기초자료에 추가할 수 있도록 메타데이터 추가
                  finalData.metadata = finalData.metadata || {};
                  if (filledFields.length > 0) {
                    console.log(
                      `✅ 기초자료에서 가져온 필드: ${filledFields.join(", ")}`,
                    );
                    finalData.metadata.filledFromMaster = filledFields;
                  }
                  // 기초자료에 있는 차량이므로 추가 옵션 제공
                  finalData.metadata.masterDataFound = true;
                  finalData.metadata.masterData = masterData; // 기초자료 데이터 저장
                } else {
                  // 차량명이 기초자료에 없으면 사용자에게 추가 옵션 제공
                  console.log(`⚠️ 차량 기초자료에 없음: ${carName}`);
                  finalData.metadata = finalData.metadata || {};
                  finalData.metadata.masterDataNotFound = true;
                  finalData.metadata.carName = carName;
                }
              }
            } catch (err) {
              console.error("❌ 차량 기초자료 조회 실패:", err);
            }
          }

          // ⭐ 검증 완료된 final_data를 DB에 저장
          try {
            const ocrResultData = {
              fileName: file.name,
              fileType: "IMAGE",
              documentType: finalData.documentType,
              flashData: file.flashResult?.data || {},
              flashModel: GEMINI_FLASH_MODEL,
              flashProcessingTime: file.flashResult?.responseTime || null,
              proData: file.proResult?.data || {},
              proModel: file.proResult?.model || GEMINI_FLASH_MODEL,
              proProcessingTime: file.proResult?.responseTime || null,
              finalData: finalData,
              hasDifferences: false,
              differenceCount: 0,
              differenceFields: [],
              userId: "system",
              groupId: "0001",
            };

            const saveResponse = await fetch(
              `${API_BASE_URL}/api/ocr-result/save`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(ocrResultData),
              },
            );

            if (saveResponse.ok) {
              const saveResult = await saveResponse.json();
              console.log(
                `✅ 검증 결과 DB 저장 완료: ocrId=${saveResult.ocrId}`,
              );
            }
          } catch (saveError) {
            console.error("❌ 검증 결과 DB 저장 오류:", saveError);
          }

          setFileList((prev) =>
            prev.map((f, idx) =>
              idx === fileIndex
                ? {
                    ...f,
                    extractedData: finalData,
                    proResult: f.proResult
                      ? {
                          ...f.proResult,
                          data: { ...f.proResult.data, ...editedData },
                        }
                      : f.proResult,
                  }
                : f,
            ),
          );
        }
      }
    }

    setValidatedPages((prev) => {
      const updated = {
        ...prev,
        [fileId]: {
          ...(prev[fileId] || {}),
          [pageNumber]: true,
        },
      };

      // ⭐ 모든 페이지 검증 완료 체크
      const file = fileList.find((f) => f.id === fileId);
      if (file && file.pageResults && file.pageResults.length > 0) {
        const totalPages = file.pageResults.length;
        const validatedCount = Object.keys(updated[fileId] || {}).filter(
          (pageNum) => updated[fileId][pageNum] === true,
        ).length;

        if (validatedCount === totalPages) {
          // 모든 페이지 검증 완료
          setTimeout(() => {
            const confirmed = window.confirm(
              `✅ 모든 페이지(${totalPages}건) 검증이 완료되었습니다.\n\n` +
                `검증 결과 화면으로 이동하시겠습니까?`,
            );
            if (confirmed) {
              setActiveTab("mapping"); // 차량등록매핑 탭으로 이동
            }
          }, 100);
        }
      }

      return updated;
    });
    console.log(`✅ 페이지 ${pageNumber} 검증 완료 (파일 ID: ${fileId})`);
  };

  const handleValidateMultiplePages = (fileId, pageNumbers) => {
    setValidatedPages((prev) => {
      const fileValidations = { ...(prev[fileId] || {}) };
      pageNumbers.forEach((pageNum) => {
        fileValidations[pageNum] = true;
      });
      return {
        ...prev,
        [fileId]: fileValidations,
      };
    });
    console.log(
      `✅ ${pageNumbers.length}개 페이지 일괄 검증 완료 (파일 ID: ${fileId})`,
    );
  };

  const handleValidateAllPages = async (
    fileId,
    totalPages,
    editedData = null,
  ) => {
    // ⭐ 전체 검증 시에도 editedData를 첫 번째 페이지에 적용
    if (editedData) {
      await handleValidatePage(fileId, 1, editedData);
    }

    // 모든 페이지에 대해 검증 처리
    const file = fileList.find((f) => f.id === fileId);
    if (file && file.pageResults) {
      for (let i = 0; i < file.pageResults.length; i++) {
        const pageResult = file.pageResults[i];
        const pageNumber = pageResult.pageNumber || i + 1;

        // 각 페이지의 extractedData로 검증 처리 (아직 검증 안 된 경우만)
        if (pageResult.extractedData && !validatedPages[fileId]?.[pageNumber]) {
          await handleValidatePage(
            fileId,
            pageNumber,
            pageResult.extractedData,
          );
        }
      }
    }

    const allPageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
    handleValidateMultiplePages(fileId, allPageNumbers);
    console.log(
      `✅ 전체 ${totalPages}개 페이지 검증 완료 (파일 ID: ${fileId})`,
    );

    // // 전체검증 그리드 자동 표시 — 비활성화 (이미지뷰어 가림 방지)
    // setShowBatchValidationGrid(true);
  };

  const isPageValidated = (fileId, pageNumber) => {
    return validatedPages[fileId]?.[pageNumber] === true;
  };

  const handleSaveVehicle = async (
    vehicleData,
    pageNumber,
    groupCode,
    userId,
  ) => {
    try {
      console.log("📝 차량 정보 저장 시작:", {
        vehicleData,
        pageNumber,
        groupCode,
        userId,
      });

      // 현재 파일 정보 가져오기
      const currentFile =
        selectedFileIndex !== null ? fileList[selectedFileIndex] : null;
      const currentPage = currentFile?.pages?.[pageNumber - 1];

      // OCR 원본 결과 및 프롬프트 정보 수집
      const ocrRawResult =
        currentPage?.extractedData ||
        currentPage?.flashResult ||
        currentPage?.proResult;
      const documentType =
        currentPage?.classifyResult?.documentType || "unknown";

      // 사용된 프롬프트 결정 (실제 사용한 모델에 따라)
      let ocrPrompt = "";
      if (currentPage?.proResult) {
        ocrPrompt = "차량등록원부 OCR 추출 (Gemini 2.5 Pro)";
      } else if (currentPage?.flashResult) {
        ocrPrompt = "차량등록원부 OCR 추출 (Gemini 1.5 Flash)";
      }

      const response = await fetch(`${API_BASE_URL}/api/vehicle/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          vehicleData,
          ocrRawResult,
          userId: userId || "jmkim",
          groupId: groupCode || "0001",
          documentType,
          pdfFilePath: currentFile?.path,
          ocrPrompt,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "차량 저장 실패");
      }

      const result = await response.json();

      console.log("✅ 차량 저장 성공:", result);
      alert(
        `차량 정보가 성공적으로 저장되었습니다!\n차량 키: ${result.carKey}\n검증 이력 ID: ${result.validationHistoryId || "N/A"}`,
      );

      // 현재 파일의 상태 업데이트
      if (selectedFileIndex !== null) {
        setFileList(
          fileList.map((f, idx) =>
            idx === selectedFileIndex
              ? {
                  ...f,
                  savedCarKey: result.carKey,
                  savedAt: new Date().toISOString(),
                }
              : f,
          ),
        );
      }

      return result;
    } catch (error) {
      console.error("❌ 차량 저장 실패:", error);
      throw error;
    }
  };

  const handleExtractSelected = async () => {
    if (selectedFileIndex === null) return;

    setLoading(true);
    setError(null);

    const selectedFile = fileList[selectedFileIndex];

    // 상태 업데이트: processing
    setFileList((prev) =>
      prev.map((f, idx) =>
        idx === selectedFileIndex ? { ...f, status: "extracting" } : f,
      ),
    );

    try {
      // ✅ 단건도 다중(extractOneFile)과 동일한 적응형 추출 경로 사용
      //    → 건별·다중건이 같은 파일이면 결과가 일치한다 (불변식).
      const { data, result, cv, receivedGrounding } = await callAdaptiveExtract(
        selectedFile.file,
        "single",
      );
      const extractedData = result;

      // 상태 업데이트: completed — 다중과 동일하게 매핑
      setFileList((prev) =>
        prev.map((f, idx) => {
          if (idx !== selectedFileIndex) return f;
          return {
            ...f,
            status: "completed",
            batchStatus: "completed",
            extractedData: result,
            flashData: result,
            proData: cv?.secondData ? { ...result, ...cv.secondData } : result,
            pageResults: result.pageResults || [],
            items: result.items || [],
            totalPages: f.totalPages || data.data.pageResults?.length || 1,
            groundedFields: {
              ...(f.groundedFields || {}),
              ...receivedGrounding,
            },
            pageDimensions: {
              ...(f.pageDimensions || {}),
              ...(data.data.pageDimensions || {}),
            },
            crossValidation: cv
              ? {
                  ...cv,
                  verificationMode:
                    data.metadata?.verificationMode || "parallel",
                }
              : null,
            error: null,
          };
        }),
      );

      // 📄 선택 파일의 페이지 체크 상태 갱신 (다중과 동일)
      {
        const actualTotalPages =
          selectedFile.totalPages ||
          data.metadata?.totalPages ||
          data.data.pageResults?.length ||
          1;
        const pageCount = Math.min(
          actualTotalPages,
          data.data.pageResults?.length || 1,
        );
        setCheckedPages(Array.from({ length: pageCount }, (_, k) => k + 1));
        setSelectedPageNumber(1);
      }

      // 📄 [PostgreSQL+GCS] 추출 결과 영구 저장 → _documentId 즉시 반영
      try {
        const savedDoc = await saveExtractionToDB({
          name: selectedFile.name,
          isPDF: selectedFile.isPDF,
          totalPages: selectedFile.totalPages || 1,
          matchedTemplate: selectedFile.matchedTemplate,
          extractedData,
          file: selectedFile.file, // PDF 원본 (GCS 업로드용)
        });
        if (savedDoc?.id) {
          setFileList((prev) =>
            prev.map((f, idx) =>
              idx === selectedFileIndex
                ? { ...f, _documentId: savedDoc.id }
                : f,
            ),
          );
        }
      } catch (dbErr) {
        console.warn("⚠️ [DB Save] 저장 실패 (무시):", dbErr.message);
      }
    } catch (err) {
      setError(err.message);

      // 상태 업데이트: error
      setFileList((prev) =>
        prev.map((f, idx) =>
          idx === selectedFileIndex
            ? { ...f, status: "error", error: err.message }
            : f,
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFile = async (index) => {
    // DB에서도 삭제: ID + 파일명 모두 전송 (belt-and-suspenders)
    const file = fileList[index];
    const ids = file?._documentId ? [file._documentId] : [];
    const names = file?.name ? [file.name] : [];
    if (ids.length > 0 || names.length > 0) {
      try {
        await deleteDocumentsFromDB(ids, names);
      } catch (err) {
        console.warn("⚠️ DB 삭제 실패:", err.message);
      }
    }

    setFileList((prev) => {
      const newList = prev.filter((_, idx) => idx !== index);

      // 선택 인덱스 조정
      if (selectedFileIndex === index) {
        setSelectedFileIndex(newList.length > 0 ? 0 : null);
      } else if (selectedFileIndex > index) {
        setSelectedFileIndex(selectedFileIndex - 1);
      }

      return newList;
    });
  };

  // 전체 초기화 (파일은 유지, 추출 결과만 클리어)
  const handleReset = () => {
    if (fileList.length === 0 && processingLogs.length === 0) {
      return; // 이미 초기화된 상태
    }

    const confirmed = window.confirm(
      "추출된 결과를 초기화하시겠습니까?\n(업로드한 파일은 유지됩니다)",
    );
    if (confirmed) {
      // 파일 리스트는 유지하되, 추출 결과만 초기화
      setFileList((prev) =>
        prev.map((file) => ({
          ...file,
          extractedData: null,
          status: "pending",
          error: null,
          pageResults: null,
        })),
      );

      // 📦 증분 추출 캐시도 초기화 (IndexedDB)
      extractionCacheRef.current = {};
      clearExtractionCacheDB();

      setProcessingLogs([]);
      setCheckedPages([]);
      setSelectedPageNumber(1);
      addLog("🔄 추출 결과 초기화 완료 (캐시 포함, 파일은 유지됨)", "info");
    }
  };

  // 🔄 현재 파일만 초기화 (해당 파일의 추출 캐시 + 결과만 클리어)
  const handleResetCurrentFile = () => {
    if (selectedFileIndex === null) return;
    const selectedFile = fileList[selectedFileIndex];
    if (!selectedFile) return;

    const cacheKey = selectedFile.name || `file_${selectedFileIndex}`;
    const batchCount = Object.keys(
      extractionCacheRef.current[cacheKey]?.extractionBatches || {},
    ).length;
    const cachedPages = Object.values(
      extractionCacheRef.current[cacheKey]?.extractionBatches || {},
    ).flatMap((b) => b.pages || []);
    const uniquePages = [...new Set(cachedPages)].sort((a, b) => a - b);

    const msg =
      batchCount > 0
        ? `"${selectedFile.name}"의 추출 결과를 초기화합니다.\n\n현재 캐시: ${batchCount}개 배치, 페이지 [${uniquePages.join(", ")}]\n\n초기화 후 다시 딥러닝 추출해야 합니다.`
        : `"${selectedFile.name}"의 추출 결과를 초기화합니다.`;

    if (!window.confirm(msg)) return;

    // 해당 파일의 캐시만 삭제
    delete extractionCacheRef.current[cacheKey];
    saveExtractionCache(); // IndexedDB 동기화

    // fileList에서 해당 파일만 초기화
    setFileList((prev) =>
      prev.map((f, idx) => {
        if (idx !== selectedFileIndex) return f;
        return {
          ...f,
          extractedData: null,
          flashData: null,
          proData: null,
          status: "pending",
          error: null,
          pageResults: null,
          items: null,
          crossValidation: null,
        };
      }),
    );

    addLog(
      `🔄 "${selectedFile.name}" 추출 결과 초기화 완료 (캐시 ${batchCount}개 배치 삭제)`,
      "info",
    );
  };

  // 검증 모델 토글 핸들러 (최대 2개까지 선택 가능)
  const toggleValidationModel = (modelId) => {
    setSelectedValidationModels((prev) => {
      if (prev.includes(modelId)) {
        // 이미 선택된 경우 - 제거 (단, 최소 1개는 유지)
        if (prev.length > 1) {
          return prev.filter((m) => m !== modelId);
        } else {
          alert("최소 1개의 검증 모델을 선택해야 합니다.");
          return prev;
        }
      } else {
        // 선택되지 않은 경우 - 추가 (최대 3개까지)
        if (prev.length < 3) {
          return [...prev, modelId];
        } else {
          alert("최대 3개의 검증 모델만 선택할 수 있습니다.");
          return prev;
        }
      }
    });
  };

  // 검증 모델 이름 가져오기 (닉네임으로 표시)
  const getModelDisplayName = (modelId) => {
    // ⭐ UI에는 닉네임 표시 (개발자만 알 수 있도록)
    return getModelNickname(modelId) || modelId;
  };

  const handleFieldChange = (field, value) => {
    if (selectedFileIndex === null) return;

    setFileList((prev) =>
      prev.map((f, idx) =>
        idx === selectedFileIndex && f.extractedData
          ? {
              ...f,
              extractedData: {
                ...f.extractedData,
                [field]: value || "",
              },
            }
          : f,
      ),
    );
  };

  // 필드 값 토글 (1차 <-> 2차)
  const toggleFieldValue = (field) => {
    if (!extractedData?.differences?.[field]) return;

    const diff = extractedData.differences[field];
    const currentValue = extractedData[field] || "";

    // 현재 값이 1차 값이면 2차로, 2차 값이면 1차로
    const pVal = diff.primary ?? diff.flash ?? "";
    const sVal = diff.secondary ?? diff.pro ?? "";
    const newValue = currentValue === pVal ? sVal : pVal;
    handleFieldChange(field, newValue);
  };

  const handleConfirm = async () => {
    if (selectedFileIndex === null) return;

    const selectedFile = fileList[selectedFileIndex];
    const extractedData = selectedFile.extractedData;

    if (!extractedData) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/ocr/confirm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          finalData: extractedData,
          originalImageId: extractedData.metadata.processedAt,
        }),
      });

      const result = await response.json();

      if (result.success) {
        alert("✅ UPS2LOGIN 시스템에 입력되었습니다!");

        // 완료된 파일 제거
        handleRemoveFile(selectedFileIndex);
      } else {
        alert("❌ 저장 실패: " + result.error);
      }
    } catch (err) {
      alert("❌ 시스템 오류: " + err.message);
    }
  };

  // 필드 렌더링 (차이 표시 포함 - Human-in-the-Loop)
  const renderField = (label, field, type = "text") => {
    // ⭐ 숫자 필드는 0도 유효한 값이므로 || '' 사용하지 않음
    const numericFields = [
      "mileage",
      "displacement",
      "totalWeight",
      "length",
      "width",
      "height",
      "cbm",
      "modelYear",
    ];
    const value = numericFields.includes(field)
      ? extractedData[field] !== null && extractedData[field] !== undefined
        ? extractedData[field]
        : ""
      : extractedData[field] || "";
    const docType = extractedData?.documentType;

    // ⭐ 부분 결과 플래그 확인 (Gemini만 완료, DeepSeek 대기 중)
    const isPartialResult = extractedData?.partial === true;

    // 프롬프트에 정의된 필드인지 확인 (DOCUMENT_FIELDS_MAP 기준)
    const fieldApplicable = isFieldApplicable(docType, field);

    // Flash vs Final 차이 정보 (백엔드에서 전달받음)
    const diff = extractedData?.differences?.[field];
    if (field === "mileage" || field === "displacement") {
      // ⭐ 디버깅용 로그 (주행거리, 배기량)
      console.log(`[renderField] ${field}:`, {
        value,
        diff,
        extractedDataDifferences: extractedData?.differences,
        isPartialResult,
      });
    }
    const hasDifference = !!diff;

    // 미인식 판단: 1차나 2차 중 하나라도 값이 있었는데 최종 결과 없음
    const primaryValue = diff?.primary ?? diff?.flash;
    const secondaryValue = diff?.secondary ?? diff?.pro;
    const hadAnyValue = primaryValue || secondaryValue;
    const isUnrecognized = fieldApplicable && !value && hadAnyValue;

    // ✅ Flash vs Final 비교 박스 표시 조건:
    // 1. 차이가 있거나 (match === false)
    // 2. Flash 또는 Pro 중 하나라도 값이 있는 경우
    // 3. 단, 미인식이 우선 (미인식이면 비교 박스 숨김)
    // 4. ⭐ 부분 결과일 때는 차이점 표시 안 함 (Gemini만 완료된 상태)
    const showDifference =
      !isPartialResult &&
      hasDifference &&
      !isUnrecognized &&
      (!diff.match || hadAnyValue);

    // ⭐ 천 단위 콤마 적용 대상 필드 (연식 제외)
    const numericFieldsWithComma = [
      "mileage",
      "displacement",
      "totalWeight",
      "length",
      "width",
      "height",
      "cbm",
    ];
    const isNumericField = numericFieldsWithComma.includes(field);

    // 표시용 값: 천 단위 콤마 적용
    let displayValue = value;
    if (isNumericField) {
      // 값이 있는 경우에만 처리 (빈 문자열, null, undefined 제외)
      if (value !== null && value !== undefined && value !== "") {
        try {
          // 콤마 제거 후 숫자로 변환
          const cleanValue = String(value).replace(/,/g, "").trim();
          const numValue = parseFloat(cleanValue);
          // 숫자 변환 성공 시 천 단위 콤마 적용
          if (!isNaN(numValue) && isFinite(numValue)) {
            displayValue = numValue.toLocaleString("ko-KR");
          }
        } catch (e) {
          console.warn(`[천단위콤마] 필드 ${field} 변환 실패:`, value, e);
        }
      }
    }

    // 입력 핸들러: 콤마 제거 후 저장
    const handleNumericChange = (e) => {
      const rawValue = e.target.value.replace(/,/g, "");
      handleFieldChange(field, rawValue);
    };

    return (
      <div>
        <label
          className="block text-sm font-medium mb-1"
          style={{ color: "#0A1037" }}
        >
          {label}
          {showDifference && (
            <span
              className="ml-2 px-2 py-0.5 text-xs font-bold rounded"
              style={{
                backgroundColor: "#FFF9E6",
                color: "#0A1037",
              }}
            >
              ⚠️ 차이 발견
            </span>
          )}
          {isUnrecognized && (
            <span
              className="ml-2 px-2 py-0.5 text-xs font-bold rounded"
              style={{
                backgroundColor: "#FFEDD5",
                color: "#9A3412",
              }}
            >
              미인식
            </span>
          )}
        </label>
        <div className="relative">
          <input
            type={type}
            value={displayValue}
            onChange={
              isNumericField
                ? handleNumericChange
                : (e) => handleFieldChange(field, e.target.value)
            }
            className={`w-full px-3 py-2 text-sm border rounded focus:ring-2 focus:outline-none ${
              isUnrecognized
                ? "border-orange-500 bg-orange-50 font-semibold placeholder-orange-400"
                : showDifference
                  ? "font-medium" // 차이점 표시
                  : "border-gray-300"
            }`}
            style={
              showDifference
                ? {
                    borderColor: "#EA8600",
                    backgroundColor: "#FEF7E0",
                    borderWidth: "2px",
                    color: "#202124", // 부드러운 텍스트 색상
                  }
                : {}
            }
            placeholder={isUnrecognized ? "값을 입력해주세요" : ""}
          />

          {/* Human-in-the-Loop: 1차 vs 2차 비교 */}
          {showDifference && (
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              {/* 1차 모델 */}
              {(() => {
                const pVal = diff.primary ?? diff.flash ?? "";
                return (
                  <button
                    type="button"
                    onClick={() => handleFieldChange(field, pVal)}
                    className="p-2 rounded text-left transition-all hover:shadow-md"
                    style={{
                      backgroundColor: value === pVal ? "#FFFFFF" : "#F9FAFB",
                      border: `2px solid ${value === pVal ? "#E94948" : "#E5E7EB"}`,
                      cursor: "pointer",
                    }}
                  >
                    <div
                      className="font-semibold mb-1"
                      style={{ color: "#0A1037" }}
                    >
                      1차 모델
                    </div>
                    <div
                      className="font-mono break-all"
                      style={{
                        color: value === pVal ? "#E94948" : "#6B7280",
                      }}
                    >
                      {pVal || "—"}
                    </div>
                    {value === pVal && (
                      <div
                        className="mt-1 text-xs font-bold"
                        style={{ color: "#E94948" }}
                      >
                        ✓ 현재 선택됨
                      </div>
                    )}
                  </button>
                );
              })()}

              {/* Final (연습) - Pro 우선 */}
              <button
                type="button"
                onClick={() => handleFieldChange(field, diff.final || "")}
                className="p-2 rounded text-left transition-all hover:shadow-md"
                style={{
                  backgroundColor: value === diff.final ? "#FFFFFF" : "#F9FAFB",
                  border: `2px solid ${value === diff.final ? "#1EE9B6" : "#E5E7EB"}`,
                  cursor: "pointer",
                }}
              >
                <div
                  className="font-semibold mb-1"
                  style={{ color: "#0A1037" }}
                >
                  Final (연습)
                </div>
                <div
                  className="font-mono break-all"
                  style={{
                    color: value === diff.final ? "#1EE9B6" : "#6B7280",
                  }}
                >
                  {diff.final || "—"}
                </div>
                {value === diff.final && (
                  <div
                    className="mt-1 text-xs font-bold"
                    style={{ color: "#1EE9B6" }}
                  >
                    ✓ 현재 선택됨
                  </div>
                )}
              </button>
            </div>
          )}

          {/* 미인식 안내 */}
          {isUnrecognized && (
            <div
              className="mt-2 text-xs p-2 rounded"
              style={{
                backgroundColor: "#FFF7ED",
                border: "1px solid #FDBA74",
              }}
            >
              <div className="font-semibold mb-1" style={{ color: "#9A3412" }}>
                프롬프트에 정의된 필드이지만 인식하지 못했습니다.
              </div>
              <div style={{ color: "#6B7280" }}>
                문서를 확인하고 값을 입력해주세요.
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: "대기", color: "bg-gray-100 text-gray-600" },
      processing: { text: "분류 완료", color: "bg-gray-600 text-white" },
      extracting: { text: "추출 중", color: "bg-gray-700 text-white" },
      extracted: { text: "추출 완료", color: "bg-gray-700 text-white" },
      validating: { text: "검증 중", color: "bg-gray-800 text-white" },
      completed: { text: "검증 완료", color: "bg-gray-900 text-white" },
      error: { text: "오류", color: "bg-[#FCE8E6] text-[#C5221F]" },
    };
    return badges[status] || badges.pending;
  };

  // ⭐ 실제 검증 상태 확인 (validatedPages 기반)
  const getActualValidationStatus = (file) => {
    if (!file || !file.id)
      return { text: "미검증", color: "bg-yellow-100 text-yellow-700" };

    const fileValidations = validatedPages[file.id] || {};

    if (file.pageResults && file.pageResults.length > 0) {
      // PDF인 경우: 모든 페이지가 검증되었는지 확인
      const totalPages = file.pageResults.length;
      const validatedPagesCount = Object.keys(fileValidations).filter(
        (pageNum) => fileValidations[pageNum] === true,
      ).length;

      if (validatedPagesCount === 0) {
        return { text: "미검증", color: "bg-yellow-100 text-yellow-700" };
      } else if (validatedPagesCount === totalPages) {
        return { text: "검증 완료", color: "bg-green-100 text-green-700" };
      } else {
        return {
          text: `검증 중 (${validatedPagesCount}/${totalPages})`,
          color: "bg-blue-100 text-blue-700",
        };
      }
    } else {
      // 단일 이미지인 경우
      return fileValidations[1] === true
        ? { text: "검증 완료", color: "bg-green-100 text-green-700" }
        : { text: "미검증", color: "bg-yellow-100 text-yellow-700" };
    }
  };

  // 오른쪽 패널 안전 바인딩: 선택 인덱스가 없으면 첫 번째 체크된 항목으로 바인딩
  const derivedSelectedIndex =
    selectedFileIndex !== null
      ? selectedFileIndex
      : fileList.findIndex((f) => f.checked) !== -1
        ? fileList.findIndex((f) => f.checked)
        : null;
  const selectedFile =
    derivedSelectedIndex !== null ? fileList[derivedSelectedIndex] : null;
  // ⭐ PDF 페이지별 바운딩 박스 및 PNG 이미지 처리
  const currentPageData = selectedFile?.pageResults?.find(
    (p) => p.pageNumber === selectedPageNumber,
  );

  // ⭐ PDF인 경우 현재 페이지의 데이터를 extractedData로 사용 (차이점 정보 포함)
  let extractedData = selectedFile?.extractedData;
  if (currentPageData) {
    const baseData =
      currentPageData.extractedData ||
      currentPageData.proData ||
      currentPageData.flashData ||
      {};
    extractedData = {
      ...baseData,
      differences: currentPageData.differences, // ⭐ 차이점 객체 병합
      partial: currentPageData.partial,
      documentType: currentPageData.documentType,
    };
  }
  const selectedFileWithBoundingBoxes = selectedFile
    ? {
        ...selectedFile,
        // PDF인 경우 현재 페이지의 바운딩 박스, 이미지인 경우 파일 레벨의 바운딩 박스
        boundingBoxes:
          currentPageData?.boundingBoxes || selectedFile.boundingBoxes || [],
        // PDF 페이지의 이미지 크기 정보 사용
        imageWidth: currentPageData?.imageWidth || selectedFile.imageWidth,
        imageHeight: currentPageData?.imageHeight || selectedFile.imageHeight,
        // ⭐ 상세 모드용: PDF 페이지의 PNG 이미지 (base64)
        pngImage: currentPageData?.pngImage,
      }
    : null;

  // ⚠️ 주의: 조건부 return 이후에 hooks를 사용할 수 없으므로 제거됨
  // 디버깅용 useEffect는 조건부 return 전으로 이동하거나 제거해야 함
  // extractedData와 selectedFileWithBoundingBoxes는 렌더링 중에 직접 사용 가능

  return (
    <div
      className="h-screen flex flex-row overflow-hidden"
      style={{
        backgroundColor: "var(--audit-bg)",
        // 🔤 글자 크기 — CSS zoom 으로 앱 전체 배율. 기본 보통(1.0)은 변환 없음.
        //   배율 적용 시 100vw/100vh 프레임이 넘치지 않도록 width/height 를 역보정 →
        //   렌더 결과는 항상 뷰포트와 정확히 일치(작게=0.9 / 크게=1.15).
        ...(fontScale !== 1.0
          ? {
              zoom: fontScale,
              width: `calc(100vw / ${fontScale})`,
              height: `calc(100vh / ${fontScale})`,
            }
          : {}),
      }}
    >
      {/* 업데이트 알림 */}
      <UpdateNotification />

      {/* 🔤 글자 크기 선택 시트 */}
      <FontSizeModal
        open={showFontSizeModal}
        value={fontScale}
        onSelect={applyFontScale}
        onClose={() => setShowFontSizeModal(false)}
      />

      {/* 🔍 청크 디버그 팝업 */}
      <ChunkDebugModal
        isOpen={showChunkDebugModal}
        onClose={() => setShowChunkDebugModal(false)}
        chunkDetails={chunkDebugData?.chunkDetails}
        referenceExample={chunkDebugData?.referenceExample}
        totalPages={chunkDebugData?.totalPages}
      />

      {/* 파일명-템플릿 매핑 규칙 편집 모달 */}
      {showMappingRulesEditor && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30"
          onClick={() => setShowMappingRulesEditor(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-[480px] max-h-[80vh] flex flex-col border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h3 className="text-[13px] font-medium text-gray-900">
                파일명-템플릿 매핑 규칙
              </h3>
              <button
                onClick={() => setShowMappingRulesEditor(false)}
                className="text-gray-400 hover:text-gray-600 text-sm"
              >
                ✕
              </button>
            </div>

            {/* Description */}
            <div className="px-5 py-2 text-[11px] text-gray-500 border-b border-gray-100">
              파일명 키워드로 템플릿을 자동 매칭합니다. 매칭 없으면 기본 템플릿
              적용.
            </div>

            {/* Rules List */}
            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
              {templateMappingRules.length === 0 && (
                <div className="text-center text-gray-400 text-[11px] py-6">
                  규칙이 없습니다. 아래에서 추가하세요.
                </div>
              )}
              {templateMappingRules.map((rule, rIdx) => (
                <div
                  key={rule.id || rIdx}
                  className="flex items-center gap-2 p-2 rounded border border-gray-200"
                >
                  <div className="flex-1 flex flex-col gap-1">
                    <input
                      type="text"
                      value={rule.keywords || ""}
                      onChange={(e) => {
                        const updated = [...templateMappingRules];
                        updated[rIdx] = {
                          ...updated[rIdx],
                          keywords: e.target.value,
                        };
                        saveTemplateMappingRules(updated);
                      }}
                      placeholder="키워드 (쉼표 구분)"
                      className="text-[11px] px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:border-gray-500 w-full"
                    />
                    <select
                      value={rule.templateName || ""}
                      onChange={(e) => {
                        const updated = [...templateMappingRules];
                        updated[rIdx] = {
                          ...updated[rIdx],
                          templateName: e.target.value,
                        };
                        saveTemplateMappingRules(updated);
                      }}
                      className="text-[11px] px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:border-gray-500 w-full bg-white"
                    >
                      <option value="">-- 템플릿 선택 --</option>
                      {Object.values(templates).map((tpl) => (
                        <option key={tpl.id} value={tpl.name}>
                          {tpl.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={() => {
                      const updated = templateMappingRules.filter(
                        (_, i) => i !== rIdx,
                      );
                      saveTemplateMappingRules(updated);
                    }}
                    className="text-gray-400 hover:text-gray-700 text-sm px-1"
                    title="삭제"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const updated = [
                      ...templateMappingRules,
                      {
                        id: Date.now().toString(),
                        keywords: "",
                        templateName: "",
                      },
                    ];
                    saveTemplateMappingRules(updated);
                  }}
                  className="text-[11px] px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  + 규칙 추가
                </button>
                <button
                  onClick={() => {
                    setShowMappingRulesEditor(false);
                    setShowMappingTableViewer(true);
                  }}
                  className="text-[11px] px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  데이터 변환표(DTO)
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setFileList((prev) =>
                      prev.map((f) => {
                        const matched = autoMatchTemplate(f.name);
                        return {
                          ...f,
                          matchedTemplate:
                            matched || f.matchedTemplate || "표준인보이스",
                        };
                      }),
                    );
                    setShowMappingRulesEditor(false);
                  }}
                  className="text-[11px] px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-gray-800 transition-colors"
                >
                  재적용
                </button>
                <button
                  onClick={() => setShowMappingRulesEditor(false)}
                  className="text-[11px] px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 📋 데이터 변환표(DTO) 시각화 */}
      <MappingTableViewer
        isOpen={showMappingTableViewer}
        onClose={() => setShowMappingTableViewer(false)}
      />

      {/* 🧭 사이드바 네비게이션 (전체 높이, 최좌측) — loginHS 모바일에선 하단 탭바로 대체 */}
      {isMobileShell && (
        <MobileTabBar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          menuVisibility={menuVisibility}
          isManager={isManager}
          userGroup={currentGroupCode}
          userId={currentUserId}
          onLogout={handleLogout}
          onOpenAccountSettings={() => setCurrentPage("account-settings")}
        />
      )}
      {!isMobileShell && (
        <Sidebar
          width={isSidebarOpen ? sidebarWidth : 240}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onLogout={handleLogout}
          onOpenAccountSettings={() => setCurrentPage("account-settings")}
          userGroup={currentGroupCode}
          userId={currentUserId}
          isManager={isManager}
          menuVisibility={menuVisibility}
          onOpenPromptManager={() => setShowPromptManager(true)}
          fileList={fileList}
          selectedFileIndex={selectedFileIndex}
          onFileSelect={(file, index) => {
            setSelectedFileIndex(index);
            // 단일 클릭: 이전 체크 모두 해제 → 현재 항목만 체크
            setFileList((prev) =>
              prev.map((f, idx) => ({ ...f, checked: idx === index })),
            );
            // 좌측 파일 선택 = 딥러닝(추출) 화면으로 전환.
            // AI HS분류/관세청사례/결정트리(?audit=) 등 다른 메뉴에 있으면 그 메뉴를 닫고 딥러닝으로 복귀.
            const isAuditPage = !!new URLSearchParams(
              window.location.search,
            ).get("audit");
            const deepLearningTabs = ["viewer", "grid", "mapping"];
            if (isAuditPage || !deepLearningTabs.includes(activeTab)) {
              if (isAuditPage) {
                window.history.pushState({}, "", "/");
                window.dispatchEvent(new PopStateEvent("popstate"));
              }
              setActiveTab("viewer");
            }
          }}
          onToggleFileCheck={(index) => toggleFileCheck(index)}
          onToggleAllChecks={toggleAllChecks}
          onRemoveFile={(index) => handleRemoveFile(index)}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onFileInputChange={handleMultipleFileUpload}
          onExtract={() => handleBatchFullProcess()}
          isExtracting={loading}
          onRemoveCheckedFiles={handleRemoveCheckedFiles}
          isOpen={isSidebarOpen}
          onToggle={() => setIsSidebarOpen((prev) => !prev)}
          onBatchExtract={(action) => {
            // 사이드바 ▶ 도 메인 "딥러닝" 버튼과 동일 동작(handleBatchFullProcess).
            // 기존 handleSchedulerBatchExtract 는 selectedOcrTemplate 전역 템플릿 필수라
            // 컨텍스트에 따라 동작 안 함 → 메인 풀 파이프라인으로 통일.
            if (action === "cancel") handleCancelProcessing();
            else handleBatchFullProcess();
          }}
          isBatchExtracting={loading}
          schedulerEnabled={schedulerEnabled}
          onToggleScheduler={() => setSchedulerEnabled((prev) => !prev)}
          onOpenMappingRules={() => setShowMappingRulesEditor(true)}
          legacyFiles={legacyFiles}
          legacyFilesTotal={legacyFilesTotal}
          legacyFilesLoading={legacyFilesLoading}
          onLegacyFileClick={async (lf) => {
            try {
              const res = await fetch(lf.fileUrl);
              const blob = await res.blob();
              const fileName = lf.fileName || `file_${lf.sq}.pdf`;
              const file = new window.File([blob], fileName, {
                type: blob.type,
              });
              const preview = blob.type.startsWith("image/")
                ? URL.createObjectURL(blob)
                : null;
              const newFile = {
                id: `legacy-${lf.sq}`,
                name: fileName,
                file,
                preview,
                status: "idle",
                extractedData: null,
                checked: true,
              };
              setFileList((prev) => [
                ...prev.map((f) => ({ ...f, checked: false })),
                newFile,
              ]);
              setSelectedFileIndex(fileList.length);
            } catch (e) {
              alert("파일 로드 실패: " + e.message);
            }
          }}
          onToggleLegacyCheck={async (lf) => {
            // 이력 파일 체크박스: DB 에서 로드 후 현재 목록(배치)에 합류(additive, checked).
            // 행 클릭(onLegacyFileClick)과 달리 기존 선택을 해제하지 않음 → 다중선택 가능.
            const fname = lf.fileName || `file_${lf.sq}.pdf`;
            const dup = (f) => f.name === fname || f.id === `legacy-${lf.sq}`;
            if (fileList.some(dup)) return; // 이미 로드됨
            try {
              const res = await fetch(lf.fileUrl);
              const blob = await res.blob();
              const file = new window.File([blob], fname, { type: blob.type });
              const preview = blob.type.startsWith("image/")
                ? URL.createObjectURL(blob)
                : null;
              setFileList((prev) =>
                prev.some(dup)
                  ? prev
                  : [
                      ...prev,
                      {
                        id: `legacy-${lf.sq}`,
                        name: fname,
                        file,
                        preview,
                        status: "idle",
                        extractedData: null,
                        checked: true,
                      },
                    ],
              );
            } catch (e) {
              console.error("이력 파일 로드 실패:", fname, e.message);
            }
          }}
        />
      )}

      {/* ↔ 사이드바 폭 드래그 핸들 — 좌(사이드바)/중앙 경계 세로선. 데스크톱·열림 시만.
          가운데 그립 막대(3×44)로 조절 가능함을 시각 인지(구 HeadingDetailPage 핸들 동일). */}
      {!isMobileShell && isSidebarOpen && (
        <div
          onMouseDown={handleSidebarResizeStart}
          onDoubleClick={() => setSidebarWidth(240)}
          title="드래그하여 사이드바 폭 조절 (더블클릭: 초기화)"
          onMouseEnter={(e) => {
            const bar = e.currentTarget.firstChild;
            if (bar) bar.style.background = "var(--audit-accent, #1a73e8)";
          }}
          onMouseLeave={(e) => {
            const bar = e.currentTarget.firstChild;
            if (bar)
              bar.style.background = "var(--audit-border-strong, #dadce0)";
          }}
          style={{
            position: "fixed",
            top: 0,
            bottom: 0,
            left: sidebarWidth - 5,
            width: 10,
            cursor: "col-resize",
            zIndex: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 3,
              height: 44,
              borderRadius: 2,
              background: "var(--audit-border-strong, #dadce0)",
              transition: "background 0.15s",
            }}
          />
        </div>
      )}

      {/* 오른쪽: 헤더 + 콘텐츠 — 모바일 셸은 하단 탭바 높이만큼 패딩 */}
      <div
        className="flex-1 flex flex-col min-w-0 h-full"
        style={
          isMobileShell
            ? { paddingBottom: "calc(54px + env(safe-area-inset-bottom))" }
            : undefined
        }
      >
        {/* 헤더 - 워크플로우 통합 */}
        <header
          className="px-6 py-3 flex-shrink-0"
          style={{
            backgroundColor: "var(--audit-bg)",
            borderBottom: "1px solid var(--audit-border)",
          }}
        >
          <div className="flex items-center justify-between">
            {/* 왼쪽: 사이드바 닫혔을 때(모바일 셸은 항상) 브랜드 표시 */}
            <div
              className="flex items-center min-w-0 flex-1"
              style={{ gap: 16 }}
            >
              {(!isSidebarOpen || isMobileShell) && (
                <span
                  onClick={() => {
                    // 로고 클릭 → 홈(대시보드). "/"면 _loginHsHome 발동(2026-07-14 배선).
                    window.history.pushState({}, "", "/");
                    window.dispatchEvent(new PopStateEvent("popstate"));
                  }}
                  className="text-[18px] font-bold tracking-tight flex-shrink-0 cursor-pointer"
                  style={{ color: "#141413", letterSpacing: "-0.02em" }}
                  title="홈으로"
                >
                  {IS_LOGINHS_PRODUCT ? "LoginHS" : "DocuAI"}
                </span>
              )}
              {(!isSidebarOpen || isMobileShell) && IS_LOGINHS_PRODUCT && (
                <span
                  title="현재 일부 고객사와 함께 기능을 고도화하고 있습니다."
                  style={{
                    marginLeft: -10,
                    padding: "1px 5px",
                    border: "1px solid #d6d3d1",
                    borderRadius: 999,
                    background: "#f5f5f4",
                    color: "#78716c",
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    lineHeight: 1.4,
                    flexShrink: 0,
                  }}
                >
                  BETA
                </span>
              )}
              {/* 전역 HS 검색 — 로그인 후 모든 업무 화면의 같은 위치에 유지한다.
                  홈은 동일 컴포넌트를 히어로에 제공하므로 헤더 중복만 제외한다. */}
              {typeof window !== "undefined" &&
                shouldShowHeaderSearch({
                  isLoginHS: IS_LOGINHS_PRODUCT,
                  auditRoute: _auditRoute,
                }) && (
                  <div
                    style={{
                      width: isMobileShell ? "auto" : 720,
                      maxWidth: isMobileShell ? "none" : "60vw",
                      minWidth: 0,
                      flex: isMobileShell ? "1 1 auto" : "0 1 auto",
                      display: "flex",
                    }}
                  >
                    <CenterSearchBar
                      variant="top"
                      defaultValue={
                        new URLSearchParams(window.location.search).get("sq") ||
                        ""
                      }
                      key={
                        new URLSearchParams(window.location.search).get("sq") ||
                        "browse"
                      }
                    />
                  </div>
                )}
              {/* 접힘 전용 상단 nav(AI HS분류 1개) — 검색과 공존. 사이드바 접힘·데스크톱·충분한 폭. */}
              {IS_LOGINHS_PRODUCT &&
                !isSidebarOpen &&
                !isMobileShell &&
                viewportW >= 900 && <CollapsedSidebarTopNav />}
            </div>

            {/* 가운데: 워크플로우 탭 - 🔧 임시 숨김 (hidden) */}
            <nav
              className="hidden flex items-center gap-1 absolute left-1/2 transform -translate-x-1/2"
              style={{ marginLeft: "-120px" }}
            >
              <button
                onClick={() => setActiveTab("viewer")}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === "viewer"
                    ? "bg-blue-600 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <span
                  className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                    activeTab === "viewer"
                      ? "bg-white text-blue-600"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  1
                </span>
                <span>문서인식</span>
              </button>

              <ChevronRight className="w-4 h-4 text-gray-300" />

              <button
                onClick={() => setActiveTab("grid")}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === "grid"
                    ? "bg-blue-600 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <span
                  className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                    activeTab === "grid"
                      ? "bg-white text-blue-600"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  2
                </span>
                <span>문서검증</span>
              </button>

              <ChevronRight className="w-4 h-4 text-gray-300" />

              <button
                onClick={() => setActiveTab("mapping")}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === "mapping"
                    ? "bg-blue-600 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <span
                  className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                    activeTab === "mapping"
                      ? "bg-white text-blue-600"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  3
                </span>
                <span>차량등록</span>
              </button>
            </nav>

            {/* 오른쪽: 업무 기능 아이콘 (Google 스타일) — 항상 유지(축소 금지). */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {IS_LOGINHS_PRODUCT && !isMobileShell && (
                <>
                  <button
                    onClick={() => {
                      const tok = localStorage.getItem("token");
                      const url = tok
                        ? `https://docuai2.tlogin.net/#sso=${encodeURIComponent(tok)}`
                        : "https://docuai2.tlogin.net/";
                      window.open(url, "docuai2");
                    }}
                    className="flex items-center gap-1 rounded-md px-2 py-1.5 text-[13px] font-semibold text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-950"
                    title="DocuAI 문서 AI 열기"
                  >
                    DocuAI
                    <ExternalLink size={12} strokeWidth={1.6} />
                  </button>
                  <span
                    aria-hidden="true"
                    className="mx-1.5 h-4 w-px flex-shrink-0 bg-gray-200"
                  />
                </>
              )}
              {/* '류 바로가기' 드롭다운 제거(2026-07-14) — 좌측 'HS 탐색' 네비가 동일 기능 제공(중복). */}
              {/* 🔤 글자 크기 토글 — 데스크톱+모바일 모두 노출. 작은 가 + 큰 가 (malso 방식) */}
              <button
                onClick={() => setShowFontSizeModal(true)}
                className="p-2 rounded-full transition-colors text-gray-600 hover:bg-gray-100 flex items-center"
                title="글자 크기"
                aria-label="글자 크기"
                style={{ gap: 1 }}
              >
                <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1 }}>
                  가
                </span>
                <span style={{ fontSize: 17, fontWeight: 700, lineHeight: 1 }}>
                  가
                </span>
              </button>

              {/* 레이아웃 모드 토글 (가로↔세로 단일 버튼) — 모바일 셸 미노출.
                  loginHS 미노출: 문서추출 그리드(AUIGrid) 방향 전환용이라 세번분류엔 무동작(사용자 요청). */}
              {!isMobileShell && !IS_LOGINHS_PRODUCT && (
                <div className="flex items-center gap-0.5 rounded-md p-0.5">
                  <button
                    onClick={() => {
                      const next =
                        layoutMode === "vertical" ? "horizontal" : "vertical";
                      setLayoutMode(next);
                      localStorage.setItem("layoutMode", next);
                      if (next === "horizontal") {
                        setTimeout(() => {
                          if (window.AUIGrid) {
                            const gridId = "#horizontal_grid";
                            if (window.AUIGrid.isCreated(gridId)) {
                              window.AUIGrid.resize(gridId);
                              if (window.AUIGrid.fitColumnWidthToContainer) {
                                window.AUIGrid.fitColumnWidthToContainer(
                                  gridId,
                                );
                              }
                            }
                          }
                        }, 350);
                      }
                    }}
                    className="p-2 rounded-full transition-colors text-gray-600 hover:bg-gray-100"
                    title={
                      layoutMode === "vertical"
                        ? "세로 레이아웃 — 클릭하여 가로로 전환"
                        : "가로 레이아웃 — 클릭하여 세로로 전환"
                    }
                  >
                    {layoutMode === "vertical" ? (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      >
                        <rect x="1" y="1" width="4" height="14" rx="1" />
                        <rect x="6" y="1" width="4" height="14" rx="1" />
                        <rect x="11" y="1" width="4" height="14" rx="1" />
                      </svg>
                    ) : (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      >
                        <rect x="1" y="1" width="14" height="6" rx="1" />
                        <rect x="1" y="9" width="14" height="6" rx="1" />
                      </svg>
                    )}
                  </button>
                </div>
              )}

              {/* AI 어시스턴트 — 모바일 셸에서도 유지 (로고 좌 + AI 우) */}
              <button
                onClick={() => {
                  setShowAIPanel((prev) => !prev);
                  window.dispatchEvent(new CustomEvent("toggleAIPanel"));
                }}
                className={`p-2 rounded-full transition-colors ${
                  showAIPanel
                    ? "bg-gray-200 text-gray-800"
                    : "text-gray-500 hover:bg-gray-100"
                }`}
                title={
                  showAIPanel ? "AI 어시스턴트 닫기" : "AI 어시스턴트 열기"
                }
              >
                {/* Custom AI Context Icon (Robot Head) */}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="11" width="18" height="10" rx="2" />
                  <circle cx="12" cy="5" r="2" />
                  <path d="M12 7v4" />
                  <line x1="8" y1="16" x2="8" y2="16" />
                  <line x1="16" y1="16" x2="16" y2="16" />
                </svg>
              </button>

              {/* loginHS: 문의·건의 — 상단 우측으로 이동(사용자 요청 2026-07-14, 구 사이드바 하단). */}
              {IS_LOGINHS_PRODUCT && (
                <button
                  onClick={() => {
                    window.history.pushState({}, "", "/?audit=inquiry");
                    window.dispatchEvent(new PopStateEvent("popstate"));
                  }}
                  className="p-2 rounded-full text-gray-500 hover:bg-gray-100 transition-colors"
                  title="문의·건의"
                  aria-label="문의·건의"
                >
                  <MessageSquare size={20} />
                </button>
              )}

              {/* 템플릿·설정·도움말 — 문서추출용이라 모바일 셸 미노출 */}
              {!isMobileShell && (
                <>
                  {/* 템플릿 관리 — 문서추출용. loginHS 미노출(사용자 요청). */}
                  {!IS_LOGINHS_PRODUCT && (
                    <button
                      onClick={() => setShowTemplateManagement((prev) => !prev)}
                      className={`p-2 rounded-full transition-colors ${showTemplateManagement ? "bg-blue-100 text-blue-600" : "text-gray-500 hover:bg-gray-100"}`}
                      title={
                        showTemplateManagement
                          ? "템플릿 관리 닫기"
                          : "템플릿 관리"
                      }
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  )}

                  {/* 설정 — 개발용(OCR/vLLM/증류 파이프라인). 관리자(MANAGER_YN='Y')에게만 노출, loginHS 완전 숨김 */}
                  {!IS_LOGINHS_PRODUCT && isManager && (
                    <button
                      onClick={() => {
                        setSettingsModalTab("ocr");
                        setShowSettings(true);
                      }}
                      className="p-2 rounded-full text-gray-500 hover:bg-gray-100 transition-colors"
                      title="설정"
                    >
                      <Settings size={20} />
                    </button>
                  )}

                  {/* 도움말 — loginHS 는 사용 가이드(?page=guide)로, DocuAI 는 그리드 단축키 안내 */}
                  <button
                    onClick={() => {
                      if (IS_LOGINHS_PRODUCT) {
                        window.location.href = "/?page=guide";
                        return;
                      }
                      alert(
                        "📌 단축키 안내\n\nF2: 셀 편집\nCtrl + +: 행/열 추가\nCtrl + -: 행/열 삭제\nCtrl + Z: 되돌리기\nCtrl + Space: 열 선택\nShift + Space: 행 선택\nDelete: 셀 내용 삭제",
                      );
                    }}
                    className="p-2 rounded-full text-gray-500 hover:bg-gray-100 transition-colors"
                    title={IS_LOGINHS_PRODUCT ? "사용 가이드" : "도움말"}
                  >
                    <HelpCircle size={20} />
                  </button>
                </>
              )}
            </div>
          </div>
        </header>

        {/* 설정 모달 - 탭 기반 */}
        {showSettings && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
            onClick={() => setShowSettings(false)}
          >
            <div
              className="bg-white rounded-xl w-full flex flex-col shadow-2xl"
              style={{ maxWidth: "900px", height: "85vh" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* 헤더 */}
              <div className="flex items-center justify-between px-6 py-4 flex-shrink-0 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <Settings className="w-5 h-5 text-gray-700" />
                  <h2
                    className="text-lg font-semibold"
                    style={{ color: "#0A1037" }}
                  >
                    설정
                  </h2>
                </div>
                <button
                  onClick={() => setShowSettings(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* 탭 + 내용 */}
              <div className="flex-1 flex overflow-hidden">
                {/* 왼쪽 탭 네비게이션 */}
                <div className="w-44 bg-gray-50 border-r border-gray-200 p-4 flex-shrink-0">
                  <nav className="space-y-1">
                    {[
                      { id: "ocr", label: "OCR 설정", Icon: Scan },
                      {
                        id: "template",
                        label: "템플릿추출",
                        Icon: LayoutTemplate,
                      },
                      { id: "model", label: "기본/검증", Icon: Shield },
                      { id: "ai", label: "AI 어시스턴트", Icon: Sparkles },
                      { id: "db", label: "DB 설정", Icon: Database },
                      { id: "vllm", label: "vLLM 서버", Icon: Server },
                      {
                        id: "harness",
                        label: "증류 파이프라인",
                        Icon: FlaskConical,
                      },
                    ].map(({ id, label, Icon }) => (
                      <button
                        key={id}
                        onClick={() => setSettingsModalTab(id)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${settingsModalTab === id ? "bg-blue-100 text-blue-700" : "text-gray-600 hover:bg-gray-100"}`}
                      >
                        <Icon
                          size={18}
                          className={
                            settingsModalTab === id
                              ? "text-blue-600"
                              : "text-gray-500"
                          }
                        />
                        {label}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* 오른쪽 탭 내용 */}
                <div className="flex-1 overflow-hidden flex flex-col">
                  {/* OCR 설정 탭 */}
                  {settingsModalTab === "ocr" && (
                    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                      {/* ⭐ Hybrid OCR 설정 (사용자 요청) */}
                      <div>
                        <label
                          className="block text-sm font-semibold mb-2"
                          style={{ color: "#0A1037" }}
                        >
                          OCR 설정
                        </label>
                        <div className="space-y-2 mb-4">
                          <label
                            className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                            style={{
                              borderColor: enableHybridOCR
                                ? "#339AF0"
                                : "#E0E0E0",
                              backgroundColor: enableHybridOCR
                                ? "#E7F5FF"
                                : "white",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={enableHybridOCR}
                              onChange={(e) => {
                                setEnableHybridOCR(e.target.checked);
                                localStorage.setItem(
                                  "enableHybridOCR",
                                  e.target.checked,
                                );
                              }}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="flex-1">
                              <div
                                className="font-medium text-sm"
                                style={{ color: "#0A1037" }}
                              >
                                Hybrid Mode (PaddleOCR + LLM)
                              </div>
                              <div className="text-xs text-gray-500">
                                활성화 시 바운딩 박스 시각화 및 향상된 좌표
                                추출을 지원합니다.
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>

                      {/* 문서 분류 활성화 체크박스 */}
                      <div>
                        <label
                          className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors mb-3"
                          style={{
                            borderColor: enableClassification
                              ? "#339AF0"
                              : "#E0E0E0",
                            backgroundColor: enableClassification
                              ? "#E7F5FF"
                              : "white",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={enableClassification}
                            onChange={(e) => {
                              setEnableClassification(e.target.checked);
                              localStorage.setItem(
                                "enableClassification",
                                e.target.checked,
                              );
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <div
                              className="font-medium text-sm"
                              style={{ color: "#0A1037" }}
                            >
                              문서 분류 사용
                            </div>
                            <div className="text-xs text-gray-500">
                              비활성화 시 템플릿이 선택되어 있으면 분류를
                              건너뛰고 바로 추출합니다. (속도 향상)
                            </div>
                          </div>
                        </label>
                      </div>

                      {/* OCR 엔진 선택 → 추출 모델 선택으로 변경 */}
                      <div
                        style={{
                          opacity: enableClassification ? 1 : 0.5,
                          pointerEvents: enableClassification ? "auto" : "none",
                        }}
                      >
                        <label
                          className="block text-sm font-semibold mb-2"
                          style={{ color: "#0A1037" }}
                        >
                          문서 분류 모델{" "}
                          {!enableClassification && (
                            <span className="text-xs text-gray-400">
                              (비활성화됨)
                            </span>
                          )}
                        </label>
                        <div className="space-y-2">
                          <label
                            className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                            style={{
                              borderColor:
                                classificationFlashModel === "2.0"
                                  ? "#339AF0"
                                  : "#E0E0E0",
                              backgroundColor:
                                classificationFlashModel === "2.0"
                                  ? "#E7F5FF"
                                  : "white",
                            }}
                          >
                            <input
                              type="radio"
                              name="classificationFlashModel"
                              checked={classificationFlashModel === "2.0"}
                              onChange={() =>
                                setClassificationFlashModel("2.0")
                              }
                              className="w-4 h-4"
                            />
                            <div
                              className="font-medium text-sm"
                              style={{ color: "#0A1037" }}
                            >
                              Gemini 2.0 Flash
                            </div>
                          </label>

                          <label
                            className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                            style={{
                              borderColor:
                                classificationFlashModel === "2.5"
                                  ? "#339AF0"
                                  : "#E0E0E0",
                              backgroundColor:
                                classificationFlashModel === "2.5"
                                  ? "#E7F5FF"
                                  : "white",
                            }}
                          >
                            <input
                              type="radio"
                              name="classificationFlashModel"
                              checked={classificationFlashModel === "2.5"}
                              onChange={() =>
                                setClassificationFlashModel("2.5")
                              }
                              className="w-4 h-4"
                            />
                            <div
                              className="font-medium text-sm"
                              style={{ color: "#0A1037" }}
                            >
                              Gemini 2.5 Flash
                            </div>
                          </label>

                          <label
                            className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                            style={{
                              borderColor:
                                classificationFlashModel === "upstage"
                                  ? "#339AF0"
                                  : "#E0E0E0",
                              backgroundColor:
                                classificationFlashModel === "upstage"
                                  ? "#E7F5FF"
                                  : "white",
                            }}
                          >
                            <input
                              type="radio"
                              name="classificationFlashModel"
                              checked={classificationFlashModel === "upstage"}
                              onChange={() =>
                                setClassificationFlashModel("upstage")
                              }
                              className="w-4 h-4"
                            />
                            <div
                              className="font-medium text-sm"
                              style={{ color: "#0A1037" }}
                            >
                              Upstage Document AI
                            </div>
                          </label>
                        </div>
                      </div>

                      {/* 설정 저장 버튼 */}
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => {
                            try {
                              localStorage.setItem(
                                "classificationFlashModel",
                                classificationFlashModel,
                              );
                              localStorage.setItem(
                                "selectedValidationModels",
                                JSON.stringify(selectedValidationModels),
                              );
                              localStorage.setItem("ocrEngine", ocrEngine);
                              localStorage.setItem(
                                "thinkingBudget",
                                thinkingBudget.toString(),
                              );
                              localStorage.setItem(
                                "thinkingLevel",
                                thinkingLevel,
                              );
                              if (ocrEngine === "deepseek") {
                                localStorage.setItem(
                                  "deepseekMode",
                                  deepseekMode,
                                );
                                localStorage.setItem(
                                  "deepseekServerUrl",
                                  deepseekServerUrl,
                                );
                                localStorage.setItem(
                                  "grounding",
                                  grounding.toString(),
                                );
                              }
                              savePreferencesToServer({
                                selectedValidationModels,
                                classificationFlashModel,
                                templateExtractionModel,
                                ocrEngine,
                                thinkingBudget,
                                thinkingLevel,
                                verifyEnabled,
                              });
                            } catch (error) {
                              console.error("설정 저장 실패:", error);
                            }
                            setShowSettings(false);
                            const classText =
                              classificationFlashModel === "2.0"
                                ? "Gemini 2.0 Flash"
                                : "Gemini 2.5 Flash";
                            addLog(
                              `✅ OCR 설정 저장: [분류: ${classText}]`,
                              "success",
                            );
                          }}
                          className="w-full px-4 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                          style={{
                            backgroundColor: "#FFDD33",
                            color: "#0A1037",
                          }}
                        >
                          설정 저장
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 템플릿추출 탭 */}
                  {settingsModalTab === "template" && (
                    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                      <div>
                        <label
                          className="block text-sm font-semibold mb-3"
                          style={{ color: "#0A1037" }}
                        >
                          템플릿 추출 모델
                        </label>
                        <div className="space-y-2">
                          {[
                            {
                              id: GEMINI_FLASH_MODEL,
                              name: "Gemini 3.7 Flash",
                            },
                          ].map((m) => (
                            <label
                              key={m.id}
                              className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                              style={{
                                borderColor:
                                  templateExtractionModel === m.id
                                    ? "#339AF0"
                                    : "#E0E0E0",
                                backgroundColor:
                                  templateExtractionModel === m.id
                                    ? "#E7F5FF"
                                    : "white",
                              }}
                            >
                              <input
                                type="radio"
                                name="templateExtractionModel"
                                checked={templateExtractionModel === m.id}
                                onChange={() => {
                                  setTemplateExtractionModel(m.id);
                                  localStorage.setItem(
                                    "templateExtractionModel",
                                    m.id,
                                  );
                                }}
                                className="w-4 h-4"
                              />
                              <span
                                className="font-medium text-sm"
                                style={{ color: "#0A1037" }}
                              >
                                {m.name}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => {
                            try {
                              savePreferencesToServer({
                                selectedValidationModels,
                                classificationFlashModel,
                                templateExtractionModel,
                                ocrEngine,
                                thinkingBudget,
                                thinkingLevel,
                                verifyEnabled,
                              });
                            } catch (error) {
                              console.error("설정 저장 실패:", error);
                            }
                            setShowSettings(false);
                            addLog(
                              `✅ 템플릿 추출 모델: ${templateExtractionModel}`,
                              "success",
                            );
                          }}
                          className="w-full px-4 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                          style={{
                            backgroundColor: "#FFDD33",
                            color: "#0A1037",
                          }}
                        >
                          설정 저장
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 기본/검증 모델 탭 — 좌우 분할 */}
                  {settingsModalTab === "model" && (
                    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                      <div className="flex gap-4">
                        {/* 왼쪽: 기본 모델 (1차) */}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-blue-700 mb-2 flex items-center gap-1">
                            📌 기본 모델 (1차)
                          </div>
                          <div className="space-y-1">
                            {[
                              {
                                id: GEMINI_FLASH_MODEL,
                                name: "Gemini 3.7 Flash",
                              },
                              {
                                id: "docuai-ft-invoice",
                                name: "DocuAI FT Invoice 🧠",
                              },
                              { id: "deepseek", name: "DeepSeek-OCR" },
                              { id: "paddleocr-label", name: "PaddleOCR 🏷️" },
                              {
                                id: "qwen3-vl-32b",
                                name: "Qwen3-VL-32B (OpenRouter) 🌐",
                              },
                              {
                                id: "qwen36-kyungin",
                                name: "Qwen3.6-27B 파인튜닝 (자동) 🎯",
                              },
                              {
                                id: "qwen36-damin-import",
                                name: "Qwen3.6-27B 수입 인보이스 v1 (1K) 🎯",
                              },
                              {
                                id: "qwen36-damin-import-v2",
                                name: "Qwen3.6-27B 수입 인보이스 v2 (24K, items 압축버그) 🎯",
                              },
                              {
                                id: "qwen36-damin-import-v3",
                                name: "Qwen3.6-27B 수입 인보이스 v3 (GT 정상화) ⭐ 🎯",
                              },
                              { id: "minimax-m2.5", name: "MiniMax M2.5 🟣" },
                              { id: "extend-ai", name: "Extend.ai 🔗" },
                            ].map((m) => (
                              <label
                                key={m.id}
                                className="flex items-center gap-2 p-2 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                                style={{
                                  borderColor:
                                    selectedValidationModels[0] === m.id
                                      ? m.id === "extend-ai"
                                        ? "#7C3AED"
                                        : "#339AF0"
                                      : "#E0E0E0",
                                  backgroundColor:
                                    selectedValidationModels[0] === m.id
                                      ? m.id === "extend-ai"
                                        ? "#F5F3FF"
                                        : "#E7F5FF"
                                      : "white",
                                }}
                              >
                                <input
                                  type="radio"
                                  name="primaryModel"
                                  checked={selectedValidationModels[0] === m.id}
                                  onChange={() => {
                                    setSelectedValidationModels((prev) => {
                                      const next = [...prev];
                                      next[0] = m.id;
                                      localStorage.setItem(
                                        "selectedValidationModels",
                                        JSON.stringify(next),
                                      );
                                      return next;
                                    });
                                  }}
                                  className="w-3.5 h-3.5 accent-blue-600"
                                />
                                <span
                                  className="font-medium text-sm"
                                  style={{ color: "#0A1037" }}
                                >
                                  {m.name}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* 오른쪽: 검증 모델 (2차) */}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                            🔍 검증 모델 (2차)
                          </div>
                          <div className="space-y-1">
                            {[
                              {
                                id: GEMINI_FLASH_MODEL,
                                name: "Gemini 3.7 Flash",
                              },
                              { id: "gpt-4.1-mini", name: "GPT-4.1 mini 🤖" },
                              {
                                id: "docuai-ft-invoice",
                                name: "DocuAI FT Invoice 🧠",
                              },
                              { id: "deepseek", name: "DeepSeek-OCR" },
                              {
                                id: "qwen3-vl-32b",
                                name: "Qwen3-VL-32B (OpenRouter) 🌐",
                              },
                              {
                                id: "qwen36-kyungin",
                                name: "Qwen3.6-27B 파인튜닝 (자동) 🎯",
                              },
                              {
                                id: "qwen36-damin-import",
                                name: "Qwen3.6-27B 수입 인보이스 v1 (1K) 🎯",
                              },
                              {
                                id: "qwen36-damin-import-v2",
                                name: "Qwen3.6-27B 수입 인보이스 v2 (24K, items 압축버그) 🎯",
                              },
                              {
                                id: "qwen36-damin-import-v3",
                                name: "Qwen3.6-27B 수입 인보이스 v3 (GT 정상화) ⭐ 🎯",
                              },
                              { id: "minimax-m2.5", name: "MiniMax M2.5 🟣" },
                              { id: "extend-ai", name: "Extend.ai 🔗" },
                            ].map((m) => (
                              <label
                                key={m.id}
                                className="flex items-center gap-2 p-2 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                                style={{
                                  borderColor:
                                    selectedValidationModels[1] === m.id
                                      ? m.id === "extend-ai"
                                        ? "#7C3AED"
                                        : "#16A34A"
                                      : "#E0E0E0",
                                  backgroundColor:
                                    selectedValidationModels[1] === m.id
                                      ? m.id === "extend-ai"
                                        ? "#F5F3FF"
                                        : "#F0FDF4"
                                      : "white",
                                }}
                              >
                                <input
                                  type="radio"
                                  name="verificationModel"
                                  checked={selectedValidationModels[1] === m.id}
                                  onChange={() => {
                                    setSelectedValidationModels((prev) => {
                                      const next = [...prev];
                                      next[1] = m.id;
                                      localStorage.setItem(
                                        "selectedValidationModels",
                                        JSON.stringify(next),
                                      );
                                      return next;
                                    });
                                  }}
                                  className="w-3.5 h-3.5 accent-green-600"
                                />
                                <span
                                  className="font-medium text-sm"
                                  style={{ color: "#0A1037" }}
                                >
                                  {m.name}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* 검증 방식 선택 */}
                      <div>
                        <div className="text-xs font-semibold text-green-700 mb-1.5 flex items-center gap-1">
                          🔍 검증 방식{" "}
                          <span className="font-normal text-gray-400">
                            — 검증 모드 ON 시 적용
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            {
                              id: "parallel",
                              name: "⚡ 빠른 비교",
                              desc: "1차/2차 동시 추출 후 비교",
                            },
                            {
                              id: "sequential",
                              name: "🔍 정밀 검증",
                              desc: "1차 추출 → 2차 리뷰 (정확도↑)",
                            },
                          ].map((mode) => (
                            <button
                              key={mode.id}
                              onClick={() => {
                                setVerificationMode(mode.id);
                                localStorage.setItem(
                                  "verificationMode",
                                  mode.id,
                                );
                              }}
                              className={`p-2 rounded-lg border text-left transition-colors ${verificationMode === mode.id ? "border-green-500 bg-green-50" : "border-gray-200 bg-white hover:bg-gray-50"}`}
                            >
                              <div
                                className="font-medium text-sm"
                                style={{ color: "#0A1037" }}
                              >
                                {mode.name}
                              </div>
                              <div className="text-xs text-gray-400 mt-0.5">
                                {mode.desc}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Extend.ai Light Mode (Extend.ai 선택 시) */}
                      {selectedValidationModels.includes("extend-ai") && (
                        <div
                          className="p-3 rounded-lg border"
                          style={{
                            borderColor: "#7C3AED33",
                            backgroundColor: "#F5F3FF",
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div
                                className="text-sm font-semibold"
                                style={{ color: "#5B21B6" }}
                              >
                                ⚡ Extend.ai Light Mode
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                extraction_light — 속도 우선 (정확도↓ 속도↑)
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                const next = !extendLightMode;
                                setExtendLightMode(next);
                                localStorage.setItem(
                                  "extendLightMode",
                                  next.toString(),
                                );
                              }}
                              className={`relative w-10 h-5 rounded-full transition-colors ${extendLightMode ? "bg-purple-500" : "bg-gray-300"}`}
                            >
                              <span
                                className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${extendLightMode ? "translate-x-5" : ""}`}
                              />
                            </button>
                          </div>
                        </div>
                      )}

                      {/* ThinkingConfig (Gemini 선택 시) */}
                      {selectedValidationModels.some((m) =>
                        m.includes("gemini-3"),
                      ) && (
                        <div
                          className="p-3 rounded-lg border"
                          style={{
                            borderColor: "#E0E0E0",
                            backgroundColor: "#F9FAFB",
                          }}
                        >
                          <div
                            className="text-sm font-semibold mb-2"
                            style={{ color: "#0A1037" }}
                          >
                            🧠 사고 수준 설정
                          </div>
                          {selectedValidationModels.includes(
                            GEMINI_FLASH_MODEL,
                          ) && (
                            <div className="mb-2">
                              <label
                                className="block text-xs font-medium mb-1"
                                style={{ color: "#0A1037" }}
                              >
                                Gemini 3.0 Pro - Thinking Level
                              </label>
                              <div className="grid grid-cols-2 gap-2">
                                <button
                                  onClick={() => setThinkingLevel("HIGH")}
                                  className={`px-2 py-1.5 rounded border text-xs ${thinkingLevel === "HIGH" ? "bg-purple-50 border-purple-300 font-semibold" : "border-gray-200"}`}
                                >
                                  🧠 HIGH
                                </button>
                                <button
                                  onClick={() => setThinkingLevel("LOW")}
                                  className={`px-2 py-1.5 rounded border text-xs ${thinkingLevel === "LOW" ? "bg-green-50 border-green-300 font-semibold" : "border-gray-200"}`}
                                >
                                  ⚡ LOW
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* DeepSeek 설정 */}
                      {selectedValidationModels.includes("deepseek") && (
                        <div
                          className="p-3 rounded-lg border"
                          style={{
                            borderColor: "#E0E0E0",
                            backgroundColor: "#F9FAFB",
                          }}
                        >
                          <div
                            className="text-sm font-semibold mb-2"
                            style={{ color: "#0A1037" }}
                          >
                            DeepSeek 설정
                          </div>
                          <div className="mb-3">
                            <label
                              className="block text-xs font-medium mb-1"
                              style={{ color: "#0A1037" }}
                            >
                              서버 선택
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                onClick={() =>
                                  setDeepseekServerUrl("http://localhost:8001")
                                }
                                className={`px-2 py-1.5 rounded border text-xs ${deepseekServerUrl.includes("localhost:8001") ? "bg-blue-50 border-blue-300 font-semibold" : "border-gray-200"}`}
                              >
                                🖥️ 로컬
                              </button>
                              <button
                                onClick={() =>
                                  setDeepseekServerUrl(
                                    "http://35.186.162.229:8003",
                                  )
                                }
                                className={`px-2 py-1.5 rounded border text-xs ${deepseekServerUrl.includes("8003") ? "bg-green-50 border-green-300 font-semibold" : "border-gray-200"}`}
                              >
                                VM
                              </button>
                            </div>
                          </div>
                          <div className="mb-3">
                            <label
                              className="block text-xs font-medium mb-1"
                              style={{ color: "#0A1037" }}
                            >
                              속도 모드
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                              <button
                                onClick={() => setDeepseekMode("default")}
                                className={`px-2 py-1.5 rounded border text-xs ${deepseekMode === "default" ? "bg-blue-50 border-blue-300" : "border-gray-200"}`}
                              >
                                기본
                              </button>
                              <button
                                onClick={() => setDeepseekMode("optimized")}
                                className={`px-2 py-1.5 rounded border text-xs ${deepseekMode === "optimized" ? "bg-blue-50 border-blue-300" : "border-gray-200"}`}
                              >
                                최적화
                              </button>
                              <button
                                onClick={() => setDeepseekMode("fast")}
                                className={`px-2 py-1.5 rounded border text-xs ${deepseekMode === "fast" ? "bg-blue-50 border-blue-300" : "border-gray-200"}`}
                              >
                                빠른
                              </button>
                            </div>
                          </div>
                          <div
                            className="p-2 rounded-lg border"
                            style={{
                              borderColor: "#E0E0E0",
                              backgroundColor: grounding
                                ? "#E7F5FF"
                                : "#FFF3E0",
                            }}
                          >
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={grounding}
                                onChange={(e) => setGrounding(e.target.checked)}
                                className="w-4 h-4 cursor-pointer"
                              />
                              <div className="flex-1">
                                <div
                                  className="font-medium text-xs"
                                  style={{ color: "#0A1037" }}
                                >
                                  바운딩 박스 (그라운딩)
                                </div>
                                <div className="text-[10px] text-gray-600 mt-0.5">
                                  {grounding
                                    ? "ON: 위치 정보 포함"
                                    : "OFF: 텍스트만"}
                                </div>
                              </div>
                            </label>
                          </div>
                        </div>
                      )}

                      {/* 선택된 모델 표시 */}
                      {selectedValidationModels.length > 0 && (
                        <div className="p-2 rounded-lg bg-blue-50 border border-blue-200">
                          <div className="text-xs font-medium text-blue-700 mb-1">
                            선택된 모델 ({selectedValidationModels.length}개):
                          </div>
                          <div className="text-xs text-blue-600">
                            {selectedValidationModels
                              .map((m) =>
                                m === "deepseek"
                                  ? "DeepSeek-OCR"
                                  : getModelDisplayName(m),
                              )
                              .join(" + ")}
                          </div>
                        </div>
                      )}

                      {/* 모델명 매핑 (간단 참조) */}
                      <div className="pt-3 border-t border-gray-100">
                        <div className="text-[10px] font-medium text-gray-400 mb-1">
                          모델 약어
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[10px] text-gray-500">
                          {Object.entries(getAllModelNicknames())
                            .filter(
                              ([name]) =>
                                name.includes("gemini-3") ||
                                name === "deepseek" ||
                                name === "docuai-ft-invoice" ||
                                name.includes("qwen") ||
                                name.includes("minimax"),
                            )
                            .map(([name, nick]) => (
                              <span key={name}>
                                <span className="font-mono">{nick}</span>=
                                {name.replace("-preview", "")}
                              </span>
                            ))}
                        </div>
                      </div>

                      {/* 설정 저장 버튼 */}
                      <div className="pt-4 border-t border-gray-200">
                        <button
                          onClick={() => {
                            try {
                              localStorage.setItem(
                                "classificationFlashModel",
                                classificationFlashModel,
                              );
                              localStorage.setItem(
                                "selectedValidationModels",
                                JSON.stringify(selectedValidationModels),
                              );
                              localStorage.setItem("ocrEngine", ocrEngine);
                              localStorage.setItem(
                                "thinkingBudget",
                                thinkingBudget.toString(),
                              );
                              localStorage.setItem(
                                "thinkingLevel",
                                thinkingLevel,
                              );
                              if (ocrEngine === "deepseek") {
                                localStorage.setItem(
                                  "deepseekMode",
                                  deepseekMode,
                                );
                                localStorage.setItem(
                                  "deepseekServerUrl",
                                  deepseekServerUrl,
                                );
                                localStorage.setItem(
                                  "grounding",
                                  grounding.toString(),
                                );
                              }
                              savePreferencesToServer({
                                selectedValidationModels,
                                classificationFlashModel,
                                templateExtractionModel,
                                ocrEngine,
                                thinkingBudget,
                                thinkingLevel,
                                verifyEnabled,
                              });
                            } catch (error) {
                              console.error("설정 저장 실패:", error);
                            }
                            setShowSettings(false);
                            const extractionText =
                              selectedValidationModels.length === 1
                                ? `${selectedValidationModels.map((m) => (m === "deepseek" ? "DeepSeek-OCR" : getModelDisplayName(m)))[0]} (단일)`
                                : `${selectedValidationModels.map((m) => (m === "deepseek" ? "DeepSeek-OCR" : getModelDisplayName(m))).join(" + ")} (병렬 추출 + 검증)`;
                            addLog(
                              `✅ 모델 설정 저장: [추출: ${extractionText}]`,
                              "success",
                            );
                          }}
                          className="w-full px-4 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                          style={{
                            backgroundColor: "#FFDD33",
                            color: "#0A1037",
                          }}
                        >
                          설정 저장
                        </button>
                      </div>
                    </div>
                  )}

                  {/* AI 어시스턴트 탭 */}
                  {settingsModalTab === "ai" && (
                    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
                      {/* 모델 선택 */}
                      <div>
                        <label
                          className="block text-sm font-semibold mb-2"
                          style={{ color: "#0A1037" }}
                        >
                          AI 어시스턴트 모델
                        </label>
                        <div className="space-y-1">
                          {[
                            { id: "gpt-oss-120b", name: "GPT-OSS 120B ⚡" },
                            {
                              id: GEMINI_FLASH_MODEL,
                              name: "Gemini 3.7 Flash",
                            },
                            { id: "qwen3-vl-32b", name: "Qwen3-VL-32B 🌐" },
                            { id: "minimax-m2.5", name: "MiniMax M2.5 🟣" },
                          ].map((m) => (
                            <label
                              key={m.id}
                              className="flex items-center gap-2 p-2 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                              style={{
                                borderColor:
                                  aiAssistantModel === m.id
                                    ? "#339AF0"
                                    : "#E0E0E0",
                                backgroundColor:
                                  aiAssistantModel === m.id
                                    ? "#E7F5FF"
                                    : "white",
                              }}
                            >
                              <input
                                type="radio"
                                name="aiModel"
                                checked={aiAssistantModel === m.id}
                                onChange={() => {
                                  setAiAssistantModel(m.id);
                                  localStorage.setItem(
                                    "aiAssistantModel",
                                    m.id,
                                  );
                                  fetch(`${API_BASE_URL}/api/ai/set-model`, {
                                    method: "POST",
                                    headers: {
                                      "Content-Type": "application/json",
                                    },
                                    body: JSON.stringify({ model: m.id }),
                                  }).catch(() => {});
                                }}
                                className="w-3.5 h-3.5 accent-blue-600"
                              />
                              <span
                                className="font-medium text-sm"
                                style={{ color: "#0A1037" }}
                              >
                                {m.name}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* 편집 키워드 관리 */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label
                            className="block text-sm font-semibold"
                            style={{ color: "#0A1037" }}
                          >
                            편집 키워드
                          </label>
                          <button
                            onClick={() => {
                              const name = prompt("새 카테고리 이름:");
                              if (name?.trim()) {
                                const updated = [
                                  ...aiQuickCommands,
                                  { category: name.trim(), items: [] },
                                ];
                                setAiQuickCommands(updated);
                                localStorage.setItem(
                                  "aiQuickCommands",
                                  JSON.stringify(updated),
                                );
                              }
                            }}
                            className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                          >
                            + 카테고리 추가
                          </button>
                        </div>
                        <div className="space-y-3">
                          {aiQuickCommands.map((cat, catIdx) => (
                            <div
                              key={catIdx}
                              className="p-3 rounded-lg border border-gray-200 bg-gray-50"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <input
                                  className="text-xs font-semibold bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-400 focus:outline-none px-0 py-0.5"
                                  style={{ color: "#0A1037" }}
                                  value={cat.category}
                                  onChange={(e) => {
                                    const updated = [...aiQuickCommands];
                                    updated[catIdx] = {
                                      ...updated[catIdx],
                                      category: e.target.value,
                                    };
                                    setAiQuickCommands(updated);
                                    localStorage.setItem(
                                      "aiQuickCommands",
                                      JSON.stringify(updated),
                                    );
                                  }}
                                />
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => {
                                      const item = prompt("추가할 키워드:");
                                      if (item?.trim()) {
                                        const updated = [...aiQuickCommands];
                                        updated[catIdx] = {
                                          ...updated[catIdx],
                                          items: [
                                            ...updated[catIdx].items,
                                            item.trim(),
                                          ],
                                        };
                                        setAiQuickCommands(updated);
                                        localStorage.setItem(
                                          "aiQuickCommands",
                                          JSON.stringify(updated),
                                        );
                                      }
                                    }}
                                    className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-600 hover:bg-green-100"
                                  >
                                    +항목
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (
                                        window.confirm(
                                          `"${cat.category}" 카테고리를 삭제하시겠습니까?`,
                                        )
                                      ) {
                                        const updated = aiQuickCommands.filter(
                                          (_, i) => i !== catIdx,
                                        );
                                        setAiQuickCommands(updated);
                                        localStorage.setItem(
                                          "aiQuickCommands",
                                          JSON.stringify(updated),
                                        );
                                      }
                                    }}
                                    className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-500 hover:bg-red-100"
                                  >
                                    삭제
                                  </button>
                                </div>
                              </div>
                              <div className="space-y-1">
                                {cat.items.map((item, itemIdx) => (
                                  <div
                                    key={itemIdx}
                                    className="flex items-center gap-1.5 group"
                                  >
                                    <span className="text-gray-400 text-[10px]">
                                      •
                                    </span>
                                    <input
                                      className="flex-1 text-xs text-gray-700 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-400 focus:outline-none px-0 py-0.5"
                                      value={item}
                                      onChange={(e) => {
                                        const updated = [...aiQuickCommands];
                                        const newItems = [
                                          ...updated[catIdx].items,
                                        ];
                                        newItems[itemIdx] = e.target.value;
                                        updated[catIdx] = {
                                          ...updated[catIdx],
                                          items: newItems,
                                        };
                                        setAiQuickCommands(updated);
                                        localStorage.setItem(
                                          "aiQuickCommands",
                                          JSON.stringify(updated),
                                        );
                                      }}
                                    />
                                    <button
                                      onClick={() => {
                                        const updated = [...aiQuickCommands];
                                        updated[catIdx] = {
                                          ...updated[catIdx],
                                          items: updated[catIdx].items.filter(
                                            (_, i) => i !== itemIdx,
                                          ),
                                        };
                                        setAiQuickCommands(updated);
                                        localStorage.setItem(
                                          "aiQuickCommands",
                                          JSON.stringify(updated),
                                        );
                                      }}
                                      className="text-[10px] text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={() => {
                            if (
                              window.confirm(
                                "기본 키워드로 초기화하시겠습니까?",
                              )
                            ) {
                              setAiQuickCommands(DEFAULT_AI_QUICK_COMMANDS);
                              localStorage.setItem(
                                "aiQuickCommands",
                                JSON.stringify(DEFAULT_AI_QUICK_COMMANDS),
                              );
                            }
                          }}
                          className="mt-2 text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          기본값으로 초기화
                        </button>
                      </div>

                      {/* 저장 버튼 */}
                      <div className="pt-4 border-t border-gray-200">
                        <button
                          onClick={async () => {
                            localStorage.setItem(
                              "aiAssistantModel",
                              aiAssistantModel,
                            );
                            localStorage.setItem(
                              "aiQuickCommands",
                              JSON.stringify(aiQuickCommands),
                            );
                            try {
                              await fetch(`${API_BASE_URL}/api/ai/set-model`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  model: aiAssistantModel,
                                }),
                              });
                            } catch (e) {
                              console.error("AI model sync failed:", e);
                            }
                            setShowSettings(false);
                            addLog(
                              `✅ AI 어시스턴트 설정 저장: [모델: ${aiAssistantModel}]`,
                              "success",
                            );
                          }}
                          className="w-full px-4 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                          style={{
                            backgroundColor: "#FFDD33",
                            color: "#0A1037",
                          }}
                        >
                          설정 저장
                        </button>
                      </div>
                    </div>
                  )}

                  {/* DB 설정 탭 */}
                  {settingsModalTab === "db" && (
                    <div className="flex-1 overflow-auto">
                      <DBSettings />
                    </div>
                  )}

                  {/* vLLM 서버 관리 탭 */}
                  {settingsModalTab === "vllm" && (
                    <div className="flex-1 overflow-auto">
                      <VLLMServerPanel />
                    </div>
                  )}

                  {/* 증류 파이프라인 대시보드 탭 */}
                  {settingsModalTab === "harness" && (
                    <div className="flex-1 overflow-auto">
                      <HarnessDashboard />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 3단 레이아웃 - 통합 (세로/가로 모드 지원) */}
        {true && (
          <div className="flex flex-1 min-h-0 relative">
            {/* 패널 토글 버튼들은 각 리사이저 핸들 내부로 이동 */}

            {/* 왼쪽 패널: 파일 리스트 (레거시 거래처 등록 시 숨김) */}
            {activeTab !== "legacy-registration" && (
              <div
                ref={leftPanelRef}
                className={`${isLeftPanelCollapsed ? "w-0" : ""} border-r border-gray-200 flex flex-col transition-all duration-300 relative overflow-hidden`}
                style={{
                  width: isLeftPanelCollapsed ? 0 : `${leftPanelWidth}px`,
                  backgroundColor: "#F9F7F3",
                }}
              >
                {/* 상단 고정: 문서 추출 시작 버튼 */}
                <div
                  className="p-4 flex-shrink-0"
                  style={{ borderBottom: "1px solid #F4F4F4" }}
                >
                  {/*
                   * 🗑️ 레거시 모드: 딥러닝/템플릿 선택기 삭제 요청으로 숨김 처리
                   * (User Request: "2. Deep learning, template -> delete")
                   */}
                  {/* 
                <div className="mb-2">
                  <select ...Triggering code... </select>
                </div>
                {loading ? (...) : (...)} 
                */}

                  {/* File List Removed (Moved to Sidebar) */}
                </div>
                <div className="flex-1" />

                {/* 레거시 파일 히스토리 → Sidebar로 이동됨 */}

                {/* 하단: 진행 상황 로그 */}
                {showLogs && (processingLogs.length > 0 || loading) && (
                  <div
                    className={`flex flex-col transition-all duration-300`}
                    style={{
                      height: "280px",
                      borderTop: "1px solid #E5E5E5",
                      backgroundColor: "#F9F7F3",
                    }}
                  >
                    <div
                      className={`px-3 py-2 flex items-center justify-between border-b border-gray-200`}
                    >
                      <span
                        className="text-xs font-medium flex items-center gap-1.5"
                        style={{ color: "#222428" }}
                      >
                        {loading ? (
                          <>
                            <div
                              className="animate-spin rounded-full h-3 w-3 border-b-2"
                              style={{ borderColor: "#222428" }}
                            ></div>
                            <span>처리중... ({processingLogs.length})</span>
                          </>
                        ) : (
                          <span>처리 로그 ({processingLogs.length})</span>
                        )}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setProcessingLogs([])}
                          className="text-[10px] px-2 py-1 font-medium transition-colors hover:text-gray-600"
                          style={{ color: "#222428" }}
                        >
                          지우기
                        </button>
                        <button
                          onClick={() => setShowLogs(false)}
                          className="text-[10px] px-2 py-1 font-medium transition-colors hover:text-gray-600"
                          style={{ color: "#222428" }}
                        >
                          숨기기
                        </button>
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto px-2 py-1.5 space-y-0.5">
                      {processingLogs.length === 0 ? (
                        <div className="flex items-center justify-center h-full text-gray-400">
                          <div className="text-center">
                            <div className="text-sm">처리 대기 중...</div>
                          </div>
                        </div>
                      ) : (
                        <>
                          {processingLogs.map((log, idx) => (
                            <div
                              key={idx}
                              className="text-[11px] flex items-start gap-2 px-1.5 py-0.5 rounded hover:bg-gray-100 transition-colors"
                            >
                              <span className="text-gray-500 flex-shrink-0 font-mono text-[10px]">
                                {log.timestamp}
                              </span>
                              <span
                                className={`flex-1 leading-tight ${
                                  log.type === "error"
                                    ? "text-red-600 font-semibold"
                                    : log.type === "success"
                                      ? "text-green-600 font-semibold"
                                      : "text-gray-800"
                                }`}
                              >
                                {log.message}
                              </span>
                            </div>
                          ))}
                          <div ref={logsEndRef} />
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* 로그 표시 버튼 (숨겨진 상태일 때) */}
                {!showLogs && (processingLogs.length > 0 || loading) && (
                  <div
                    className="p-2"
                    style={{
                      borderTop: "1px solid #E5E5E5",
                      backgroundColor: "#F9F7F3",
                    }}
                  >
                    <button
                      onClick={() => setShowLogs(true)}
                      className="w-full text-[10px] px-2 py-1.5 font-medium transition-all flex items-center justify-center gap-2 hover:text-gray-600"
                      style={{ color: "#222428" }}
                    >
                      {loading && (
                        <div
                          className="animate-spin rounded-full h-3 w-3 border-b-2"
                          style={{ borderColor: "#222428" }}
                        ></div>
                      )}
                      {loading ? "처리중..." : "처리 로그 보기"} (
                      {processingLogs.length})
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* 왼쪽 패널 리사이저 (레거시 거래처 등록 시 숨김) */}
            {activeTab !== "legacy-registration" && !isLeftPanelCollapsed ? (
              <div
                className="relative w-[6px] cursor-col-resize flex-shrink-0 group hover:bg-blue-100 transition-colors"
                onMouseDown={(e) => {
                  // ⭐ 버튼 클릭인 경우 리사이징 방지
                  const target = e.target;
                  const isButton = target.closest("button");
                  if (isButton) {
                    return; // 버튼 클릭은 리사이징 처리하지 않음
                  }

                  e.preventDefault();
                  e.stopPropagation();

                  const startX = e.clientX;
                  const startWidth = leftPanelWidth;
                  const panelElement = leftPanelRef.current;

                  if (!panelElement) return;

                  // 드래그 중 선택 방지
                  document.body.style.userSelect = "none";
                  document.body.style.cursor = "col-resize";
                  document.body.style.pointerEvents = "none";
                  if (panelElement) {
                    panelElement.style.pointerEvents = "auto";
                  }

                  // ⭐ requestAnimationFrame으로 매끄러운 업데이트
                  const handleMouseMove = (moveEvent) => {
                    moveEvent.preventDefault();

                    // 기존 애니메이션 프레임 취소
                    if (resizeAnimationFrameRef.current) {
                      cancelAnimationFrame(resizeAnimationFrameRef.current);
                    }

                    // 즉시 DOM에 반영 (상태 업데이트 없이)
                    resizeAnimationFrameRef.current = requestAnimationFrame(
                      () => {
                        const delta = moveEvent.clientX - startX;
                        const newWidth = Math.max(
                          200,
                          Math.min(600, startWidth + delta),
                        );

                        // DOM에 직접 적용 (즉시 반응)
                        if (panelElement) {
                          panelElement.style.width = `${newWidth}px`;
                        }
                      },
                    );
                  };

                  const handleMouseUp = () => {
                    // 마지막 상태 업데이트 (상태 동기화)
                    if (panelElement) {
                      const finalWidth =
                        parseInt(panelElement.style.width) || leftPanelWidth;
                      setLeftPanelWidth(finalWidth);
                    }

                    // 🔧 AUIGrid 리사이즈 (컨테이너 크기에 맞게)
                    setTimeout(() => {
                      if (
                        window.AUIGrid &&
                        window.AUIGrid.isCreated("#horizontal_grid")
                      ) {
                        window.AUIGrid.resize("#horizontal_grid");
                        console.log("📐 [Left Splitter] AUIGrid resize");
                      }
                    }, 100);

                    // 이벤트 리스너 제거
                    document.removeEventListener("mousemove", handleMouseMove);
                    document.removeEventListener("mouseup", handleMouseUp);

                    // 애니메이션 프레임 취소
                    if (resizeAnimationFrameRef.current) {
                      cancelAnimationFrame(resizeAnimationFrameRef.current);
                      resizeAnimationFrameRef.current = null;
                    }

                    // 드래그 종료 후 스타일 복원
                    document.body.style.userSelect = "";
                    document.body.style.cursor = "";
                    document.body.style.pointerEvents = "";
                  };

                  // ⭐ passive: false로 즉시 반응
                  document.addEventListener("mousemove", handleMouseMove, {
                    passive: false,
                  });
                  document.addEventListener("mouseup", handleMouseUp, {
                    passive: false,
                  });
                }}
                title="드래그하여 크기 조절"
              >
                {/* 🎨 세로 구분선 + 그립 도트 */}
                <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[2px] bg-gray-200 group-hover:bg-blue-500 transition-all" />
                {/* 드래그 그립 도트 (중앙) */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[40px] flex flex-col gap-[3px] opacity-40 group-hover:opacity-100 transition-opacity pointer-events-none">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="w-[3px] h-[3px] rounded-full bg-gray-400 group-hover:bg-blue-500"
                    />
                  ))}
                </div>

                {/* 토글 버튼 */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1.5 z-40">
                  {/* 파일 목록 접기/펴기 버튼 */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setIsLeftPanelCollapsed(!isLeftPanelCollapsed);
                    }}
                    style={{ pointerEvents: "auto" }}
                    className="w-2 h-7 bg-gray-700 border border-transparent rounded-sm flex items-center justify-center text-white hover:bg-gray-800 transition-all cursor-pointer"
                    title="파일 목록 접기/펴기"
                  >
                    {isLeftPanelCollapsed ? (
                      <ChevronRight size={10} strokeWidth={1.5} />
                    ) : (
                      <ChevronLeft size={10} strokeWidth={1.5} />
                    )}
                  </button>
                </div>
              </div>
            ) : (
              /* 패널이 닫혀있을 때 토글 버튼 - 레거시 모드에서는 숨김 */
              activeTab !== "legacy-registration" && (
                <div className="relative w-0 flex-shrink-0 group hover:bg-transparent transition-all">
                  <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[1px] bg-transparent group-hover:bg-black transition-all" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1.5 z-40">
                    {/* 파일 목록 접기/펴기 버튼 */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setIsLeftPanelCollapsed(!isLeftPanelCollapsed);
                      }}
                      style={{ pointerEvents: "auto" }}
                      className="w-2 h-7 bg-gray-700 border border-transparent rounded-sm flex items-center justify-center text-white hover:bg-gray-800 transition-all cursor-pointer"
                      title="파일 목록 접기/펴기"
                    >
                      {isLeftPanelCollapsed ? (
                        <ChevronRight size={10} strokeWidth={1.5} />
                      ) : (
                        <ChevronLeft size={10} strokeWidth={1.5} />
                      )}
                    </button>
                  </div>
                </div>
              )
            )}

            {/* ✏️ 메인 콘텐츠 + AI 패널 Wrapper */}
            <div className="flex-1 flex flex-row overflow-hidden">
              {/* PDF 뷰어 + 오른쪽 패널 Wrapper (layoutMode에 따라 flex 방향 변경) */}
              <div
                className={`flex-1 flex ${layoutMode === "horizontal" ? "flex-col" : "flex-row"} overflow-hidden`}
              >
                {/* 가운데 패널: 문서 뷰어 또는 검증 결과 그리드 */}
                <div
                  className={`${isCenterPanelCollapsed ? "w-0" : layoutMode === "horizontal" ? "h-[60%]" : "flex-1"} flex flex-col relative transition-all duration-300 overflow-hidden`}
                  style={{ backgroundColor: "#F9F7F3" }}
                >
                  {/* ⭐ 처리 중 — 중앙 overlay (검은 반투명 + 큰 progress) */}
                  {(loading || isBatchExtracting) &&
                    processingProgress.totalPages > 0 &&
                    (() => {
                      // 단일 % — 2-model 비교 시 평균, 단일 시 그대로
                      const m1 = processingProgress.model1;
                      const m2 = processingProgress.model2;
                      const pct =
                        m1 && m2
                          ? Math.floor(
                              ((m1.percentage || 0) + (m2.percentage || 0)) / 2,
                            )
                          : processingProgress.percentage || 0;
                      const elapsedSec = processingProgress.startTime
                        ? Math.floor(
                            (Date.now() - processingProgress.startTime) / 1000,
                          )
                        : 0;
                      const isDone = pct >= 100;
                      return (
                        <div
                          className="absolute inset-0 z-50 flex items-center justify-center"
                          style={{
                            background: "rgba(32,33,36,0.55)",
                            backdropFilter: "blur(1px)",
                          }}
                        >
                          <div
                            className="flex flex-col items-center"
                            style={{
                              minWidth: 360,
                              maxWidth: "60%",
                              padding: 28,
                            }}
                          >
                            <div
                              className="text-white"
                              style={{
                                fontSize: 18,
                                fontWeight: 500,
                                letterSpacing: 0.3,
                              }}
                            >
                              {isDone ? "완료" : "처리 중…"}
                            </div>
                            {/* progress bar */}
                            <div
                              style={{
                                width: 360,
                                height: 6,
                                background: "rgba(255,255,255,0.18)",
                                borderRadius: 6,
                                overflow: "hidden",
                                marginTop: 18,
                              }}
                            >
                              <div
                                style={{
                                  height: "100%",
                                  width: `${Math.max(2, pct)}%`,
                                  background: "#ffffff",
                                  borderRadius: 6,
                                  transition: "width 0.3s ease-out",
                                }}
                              />
                            </div>
                            <div
                              className="text-white"
                              style={{
                                fontSize: 28,
                                fontWeight: 600,
                                marginTop: 18,
                                letterSpacing: 0.5,
                              }}
                            >
                              {pct}%
                            </div>
                            <div
                              style={{
                                fontSize: 12,
                                color: "rgba(255,255,255,0.78)",
                                marginTop: 6,
                              }}
                            >
                              {processingProgress.totalPages}건 중{" "}
                              {processingProgress.currentPage || 0}건 완료
                              <span
                                style={{
                                  color: "rgba(255,255,255,0.5)",
                                  marginLeft: 10,
                                }}
                              >
                                · {elapsedSec}s 경과
                                {processingProgress.estimatedSeconds
                                  ? ` / 예상 ~${processingProgress.estimatedSeconds}s`
                                  : ""}
                              </span>
                            </div>
                            {processingProgress.fileName && (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: "rgba(255,255,255,0.6)",
                                  marginTop: 8,
                                  maxWidth: 360,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={processingProgress.fileName}
                              >
                                {processingProgress.fileName}
                              </div>
                            )}
                            {/* 2-model 비교 시 모델별 mini-bar (작은 텍스트) */}
                            {m1 && m2 && (
                              <div
                                style={{
                                  marginTop: 16,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 4,
                                  width: 360,
                                }}
                              >
                                {[m1, m2].map((m, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                      fontSize: 10,
                                      color: "rgba(255,255,255,0.7)",
                                    }}
                                  >
                                    <span
                                      style={{
                                        width: 80,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                      title={m.name}
                                    >
                                      {m.name}
                                    </span>
                                    <div
                                      style={{
                                        flex: 1,
                                        height: 3,
                                        background: "rgba(255,255,255,0.18)",
                                        borderRadius: 3,
                                        overflow: "hidden",
                                      }}
                                    >
                                      <div
                                        style={{
                                          height: "100%",
                                          width: `${m.percentage || 0}%`,
                                          background:
                                            m.status === "done"
                                              ? "rgba(255,255,255,0.6)"
                                              : "#ffffff",
                                          transition: "width 0.3s",
                                        }}
                                      />
                                    </div>
                                    <span
                                      style={{ width: 36, textAlign: "right" }}
                                    >
                                      {m.status === "done"
                                        ? `${m.elapsed}s`
                                        : `${m.percentage}%`}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                  {/* ⭐ 완료 메시지 하단 바 (처리 완료 후 표시) */}
                  {!loading && lastCompletionLog && (
                    <div className="absolute bottom-0 left-0 right-0 z-50 bg-green-50 border-t border-green-200 px-4 py-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-green-700 font-medium">
                          {lastCompletionLog}
                        </span>
                        <button
                          onClick={() => setLastCompletionLog(null)}
                          className="text-gray-400 hover:text-gray-600 text-sm px-2"
                          title="닫기"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  )}
                  {!isCenterPanelCollapsed && (
                    <>
                      {/* 탭 내용 영역 - 고정 레이아웃 (스크롤 제거) */}
                      <div className="flex-1 overflow-hidden flex flex-col">
                        {activeTab === "viewer" && (
                          <div
                            className={`${showBatchValidationGrid ? "flex-1 min-h-0" : "h-full"} flex flex-col`}
                          >
                            {/* 🎯 템플릿/문서 탭 헤더 제거됨 — 항상 문서 뷰어 표시 */}

                            {/* 템플릿 탭 콘텐츠 */}
                            {centerViewTab === "template" && (
                              <div className="flex-1 overflow-hidden p-6 flex flex-col">
                                {(() => {
                                  // 선택된 템플릿 찾기
                                  const selectedTemplate = Object.values(
                                    templates,
                                  ).find((t) => t.name === selectedOcrTemplate);
                                  const sampleImageUrl =
                                    selectedTemplate?.sampleImage
                                      ? `${API_BASE_URL}${selectedTemplate.sampleImage}`
                                      : null;

                                  // 🎯 템플릿 관리에서 파일 업로드 시 우선 표시
                                  // ⚠️ 중요: selectedFile은 derivedSelectedIndex를 사용하므로 항상 값이 있을 수 있음
                                  // 따라서 직접 selectedFileIndex !== null을 체크해야 함
                                  const showUploadedFile =
                                    showTemplateManagement &&
                                    selectedFileIndex !== null &&
                                    selectedFile;

                                  // 디버그 로그
                                  console.log("🎯 [템플릿 탭] 렌더링:", {
                                    showUploadedFile,
                                    selectedFileIndex,
                                    selectedFile: selectedFile?.name,
                                    selectedOcrTemplate,
                                    sampleImageUrl,
                                    showTemplateManagement,
                                  });

                                  // 🎯 1순위: 템플릿 관리에서 업로드한 파일 표시
                                  if (showUploadedFile) {
                                    const fileType =
                                      selectedFile.file?.type || "";
                                    const isPdf =
                                      fileType === "application/pdf";

                                    return (
                                      <div
                                        className="bg-white rounded-lg shadow-sm border-2 border-blue-300 overflow-hidden relative flex-1 flex flex-col"
                                        style={{ minHeight: 0 }}
                                      >
                                        {isPdf ? (
                                          <div
                                            className="relative w-full h-full flex-1"
                                            style={{ minHeight: 0 }}
                                          >
                                            <PDFViewer
                                              file={selectedFile.preview}
                                              pageNumber={selectedPageNumber}
                                              onLoadSuccess={() =>
                                                console.log(
                                                  "📋 템플릿 PDF 로드 완료",
                                                )
                                              }
                                            />
                                          </div>
                                        ) : (
                                          <div
                                            className="relative w-full h-full flex-1 overflow-auto flex items-center justify-center p-4"
                                            style={{ minHeight: 0 }}
                                          >
                                            <img
                                              src={selectedFile.preview}
                                              alt={selectedFile.name}
                                              className="max-w-full max-h-full object-contain"
                                            />
                                          </div>
                                        )}
                                        <div className="absolute bottom-2 left-2 bg-orange-500 text-white px-2 py-1 rounded text-xs font-medium">
                                          📋 새 샘플 (저장 대기중)
                                        </div>
                                      </div>
                                    );
                                  }

                                  // 🎯 2순위: 템플릿의 sampleImage 표시
                                  if (sampleImageUrl) {
                                    return (
                                      <div
                                        className="bg-white rounded-lg shadow-sm border-2 border-gray-300 overflow-hidden relative flex-1 flex flex-col"
                                        style={{ minHeight: 0 }}
                                      >
                                        <TemplateSampleViewer
                                          url={sampleImageUrl}
                                          templateName={selectedOcrTemplate}
                                        />
                                        <div className="absolute bottom-2 left-2 bg-blue-500 text-white px-2 py-1 rounded text-xs font-medium pointer-events-none">
                                          📋 {selectedOcrTemplate}
                                        </div>
                                      </div>
                                    );
                                  }

                                  // 템플릿 미선택 시 (sampleImage도 없고 uploadedFile도 없는 경우)
                                  return (
                                    <div className="h-full flex items-center justify-center text-gray-500">
                                      <div className="text-center">
                                        <LayoutTemplate className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                                        <p className="font-medium">
                                          템플릿을 선택해주세요
                                        </p>
                                        <p className="text-sm mt-2">
                                          왼쪽 패널에서 템플릿을 선택하면
                                          <br />
                                          해당 템플릿의 샘플 문서가 표시됩니다
                                        </p>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            )}

                            {/* OCR 문서 탭 콘텐츠 */}
                            {centerViewTab === "ocr" && (
                              <div
                                className="flex-1 overflow-hidden p-6 flex flex-col relative"
                                style={{ minHeight: 0 }}
                              >
                                {selectedFile ? (
                                  (() => {
                                    // ── xlsx/docx 분기 (OCR 탭에서도 SpreadJS 뷰어 표시) ──
                                    const _ocrName = (
                                      selectedFile.file?.name ||
                                      selectedFile.originalName ||
                                      selectedFile.name ||
                                      ""
                                    ).toLowerCase();
                                    const _ocrMt = (
                                      selectedFile.file?.type || ""
                                    ).toLowerCase();
                                    const _ocrFt = (
                                      selectedFile.fileType || ""
                                    ).toLowerCase(); // history 복원 파일의 DB 메타
                                    const _ocrIsXlsx =
                                      /\.(xlsx|xlsm|xls)$/i.test(_ocrName) ||
                                      _ocrMt.includes("spreadsheetml") ||
                                      _ocrMt.includes("ms-excel") ||
                                      _ocrFt === "xlsx" ||
                                      _ocrFt === "xls" ||
                                      _ocrFt === "xlsm";
                                    const _ocrIsDoc =
                                      /\.(docx|doc)$/i.test(_ocrName) ||
                                      _ocrMt.includes("wordprocessingml") ||
                                      _ocrMt === "application/msword" ||
                                      _ocrFt === "docx" ||
                                      _ocrFt === "doc";
                                    if (_ocrIsXlsx || _ocrIsDoc) {
                                      // ExcelViewer (SpreadJS iframe) — 백엔드 serve-temp URL 필요
                                      const _apiOrigin =
                                        API_BASE_URL || window.location.origin;
                                      const _serveUrl =
                                        selectedFile.uploadedFileId
                                          ? `${_apiOrigin}/api/files/serve-temp/${selectedFile.uploadedFileId}`
                                          : null;
                                      const _canShowViewer =
                                        _ocrIsXlsx && _serveUrl;
                                      const _needsReupload =
                                        !selectedFile.file && !_serveUrl;
                                      return (
                                        <div
                                          className="bg-white rounded-lg shadow-sm border-2 border-gray-300 overflow-hidden relative flex-1 flex flex-col"
                                          style={{ minHeight: 0 }}
                                        >
                                          {_canShowViewer ? (
                                            <ExcelViewer
                                              url={_serveUrl}
                                              fileName={selectedFile.name}
                                            />
                                          ) : selectedFile.previewUploading ? (
                                            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-500">
                                              <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                                              <div className="text-sm">
                                                미리보기 준비 중...
                                              </div>
                                              <div className="text-xs text-gray-400">
                                                {selectedFile.name}
                                              </div>
                                            </div>
                                          ) : _needsReupload ? (
                                            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-500 p-6 text-center">
                                              <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center text-3xl">
                                                📂
                                              </div>
                                              <div className="text-sm font-medium text-gray-700">
                                                {selectedFile.name}
                                              </div>
                                              <div className="text-sm text-amber-700">
                                                미리보기를 보려면 파일을 다시
                                                업로드해주세요.
                                              </div>
                                              <div className="text-xs text-gray-500">
                                                (이전에 업로드한 파일은 페이지
                                                새로고침 후 미리보기가
                                                사라집니다.)
                                              </div>
                                              <div className="text-xs text-gray-400">
                                                {_ocrIsXlsx ? "Excel" : "Word"}{" "}
                                                파일 — 추출은 정상 동작합니다 (▶
                                                실행)
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-500 p-6 text-center">
                                              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center text-3xl">
                                                {_ocrIsXlsx ? "📊" : "📝"}
                                              </div>
                                              <div className="text-sm font-medium text-gray-700">
                                                {selectedFile.name}
                                              </div>
                                              <div className="text-xs text-gray-500">
                                                {_ocrIsXlsx ? "Excel" : "Word"}{" "}
                                                파일이 선택되었습니다.
                                              </div>
                                              <div className="text-xs text-gray-400">
                                                우측 ▶ 실행 버튼 → 추출 시작
                                              </div>
                                              {_ocrIsDoc && (
                                                <div className="text-xs text-gray-400 mt-1">
                                                  (Word 미리보기는 미지원 —
                                                  추출은 정상)
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    }
                                    return (
                                      <div
                                        className="bg-white rounded-lg shadow-sm border-2 border-gray-300 overflow-hidden relative flex-1 flex flex-col"
                                        style={{ minHeight: 0 }}
                                      >
                                        <OCRLabelingViewer
                                          imageUrl={
                                            // ⭐ OCR PNG 이미지 우선 사용 (좌표 정확도)
                                            (() => {
                                              // ⭐ 수정: selectedPageNumber에 맞는 페이지 데이터 사용
                                              const currentPage =
                                                selectedFile.pageResults?.find(
                                                  (p) =>
                                                    p.pageNumber ===
                                                    selectedPageNumber,
                                                ) ||
                                                selectedFile.pageResults?.[0];
                                              const pngImg =
                                                currentPage?.pngImage;
                                              console.log(
                                                "🔍 [App→OCRViewer] pngImage:",
                                                pngImg
                                                  ? pngImg.substring(0, 50) +
                                                      "..."
                                                  : "null",
                                                "pageNumber:",
                                                selectedPageNumber,
                                              );
                                              return (
                                                pngImg || selectedFile.preview
                                              );
                                            })()
                                          }
                                          fileType={(() => {
                                            const currentPage =
                                              selectedFile.pageResults?.find(
                                                (p) =>
                                                  p.pageNumber ===
                                                  selectedPageNumber,
                                              ) ||
                                              selectedFile.pageResults?.[0];
                                            return currentPage?.pngImage
                                              ? "image/png"
                                              : selectedFile.file?.type || "";
                                          })()}
                                          initialBoundingBoxes={(() => {
                                            const currentPage =
                                              selectedFile.pageResults?.find(
                                                (p) =>
                                                  p.pageNumber ===
                                                  selectedPageNumber,
                                              ) ||
                                              selectedFile.pageResults?.[0];
                                            return (
                                              currentPage?.boundingBoxes ||
                                              selectedFile.extractedData
                                                ?.boundingBoxes ||
                                              selectedFile.flashResult?.data
                                                ?.boundingBoxes ||
                                              []
                                            );
                                          })()}
                                          ocrImageSize={(() => {
                                            const currentPage =
                                              selectedFile.pageResults?.find(
                                                (p) =>
                                                  p.pageNumber ===
                                                  selectedPageNumber,
                                              ) ||
                                              selectedFile.pageResults?.[0];
                                            return (
                                              currentPage?.ocrImageSize ||
                                              selectedFile.extractedData
                                                ?.ocrImageSize ||
                                              null
                                            );
                                          })()}
                                          isHybridMode={
                                            !!selectedFile.extractedData
                                              ?.paddleRaw ||
                                            !!(
                                              selectedFile.pageResults &&
                                              selectedFile.pageResults.some(
                                                (p) => p.paddleRaw,
                                              )
                                            ) ||
                                            !!selectedFile.flashResult?.data
                                              ?.paddleRaw
                                          }
                                          onSave={(newBoxes) => {
                                            // Update local extracted data with edited boxes
                                            // Update fileList directly as extractedData is derived from it
                                            if (selectedFileIndex !== null) {
                                              setFileList((prev) =>
                                                prev.map((f, idx) =>
                                                  idx === selectedFileIndex &&
                                                  f.extractedData
                                                    ? {
                                                        ...f,
                                                        extractedData: {
                                                          ...f.extractedData,
                                                          boundingBoxes:
                                                            newBoxes,
                                                        },
                                                      }
                                                    : f,
                                                ),
                                              );
                                            }
                                          }}
                                        />
                                      </div>
                                    );
                                  })()
                                ) : (
                                  <div className="h-full flex items-center justify-center text-gray-500">
                                    <div className="text-center">
                                      <Scan className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                                      <p className="font-medium">
                                        문서를 선택해주세요
                                      </p>
                                      <p className="text-sm mt-2">
                                        왼쪽 패널에서 문서를 선택하고
                                        <br />
                                        OCR 추출을 실행하면 결과가 표시됩니다
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* 문서 탭 콘텐츠 (기존 PDF 뷰어 영역) */}
                            {centerViewTab === "document" && (
                              <div className="flex-1 overflow-hidden flex flex-col">
                                {" "}
                                {/* 꽉찬 화면을 위해 p-6 제거 */}
                                {selectedFile ? (
                                  <>
                                    {/* 바운딩 박스 토글 버튼 (PDF Canvas 또는 이미지) */}
                                    {((selectedValidationModels.includes(
                                      "deepseek",
                                    ) &&
                                      grounding) ||
                                      (selectedValidationModels.includes(
                                        "datalab-marker",
                                      ) &&
                                        datalabBoundingBox)) &&
                                      selectedFileWithBoundingBoxes?.boundingBoxes &&
                                      selectedFileWithBoundingBoxes
                                        .boundingBoxes.length > 0 && (
                                        <div className="px-4 pt-4 mb-3 flex items-center justify-between">
                                          {" "}
                                          {/* 상단 컨트롤에만 패딩 추가 */}
                                          <button
                                            onClick={() =>
                                              setShowBoundingBoxes(
                                                !showBoundingBoxes,
                                              )
                                            }
                                            className={`px-4 py-2 rounded-lg border-2 font-medium text-sm transition-all ${
                                              showBoundingBoxes
                                                ? "bg-blue-50 border-blue-400 text-blue-700"
                                                : "bg-gray-50 border-gray-300 text-gray-600"
                                            }`}
                                          >
                                            {showBoundingBoxes ? "✅" : "⬜"}{" "}
                                            바운딩 박스 표시 (
                                            {
                                              selectedFileWithBoundingBoxes
                                                .boundingBoxes.length
                                            }
                                            개)
                                          </button>
                                          <span className="text-xs text-gray-500">
                                            💡 오른쪽 필드에 마우스를 올리면
                                            해당 위치를 골드색으로
                                            하이라이트합니다
                                          </span>
                                        </div>
                                      )}

                                    {/* 에러만 최소 표시 (로딩은 왼쪽 패널 로그에서 확인) */}
                                    {error && (
                                      <div className="px-4 mb-2">
                                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200 text-xs">
                                          ⚠️ {error}
                                        </span>
                                      </div>
                                    )}

                                    {(() => {
                                      // ── 미리 계산: xlsx/docx 감지 (IIFE in JSX ternary 회피) ──
                                      const _selName = (
                                        selectedFile.file?.name ||
                                        selectedFile.name ||
                                        ""
                                      ).toLowerCase();
                                      const _selMt = (
                                        selectedFile.file?.type || ""
                                      ).toLowerCase();
                                      const _selIsXlsx =
                                        /\.(xlsx|xlsm|xls)$/i.test(_selName) ||
                                        _selMt.includes("spreadsheetml") ||
                                        _selMt.includes("ms-excel");
                                      const _apiOrigin =
                                        API_BASE_URL || window.location.origin;
                                      const _docServeUrl =
                                        selectedFile.uploadedFileId
                                          ? `${_apiOrigin}/api/files/serve-temp/${selectedFile.uploadedFileId}`
                                          : null;
                                      const _docCanShowExcel =
                                        _selIsXlsx && _docServeUrl;
                                      return (
                                        <div
                                          className="bg-white overflow-hidden relative flex-1 flex flex-col"
                                          style={{ minHeight: 0 }}
                                        >
                                          {" "}
                                          {/* border, rounded 제거 */}
                                          {/* 🔄 이미지→PDF 변환 중 표시 */}
                                          {selectedFile.isConverting ? (
                                            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-500">
                                              <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                                              <div className="text-sm font-medium">
                                                {selectedFile.name} → PDF 변환
                                                중...
                                              </div>
                                              <div className="text-xs text-gray-400">
                                                변환 완료 후 자동으로 표시됩니다
                                              </div>
                                            </div>
                                          ) : _selIsXlsx ? (
                                            <div
                                              className="relative w-full h-full flex-1"
                                              style={{ minHeight: 0 }}
                                            >
                                              {_docCanShowExcel ? (
                                                <ExcelViewer
                                                  url={_docServeUrl}
                                                  fileName={selectedFile.name}
                                                />
                                              ) : (
                                                <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-500 p-6 text-center">
                                                  <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center text-3xl">
                                                    📂
                                                  </div>
                                                  <div className="text-sm font-medium text-gray-700">
                                                    {selectedFile.name}
                                                  </div>
                                                  <div className="text-sm text-amber-700">
                                                    미리보기를 보려면 파일을
                                                    다시 업로드해주세요.
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          ) : selectedFile.file?.type ===
                                              "application/pdf" ||
                                            selectedFile.isPDF ? (
                                            <div
                                              className="relative w-full h-full flex-1 flex flex-col"
                                              style={{ minHeight: 0 }}
                                            >
                                              {/* 📄 멀티페이지 PDF 네비게이션 (PDF 뷰어용) */}

                                              {/* 네비게이션 바 삭제됨 - @react-pdf-viewer 내장 네비게이션 사용 */}

                                              {/* 📘 @react-pdf-viewer 기반 PDF 뷰어 + Grounding 하이라이트 */}
                                              {/* 📄 [DB Restore] PDF 로딩 — fetch fail 또는 _hasPdf X 시 안내 (무한 spinner 차단) */}
                                              {!selectedFile.file ? (
                                                selectedFile._pdfFetchFailed ||
                                                (selectedFile._restoredFromDB &&
                                                  !selectedFile._hasPdf) ? (
                                                  <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-500 p-6 text-center">
                                                    <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center text-2xl">
                                                      📂
                                                    </div>
                                                    <div className="text-sm font-medium text-gray-700">
                                                      PDF 미리보기를 불러올 수
                                                      없습니다
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                      클라우드에 PDF 가 없거나
                                                      다운로드에 실패했습니다.
                                                      <br />
                                                      파일을 다시
                                                      업로드해주세요.
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-500">
                                                    <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                                                    <div className="text-sm font-medium">
                                                      PDF 파일 로딩 중...
                                                    </div>
                                                    <div className="text-xs text-gray-400">
                                                      클라우드에서 다운로드하고
                                                      있습니다
                                                    </div>
                                                  </div>
                                                )
                                              ) : (
                                                <>
                                                  <div
                                                    className="flex-1 relative"
                                                    style={{ minHeight: 0 }}
                                                  >
                                                    <ReactPDFViewer
                                                      file={selectedFile.file}
                                                      pageNumber={
                                                        selectedPageNumber
                                                      }
                                                      pageJumpToken={
                                                        pageJumpToken
                                                      }
                                                      onLoadSuccess={(info) => {
                                                        console.log(
                                                          "📘 [ReactPDFViewer] 로드 완료:",
                                                          info.numPages,
                                                          "페이지",
                                                        );
                                                        setTotalPdfPages(
                                                          info.numPages,
                                                        );
                                                      }}
                                                      onPageChange={(page) => {
                                                        setSelectedPageNumber(
                                                          page,
                                                        );
                                                      }}
                                                      highlightedField={
                                                        groundingHighlight?.fieldKey ||
                                                        highlightedField
                                                      }
                                                      groundingCoordinates={
                                                        groundingHighlight?.coordinates ||
                                                        null
                                                      }
                                                      highlightText={
                                                        groundingHighlight?.fieldValue ||
                                                        null
                                                      }
                                                      pdfDimensions={
                                                        selectedFile
                                                          .pageDimensions?.[
                                                          `page_${selectedPageNumber - 1}`
                                                        ] || null
                                                      }
                                                    />
                                                  </div>

                                                  {/* Canvas 하이라이트 오버레이 */}
                                                  {highlightedField &&
                                                    selectedFileWithBoundingBoxes?.boundingBoxes &&
                                                    selectedFileWithBoundingBoxes
                                                      .boundingBoxes.length >
                                                      0 && (
                                                      <FieldHighlightOverlay
                                                        boundingBoxes={
                                                          selectedFileWithBoundingBoxes.boundingBoxes
                                                        }
                                                        highlightedField={
                                                          highlightedField
                                                        }
                                                        fieldValueMap={
                                                          selectedFile
                                                            .extractedData
                                                            ?.fields || {}
                                                        }
                                                        imageWidth={800} // PDF 기본 너비
                                                        imageHeight={1131} // PDF 기본 높이 (A4 비율)
                                                        visible={true}
                                                        autoScroll={
                                                          clickedField ===
                                                          highlightedField
                                                        }
                                                      />
                                                    )}
                                                </>
                                              )}
                                            </div>
                                          ) : selectedFile.conversionError ? (
                                            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-500 p-8">
                                              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
                                                <span className="text-2xl">
                                                  ⚠️
                                                </span>
                                              </div>
                                              <div className="text-sm font-medium text-red-600">
                                                {selectedFile.name}
                                              </div>
                                              <div className="text-xs text-red-500">
                                                PDF 변환 실패:{" "}
                                                {selectedFile.conversionError}
                                              </div>
                                              <div className="text-xs text-gray-400 mt-1">
                                                TIF/TIFF 파일은 PDF로 변환 후
                                                표시됩니다. 서버에 Python
                                                의존성(Pillow, img2pdf)이
                                                설치되어 있는지 확인하세요.
                                              </div>
                                            </div>
                                          ) : (
                                            <div
                                              className="relative w-full h-full flex-1 overflow-hidden flex flex-col"
                                              style={{ minHeight: 0 }}
                                            >
                                              {/* 📄 멀티페이지 PDF 네비게이션 */}
                                              {totalPdfPages > 1 && (
                                                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-gray-100 border-b border-gray-300">
                                                  <button
                                                    onClick={() =>
                                                      handlePdfPageChange(
                                                        currentPdfPage - 1,
                                                      )
                                                    }
                                                    disabled={
                                                      currentPdfPage <= 1
                                                    }
                                                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                                                      currentPdfPage <= 1
                                                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                                        : "bg-blue-500 text-white hover:bg-blue-600"
                                                    }`}
                                                  >
                                                    ◀ 이전
                                                  </button>

                                                  <div className="flex items-center gap-1">
                                                    {Array.from(
                                                      { length: totalPdfPages },
                                                      (_, i) => i + 1,
                                                    ).map((pageNum) => (
                                                      <button
                                                        key={pageNum}
                                                        onClick={() =>
                                                          handlePdfPageChange(
                                                            pageNum,
                                                          )
                                                        }
                                                        className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                                                          pageNum ===
                                                          currentPdfPage
                                                            ? "bg-blue-600 text-white"
                                                            : "bg-white text-gray-700 hover:bg-blue-100 border border-gray-300"
                                                        }`}
                                                      >
                                                        {pageNum}
                                                      </button>
                                                    ))}
                                                  </div>

                                                  <button
                                                    onClick={() =>
                                                      handlePdfPageChange(
                                                        currentPdfPage + 1,
                                                      )
                                                    }
                                                    disabled={
                                                      currentPdfPage >=
                                                      totalPdfPages
                                                    }
                                                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                                                      currentPdfPage >=
                                                      totalPdfPages
                                                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                                        : "bg-blue-500 text-white hover:bg-blue-600"
                                                    }`}
                                                  >
                                                    다음 ▶
                                                  </button>

                                                  <span className="ml-2 text-sm text-gray-600">
                                                    {currentPdfPage} /{" "}
                                                    {totalPdfPages} 페이지
                                                  </span>
                                                </div>
                                              )}

                                              <div className="flex-1 overflow-hidden flex items-center justify-center">
                                                <img
                                                  id={`image-viewer-${selectedFile.id}`}
                                                  src={
                                                    // 🎯 OCR 파이프라인 보정 이미지 우선
                                                    correctedImageUrl
                                                      ? correctedImageUrl
                                                      : // ⭐ 상세 모드 + PDF: PNG 이미지 사용, 그 외: 원본 프리뷰
                                                        (selectedFile.file
                                                            ?.type ===
                                                            "application/pdf" ||
                                                            selectedFile.isPDF) &&
                                                          showDetailMode &&
                                                          selectedFileWithBoundingBoxes?.pngImage
                                                        ? selectedFileWithBoundingBoxes.pngImage
                                                        : selectedFile.preview
                                                  }
                                                  alt={selectedFile.name}
                                                  className="max-w-full max-h-full object-contain"
                                                  onLoad={(e) => {
                                                    console.log(
                                                      `🖼️ 이미지 로드: naturalWidth=${e.target.naturalWidth}, naturalHeight=${e.target.naturalHeight}`,
                                                    );

                                                    // ⭐ 이미지 크기 정보 저장 (DeepSeek 바운딩 박스용)
                                                    if (
                                                      grounding &&
                                                      selectedFile.extractedData
                                                        ?.text
                                                    ) {
                                                      const boundingBoxes =
                                                        parseBoundingBoxes(
                                                          selectedFile
                                                            .extractedData.text,
                                                        );
                                                      console.log(
                                                        `📦 바운딩 박스 ${boundingBoxes.length}개 감지 (DeepSeek)`,
                                                      );

                                                      // 파일 객체에 바운딩 박스 데이터 저장
                                                      setFileList((prev) =>
                                                        prev.map((f) =>
                                                          f.id ===
                                                          selectedFile.id
                                                            ? {
                                                                ...f,
                                                                boundingBoxes,
                                                                imageWidth:
                                                                  e.target
                                                                    .naturalWidth,
                                                                imageHeight:
                                                                  e.target
                                                                    .naturalHeight,
                                                              }
                                                            : f,
                                                        ),
                                                      );
                                                    }

                                                    // ⭐ 이미지 파일 또는 상세 모드의 바운딩 박스 데이터가 있으면 이미지 크기 저장
                                                    if (
                                                      selectedFileWithBoundingBoxes?.boundingBoxes &&
                                                      selectedFileWithBoundingBoxes
                                                        .boundingBoxes.length >
                                                        0
                                                    ) {
                                                      // ⭐ 상세 모드 + PDF: 페이지별 이미지 크기 저장
                                                      if (
                                                        (selectedFile.file
                                                          ?.type ===
                                                          "application/pdf" ||
                                                          selectedFile.isPDF) &&
                                                        showDetailMode
                                                      ) {
                                                        setFileList((prev) =>
                                                          prev.map((f) => {
                                                            if (
                                                              f.id ===
                                                                selectedFile.id &&
                                                              f.pageResults
                                                            ) {
                                                              const updatedPageResults =
                                                                f.pageResults.map(
                                                                  (p) =>
                                                                    p.pageNumber ===
                                                                    selectedPageNumber
                                                                      ? {
                                                                          ...p,
                                                                          imageWidth:
                                                                            e
                                                                              .target
                                                                              .naturalWidth,
                                                                          imageHeight:
                                                                            e
                                                                              .target
                                                                              .naturalHeight,
                                                                        }
                                                                      : p,
                                                                );
                                                              return {
                                                                ...f,
                                                                pageResults:
                                                                  updatedPageResults,
                                                              };
                                                            }
                                                            return f;
                                                          }),
                                                        );
                                                        console.log(
                                                          `📦 [페이지 ${selectedPageNumber}] 이미지 크기 저장: ${e.target.naturalWidth}x${e.target.naturalHeight}`,
                                                        );
                                                      } else {
                                                        // ⭐ 이미지 파일: 파일 레벨 이미지 크기 저장
                                                        setFileList((prev) =>
                                                          prev.map((f) => {
                                                            if (
                                                              f.id ===
                                                              selectedFile.id
                                                            ) {
                                                              return {
                                                                ...f,
                                                                imageWidth:
                                                                  e.target
                                                                    .naturalWidth,
                                                                imageHeight:
                                                                  e.target
                                                                    .naturalHeight,
                                                              };
                                                            }
                                                            return f;
                                                          }),
                                                        );
                                                        console.log(
                                                          `📦 이미지 크기 저장: ${e.target.naturalWidth}x${e.target.naturalHeight}`,
                                                        );
                                                      }
                                                    }

                                                    // 🎯 OCR 파이프라인: 보정 이미지 로드 완료 시 표시된 크기 저장
                                                    if (ocrLabels.length > 0) {
                                                      console.log(
                                                        `🎯 [OCR Labels] 이미지 로드 완료: display=${e.target.width}x${e.target.height}, natural=${e.target.naturalWidth}x${e.target.naturalHeight}`,
                                                      );
                                                      // 이미지 표시 크기 저장하여 SVG 리렌더링 유발
                                                      setOcrImageSize(
                                                        (prev) => ({
                                                          ...prev,
                                                          displayWidth:
                                                            e.target.width,
                                                          displayHeight:
                                                            e.target.height,
                                                        }),
                                                      );
                                                    }
                                                  }}
                                                />

                                                {/* 바운딩 박스 오버레이 (이미지 또는 상세 모드, DeepSeek/Datalab) */}
                                                {((selectedValidationModels.includes(
                                                  "deepseek",
                                                ) &&
                                                  grounding) ||
                                                  (selectedValidationModels.includes(
                                                    "datalab-marker",
                                                  ) &&
                                                    datalabBoundingBox)) &&
                                                  showBoundingBoxes &&
                                                  selectedFileWithBoundingBoxes?.boundingBoxes &&
                                                  selectedFileWithBoundingBoxes
                                                    .boundingBoxes.length >
                                                    0 && (
                                                    <BoundingBoxOverlay
                                                      boundingBoxes={scaleBoundingBoxes(
                                                        selectedFileWithBoundingBoxes.boundingBoxes,
                                                        selectedFileWithBoundingBoxes.imageWidth ||
                                                          1,
                                                        selectedFileWithBoundingBoxes.imageHeight ||
                                                          1,
                                                        document.getElementById(
                                                          `image-viewer-${selectedFile.id}`,
                                                        )?.width || 1,
                                                        document.getElementById(
                                                          `image-viewer-${selectedFile.id}`,
                                                        )?.height || 1,
                                                      )}
                                                      imageWidth={
                                                        document.getElementById(
                                                          `image-viewer-${selectedFile.id}`,
                                                        )?.width || 0
                                                      }
                                                      imageHeight={
                                                        document.getElementById(
                                                          `image-viewer-${selectedFile.id}`,
                                                        )?.height || 0
                                                      }
                                                      visible={
                                                        showBoundingBoxes
                                                      }
                                                      color="#339AF0"
                                                    />
                                                  )}

                                                {/* ⭐ 필드별 하이라이트 오버레이 (호버 시) */}
                                                {highlightedField &&
                                                  selectedFileWithBoundingBoxes?.boundingBoxes &&
                                                  selectedFileWithBoundingBoxes
                                                    .boundingBoxes.length >
                                                    0 && (
                                                    <FieldHighlightOverlay
                                                      boundingBoxes={scaleBoundingBoxes(
                                                        selectedFileWithBoundingBoxes.boundingBoxes,
                                                        selectedFileWithBoundingBoxes.imageWidth ||
                                                          1,
                                                        selectedFileWithBoundingBoxes.imageHeight ||
                                                          1,
                                                        document.getElementById(
                                                          `image-viewer-${selectedFile.id}`,
                                                        )?.width || 1,
                                                        document.getElementById(
                                                          `image-viewer-${selectedFile.id}`,
                                                        )?.height || 1,
                                                      )}
                                                      highlightedField={
                                                        highlightedField
                                                      }
                                                      fieldValueMap={{
                                                        [highlightedField]:
                                                          highlightedFieldValue,
                                                      }}
                                                      imageWidth={
                                                        document.getElementById(
                                                          `image-viewer-${selectedFile.id}`,
                                                        )?.width || 0
                                                      }
                                                      imageHeight={
                                                        document.getElementById(
                                                          `image-viewer-${selectedFile.id}`,
                                                        )?.height || 0
                                                      }
                                                      visible={true}
                                                      autoScroll={
                                                        clickedField ===
                                                        highlightedField
                                                      }
                                                    />
                                                  )}

                                                {/* 🎯 OCR 파이프라인 Labels 오버레이 - OCRLabelingModal과 동일한 bbox 형식 */}
                                                {ocrLabels.length > 0 &&
                                                  ocrImageSize.displayWidth &&
                                                  ocrImageSize.displayHeight &&
                                                  (() => {
                                                    // 📄 멀티페이지: 현재 페이지의 labels만 필터링
                                                    const currentPageLabels =
                                                      totalPdfPages > 1
                                                        ? ocrLabels.filter(
                                                            (label) =>
                                                              label.pageNumber ===
                                                              currentPdfPage,
                                                          )
                                                        : ocrLabels;

                                                    return (
                                                      currentPageLabels.length >
                                                        0 && (
                                                        <svg
                                                          style={{
                                                            position:
                                                              "absolute",
                                                            // 🎯 SVG를 이미지와 동일한 크기로 설정
                                                            width:
                                                              ocrImageSize.displayWidth,
                                                            height:
                                                              ocrImageSize.displayHeight,
                                                            // 🎯 이미지가 중앙 정렬되므로 SVG도 중앙 정렬
                                                            top: "50%",
                                                            left: "50%",
                                                            transform:
                                                              "translate(-50%, -50%)",
                                                            pointerEvents:
                                                              "none",
                                                          }}
                                                          // 🎯 viewBox를 사용하여 1:1 좌표 매칭 (OCRLabelingModal과 동일)
                                                          viewBox={`0 0 ${ocrImageSize.width || ocrImageSize.displayWidth} ${ocrImageSize.height || ocrImageSize.displayHeight}`}
                                                          preserveAspectRatio="xMidYMid slice"
                                                        >
                                                          {currentPageLabels.map(
                                                            (label, idx) => {
                                                              // bbox: {x, y, width, height} 형식 (OCRLabelingModal과 동일)
                                                              if (!label.bbox)
                                                                return null;

                                                              // 🎯 1:1 좌표 사용 - viewBox가 스케일링 처리
                                                              const x =
                                                                label.bbox.x;
                                                              const y =
                                                                label.bbox.y;
                                                              const width =
                                                                label.bbox
                                                                  .width;
                                                              const height =
                                                                label.bbox
                                                                  .height;

                                                              // 색상: 녹색 점선 (라벨링 팝업과 동일)
                                                              const color =
                                                                "#22C55E";

                                                              return (
                                                                <g key={idx}>
                                                                  <rect
                                                                    x={x}
                                                                    y={y}
                                                                    width={
                                                                      width
                                                                    }
                                                                    height={
                                                                      height
                                                                    }
                                                                    fill="none"
                                                                    stroke={
                                                                      color
                                                                    }
                                                                    strokeWidth={
                                                                      1.5
                                                                    }
                                                                    strokeDasharray="4 2"
                                                                    rx={2}
                                                                  />
                                                                  {/* 필드명 라벨 (있는 경우) */}
                                                                  {label.fieldKey && (
                                                                    <text
                                                                      x={x}
                                                                      y={y - 3}
                                                                      fill={
                                                                        color
                                                                      }
                                                                      fontSize="12"
                                                                      fontWeight="bold"
                                                                    >
                                                                      {
                                                                        label.fieldKey
                                                                      }
                                                                    </text>
                                                                  )}
                                                                </g>
                                                              );
                                                            },
                                                          )}
                                                        </svg>
                                                      )
                                                    );
                                                  })()}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </>
                                ) : (
                                  <div className="h-full flex items-center justify-center text-gray-500">
                                    <div className="text-center">
                                      <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                                      <p>파일을 선택해주세요</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            {/* PDF 뷰어 영역 종료 */}
                          </div>
                        )}

                        {/* 검증 결과 그리드 탭 */}
                        {activeTab === "grid" && (
                          <BatchValidationGrid
                            fileList={fileList}
                            API_BASE_URL={API_BASE_URL}
                            templates={templates}
                            // ⭐ 왼쪽 패널 템플릿 선택을 그리드로 전달 (이름 → ID 변환)
                            selectedTemplateId={
                              Object.values(templates).find(
                                (t) => t.name === selectedOcrTemplate,
                              )?.id || ""
                            }
                            onClose={() => setActiveTab("viewer")}
                            onRefresh={() => {
                              // 파일 리스트 새로고침 또는 상태 업데이트
                            }}
                            // ⭐ 그리드 행 선택 시 오른쪽 폼 연동
                            onRowSelect={(fileId, pageNumber, vehicle) => {
                              console.log("📋 그리드 행 선택:", {
                                fileId,
                                pageNumber,
                                vehicle,
                              });
                              // 파일 선택
                              const fileIndex = fileList.findIndex(
                                (f) => f.id === fileId,
                              );
                              if (fileIndex !== -1) {
                                setSelectedFileIndex(fileIndex);
                                // 페이지 선택 (PDF인 경우)
                                if (pageNumber) {
                                  setSelectedPageNumber(pageNumber);
                                  // 체크된 페이지도 업데이트
                                  setCheckedPages([pageNumber]);
                                }
                              }
                            }}
                          />
                        )}

                        {/* 전체검증 그리드 (전체검증 후 표시) */}
                        {showBatchValidationGrid && activeTab === "viewer" && (
                          <div
                            className="flex-1 min-h-0 border-t-2 border-blue-300"
                            style={{ maxHeight: "50%" }}
                          >
                            <BatchValidationGrid
                              fileList={fileList}
                              API_BASE_URL={API_BASE_URL}
                              templates={templates}
                              // ⭐ 왼쪽 패널 템플릿 선택을 그리드로 전달
                              selectedTemplateId={
                                Object.values(templates).find(
                                  (t) => t.name === selectedOcrTemplate,
                                )?.id || ""
                              }
                              onClose={() => setShowBatchValidationGrid(false)}
                              onRefresh={() => {
                                // 파일 리스트 새로고침 또는 상태 업데이트
                                setShowBatchValidationGrid(false);
                                setTimeout(
                                  () => setShowBatchValidationGrid(true),
                                  100,
                                );
                              }}
                              // ⭐ 그리드 행 선택 시 오른쪽 폼 연동
                              onRowSelect={(fileId, pageNumber, vehicle) => {
                                console.log("📋 그리드 행 선택:", {
                                  fileId,
                                  pageNumber,
                                  vehicle,
                                });
                                // 파일 선택
                                const fileIndex = fileList.findIndex(
                                  (f) => f.id === fileId,
                                );
                                if (fileIndex !== -1) {
                                  setSelectedFileIndex(fileIndex);
                                  // 페이지 선택 (PDF인 경우)
                                  if (pageNumber) {
                                    setSelectedPageNumber(pageNumber);
                                    // 체크된 페이지도 업데이트
                                    setCheckedPages([pageNumber]);
                                  }
                                }
                              }}
                            />
                          </div>
                        )}

                        {/* 차량등록매핑 탭 */}
                        {activeTab === "mapping" &&
                          !showBatchValidationGrid && (
                            <VehicleMatchingPanel
                              fileList={fileList}
                              apiBaseUrl={API_BASE_URL}
                              isRightPanelCollapsed={isRightPanelCollapsed}
                              isMainLeftPanelCollapsed={isLeftPanelCollapsed}
                              setIsMainLeftPanelCollapsed={
                                setIsLeftPanelCollapsed
                              }
                              isVehicleRegistrationPanelCollapsed={
                                isVehicleRegistrationPanelCollapsed
                              }
                              setIsVehicleRegistrationPanelCollapsed={
                                setIsVehicleRegistrationPanelCollapsed
                              }
                            />
                          )}

                        {/* 🏢 납세자매칭 탭 (UNL 통관 — 하네스 1차 목업) */}
                        {activeTab === "taxpayer-matching" &&
                          !showBatchValidationGrid && (
                            <div
                              className="fixed inset-y-0 right-0 z-30 bg-[#F5F1EC]"
                              style={{
                                left: isSidebarOpen ? sidebarWidth : 48,
                              }}
                            >
                              <TaxpayerMatchingPanel
                                apiBaseUrl={API_BASE_URL}
                              />
                            </div>
                          )}

                        {/* 🌐 HS Code 분류 탭 (hs_classifier FastAPI proxy) */}
                        {activeTab === "hs-classifier" &&
                          !showBatchValidationGrid && (
                            <div
                              className="fixed inset-y-0 right-0 z-30 bg-[#F5F1EC]"
                              style={{
                                left: isSidebarOpen ? sidebarWidth : 48,
                                top: 64,
                              }}
                            >
                              <HSClassifierPanel apiBaseUrl={API_BASE_URL} />
                            </div>
                          )}

                        {/* 📊 HS 분류 이력/통계 탭 */}
                        {activeTab === "hs-classifier-audit" &&
                          !showBatchValidationGrid && (
                            <div
                              className="fixed inset-y-0 right-0 z-30 bg-[#F5F1EC]"
                              style={{
                                left: isSidebarOpen ? sidebarWidth : 48,
                                top: 64,
                              }}
                            >
                              <HSClassifierAuditPanel
                                apiBaseUrl={API_BASE_URL}
                              />
                            </div>
                          )}

                        {/* 🎓 학습데이타검증 탭 — distillation GT 3-panel UI */}
                        {activeTab === "training-verify" &&
                          !showBatchValidationGrid && (
                            <div
                              className="fixed inset-y-0 right-0 z-30 bg-white"
                              style={{
                                left: isSidebarOpen ? sidebarWidth : 48,
                                top: 64,
                              }}
                            >
                              <TrainingVerifyPanel />
                            </div>
                          )}

                        {/* 🚗 학습데이타검증 (수출) — adapter v1 평가 188건 viewer */}
                        {activeTab === "training-verify-export" &&
                          !showBatchValidationGrid && (
                            <div
                              className="fixed inset-y-0 right-0 z-30 bg-white"
                              style={{
                                left: isSidebarOpen ? sidebarWidth : 48,
                                top: 64,
                              }}
                            >
                              <TrainingVerifyExportPanel />
                            </div>
                          )}

                        {/* 🍩 Donut 학습데이타 — v4 manifest 검증 + GT 수정 */}
                        {activeTab === "donut-learning-data" &&
                          !showBatchValidationGrid && (
                            <div
                              className="fixed inset-y-0 right-0 z-30 bg-white"
                              style={{
                                left: isSidebarOpen ? sidebarWidth : 48,
                                top: 64,
                              }}
                            >
                              <DonutLearningDataPanel />
                            </div>
                          )}

                        {/* 🔍 GT 검증 — Gemini diff vs DB GT */}
                        {activeTab === "gt-verify" &&
                          !showBatchValidationGrid && (
                            <div
                              className="fixed inset-y-0 right-0 z-30 bg-white"
                              style={{
                                left: isSidebarOpen ? sidebarWidth : 48,
                                top: 64,
                              }}
                            >
                              <GTVerifyPanel />
                            </div>
                          )}

                        {/* 📦 FedEx 추출 검증 — TW 400건 LLM 추출 검수 + 박스 하이라이트 */}
                        {activeTab === "fedex-verify" &&
                          !showBatchValidationGrid && (
                            <div
                              className="fixed inset-y-0 right-0 z-30 bg-white"
                              style={{
                                left: isSidebarOpen ? sidebarWidth : 48,
                                top: 64,
                              }}
                            >
                              <FedexVerifyPanel />
                            </div>
                          )}

                        {/* 🌳 AI HS 분류 (onto-detail) — keep-alive (한 번 mount 후
                            display 로 show/hide, state 보존). */}
                        {/* 🏠 홈 대시보드 (home) — 검색 히어로 + 최근검색·내품목·새소식 */}
                         {mountedAudit["home"] && (
                          <div
                            className="fixed inset-y-0 right-0 z-30 bg-white"
                            style={{
                              left: isMobileShell
                                ? 0
                                : isSidebarOpen
                                  ? sidebarWidth
                                  : 48,
                              top: 64,
                              zIndex: 55,
                              bottom: isMobileShell
                                ? "calc(54px + env(safe-area-inset-bottom))"
                                : 0,
                              overflow: "auto",
                              display:
                                _auditRoute === "home" ? "block" : "none",
                            }}
                          >
                            <HomeDashboard />
                           </div>
                         )}

                          {/* 🔔 새소식 — 공개 콘텐츠를 로그인 업무 셸 안에서 표시 */}
                          {mountedAudit["news"] && (
                            <div
                              className="fixed inset-y-0 right-0 z-30 bg-white"
                              style={{
                                left: isMobileShell
                                  ? 0
                                  : isSidebarOpen
                                    ? sidebarWidth
                                    : 48,
                                top: 64,
                                zIndex: 55,
                                bottom: isMobileShell
                                  ? "calc(54px + env(safe-area-inset-bottom))"
                                  : 0,
                                overflow: "auto",
                                display:
                                  _auditRoute === "news" ? "block" : "none",
                              }}
                            >
                              <NewsPage embedded />
                            </div>
                          )}

                          {/* 📊 사용량 — 제안 모드 LLM 호출 집계(이번 달·오늘·최근 내역) */}
                          {mountedAudit["usage"] && (
                            <div
                              className="fixed inset-y-0 right-0 z-30 bg-white"
                              style={{
                                left: isMobileShell
                                  ? 0
                                  : isSidebarOpen
                                    ? sidebarWidth
                                    : 48,
                                top: 64,
                                zIndex: 55,
                                bottom: isMobileShell
                                  ? "calc(54px + env(safe-area-inset-bottom))"
                                  : 0,
                                overflow: "auto",
                                display:
                                  _auditRoute === "usage" ? "block" : "none",
                              }}
                            >
                              <UsagePage />
                            </div>
                          )}

                          {/* 🏢 회사 계정 · 신고분포 정보공개 동의(63차) */}
                          {mountedAudit["company"] && (
                            <div
                              className="fixed inset-y-0 right-0 z-30 bg-white"
                              style={{
                                left: isMobileShell
                                  ? 0
                                  : isSidebarOpen
                                    ? sidebarWidth
                                    : 48,
                                top: 64,
                                zIndex: 55,
                                bottom: isMobileShell
                                  ? "calc(54px + env(safe-area-inset-bottom))"
                                  : 0,
                                overflow: "auto",
                                display:
                                  _auditRoute === "company" ? "block" : "none",
                              }}
                            >
                              <CompanyAccountPage />
                            </div>
                          )}

                          {/* 🧮 무료 실무 도구 — 사이드바·상단 검색을 유지한 내부 화면 */}
                          {mountedAudit["tools"] && (
                            <div
                              className="fixed inset-y-0 right-0 z-30 bg-white"
                              style={{
                                left: isMobileShell
                                  ? 0
                                  : isSidebarOpen
                                    ? sidebarWidth
                                    : 48,
                                top: 64,
                                zIndex: 55,
                                bottom: isMobileShell
                                  ? "calc(54px + env(safe-area-inset-bottom))"
                                  : 0,
                                overflow: "auto",
                                display:
                                  _auditRoute === "tools" ? "block" : "none",
                              }}
                            >
                              {_auditTool === "freight" ? (
                                <CourierFreightPage embedded />
                              ) : _auditTool === "taxcalc" ? (
                                <TaxCalcPage embedded />
                              ) : _auditTool === "forms" ? (
                                <FormsPage embedded />
                              ) : _auditTool === "trade-codes" ? (
                                <TradeCodesPage embedded />
                              ) : _auditTool === "req-agencies" ? (
                                <ReqAgencyDirectoryPage embedded />
                              ) : (
                                <ExchangeRatePage embedded />
                              )}
                            </div>
                          )}

                         {/* 📋 수출입요령 (req-guide) — keep-alive. 요건 유형별 세번 조회 참조 섹션 */}
                        {mountedAudit["req-guide"] && (
                          <div
                            className="fixed inset-y-0 right-0 z-30 bg-white"
                            style={{
                              left: isMobileShell
                                ? 0
                                : isSidebarOpen
                                  ? sidebarWidth
                                  : 48,
                              top: 64,
                              zIndex: 55,
                              bottom: isMobileShell
                                ? "calc(54px + env(safe-area-inset-bottom))"
                                : 0,
                              overflow: "auto",
                              display:
                                _auditRoute === "req-guide" ? "block" : "none",
                            }}
                          >
                            <ReqGuidePage />
                          </div>
                        )}

                        {/* 관세감면 (exemption) — keep-alive. 관세법 제88~101조 조문별 안내 */}
                        {mountedAudit["exemption"] && (
                          <div
                            className="fixed inset-y-0 right-0 z-30 bg-white"
                            style={{
                              left: isMobileShell
                                ? 0
                                : isSidebarOpen
                                  ? sidebarWidth
                                  : 48,
                              top: 64,
                              zIndex: 55,
                              bottom: isMobileShell
                                ? "calc(54px + env(safe-area-inset-bottom))"
                                : 0,
                              overflow: "auto",
                              display:
                                _auditRoute === "exemption" ? "block" : "none",
                            }}
                          >
                            <ExemptionPage />
                          </div>
                        )}

                        {/* 간이세율 (simplified-rate) — keep-alive. 관세법 제81조 */}
                        {mountedAudit["simplified-rate"] && (
                          <div
                            className="fixed inset-y-0 right-0 z-30 bg-white"
                            style={{
                              left: isMobileShell
                                ? 0
                                : isSidebarOpen
                                  ? sidebarWidth
                                  : 48,
                              top: 64,
                              zIndex: 55,
                              bottom: isMobileShell
                                ? "calc(54px + env(safe-area-inset-bottom))"
                                : 0,
                              overflow: "auto",
                              display:
                                _auditRoute === "simplified-rate"
                                  ? "block"
                                  : "none",
                            }}
                          >
                            <SimplifiedRatePage />
                          </div>
                        )}

                        {/* 분할납부 (installment) — keep-alive. 관세법 제107조 */}
                        {mountedAudit["installment"] && (
                          <div
                            className="fixed inset-y-0 right-0 z-30 bg-white"
                            style={{
                              left: isMobileShell
                                ? 0
                                : isSidebarOpen
                                  ? sidebarWidth
                                  : 48,
                              top: 64,
                              zIndex: 55,
                              bottom: isMobileShell
                                ? "calc(54px + env(safe-area-inset-bottom))"
                                : 0,
                              overflow: "auto",
                              display:
                                _auditRoute === "installment"
                                  ? "block"
                                  : "none",
                            }}
                          >
                            <InstallmentPage />
                          </div>
                        )}

                        {/* 용도세율 (enduse-rate) — keep-alive. 관세법 제83조 */}
                        {mountedAudit["enduse-rate"] && (
                          <div
                            className="fixed inset-y-0 right-0 z-30 bg-white"
                            style={{
                              left: isMobileShell
                                ? 0
                                : isSidebarOpen
                                  ? sidebarWidth
                                  : 48,
                              top: 64,
                              zIndex: 55,
                              bottom: isMobileShell
                                ? "calc(54px + env(safe-area-inset-bottom))"
                                : 0,
                              overflow: "auto",
                              display:
                                _auditRoute === "enduse-rate"
                                  ? "block"
                                  : "none",
                            }}
                          >
                            <EndUseRatePage />
                          </div>
                        )}

                        {/* 관세 제도 안내 허브 (customs-guide) — keep-alive */}
                        {mountedAudit["customs-guide"] && (
                          <div
                            className="fixed inset-y-0 right-0 z-30 bg-white"
                            style={{
                              left: isMobileShell
                                ? 0
                                : isSidebarOpen
                                  ? sidebarWidth
                                  : 48,
                              top: 64,
                              zIndex: 55,
                              bottom: isMobileShell
                                ? "calc(54px + env(safe-area-inset-bottom))"
                                : 0,
                              overflow: "auto",
                              display:
                                _auditRoute === "customs-guide"
                                  ? "block"
                                  : "none",
                            }}
                          >
                            <CustomsGuideHubPage />
                          </div>
                        )}

                        {mountedAudit["onto-detail"] && (
                          <div
                            className="fixed inset-y-0 right-0 z-30 bg-white"
                            style={{
                              // 모바일 셸: 사이드바 없음 → left 0, 하단 탭바만큼 띄움
                              left: isMobileShell
                                ? 0
                                : isSidebarOpen
                                  ? sidebarWidth
                                  : 48,
                              top: 70, // 상단 헤더(검색바 포함) 높이에 맞춤 — 겹침/스크롤 방지
                              zIndex: 55, // 문서인식 패널 접기 핸들(z-40/50) 비침 방지
                              bottom: isMobileShell
                                ? "calc(54px + env(safe-area-inset-bottom))"
                                : 0,
                              overflow: "auto",
                              display:
                                _auditRoute === "onto-detail"
                                  ? "block"
                                  : "none",
                            }}
                          >
                            <HeadingDetailPage embedded />
                          </div>
                        )}

                        {/* 🔎 관세청 품목분류 사례 (precedents) — keep-alive.
                            overflow hidden — PrecedentsPage 가 자체 height 100% +
                            AUI Grid 자체 스크롤 (overflow auto 면 가로 점프 가로챔). */}
                        {mountedAudit["precedents"] && (
                          <div
                            className="fixed inset-y-0 right-0 z-30 bg-white"
                            style={{
                              left: isMobileShell
                                ? 0
                                : isSidebarOpen
                                  ? sidebarWidth
                                  : 48,
                              top: 64,
                              zIndex: 55, // 문서인식 패널 접기 핸들(z-40/50) 비침 방지
                              bottom: isMobileShell
                                ? "calc(54px + env(safe-area-inset-bottom))"
                                : 0,
                              overflow: "hidden",
                              display:
                                _auditRoute === "precedents" ? "block" : "none",
                            }}
                          >
                            <PrecedentsPage embedded />
                          </div>
                        )}

                        {/* 📄 내 검토보고서 (my-reports) — keep-alive, embedded.
                            발급한 검토보고서 이력·재발급(내 품목/내 사례철과 같은 개인 컬렉션). */}
                        {mountedAudit["my-reports"] && (
                          <div
                            className="fixed inset-y-0 right-0 z-30 bg-white"
                            style={{
                              left: isMobileShell
                                ? 0
                                : isSidebarOpen
                                  ? sidebarWidth
                                  : 48,
                              top: 64,
                              zIndex: 55,
                              bottom: isMobileShell
                                ? "calc(54px + env(safe-area-inset-bottom))"
                                : 0,
                              overflow: "auto",
                              display:
                                _auditRoute === "my-reports" ? "block" : "none",
                            }}
                          >
                            <div
                              style={{
                                padding: "0 20px",
                                maxWidth: 980,
                                margin: "0 auto",
                              }}
                            >
                              <MyReportsPage />
                            </div>
                          </div>
                        )}

                        {/* 🌳 결정트리 검토 (onto-review) — keep-alive, embedded.
                            AI HS 분류와 동일 패턴 — 메인 Sidebar 고정 + 같은 맥락 비교. */}
                        {mountedAudit["onto-review"] && (
                          <div
                            className="fixed inset-y-0 right-0 z-30 bg-white"
                            style={{
                              left: isMobileShell
                                ? 0
                                : isSidebarOpen
                                  ? sidebarWidth
                                  : 48,
                              top: 64,
                              zIndex: 55, // 문서인식 패널 접기 핸들(z-40/50) 비침 방지
                              bottom: isMobileShell
                                ? "calc(54px + env(safe-area-inset-bottom))"
                                : 0,
                              overflow: "auto",
                              display:
                                _auditRoute === "onto-review"
                                  ? "block"
                                  : "none",
                            }}
                          >
                            <OntologyReviewPage embedded />
                          </div>
                        )}

                        {/* 📑 속견표 (quickref) — keep-alive, embedded.
                            류 위치격자 + 부·류 목록. 류 클릭 → onto-detail&chapter. */}
                        {mountedAudit["quickref"] && (
                          <div
                            className="fixed inset-y-0 right-0 z-30 bg-white"
                            style={{
                              left: isMobileShell
                                ? 0
                                : isSidebarOpen
                                  ? sidebarWidth
                                  : 48,
                              top: 64,
                              zIndex: 55,
                              bottom: isMobileShell
                                ? "calc(54px + env(safe-area-inset-bottom))"
                                : 0,
                              overflow: "auto",
                              // 64차: display:none 은 Chrome 에서 안·밖 스크롤 위치를 0 으로 되돌린다 →
                              //   visibility 로 숨겨 통관정보 등에 다녀와도 보던 자리를 유지한다(fixed 층이라 배치 영향 없음).
                              display: "block",
                              visibility: _auditRoute === "quickref" ? "visible" : "hidden",
                              pointerEvents: _auditRoute === "quickref" ? "auto" : "none",
                            }}
                            aria-hidden={_auditRoute === "quickref" ? undefined : true}
                          >
                            <QuickRefPage embedded />
                          </div>
                        )}

                        {/* 🌐 HS 비교 (hs-compare) — keep-alive, embedded.
                            나라별 세번 비교 (UNIPASS 세계HS 실시간 프록시). */}
                        {mountedAudit["hs-compare"] && (
                          <div
                            className="fixed inset-y-0 right-0 z-30 bg-white"
                            style={{
                              left: isMobileShell
                                ? 0
                                : isSidebarOpen
                                  ? sidebarWidth
                                  : 48,
                              top: 64,
                              zIndex: 55,
                              bottom: isMobileShell
                                ? "calc(54px + env(safe-area-inset-bottom))"
                                : 0,
                              overflow: "auto",
                              display:
                                _auditRoute === "hs-compare" ? "block" : "none",
                            }}
                          >
                            <HsComparePage embedded />
                          </div>
                        )}

                        {/* 💬 문의·건의 게시판 (inquiry) — keep-alive, embedded */}
                        {mountedAudit["inquiry"] && (
                          <div
                            className="fixed inset-y-0 right-0 z-30 bg-white"
                            style={{
                              left: isMobileShell
                                ? 0
                                : isSidebarOpen
                                  ? sidebarWidth
                                  : 48,
                              top: 64,
                              zIndex: 55,
                              bottom: isMobileShell
                                ? "calc(54px + env(safe-area-inset-bottom))"
                                : 0,
                              overflow: "hidden",
                              display:
                                _auditRoute === "inquiry" ? "block" : "none",
                            }}
                          >
                            <InquiryBoardPage />
                          </div>
                        )}

                        {/* 📋 대량 세번분류 (bulk-classify) — keep-alive, embedded.
                            AUIGrid 호스트라 overflow:hidden (스크롤은 그리드 내부에서). */}
                        {mountedAudit["bulk-classify"] && (
                          <div
                            className="fixed inset-y-0 right-0 z-30 bg-white"
                            style={{
                              left: isMobileShell
                                ? 0
                                : isSidebarOpen
                                  ? sidebarWidth
                                  : 48,
                              top: 64,
                              zIndex: 55,
                              bottom: isMobileShell
                                ? "calc(54px + env(safe-area-inset-bottom))"
                                : 0,
                              overflow: "hidden",
                              display:
                                _auditRoute === "bulk-classify"
                                  ? "block"
                                  : "none",
                            }}
                          >
                            <BulkClassifyPanel embedded />
                          </div>
                        )}

                        {/* 📋 마스터 세번관리 (master-hs) — keep-alive, embedded.
                            AUIGrid 호스트라 overflow:hidden (스크롤은 그리드 내부에서). */}
                        {mountedAudit["master-hs"] && (
                          <div
                            className="fixed inset-y-0 right-0 z-30 bg-white"
                            style={{
                              left: isMobileShell
                                ? 0
                                : isSidebarOpen
                                  ? sidebarWidth
                                  : 48,
                              top: 64,
                              zIndex: 55,
                              bottom: isMobileShell
                                ? "calc(54px + env(safe-area-inset-bottom))"
                                : 0,
                              overflow: "hidden",
                              display:
                                _auditRoute === "master-hs" ? "block" : "none",
                            }}
                          >
                            <MasterHsPanel embedded />
                          </div>
                        )}

                        {/* 🏛️ 레거시 거래처 등록 탭 */}
                        {activeTab === "legacy-registration" && (
                          <div
                            className="flex-1 overflow-hidden bg-gray-50"
                            style={{ height: "calc(100vh - 64px)" }}
                          >
                            <LegacyCustomerRegistration
                              API_BASE_URL={API_BASE_URL}
                            />
                          </div>
                        )}

                        {/* OLD 차량등록매핑 탭 (백업) */}
                        {activeTab === "mapping_old" && (
                          <div className="h-full p-6 overflow-auto">
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                              {/* 필터 및 액션 */}
                              <div className="p-4 border-b border-gray-200 bg-gray-50">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-4">
                                    <label className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        className="w-4 h-4 text-gray-900 rounded"
                                      />
                                      <span className="text-sm text-gray-700">
                                        매핑 완료만 표시
                                      </span>
                                    </label>
                                    <span className="text-sm text-gray-500">
                                      전체{" "}
                                      {
                                        fileList.filter(
                                          (f) =>
                                            f.extractedData?.documentType &&
                                            [
                                              "말소등록증",
                                              "자동차등록증",
                                              "폐차증",
                                              "자동차등록원부",
                                              "경락확인서",
                                            ].includes(
                                              f.extractedData.documentType,
                                            ),
                                        ).length
                                      }
                                      건 차량서류
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition font-medium">
                                      📥 엑셀 다운로드
                                    </button>
                                    <button className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition font-medium">
                                      🚗 선택 항목 차량등록
                                    </button>
                                  </div>
                                </div>
                              </div>

                              {/* 파일 업로드 영역 (레거시 거래처 등록 시 숨김) */}
                              {activeTab !== "legacy-registration" &&
                                fileList.length === 0 && (
                                  <div
                                    className="flex-1 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer min-h-[400px]"
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      setIsDragging(true);
                                    }}
                                    onClick={() =>
                                      document
                                        .getElementById("fileInput")
                                        ?.click()
                                    }
                                  >
                                    <Upload className="w-12 h-12 text-gray-400 mb-4" />
                                    <p className="text-gray-500 font-medium text-lg">
                                      파일을 업로드해주세요
                                    </p>
                                    <p className="text-gray-400 text-sm mt-1">
                                      클릭하거나 파일을 드래그하세요
                                    </p>
                                    <div
                                      className="mt-6 flex items-center gap-2 text-xs text-gray-400"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <HarnessWorkflowGuide />
                                      <span>
                                        AI 자동 검증 & 학습 파이프라인이
                                        작동합니다
                                      </span>
                                    </div>
                                  </div>
                                )}

                              {/* 매핑 리스트 */}
                              <div className="overflow-x-auto">
                                {(() => {
                                  // 차량 서류 5가지 타입 (정확한 문서 타입명 사용)
                                  const vehicleDocTypes = [
                                    "말소등록증",
                                    "자동차등록증",
                                    "폐차증",
                                    "자동차등록원부",
                                    "경락확인서",
                                  ];

                                  // 1단계: 차량 서류만 필터링
                                  // ⭐ PDF의 경우 pageResults의 각 페이지를 개별 차량으로 처리
                                  const vehicleDocs = fileList.flatMap(
                                    (file, index) => {
                                      // PDF인 경우: pageResults의 각 페이지를 개별 차량으로 처리
                                      if (
                                        file.pageResults &&
                                        file.pageResults.length > 0
                                      ) {
                                        return file.pageResults
                                          .map((pageResult, pageIdx) => {
                                            // ⭐ 데이터 우선순위: extractedData > proData > flashData
                                            // extractedData는 검증 후 수정된 최종 데이터
                                            const extractedData =
                                              pageResult.extractedData || {};
                                            const proData =
                                              pageResult.proData || {};
                                            const flashData =
                                              pageResult.flashData || {};

                                            // 필드별로 우선순위 적용하여 병합 (extractedData가 최우선)
                                            // ⭐ 명시적 필드 할당이 스프레드 연산자보다 우선하도록 순서 조정
                                            const mergedData = {
                                              // 1단계: flashData와 proData를 먼저 병합
                                              ...flashData,
                                              ...proData,
                                              // 2단계: extractedData로 덮어쓰기 (검증 후 수정된 값)
                                              ...extractedData,
                                              // 3단계: 개별 필드 명시적 우선순위 적용 (null/undefined/빈 문자열 처리)
                                              modelYear:
                                                extractedData.modelYear ??
                                                proData.modelYear ??
                                                flashData.modelYear ??
                                                null,
                                              totalWeight:
                                                extractedData.totalWeight ??
                                                proData.totalWeight ??
                                                flashData.totalWeight ??
                                                null,
                                              length:
                                                extractedData.length ??
                                                proData.length ??
                                                flashData.length ??
                                                null,
                                              width:
                                                extractedData.width ??
                                                proData.width ??
                                                flashData.width ??
                                                null,
                                              height:
                                                extractedData.height ??
                                                proData.height ??
                                                flashData.height ??
                                                null,
                                              cbm:
                                                extractedData.cbm ??
                                                proData.cbm ??
                                                flashData.cbm ??
                                                null,
                                            };

                                            // ⭐ 디버깅 로그 제거 (무한 루프 방지)

                                            // ⭐ CBM 계산: 길이, 너비, 높이가 모두 있으면 CBM 계산
                                            // CBM = FLOOR(길이 × 너비 × 높이 ÷ 1,000,000,000, 1)
                                            if (
                                              !mergedData.cbm &&
                                              mergedData.length &&
                                              mergedData.width &&
                                              mergedData.height
                                            ) {
                                              const lengthNum = parseFloat(
                                                String(
                                                  mergedData.length,
                                                ).replace(/[^0-9.]/g, ""),
                                              );
                                              const widthNum = parseFloat(
                                                String(
                                                  mergedData.width,
                                                ).replace(/[^0-9.]/g, ""),
                                              );
                                              const heightNum = parseFloat(
                                                String(
                                                  mergedData.height,
                                                ).replace(/[^0-9.]/g, ""),
                                              );
                                              if (
                                                !isNaN(lengthNum) &&
                                                !isNaN(widthNum) &&
                                                !isNaN(heightNum)
                                              ) {
                                                // CBM = INT(길이 × 너비 × 높이 ÷ 1,000,000,000)
                                                const cbmValue =
                                                  (lengthNum *
                                                    widthNum *
                                                    heightNum) /
                                                  1000000000;
                                                mergedData.cbm =
                                                  Math.floor(
                                                    cbmValue,
                                                  ).toString(); // 정수로 변환 (예: 13)
                                              }
                                            }

                                            if (!mergedData.documentType)
                                              return null;
                                            if (
                                              !vehicleDocTypes.includes(
                                                mergedData.documentType,
                                              )
                                            )
                                              return null;

                                            return {
                                              ...file,
                                              fileIndex: index,
                                              pageNumber: pageResult.pageNumber,
                                              extractedData: mergedData, // ⭐ 병합된 데이터 사용
                                              proData: pageResult.proData, // ⭐ proData도 저장 (필드 값이 없을 경우 대체)
                                              flashData: pageResult.flashData, // ⭐ flashData도 저장 (필드 값이 없을 경우 대체)
                                              pageIndex: pageIdx,
                                            };
                                          })
                                          .filter(Boolean); // null 제거
                                      }

                                      // 단일 이미지인 경우: file.extractedData 사용
                                      if (
                                        file.extractedData &&
                                        vehicleDocTypes.includes(
                                          file.extractedData.documentType,
                                        )
                                      ) {
                                        const extractedData =
                                          file.extractedData || {};
                                        const proData =
                                          file.proResult?.data || {};
                                        const flashData =
                                          file.flashResult?.data || {};

                                        // 필드별로 우선순위 적용하여 병합
                                        const mergedData = {
                                          ...flashData,
                                          ...proData,
                                          ...extractedData,
                                          totalWeight:
                                            extractedData.totalWeight ??
                                            proData.totalWeight ??
                                            flashData.totalWeight,
                                          length:
                                            extractedData.length ??
                                            proData.length ??
                                            flashData.length,
                                          width:
                                            extractedData.width ??
                                            proData.width ??
                                            flashData.width,
                                          height:
                                            extractedData.height ??
                                            proData.height ??
                                            flashData.height,
                                          cbm:
                                            extractedData.cbm ??
                                            proData.cbm ??
                                            flashData.cbm,
                                        };

                                        // ⭐ CBM 계산
                                        // CBM = FLOOR(길이 × 너비 × 높이 ÷ 1,000,000,000, 1)
                                        if (
                                          !mergedData.cbm &&
                                          mergedData.length &&
                                          mergedData.width &&
                                          mergedData.height
                                        ) {
                                          const lengthNum = parseFloat(
                                            String(mergedData.length).replace(
                                              /[^0-9.]/g,
                                              "",
                                            ),
                                          );
                                          const widthNum = parseFloat(
                                            String(mergedData.width).replace(
                                              /[^0-9.]/g,
                                              "",
                                            ),
                                          );
                                          const heightNum = parseFloat(
                                            String(mergedData.height).replace(
                                              /[^0-9.]/g,
                                              "",
                                            ),
                                          );
                                          if (
                                            !isNaN(lengthNum) &&
                                            !isNaN(widthNum) &&
                                            !isNaN(heightNum)
                                          ) {
                                            // CBM = INT(길이 × 너비 × 높이 ÷ 1,000,000,000)
                                            const cbmValue =
                                              (lengthNum *
                                                widthNum *
                                                heightNum) /
                                              1000000000;
                                            mergedData.cbm =
                                              Math.floor(cbmValue).toString(); // 정수로 변환 (예: 13)
                                          }
                                        }

                                        return [
                                          {
                                            ...file,
                                            fileIndex: index,
                                            pageNumber: 1,
                                            extractedData: mergedData,
                                            proData: file.proResult?.data,
                                            flashData: file.flashResult?.data,
                                          },
                                        ];
                                      }

                                      return [];
                                    },
                                  );

                                  // 2단계: 이름별로 관련 서류 매핑 생성
                                  // ⭐ PDF의 경우 pageResults의 각 페이지를 개별 문서로 처리
                                  const relatedDocsMap = {};
                                  fileList.forEach((file, index) => {
                                    // PDF인 경우: pageResults의 각 페이지를 개별 문서로 처리
                                    if (
                                      file.pageResults &&
                                      file.pageResults.length > 0
                                    ) {
                                      file.pageResults.forEach(
                                        (pageResult, pageIdx) => {
                                          // ⭐ 데이터 우선순위: extractedData > proData > flashData
                                          const data =
                                            pageResult.extractedData ||
                                            pageResult.proData ||
                                            pageResult.flashData ||
                                            {};
                                          if (!data || !data.documentType)
                                            return;

                                          // 이름 키 생성
                                          const nameKey =
                                            data.ownerName ||
                                            data.name ||
                                            data.businessName;
                                          if (!nameKey) return;

                                          if (!relatedDocsMap[nameKey]) {
                                            relatedDocsMap[nameKey] = {
                                              identity: null, // 신분증
                                              businessReg: null, // 사업자등록증
                                              contract: null, // 계약서
                                              taxInvoice: null, // 세금계산서
                                            };
                                          }

                                          // 관련 서류 분류
                                          if (data.documentType === "신분증") {
                                            relatedDocsMap[nameKey].identity = {
                                              ...data,
                                              fileIndex: index,
                                              fileName: file.name,
                                              pageNumber: pageResult.pageNumber,
                                            };
                                          } else if (
                                            data.documentType === "사업자등록증"
                                          ) {
                                            relatedDocsMap[
                                              nameKey
                                            ].businessReg = {
                                              ...data,
                                              fileIndex: index,
                                              fileName: file.name,
                                              pageNumber: pageResult.pageNumber,
                                            };
                                          } else if (
                                            data.documentType ===
                                            "자동차매매계약서"
                                          ) {
                                            relatedDocsMap[nameKey].contract = {
                                              ...data,
                                              fileIndex: index,
                                              fileName: file.name,
                                              pageNumber: pageResult.pageNumber,
                                            };
                                          } else if (
                                            data.documentType === "세금계산서"
                                          ) {
                                            relatedDocsMap[nameKey].taxInvoice =
                                              {
                                                ...data,
                                                fileIndex: index,
                                                fileName: file.name,
                                                pageNumber:
                                                  pageResult.pageNumber,
                                              };
                                          }
                                        },
                                      );
                                    } else {
                                      // 단일 이미지인 경우: file.extractedData 사용
                                      const data = file.extractedData;
                                      if (!data) return;

                                      // 이름 키 생성
                                      const nameKey =
                                        data.ownerName ||
                                        data.name ||
                                        data.businessName;
                                      if (!nameKey) return;

                                      if (!relatedDocsMap[nameKey]) {
                                        relatedDocsMap[nameKey] = {
                                          identity: null, // 신분증
                                          businessReg: null, // 사업자등록증
                                          contract: null, // 계약서
                                          taxInvoice: null, // 세금계산서
                                        };
                                      }

                                      // 관련 서류 분류
                                      if (data.documentType === "신분증") {
                                        relatedDocsMap[nameKey].identity = {
                                          ...data,
                                          fileIndex: index,
                                          fileName: file.name,
                                        };
                                      } else if (
                                        data.documentType === "사업자등록증"
                                      ) {
                                        relatedDocsMap[nameKey].businessReg = {
                                          ...data,
                                          fileIndex: index,
                                          fileName: file.name,
                                        };
                                      } else if (
                                        data.documentType === "자동차매매계약서"
                                      ) {
                                        relatedDocsMap[nameKey].contract = {
                                          ...data,
                                          fileIndex: index,
                                          fileName: file.name,
                                        };
                                      } else if (
                                        data.documentType === "세금계산서"
                                      ) {
                                        relatedDocsMap[nameKey].taxInvoice = {
                                          ...data,
                                          fileIndex: index,
                                          fileName: file.name,
                                        };
                                      }
                                    }
                                  });

                                  // 3단계: 각 차량 서류에 관련 서류 매핑
                                  const mappedVehicles = vehicleDocs.map(
                                    (vehicle) => {
                                      // ⭐ 필드별 우선순위 적용: extractedData > proData > flashData
                                      // (vehicle.extractedData는 이미 병합된 데이터이지만, proData와 flashData도 보존)
                                      const extractedData =
                                        vehicle.extractedData || {};
                                      const proData = vehicle.proData || {};
                                      const flashData = vehicle.flashData || {};

                                      // 필드별로 우선순위 적용하여 병합 (extractedData가 최우선)
                                      // ⭐ 명시적 필드 할당이 스프레드 연산자보다 우선하도록 순서 조정
                                      const mergedData = {
                                        // 1단계: flashData와 proData를 먼저 병합
                                        ...flashData,
                                        ...proData,
                                        // 2단계: extractedData로 덮어쓰기 (검증 후 수정된 값)
                                        ...extractedData,
                                        // 3단계: 개별 필드 명시적 우선순위 적용 (null/undefined/빈 문자열 처리)
                                        modelYear:
                                          extractedData.modelYear ??
                                          proData.modelYear ??
                                          flashData.modelYear ??
                                          null,
                                        totalWeight:
                                          extractedData.totalWeight ??
                                          proData.totalWeight ??
                                          flashData.totalWeight ??
                                          null,
                                        length:
                                          extractedData.length ??
                                          proData.length ??
                                          flashData.length ??
                                          null,
                                        width:
                                          extractedData.width ??
                                          proData.width ??
                                          flashData.width ??
                                          null,
                                        height:
                                          extractedData.height ??
                                          proData.height ??
                                          flashData.height ??
                                          null,
                                        cbm:
                                          extractedData.cbm ??
                                          proData.cbm ??
                                          flashData.cbm ??
                                          null,
                                      };

                                      // ⭐ CBM 계산: 길이, 너비, 높이가 모두 있으면 CBM 계산 (없는 경우에만)
                                      // CBM = FLOOR(길이 × 너비 × 높이 ÷ 1,000,000,000, 1)
                                      if (
                                        !mergedData.cbm &&
                                        mergedData.length &&
                                        mergedData.width &&
                                        mergedData.height
                                      ) {
                                        const lengthNum = parseFloat(
                                          String(mergedData.length).replace(
                                            /[^0-9.]/g,
                                            "",
                                          ),
                                        );
                                        const widthNum = parseFloat(
                                          String(mergedData.width).replace(
                                            /[^0-9.]/g,
                                            "",
                                          ),
                                        );
                                        const heightNum = parseFloat(
                                          String(mergedData.height).replace(
                                            /[^0-9.]/g,
                                            "",
                                          ),
                                        );
                                        if (
                                          !isNaN(lengthNum) &&
                                          !isNaN(widthNum) &&
                                          !isNaN(heightNum)
                                        ) {
                                          // CBM = INT(길이 × 너비 × 높이 ÷ 1,000,000,000)
                                          const cbmValue =
                                            (lengthNum * widthNum * heightNum) /
                                            1000000000;
                                          mergedData.cbm =
                                            Math.floor(cbmValue).toString(); // 정수로 변환 (예: 13)
                                        }
                                      }

                                      const ownerName =
                                        mergedData.ownerName || mergedData.name;
                                      const related =
                                        relatedDocsMap[ownerName] || {};

                                      return {
                                        ...vehicle,
                                        extractedData: mergedData, // ⭐ 병합된 데이터 사용
                                        ownerName,
                                        isCompany: isCompanyType(mergedData),
                                        identity: related.identity,
                                        businessReg: related.businessReg,
                                        contract: related.contract,
                                        taxInvoice: related.taxInvoice,
                                      };
                                    },
                                  );

                                  if (mappedVehicles.length === 0) {
                                    return (
                                      <div className="text-center py-12 text-gray-500">
                                        <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                                        <p>매핑할 차량이 없습니다</p>
                                        <p className="text-xs mt-2">
                                          차량 서류를 업로드하고 검증을
                                          완료하세요
                                        </p>
                                      </div>
                                    );
                                  }

                                  return (
                                    <table
                                      className="w-full text-sm"
                                      style={{ minWidth: "2200px" }}
                                    >
                                      <thead className="bg-gray-100 border-b border-gray-200">
                                        <tr>
                                          <th
                                            className="px-4 py-3 text-left font-semibold text-gray-700"
                                            style={{ minWidth: "120px" }}
                                          >
                                            소유자명
                                          </th>
                                          <th
                                            className="px-4 py-3 text-center font-semibold text-gray-700"
                                            style={{ minWidth: "60px" }}
                                          >
                                            구분
                                          </th>
                                          <th
                                            className="px-4 py-3 text-left font-semibold text-blue-700"
                                            style={{ minWidth: "130px" }}
                                          >
                                            주민번호
                                            <br />
                                            <span className="text-xs font-normal">
                                              (신분증)
                                            </span>
                                          </th>
                                          <th
                                            className="px-4 py-3 text-left font-semibold text-orange-700"
                                            style={{ minWidth: "130px" }}
                                          >
                                            사업자번호
                                            <br />
                                            <span className="text-xs font-normal">
                                              (사업자등록증)
                                            </span>
                                          </th>
                                          <th
                                            className="px-4 py-3 text-left font-semibold text-gray-700"
                                            style={{ minWidth: "200px" }}
                                          >
                                            주소
                                          </th>
                                          <th
                                            className="px-4 py-3 text-left font-semibold text-gray-700"
                                            style={{ minWidth: "90px" }}
                                          >
                                            차량번호
                                          </th>
                                          <th
                                            className="px-4 py-3 text-left font-semibold text-gray-700"
                                            style={{ minWidth: "150px" }}
                                          >
                                            차대번호(VIN)
                                          </th>
                                          <th
                                            className="px-4 py-3 text-left font-semibold text-gray-700"
                                            style={{ minWidth: "120px" }}
                                          >
                                            차명
                                          </th>
                                          <th
                                            className="px-4 py-3 text-center font-semibold text-gray-700"
                                            style={{ minWidth: "60px" }}
                                          >
                                            연식
                                          </th>
                                          <th
                                            className="px-4 py-3 text-right font-semibold text-gray-700"
                                            style={{ minWidth: "80px" }}
                                          >
                                            배기량
                                          </th>
                                          <th
                                            className="px-4 py-3 text-right font-semibold text-gray-700"
                                            style={{ minWidth: "90px" }}
                                          >
                                            주행거리
                                          </th>
                                          <th
                                            className="px-4 py-3 text-right font-semibold text-gray-700"
                                            style={{ minWidth: "80px" }}
                                          >
                                            총중량
                                          </th>
                                          <th
                                            className="px-4 py-3 text-right font-semibold text-gray-700"
                                            style={{ minWidth: "70px" }}
                                          >
                                            길이
                                          </th>
                                          <th
                                            className="px-4 py-3 text-right font-semibold text-gray-700"
                                            style={{ minWidth: "70px" }}
                                          >
                                            너비
                                          </th>
                                          <th
                                            className="px-4 py-3 text-right font-semibold text-gray-700"
                                            style={{ minWidth: "70px" }}
                                          >
                                            높이
                                          </th>
                                          <th
                                            className="px-4 py-3 text-right font-semibold text-gray-700"
                                            style={{ minWidth: "70px" }}
                                          >
                                            CBM
                                          </th>
                                          <th
                                            className="px-4 py-3 text-center font-semibold text-gray-700"
                                            style={{ minWidth: "100px" }}
                                          >
                                            말소일
                                          </th>
                                          <th
                                            className="px-4 py-3 text-right font-semibold text-green-700"
                                            style={{ minWidth: "120px" }}
                                          >
                                            계약금액
                                            <br />
                                            <span className="text-xs font-normal">
                                              (계약서)
                                            </span>
                                          </th>
                                          <th
                                            className="px-4 py-3 text-left font-semibold text-purple-700"
                                            style={{ minWidth: "120px" }}
                                          >
                                            세금계산서
                                            <br />
                                            <span className="text-xs font-normal">
                                              (공급자)
                                            </span>
                                          </th>
                                          <th
                                            className="px-4 py-3 text-left font-semibold text-gray-700"
                                            style={{ minWidth: "100px" }}
                                          >
                                            문서타입
                                          </th>
                                          <th
                                            className="px-4 py-3 text-center font-semibold text-gray-700"
                                            style={{ minWidth: "100px" }}
                                          >
                                            상태
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-200">
                                        {mappedVehicles.map((vehicle, idx) => {
                                          // ⭐ vehicle.extractedData는 이미 병합된 데이터 (필드별 우선순위 적용 + CBM 계산 완료)
                                          const data =
                                            vehicle.extractedData || {};

                                          return (
                                            <tr
                                              key={idx}
                                              className="hover:bg-gray-50 cursor-pointer"
                                            >
                                              {/* 소유자명 */}
                                              <td
                                                className="px-4 py-3 text-gray-900"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                <button
                                                  onClick={() => {
                                                    setSelectedFileIndex(
                                                      vehicle.fileIndex,
                                                    );
                                                    setActiveTab("viewer");
                                                  }}
                                                  className="font-semibold text-gray-900 hover:text-blue-600 underline"
                                                >
                                                  {vehicle.ownerName || "-"}
                                                </button>
                                              </td>
                                              {/* 구분 */}
                                              <td
                                                className="px-4 py-3 text-center"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {vehicle.isCompany ? (
                                                  <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded font-bold">
                                                    법인
                                                  </span>
                                                ) : (
                                                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded font-bold">
                                                    개인
                                                  </span>
                                                )}
                                              </td>
                                              {/* 주민번호 (신분증에서 매핑) */}
                                              <td
                                                className="px-4 py-3 text-gray-700"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {vehicle.identity ? (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setSelectedFileIndex(
                                                        vehicle.identity
                                                          .fileIndex,
                                                      );
                                                      setActiveTab("viewer");
                                                    }}
                                                    className="text-blue-700 hover:underline"
                                                  >
                                                    {vehicle.identity
                                                      .residentNumber || "-"}
                                                  </button>
                                                ) : (
                                                  <span className="text-gray-400">
                                                    -
                                                  </span>
                                                )}
                                              </td>
                                              {/* 사업자번호 (사업자등록증에서 매핑) */}
                                              <td
                                                className="px-4 py-3 text-gray-700"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {vehicle.businessReg ? (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setSelectedFileIndex(
                                                        vehicle.businessReg
                                                          .fileIndex,
                                                      );
                                                      setActiveTab("viewer");
                                                    }}
                                                    className="text-orange-700 hover:underline"
                                                  >
                                                    {vehicle.businessReg
                                                      .businessNumber || "-"}
                                                  </button>
                                                ) : (
                                                  <span className="text-gray-400">
                                                    -
                                                  </span>
                                                )}
                                              </td>
                                              {/* 주소 */}
                                              <td
                                                className="px-4 py-3 text-gray-600"
                                                style={{
                                                  maxWidth: "200px",
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  whiteSpace: "nowrap",
                                                }}
                                                title={data.address}
                                              >
                                                {renderTableCell(
                                                  data,
                                                  "address",
                                                  data.address,
                                                )}
                                              </td>
                                              {/* 차량번호 */}
                                              <td
                                                className="px-4 py-3"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {isFieldApplicable(
                                                  data?.documentType,
                                                  "plateNumber",
                                                ) ? (
                                                  data?.plateNumber ? (
                                                    <span className="font-semibold text-blue-900">
                                                      {data.plateNumber}
                                                    </span>
                                                  ) : (
                                                    <span className="text-gray-400">
                                                      -
                                                    </span>
                                                  )
                                                ) : (
                                                  <span className="text-gray-300 text-xs">
                                                    -
                                                  </span>
                                                )}
                                              </td>
                                              {/* 차대번호(VIN) */}
                                              <td
                                                className="px-4 py-3 font-mono text-xs text-gray-700"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {renderTableCell(
                                                  data,
                                                  "vin",
                                                  data.vin,
                                                )}
                                              </td>
                                              {/* 차명 */}
                                              <td
                                                className="px-4 py-3 text-gray-700"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {renderTableCell(
                                                  data,
                                                  "model",
                                                  data.model,
                                                )}
                                              </td>
                                              {/* 연식 */}
                                              <td
                                                className="px-4 py-3 text-center text-gray-700"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {renderTableCell(
                                                  data,
                                                  "modelYear",
                                                  data.modelYear,
                                                )}
                                              </td>
                                              {/* 배기량 */}
                                              <td
                                                className="px-4 py-3 text-right text-gray-700"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {(() => {
                                                  const applicable =
                                                    isFieldApplicable(
                                                      data?.documentType,
                                                      "displacement",
                                                    );
                                                  const value =
                                                    data?.displacement;
                                                  if (!applicable)
                                                    return (
                                                      <span className="text-gray-300 text-xs">
                                                        -
                                                      </span>
                                                    );
                                                  if (
                                                    value === null ||
                                                    value === undefined ||
                                                    value === ""
                                                  )
                                                    return (
                                                      <span className="text-orange-400 text-xs font-semibold">
                                                        미인식
                                                      </span>
                                                    );
                                                  // 숫자 변환 및 천단위 콤마 적용
                                                  try {
                                                    const strValue =
                                                      String(value);
                                                    const cleanValue = strValue
                                                      .replace(/,/g, "")
                                                      .replace(/[^0-9.]/g, "")
                                                      .trim();

                                                    // 빈 문자열 체크
                                                    if (!cleanValue) {
                                                      return (
                                                        <span className="text-orange-400 text-xs font-semibold">
                                                          미인식
                                                        </span>
                                                      );
                                                    }

                                                    const numValue =
                                                      Number(cleanValue);

                                                    // 숫자 변환 실패 체크
                                                    if (
                                                      isNaN(numValue) ||
                                                      !isFinite(numValue)
                                                    ) {
                                                      return value;
                                                    }

                                                    // 천단위 콤마 적용
                                                    return numValue.toLocaleString(
                                                      "ko-KR",
                                                    );
                                                  } catch (e) {
                                                    return value;
                                                  }
                                                })()}
                                              </td>
                                              {/* 주행거리 */}
                                              <td
                                                className="px-4 py-3 text-right text-gray-700"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {(() => {
                                                  const applicable =
                                                    isFieldApplicable(
                                                      data?.documentType,
                                                      "mileage",
                                                    );
                                                  const value = data?.mileage;
                                                  if (!applicable)
                                                    return (
                                                      <span className="text-gray-300 text-xs">
                                                        -
                                                      </span>
                                                    );
                                                  if (
                                                    value === null ||
                                                    value === undefined ||
                                                    value === ""
                                                  )
                                                    return (
                                                      <span className="text-orange-400 text-xs font-semibold">
                                                        미인식
                                                      </span>
                                                    );
                                                  // 숫자 변환 및 천단위 콤마 적용
                                                  try {
                                                    const strValue =
                                                      String(value);
                                                    const cleanValue = strValue
                                                      .replace(/,/g, "")
                                                      .replace(/[^0-9.]/g, "")
                                                      .trim();

                                                    // 빈 문자열 체크
                                                    if (!cleanValue) {
                                                      return (
                                                        <span className="text-orange-400 text-xs font-semibold">
                                                          미인식
                                                        </span>
                                                      );
                                                    }

                                                    const numValue =
                                                      Number(cleanValue);

                                                    // 숫자 변환 실패 체크
                                                    if (
                                                      isNaN(numValue) ||
                                                      !isFinite(numValue)
                                                    ) {
                                                      return value;
                                                    }

                                                    // 천단위 콤마 적용
                                                    return numValue.toLocaleString(
                                                      "ko-KR",
                                                    );
                                                  } catch (e) {
                                                    return value;
                                                  }
                                                })()}
                                              </td>
                                              {/* 총중량 */}
                                              <td
                                                className="px-4 py-3 text-right text-gray-700"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {(() => {
                                                  const applicable =
                                                    isFieldApplicable(
                                                      data?.documentType,
                                                      "totalWeight",
                                                    );
                                                  const value =
                                                    data?.totalWeight;
                                                  if (!applicable)
                                                    return (
                                                      <span className="text-gray-300 text-xs">
                                                        -
                                                      </span>
                                                    );
                                                  if (
                                                    value === null ||
                                                    value === undefined ||
                                                    value === ""
                                                  )
                                                    return (
                                                      <span className="text-orange-400 text-xs font-semibold">
                                                        미인식
                                                      </span>
                                                    );
                                                  // 숫자 변환 및 천단위 콤마 적용
                                                  try {
                                                    if (
                                                      value === 0 ||
                                                      value === "0"
                                                    )
                                                      return "0";
                                                    const strValue =
                                                      String(value);
                                                    const cleanValue = strValue
                                                      .replace(/,/g, "")
                                                      .replace(/[^0-9.]/g, "")
                                                      .trim();
                                                    if (
                                                      !cleanValue ||
                                                      cleanValue === "0"
                                                    )
                                                      return (
                                                        <span className="text-orange-400 text-xs font-semibold">
                                                          미인식
                                                        </span>
                                                      );
                                                    const numValue =
                                                      Number(cleanValue);
                                                    if (
                                                      isNaN(numValue) ||
                                                      !isFinite(numValue)
                                                    )
                                                      return value;
                                                    return numValue.toLocaleString(
                                                      "ko-KR",
                                                    );
                                                  } catch (e) {
                                                    return value;
                                                  }
                                                })()}
                                              </td>
                                              {/* 길이 */}
                                              <td
                                                className="px-4 py-3 text-right text-gray-700"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {(() => {
                                                  const applicable =
                                                    isFieldApplicable(
                                                      data?.documentType,
                                                      "length",
                                                    );
                                                  const value = data?.length;
                                                  if (!applicable)
                                                    return (
                                                      <span className="text-gray-300 text-xs">
                                                        -
                                                      </span>
                                                    );
                                                  if (
                                                    value === null ||
                                                    value === undefined ||
                                                    value === ""
                                                  )
                                                    return (
                                                      <span className="text-orange-400 text-xs font-semibold">
                                                        미인식
                                                      </span>
                                                    );
                                                  // 숫자 변환 및 천단위 콤마 적용
                                                  try {
                                                    if (
                                                      value === 0 ||
                                                      value === "0"
                                                    )
                                                      return "0";
                                                    const strValue =
                                                      String(value);
                                                    const cleanValue = strValue
                                                      .replace(/,/g, "")
                                                      .replace(/[^0-9.]/g, "")
                                                      .trim();
                                                    if (
                                                      !cleanValue ||
                                                      cleanValue === "0"
                                                    )
                                                      return (
                                                        <span className="text-orange-400 text-xs font-semibold">
                                                          미인식
                                                        </span>
                                                      );
                                                    const numValue =
                                                      Number(cleanValue);
                                                    if (
                                                      isNaN(numValue) ||
                                                      !isFinite(numValue)
                                                    )
                                                      return value;
                                                    return numValue.toLocaleString(
                                                      "ko-KR",
                                                    );
                                                  } catch (e) {
                                                    return value;
                                                  }
                                                })()}
                                              </td>
                                              {/* 너비 */}
                                              <td
                                                className="px-4 py-3 text-right text-gray-700"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {(() => {
                                                  const applicable =
                                                    isFieldApplicable(
                                                      data?.documentType,
                                                      "width",
                                                    );
                                                  const value = data?.width;
                                                  if (!applicable)
                                                    return (
                                                      <span className="text-gray-300 text-xs">
                                                        -
                                                      </span>
                                                    );
                                                  if (
                                                    value === null ||
                                                    value === undefined ||
                                                    value === ""
                                                  )
                                                    return (
                                                      <span className="text-orange-400 text-xs font-semibold">
                                                        미인식
                                                      </span>
                                                    );
                                                  // 숫자 변환 및 천단위 콤마 적용
                                                  try {
                                                    if (
                                                      value === 0 ||
                                                      value === "0"
                                                    )
                                                      return "0";
                                                    const strValue =
                                                      String(value);
                                                    const cleanValue = strValue
                                                      .replace(/,/g, "")
                                                      .replace(/[^0-9.]/g, "")
                                                      .trim();
                                                    if (
                                                      !cleanValue ||
                                                      cleanValue === "0"
                                                    )
                                                      return (
                                                        <span className="text-orange-400 text-xs font-semibold">
                                                          미인식
                                                        </span>
                                                      );
                                                    const numValue =
                                                      Number(cleanValue);
                                                    if (
                                                      isNaN(numValue) ||
                                                      !isFinite(numValue)
                                                    )
                                                      return value;
                                                    return numValue.toLocaleString(
                                                      "ko-KR",
                                                    );
                                                  } catch (e) {
                                                    return value;
                                                  }
                                                })()}
                                              </td>
                                              {/* 높이 */}
                                              <td
                                                className="px-4 py-3 text-right text-gray-700"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {(() => {
                                                  const applicable =
                                                    isFieldApplicable(
                                                      data?.documentType,
                                                      "height",
                                                    );
                                                  const value = data?.height;
                                                  if (!applicable)
                                                    return (
                                                      <span className="text-gray-300 text-xs">
                                                        -
                                                      </span>
                                                    );
                                                  if (
                                                    value === null ||
                                                    value === undefined ||
                                                    value === ""
                                                  )
                                                    return (
                                                      <span className="text-orange-400 text-xs font-semibold">
                                                        미인식
                                                      </span>
                                                    );
                                                  // 숫자 변환 및 천단위 콤마 적용
                                                  try {
                                                    if (
                                                      value === 0 ||
                                                      value === "0"
                                                    )
                                                      return "0";
                                                    const strValue =
                                                      String(value);
                                                    const cleanValue = strValue
                                                      .replace(/,/g, "")
                                                      .replace(/[^0-9.]/g, "")
                                                      .trim();
                                                    if (
                                                      !cleanValue ||
                                                      cleanValue === "0"
                                                    )
                                                      return (
                                                        <span className="text-orange-400 text-xs font-semibold">
                                                          미인식
                                                        </span>
                                                      );
                                                    const numValue =
                                                      Number(cleanValue);
                                                    if (
                                                      isNaN(numValue) ||
                                                      !isFinite(numValue)
                                                    )
                                                      return value;
                                                    return numValue.toLocaleString(
                                                      "ko-KR",
                                                    );
                                                  } catch (e) {
                                                    return value;
                                                  }
                                                })()}
                                              </td>
                                              {/* CBM */}
                                              <td
                                                className="px-4 py-3 text-right font-semibold text-green-700"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {(() => {
                                                  const applicable =
                                                    isFieldApplicable(
                                                      data?.documentType,
                                                      "cbm",
                                                    );
                                                  const value = data?.cbm;
                                                  if (!applicable)
                                                    return (
                                                      <span className="text-gray-300 text-xs">
                                                        -
                                                      </span>
                                                    );
                                                  if (
                                                    value === null ||
                                                    value === undefined ||
                                                    value === ""
                                                  )
                                                    return (
                                                      <span className="text-orange-400 text-xs font-semibold">
                                                        미인식
                                                      </span>
                                                    );
                                                  // 숫자 변환 및 천단위 콤마 적용
                                                  try {
                                                    if (
                                                      value === 0 ||
                                                      value === "0"
                                                    )
                                                      return "0";
                                                    const strValue =
                                                      String(value);
                                                    const cleanValue = strValue
                                                      .replace(/,/g, "")
                                                      .replace(/[^0-9.]/g, "")
                                                      .trim();
                                                    if (
                                                      !cleanValue ||
                                                      cleanValue === "0"
                                                    )
                                                      return (
                                                        <span className="text-orange-400 text-xs font-semibold">
                                                          미인식
                                                        </span>
                                                      );
                                                    const numValue =
                                                      Number(cleanValue);
                                                    if (
                                                      isNaN(numValue) ||
                                                      !isFinite(numValue)
                                                    )
                                                      return value;
                                                    return numValue.toLocaleString(
                                                      "ko-KR",
                                                    );
                                                  } catch (e) {
                                                    return value;
                                                  }
                                                })()}
                                              </td>
                                              {/* 말소일 */}
                                              <td
                                                className="px-4 py-3 text-center text-gray-700"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {renderTableCell(
                                                  data,
                                                  "deregistrationDate",
                                                  data.deregistrationDate,
                                                )}
                                              </td>
                                              {/* 계약금액 (계약서에서 매핑) */}
                                              <td
                                                className="px-4 py-3 text-right font-semibold text-green-700"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {vehicle.contract ? (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setSelectedFileIndex(
                                                        vehicle.contract
                                                          .fileIndex,
                                                      );
                                                      setActiveTab("viewer");
                                                    }}
                                                    className="text-green-700 hover:underline"
                                                  >
                                                    {vehicle.contract
                                                      .totalAmount
                                                      ? `${Number(vehicle.contract.totalAmount).toLocaleString()}원`
                                                      : "-"}
                                                  </button>
                                                ) : (
                                                  <span className="text-gray-400">
                                                    -
                                                  </span>
                                                )}
                                              </td>
                                              {/* 세금계산서 (세금계산서에서 매핑) */}
                                              <td
                                                className="px-4 py-3 text-gray-700"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {vehicle.taxInvoice ? (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setSelectedFileIndex(
                                                        vehicle.taxInvoice
                                                          .fileIndex,
                                                      );
                                                      setActiveTab("viewer");
                                                    }}
                                                    className="text-purple-700 hover:underline text-xs"
                                                  >
                                                    {vehicle.taxInvoice
                                                      .supplierBusinessName ||
                                                      vehicle.taxInvoice
                                                        .supplierBusinessNumber ||
                                                      "세금계산서"}
                                                  </button>
                                                ) : (
                                                  <span className="text-gray-400">
                                                    -
                                                  </span>
                                                )}
                                              </td>
                                              {/* 문서타입 */}
                                              <td
                                                className="px-4 py-3"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded font-semibold">
                                                  {data.documentType}
                                                </span>
                                              </td>
                                              {/* 상태 */}
                                              <td
                                                className="px-4 py-3 text-center"
                                                style={{ whiteSpace: "nowrap" }}
                                              >
                                                {!vehicle.isCompany &&
                                                  !vehicle.identity && (
                                                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded font-semibold whitespace-nowrap">
                                                      ⚠️ 신분증 필요
                                                    </span>
                                                  )}
                                                {vehicle.isCompany &&
                                                  !vehicle.businessReg && (
                                                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded font-semibold whitespace-nowrap">
                                                      ⚠️ 사업자 필요
                                                    </span>
                                                  )}
                                                {((vehicle.identity &&
                                                  !vehicle.isCompany) ||
                                                  (vehicle.businessReg &&
                                                    vehicle.isCompany)) && (
                                                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded font-semibold whitespace-nowrap">
                                                    ✓ 매핑 완료
                                                  </span>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  );
                                })()}
                              </div>

                              {/* 통계 */}
                              <div className="p-4 border-t border-gray-200 bg-gray-50">
                                <div className="flex items-center gap-6 text-sm">
                                  <div>
                                    <span className="font-semibold text-gray-700">
                                      전체 차량:{" "}
                                    </span>
                                    <span className="text-gray-900">
                                      {
                                        fileList.filter(
                                          (f) =>
                                            f.extractedData &&
                                            [
                                              "말소등록증",
                                              "등록증",
                                              "폐차증",
                                              "등록원부",
                                              "경락확인서",
                                            ].includes(
                                              f.extractedData.documentType,
                                            ),
                                        ).length
                                      }
                                      건
                                    </span>
                                  </div>
                                  <div>
                                    <span className="font-semibold text-gray-700">
                                      매핑 완료:{" "}
                                    </span>
                                    <span className="text-green-600">
                                      {
                                        Object.values(
                                          fileList.reduce((acc, f) => {
                                            if (!f.extractedData) return acc;
                                            const key =
                                              f.extractedData.ownerName ||
                                              f.extractedData.name ||
                                              f.extractedData.businessName;
                                            if (!key) return acc;
                                            if (!acc[key])
                                              acc[key] = {
                                                hasVehicle: false,
                                                hasId: false,
                                              };
                                            if (
                                              [
                                                "말소등록증",
                                                "등록증",
                                                "폐차증",
                                                "등록원부",
                                                "경락확인서",
                                              ].includes(
                                                f.extractedData.documentType,
                                              )
                                            ) {
                                              acc[key].hasVehicle = true;
                                            }
                                            if (
                                              f.extractedData.documentType ===
                                                "신분증" ||
                                              f.extractedData.documentType ===
                                                "사업자등록증"
                                            ) {
                                              acc[key].hasId = true;
                                            }
                                            return acc;
                                          }, {}),
                                        ).filter((g) => g.hasVehicle && g.hasId)
                                          .length
                                      }
                                      건
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      {/* 탭 내용 영역 종료 */}
                    </>
                  )}
                </div>

                {/* 페이지 선택 패널 (PDF일 때만 표시) */}
                {selectedFile?.pageResults &&
                  selectedFile.pageResults.length > 0 &&
                  (() => {
                    const toggleAllPages = () => {
                      if (
                        checkedPages.length === selectedFile.pageResults.length
                      ) {
                        // 모두 선택 해제
                        setCheckedPages([]);
                      } else {
                        // 모두 선택
                        const allPages = selectedFile.pageResults.map(
                          (p) => p.pageNumber,
                        );
                        setCheckedPages(allPages);
                      }
                    };

                    const togglePage = (pageNum) => {
                      setCheckedPages((prev) => {
                        if (prev.includes(pageNum)) {
                          return prev.filter((p) => p !== pageNum);
                        } else {
                          return [...prev, pageNum].sort((a, b) => a - b);
                        }
                      });
                    };

                    return null; // 페이지 선택 UI는 오른쪽 패널 안으로 이동
                  })()}

                {/* 오른쪽 패널 리사이저 - layoutMode에 따라 방향 변경 */}
                {!isRightPanelCollapsed &&
                activeTab !== "legacy-registration" ? (
                  <div
                    className={`relative flex-shrink-0 group transition-colors ${
                      layoutMode === "horizontal"
                        ? "h-[6px] w-full cursor-row-resize hover:bg-blue-100"
                        : "w-[6px] cursor-col-resize hover:bg-blue-100"
                    }`}
                    onMouseDown={(e) => {
                      // ⭐ 버튼 클릭인 경우 리사이징 방지
                      const target = e.target;
                      const isButton = target.closest("button");
                      if (isButton) {
                        return; // 버튼 클릭은 리사이징 처리하지 않음
                      }

                      e.preventDefault();
                      e.stopPropagation();

                      const isHorizontal = layoutMode === "horizontal";
                      const startPos = isHorizontal ? e.clientY : e.clientX;
                      const startSize = isHorizontal
                        ? rightPanelWidth
                        : rightPanelWidth; // 가로모드에서는 height 사용 필요
                      const panelElement = rightPanelRef.current;

                      if (!panelElement) return;

                      // 드래그 중 선택 방지
                      document.body.style.userSelect = "none";
                      document.body.style.cursor = isHorizontal
                        ? "row-resize"
                        : "col-resize";
                      document.body.style.pointerEvents = "none";
                      if (panelElement) {
                        panelElement.style.pointerEvents = "auto";
                      }

                      // ⭐ requestAnimationFrame으로 매끄러운 업데이트
                      const handleMouseMove = (moveEvent) => {
                        moveEvent.preventDefault();

                        // 기존 애니메이션 프레임 취소
                        if (resizeAnimationFrameRef.current) {
                          cancelAnimationFrame(resizeAnimationFrameRef.current);
                        }

                        // 즉시 DOM에 반영 (상태 업데이트 없이)
                        resizeAnimationFrameRef.current = requestAnimationFrame(
                          () => {
                            if (isHorizontal) {
                              // 가로모드: 위로 드래그하면 하단 패널이 커져야 함
                              const currentY = moveEvent.clientY;
                              const delta = startPos - currentY;
                              const containerHeight =
                                panelElement.parentElement?.clientHeight || 600;
                              const newHeight = Math.max(
                                100,
                                Math.min(
                                  containerHeight - 100,
                                  startSize + delta,
                                ),
                              );
                              if (panelElement) {
                                panelElement.style.height = `${newHeight}px`;
                              }
                            } else {
                              // 세로모드: 좌우 드래그
                              const delta = startPos - moveEvent.clientX;
                              const newWidth = Math.max(
                                300,
                                Math.min(1500, startSize + delta),
                              );
                              if (panelElement) {
                                panelElement.style.width = `${newWidth}px`;
                              }
                            }
                          },
                        );
                      };

                      const handleMouseUp = () => {
                        // 마지막 상태 업데이트 (상태 동기화)
                        if (panelElement) {
                          const finalSize = isHorizontal
                            ? parseInt(panelElement.style.height) ||
                              rightPanelWidth
                            : parseInt(panelElement.style.width) ||
                              rightPanelWidth;
                          setRightPanelWidth(finalSize);
                        }

                        // 🔧 AUIGrid 리사이즈 (컨테이너 크기에 맞게)
                        setTimeout(() => {
                          if (
                            window.AUIGrid &&
                            window.AUIGrid.isCreated("#horizontal_grid")
                          ) {
                            window.AUIGrid.resize("#horizontal_grid");
                            console.log("📐 [Splitter] AUIGrid resize");
                          }
                        }, 100);

                        // 이벤트 리스너 제거
                        document.removeEventListener(
                          "mousemove",
                          handleMouseMove,
                        );
                        document.removeEventListener("mouseup", handleMouseUp);

                        // 애니메이션 프레임 취소
                        if (resizeAnimationFrameRef.current) {
                          cancelAnimationFrame(resizeAnimationFrameRef.current);
                          resizeAnimationFrameRef.current = null;
                        }

                        // 드래그 종료 후 스타일 복원
                        document.body.style.userSelect = "";
                        document.body.style.cursor = "";
                        document.body.style.pointerEvents = "";
                      };

                      // ⭐ passive: false로 즉시 반응
                      document.addEventListener("mousemove", handleMouseMove, {
                        passive: false,
                      });
                      document.addEventListener("mouseup", handleMouseUp, {
                        passive: false,
                      });
                    }}
                    title="드래그하여 크기 조절"
                  >
                    {/* 구분선 - layoutMode에 따라 방향 변경 */}
                    <div
                      className={`absolute ${
                        layoutMode === "horizontal"
                          ? "inset-x-0 top-1/2 -translate-y-1/2 h-[2px] bg-gray-200 group-hover:bg-blue-500"
                          : "inset-y-0 left-1/2 -translate-x-1/2 w-[2px] bg-gray-200 group-hover:bg-blue-500"
                      } transition-all`}
                    />
                    {/* 드래그 그립 도트 */}
                    <div
                      className={`absolute top-1/2 left-1/2 -translate-x-1/2 ${
                        layoutMode === "horizontal"
                          ? "-translate-y-1/2 flex flex-row gap-[3px]"
                          : "-translate-y-[40px] flex flex-col gap-[3px]"
                      } opacity-40 group-hover:opacity-100 transition-opacity pointer-events-none`}
                    >
                      {[...Array(5)].map((_, i) => (
                        <div
                          key={i}
                          className="w-[3px] h-[3px] rounded-full bg-gray-400 group-hover:bg-blue-500"
                        />
                      ))}
                    </div>

                    {/* 토글 버튼 (오른쪽 패널) - layoutMode에 따라 화살표 방향 및 동작 변경 */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1.5 z-40">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (layoutMode === "horizontal") {
                            // 가로모드: PDF 뷰어(가운데 패널)를 토글하여 그리드 전체 화면
                            setIsCenterPanelCollapsed(!isCenterPanelCollapsed);
                          } else {
                            // 세로모드: 오른쪽 패널 토글
                            setIsRightPanelCollapsed(!isRightPanelCollapsed);
                          }
                        }}
                        style={{ pointerEvents: "auto" }}
                        className={`${layoutMode === "horizontal" ? "h-2 w-7" : "w-2 h-7"} bg-gray-700 border border-transparent rounded-sm flex items-center justify-center text-white hover:bg-gray-800 transition-all cursor-pointer`}
                        title={
                          layoutMode === "horizontal"
                            ? "PDF 뷰어 접기/펴기"
                            : "오른쪽 패널 접기/펴기"
                        }
                      >
                        {layoutMode === "horizontal" ? (
                          // 가로모드: 위/아래 화살표 (그리드를 전체로 보려면 위로)
                          isCenterPanelCollapsed ? (
                            <ChevronDown size={10} strokeWidth={1.5} />
                          ) : (
                            <ChevronUp size={10} strokeWidth={1.5} />
                          )
                        ) : // 세로모드: 좌/우 화살표
                        isRightPanelCollapsed ? (
                          <ChevronLeft size={10} strokeWidth={1.5} />
                        ) : (
                          <ChevronRight size={10} strokeWidth={1.5} />
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* 패널이 닫혀있을 때 토글 버튼 */
                  <div className="relative w-0 flex-shrink-0 group hover:bg-transparent transition-all">
                    <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[1px] bg-transparent group-hover:bg-black transition-all" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1.5 z-40">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setIsRightPanelCollapsed(!isRightPanelCollapsed);
                        }}
                        style={{ pointerEvents: "auto" }}
                        className="w-2 h-7 bg-gray-700 border border-transparent rounded-sm flex items-center justify-center text-white hover:bg-gray-800 transition-all cursor-pointer"
                        title="오른쪽 패널 접기/펴기"
                      >
                        {isRightPanelCollapsed ? (
                          <ChevronLeft size={10} strokeWidth={1.5} />
                        ) : (
                          <ChevronRight size={10} strokeWidth={1.5} />
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* 오른쪽 패널: OCR 결과 및 템플릿 관리 (레거시 거래처 등록 시 숨김) */}
                {activeTab !== "legacy-registration" && (
                  <div
                    ref={rightPanelRef}
                    className={`${
                      isRightPanelCollapsed
                        ? layoutMode === "horizontal"
                          ? "h-0"
                          : "w-0"
                        : isCenterPanelCollapsed
                          ? "flex-1"
                          : layoutMode === "horizontal"
                            ? "h-[40%]"
                            : ""
                    } ${layoutMode === "horizontal" ? "border-t" : "border-l"} border-gray-200 overflow-hidden transition-all duration-300 relative bg-white`}
                    style={
                      layoutMode === "horizontal"
                        ? {
                            height: isRightPanelCollapsed
                              ? 0
                              : isCenterPanelCollapsed
                                ? "auto"
                                : `${rightPanelWidth}px`,
                          }
                        : {
                            width: isRightPanelCollapsed
                              ? 0
                              : isCenterPanelCollapsed
                                ? "auto"
                                : `${rightPanelWidth}px`,
                          }
                    }
                  >
                    {/* 추출결과 + 템플릿관리 (툴바 토글로 전환) */}
                    {!isRightPanelCollapsed && (
                      <div className="h-full flex flex-col overflow-hidden">
                        {(() => {
                          // 디버깅을 위한 상태 로깅
                          const hasFlashData =
                            !!selectedFile?.flashResult?.data;
                          const hasProData = !!selectedFile?.proResult?.data;
                          const hasExtractedData = !!extractedData;

                          console.log("🔍 [오른쪽 패널] 렌더링 조건 확인:", {
                            hasFlashData,
                            hasProData,
                            hasExtractedData,
                            flashResult: selectedFile?.flashResult,
                            proResult: selectedFile?.proResult,
                            extractedData: extractedData,
                            shouldShowCompare:
                              hasFlashData && hasProData && !hasExtractedData,
                          });

                          return null;
                        })()}

                        {/* LLM 비교 및 휴먼인더루프 화면 - 파일이 선택되면 즉시 표시 */}
                        {selectedFile ? (
                          <RightPanelTabs
                            selectedFile={selectedFile}
                            templates={templates}
                            selectedTemplateId={
                              Object.values(templates).find(
                                (t) => t.name === selectedOcrTemplate,
                              )?.id || ""
                            }
                            apiBaseUrl={API_BASE_URL}
                            initialPrompt={editablePrompt}
                            availableModels={selectedValidationModels}
                            onRun={handleReExtract}
                            isProcessing={playgroundProcessing}
                            onPromptChange={setEditablePrompt}
                            onTemplateUpdate={fetchTemplates}
                            showTemplateManagement={showTemplateManagement}
                            onToggleTemplateManagement={() => {
                              setTemplateManagementActiveTab("fields");
                              setShowTemplateManagement((prev) => !prev);
                            }}
                            onOpenPromptTab={() => {
                              if (showTemplateManagement) {
                                setShowTemplateManagement(false);
                              } else {
                                setTemplateManagementActiveTab("prompt");
                                setShowTemplateManagement(true);
                              }
                            }}
                            onReset={handleReset}
                            onResetCurrentFile={handleResetCurrentFile}
                            onTemplateChange={(templateId) => {
                              const tmpl = Object.values(templates).find(
                                (t) => t.id === templateId,
                              );
                              if (tmpl) setSelectedOcrTemplate(tmpl.name);
                            }}
                            // 🔧 문서추출 툴바 props
                            onExtract={() => handleBatchFullProcess()}
                            isExtracting={loading}
                            extractLabel={
                              checkedCount > 0
                                ? `딥러닝 (${checkedCount})`
                                : "딥러닝"
                            }
                            extractDisabled={
                              checkedCount === 0 && selectedFileIndex === null
                            }
                            onCancelExtract={handleCancelProcessing}
                            verifyEnabled={verifyEnabled}
                            onVerifyChange={(val) => {
                              setVerifyEnabled(val);
                              localStorage.setItem(
                                "verifyEnabled",
                                String(val),
                              );
                            }}
                            verificationMode={verificationMode}
                            onVerificationModeChange={(mode) => {
                              setVerificationMode(mode);
                              localStorage.setItem("verificationMode", mode);
                            }}
                            ocrEnabled={ocrEnabled}
                            onOcrChange={(val) => {
                              setOcrEnabled(val);
                              localStorage.setItem("ocrEnabled", String(val));
                            }}
                            donutEnabled={donutEnabled}
                            onDonutChange={(val) => {
                              setDonutEnabled(val);
                              localStorage.setItem("donutEnabled", String(val));
                            }}
                            onOpenVllmSettings={() => setVllmSettingsOpen(true)}
                            onOpenEvalLabeling={() => setEvalLabelingOpen(true)}
                            effectiveModelNames={effectiveValidationModels.map(
                              getModelDisplayName || ((m) => m),
                            )}
                            onExcelDownload={() => {
                              window.dispatchEvent(
                                new CustomEvent("triggerExcelDownload"),
                              );
                            }}
                            // 📄 페이지 리스트 관련 props
                            totalPages={Math.max(
                              totalPdfPages || 0,
                              selectedFile.totalPages || 0,
                              selectedFile.pageResults?.length || 0,
                            )}
                            checkedPages={checkedPages}
                            selectedPageNumber={selectedPageNumber}
                            onPageChange={handlePdfPageChange}
                            onPageToggle={(pageNumOrArray) => {
                              setCheckedPages((prev) => {
                                // 배열 입력: Shift+클릭 범위 선택 (한번에 여러 페이지)
                                if (Array.isArray(pageNumOrArray)) {
                                  const merged = new Set([
                                    ...prev,
                                    ...pageNumOrArray,
                                  ]);
                                  return [...merged].sort((a, b) => a - b);
                                }
                                // 단일 입력: 토글
                                const pageNum = pageNumOrArray;
                                if (prev.includes(pageNum)) {
                                  return prev.filter((p) => p !== pageNum);
                                } else {
                                  return [...prev, pageNum].sort(
                                    (a, b) => a - b,
                                  );
                                }
                              });
                            }}
                          >
                            {/* 템플릿관리 ON이면 TemplateManagement, OFF이면 기존 그리드/결과 */}
                            {showTemplateManagement ? (
                              <TemplateManagement
                                onClose={() => setShowTemplateManagement(false)}
                                isModal={false}
                                apiBaseUrl={API_BASE_URL}
                                selectedFile={selectedFile}
                                currentDocumentImage={
                                  selectedFileWithBoundingBoxes?.pngImage
                                }
                                onUpdateViewer={handleExternalFileSelect}
                                onUpdate={fetchTemplates}
                                onTemplateSelect={(templateName) => {
                                  // 🔧 인라인 토글이므로 파일 선택/뷰 탭은 변경하지 않음
                                  console.log(
                                    "🎯 템플릿 관리에서 선택됨:",
                                    templateName,
                                  );
                                  setSelectedOcrTemplate(templateName);
                                }}
                                onExtractFromCurrent={async (file) => {
                                  const formData = new FormData();
                                  formData.append("file", file.file);
                                  const res = await fetch(
                                    `${API_BASE_URL}/api/ocr/extract-header`,
                                    {
                                      method: "POST",
                                      body: formData,
                                    },
                                  );
                                  const data = await res.json();
                                  if (data.success) {
                                    return {
                                      headers: data.headers,
                                      values: data.values,
                                    };
                                  } else {
                                    throw new Error(data.error);
                                  }
                                }}
                                initialSelectedId={
                                  templateManagementTargetId ||
                                  Object.values(templates).find(
                                    (t) => t.name === selectedOcrTemplate,
                                  )?.id ||
                                  ""
                                }
                                initialMode={templateManagementMode}
                                initialActiveTab={templateManagementActiveTab}
                              />
                            ) : selectedFile.isPDF ? (
                              /* 🔍 Debug: selectedFile.groundedFields 확인 */
                              console.log(
                                "📤 [App→PageBasedGridPanel] groundedFields:",
                                {
                                  hasGroundedFields:
                                    !!selectedFile.groundedFields,
                                  keys: Object.keys(
                                    selectedFile.groundedFields || {},
                                  ),
                                  hasItems0:
                                    !!selectedFile.groundedFields?.["items[0]"], // 🆕 items[0] 명시적 확인
                                  items0Data:
                                    selectedFile.groundedFields?.["items[0]"], // 🆕 items[0] 데이터
                                  withBbox: Object.values(
                                    selectedFile.groundedFields || {},
                                  ).filter((v) => v?.bbox?.coordinates).length,
                                },
                              ) ||
                              console.log(
                                "🔍 [App→Grid 진단] pageResults/items 상태:",
                                {
                                  pageResultsCount:
                                    selectedFile.pageResults?.length,
                                  pageResultsPages:
                                    selectedFile.pageResults?.map(
                                      (p) => p.pageNumber,
                                    ),
                                  itemsCount: selectedFile.items?.length,
                                  itemsUniquePages: [
                                    ...new Set(
                                      (selectedFile.items || []).map((i) =>
                                        Number(i.pageNumber ?? i.P ?? 1),
                                      ),
                                    ),
                                  ].sort(),
                                  checkedPages,
                                  extractedDataKeys: Object.keys(
                                    selectedFile.extractedData || {},
                                  ).slice(0, 10),
                                  extractedDataPageResults:
                                    selectedFile.extractedData?.pageResults
                                      ?.length,
                                  extractedDataItems:
                                    selectedFile.extractedData?.items?.length,
                                },
                              ) || (
                                <PageBasedGridPanel
                                  documentType={
                                    // ⭐ pageResults에서 우선 가져오기 (OCR 파이프라인이 여기에 저장)
                                    selectedFile.pageResults?.[0]?.flashData
                                      ?.documentType ||
                                    selectedFile.pageResults?.[0]?.proData
                                      ?.documentType ||
                                    selectedFile.pageResults?.[0]?.extractedData
                                      ?.documentType ||
                                    // 기존 폴백
                                    selectedFile.flashResult?.data
                                      ?.documentType ||
                                    selectedFile.proResult?.data
                                      ?.documentType ||
                                    selectedFile.classifyResult?.data
                                      ?.documentType ||
                                    "기타"
                                  }
                                  flashData={
                                    selectedFile.pageResults?.[0]?.flashData ||
                                    selectedFile.flashData ||
                                    selectedFile.flashResult?.data
                                  }
                                  proData={
                                    selectedFile.pageResults?.[0]?.proData ||
                                    selectedFile.proData ||
                                    selectedFile.proResult?.data
                                  }
                                  extractedData={
                                    selectedFile.pageResults?.[0]
                                      ?.extractedData ||
                                    selectedFile.extractedData
                                  }
                                  pageResults={selectedFile.pageResults}
                                  allItems={selectedFile.items}
                                  totalPages={selectedFile.totalPages}
                                  isRightPanelCollapsed={isRightPanelCollapsed}
                                  selectedPageNumber={selectedPageNumber}
                                  checkedPages={checkedPages}
                                  fileId={selectedFile.id}
                                  validatedPages={
                                    validatedPages[selectedFile.id] || {}
                                  }
                                  templates={templates}
                                  selectedTemplateId={
                                    Object.values(templates).find(
                                      (t) => t.name === selectedOcrTemplate,
                                    )?.id || ""
                                  } // ⭐ 왼쪽 패널 템플릿 동기화
                                  groundedFields={
                                    selectedFile.groundedFields || {}
                                  } // ⭐ [Grounding] LangExtract 필드 위치 정보
                                  crossValidation={
                                    selectedFile.crossValidation || null
                                  } // 🔧 교차 검증 데이터
                                  paddleOcrResult={
                                    paddleOcrResults?.[selectedFile.name] ||
                                    null
                                  } // 🆕 PaddleOCR 1차 추출 결과
                                  ocrEnabled={ocrEnabled} // 🆕 OCR 비교 표시 토글
                                  donutResult={
                                    donutResults?.[selectedFile.name] || null
                                  } // 🆕 Donut 1차 모델 결과
                                  donutEnabled={donutEnabled} // 🆕 Donut 비교 표시 토글
                                  pdfFile={selectedFile.file} // 📄 PDF 파일 (교차검증 뷰어용)
                                  currentFilePreview={
                                    selectedFile.preview || null
                                  } // 🖼️ AI 어시스턴트 이미지 질의용
                                  uploadedFilePath={
                                    selectedFile.uploadedFilePath || ""
                                  }
                                  onFieldHover={handleFieldHover}
                                  onFieldClick={handleFieldClick}
                                  onPageChange={handlePdfPageChange}
                                  onPageToggle={(pageNumOrArray) => {
                                    setCheckedPages((prev) => {
                                      // 배열 입력: Shift+클릭 범위 선택
                                      if (Array.isArray(pageNumOrArray)) {
                                        const merged = new Set([
                                          ...prev,
                                          ...pageNumOrArray,
                                        ]);
                                        return [...merged].sort(
                                          (a, b) => a - b,
                                        );
                                      }
                                      // 단일 입력: 토글
                                      const pageNum = pageNumOrArray;
                                      if (prev.includes(pageNum)) {
                                        return prev.filter(
                                          (p) => p !== pageNum,
                                        );
                                      } else {
                                        return [...prev, pageNum].sort(
                                          (a, b) => a - b,
                                        );
                                      }
                                    });
                                  }}
                                  onValidatePage={handleValidatePage}
                                  onValidateMultiplePages={
                                    handleValidateMultiplePages
                                  }
                                  onValidateAllPages={handleValidateAllPages}
                                  isPageValidated={isPageValidated}
                                  onSaveVehicle={handleSaveVehicle}
                                  onProceed={(editedData) => {
                                    // 사용자가 수정한 데이터 사용
                                    console.log(
                                      "✅ 사용자 수정 완료 (PDF 페이지별):",
                                      editedData,
                                    );

                                    setFileList(
                                      fileList.map((f, idx) =>
                                        idx === selectedFileIndex
                                          ? {
                                              ...f,
                                              extractedData: editedData,
                                              status: "completed",
                                            }
                                          : f,
                                      ),
                                    );

                                    // 검증 결과 그리드 탭으로 자동 이동
                                    setActiveTab("grid");
                                  }}
                                />
                              )
                            ) : useGridMode ? (
                              <PageBasedGridPanel
                                documentType={
                                  // ⭐ pageResults에서 우선 가져오기 (OCR 파이프라인이 여기에 저장)
                                  selectedFile.pageResults?.[0]?.flashData
                                    ?.documentType ||
                                  selectedFile.pageResults?.[0]?.proData
                                    ?.documentType ||
                                  selectedFile.pageResults?.[0]?.extractedData
                                    ?.documentType ||
                                  // 🎯 JPG 파일용: file.flashData 등에서 가져오기
                                  selectedFile.flashData?.documentType ||
                                  selectedFile.proData?.documentType ||
                                  selectedFile.extractedData?.documentType ||
                                  selectedFile.flashResult?.data
                                    ?.documentType ||
                                  selectedFile.proResult?.data?.documentType ||
                                  selectedFile.classifyResult?.data
                                    ?.documentType ||
                                  "기타"
                                }
                                flashData={
                                  // ⭐ pageResults[0]에서 우선 가져오기
                                  selectedFile.pageResults?.[0]?.flashData ||
                                  // 🎯 JPG 파일용: file.flashData 직접 접근
                                  selectedFile.flashData ||
                                  selectedFile.flashResult?.data
                                }
                                proData={
                                  selectedFile.pageResults?.[0]?.proData ||
                                  // 🎯 JPG 파일용: file.proData 직접 접근
                                  selectedFile.proData ||
                                  selectedFile.proResult?.data
                                }
                                extractedData={
                                  // ⭐ pageResults[0]의 extractedData 또는 file 레벨의 extractedData
                                  selectedFile.pageResults?.[0]
                                    ?.extractedData ||
                                  selectedFile.extractedData
                                }
                                pageResults={selectedFile.pageResults || null} // ⭐ pageResults도 전달
                                allItems={selectedFile.items}
                                totalPages={
                                  selectedFile.pageResults?.length || 1
                                }
                                selectedPageNumber={1} // ⭐ 단일 이미지는 항상 페이지 1
                                checkedPages={[1]} // ⭐ 단일 이미지는 항상 페이지 1 체크됨
                                fileId={selectedFile.id} // ⭐ 파일 ID 추가
                                validatedPages={
                                  validatedPages[selectedFile.id] || {}
                                } // ⭐ 검증 상태 추가
                                isRightPanelCollapsed={isRightPanelCollapsed}
                                templates={templates}
                                selectedTemplateId={
                                  Object.values(templates).find(
                                    (t) => t.name === selectedOcrTemplate,
                                  )?.id || ""
                                } // ⭐ 왼쪽 패널 템플릿 동기화
                                groundedFields={
                                  selectedFile.groundedFields || {}
                                } // ⭐ [Grounding] LangExtract 필드 위치 정보
                                crossValidation={
                                  selectedFile.crossValidation || null
                                } // 🔧 교차 검증 데이터
                                paddleOcrResult={
                                  paddleOcrResults?.[selectedFile.name] || null
                                } // 🆕 PaddleOCR 1차 추출 결과
                                ocrEnabled={ocrEnabled} // 🆕 OCR 비교 표시 토글
                                donutResult={
                                  donutResults?.[selectedFile.name] || null
                                } // 🆕 Donut 1차 모델 결과
                                donutEnabled={donutEnabled} // 🆕 Donut 비교 표시 토글
                                pdfFile={selectedFile.file} // 📄 PDF 파일 (교차검증 뷰어용)
                                currentFilePreview={
                                  selectedFile.preview || null
                                } // 🖼️ AI 어시스턴트 이미지 질의용
                                uploadedFilePath={
                                  selectedFile.uploadedFilePath || ""
                                }
                                onFieldHover={handleFieldHover}
                                onFieldClick={handleFieldClick}
                                onValidatePage={handleValidatePage}
                                onValidateMultiplePages={
                                  handleValidateMultiplePages
                                }
                                onValidateAllPages={handleValidateAllPages}
                                isPageValidated={isPageValidated}
                                onSaveVehicle={handleSaveVehicle} // ⭐ 차량 저장 핸들러 추가
                                onProceed={(editedData) => {
                                  // 사용자가 수정한 데이터 사용
                                  console.log(
                                    "✅ 사용자 수정 완료 (그리드):",
                                    editedData,
                                  );

                                  setFileList(
                                    fileList.map((f, idx) =>
                                      idx === selectedFileIndex
                                        ? {
                                            ...f,
                                            extractedData: editedData,
                                            status: "completed",
                                          }
                                        : f,
                                    ),
                                  );

                                  // 검증 결과 그리드 탭으로 자동 이동
                                  setActiveTab("grid");
                                }}
                              />
                            ) : (
                              <>
                                {/* Prompt Playground (이미지 모드) */}
                                <PromptPlayground
                                  isOpen={isPromptPlaygroundOpen}
                                  onClose={() =>
                                    setIsPromptPlaygroundOpen(false)
                                  }
                                  onRun={handleReExtract}
                                  initialPrompt={editablePrompt}
                                  availableModels={selectedValidationModels}
                                  currentDocumentType={
                                    selectedFile.classifyResult?.data
                                      ?.documentType || "기타"
                                  }
                                  isProcessing={playgroundProcessing}
                                  apiBaseUrl={API_BASE_URL}
                                  templates={templates} // ⭐ 템플릿 전달
                                />
                                <CompareResultPanel
                                  documentType={
                                    selectedFile.flashResult?.data
                                      ?.documentType ||
                                    selectedFile.proResult?.data
                                      ?.documentType ||
                                    selectedFile.classifyResult?.data
                                      ?.documentType ||
                                    "기타"
                                  }
                                  flashData={selectedFile.flashResult?.data}
                                  proData={selectedFile.proResult?.data}
                                  onProceed={(editedData) => {
                                    // 사용자가 수정한 데이터 사용
                                    console.log(
                                      "✅ 사용자 수정 완료:",
                                      editedData,
                                    );

                                    setFileList(
                                      fileList.map((f, idx) =>
                                        idx === selectedFileIndex
                                          ? {
                                              ...f,
                                              extractedData: editedData,
                                              status: "completed",
                                            }
                                          : f,
                                      ),
                                    );

                                    // 검증 결과 그리드 탭으로 자동 이동
                                    setActiveTab("grid");
                                  }}
                                />
                              </>
                            )}
                          </RightPanelTabs>
                        ) : (
                          <>
                            {/* 일반 모드: 기존 UI */}
                            {extractedData ? (
                              <div className="h-full flex flex-col">
                                <div
                                  className="p-4 border-b border-gray-200"
                                  style={{ backgroundColor: "#F4F4F4" }}
                                >
                                  <div className="flex items-center justify-between">
                                    <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                                      <Shield className="w-5 h-5 text-gray-900" />
                                      {extractedData.metadata?.validated
                                        ? "검증 완료"
                                        : "1차 추출 결과"}
                                    </h2>

                                    {/* 일치율 표시 */}
                                    {extractedData?.metadata?.matchRate !==
                                      undefined && (
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-600">
                                          Flash vs Pro
                                        </span>
                                        <span
                                          className={`inline-block px-3 py-1 text-sm rounded-full font-bold ${
                                            extractedData.metadata.matchRate ===
                                            100
                                              ? "text-[#0A1037]"
                                              : extractedData.metadata
                                                    .matchRate >= 90
                                                ? "text-[#0A1037]"
                                                : "text-white"
                                          }`}
                                          style={{
                                            backgroundColor:
                                              extractedData.metadata
                                                .matchRate === 100
                                                ? "#1EE9B6"
                                                : extractedData.metadata
                                                      .matchRate >= 90
                                                  ? "#FFDD33"
                                                  : "#E94948",
                                          }}
                                        >
                                          {extractedData.metadata.matchRate}%
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Flash vs Pro 비교 보기 버튼 + 상세 정보 */}
                                  <div className="mt-3 flex items-center justify-between">
                                    {extractedData?.metadata && (
                                      <div className="text-xs text-gray-600">
                                        일치{" "}
                                        {extractedData.metadata.matchCount || 0}
                                        개 · 불일치{" "}
                                        {(extractedData.metadata.totalFields ||
                                          0) -
                                          (extractedData.metadata.matchCount ||
                                            0)}
                                        개 · 총{" "}
                                        {extractedData.metadata.totalFields ||
                                          0}
                                        개 필드
                                      </div>
                                    )}

                                    {/* Flash vs Pro 비교 보기 버튼 */}
                                    {selectedFile?.flashResult &&
                                      selectedFile?.proResult && (
                                        <button
                                          onClick={() => {
                                            // extractedData를 임시로 제거하여 CompareResultPanel 표시
                                            setFileList(
                                              fileList.map((f, idx) =>
                                                idx === selectedFileIndex
                                                  ? {
                                                      ...f,
                                                      extractedData: null,
                                                    }
                                                  : f,
                                              ),
                                            );
                                          }}
                                          className="text-xs px-3 py-1.5 rounded font-semibold hover:opacity-80 transition-opacity flex items-center gap-1.5"
                                          style={{
                                            backgroundColor: "#339AF0",
                                            color: "white",
                                          }}
                                        >
                                          <Eye className="w-3.5 h-3.5" />
                                          Flash vs Pro 비교 보기
                                        </button>
                                      )}
                                  </div>
                                </div>

                                <div className="flex-1 overflow-y-auto p-4">
                                  {/* 디버깅: 데이터 확인 */}
                                  {process.env.NODE_ENV === "development" && (
                                    <div className="mb-4 p-2 bg-gray-100 border border-gray-300 rounded text-xs">
                                      <pre className="whitespace-pre-wrap overflow-auto max-h-40">
                                        {JSON.stringify(
                                          {
                                            plateNumber:
                                              extractedData.plateNumber,
                                            vin: extractedData.vin,
                                            model: extractedData.model,
                                            validated:
                                              extractedData.metadata?.validated,
                                          },
                                          null,
                                          2,
                                        )}
                                      </pre>
                                    </div>
                                  )}

                                  {!extractedData.metadata?.validated && (
                                    <div className="mb-4 p-3 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                                      <div className="flex items-start gap-2">
                                        <span className="text-xl">⚠️</span>
                                        <div className="flex-1">
                                          <p className="text-yellow-900 text-sm font-bold mb-1">
                                            1차 추출 결과 (검증 전)
                                          </p>
                                          <p className="text-yellow-800 text-xs mb-2">
                                            Flash 모델로 추출한 결과입니다. Pro
                                            모델과 비교 검증이 필요합니다.
                                          </p>
                                          <div className="bg-white p-2 rounded border border-yellow-200 text-xs">
                                            <p className="font-semibold text-gray-900 mb-1">
                                              검증이 필요한 이유:
                                            </p>
                                            <ul className="list-disc list-inside text-gray-700 space-y-0.5">
                                              <li>
                                                Flash는 빠르지만 일부 필드를
                                                놓칠 수 있음
                                              </li>
                                              <li>
                                                Pro와 비교하여 정확도 확인 필요
                                              </li>
                                              <li>
                                                일치율 100%일 때만 자동 통과
                                                가능
                                              </li>
                                            </ul>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {extractedData.metadata?.validated &&
                                    extractedData.metadata
                                      ?.comparisonResult && (
                                      <div
                                        className={`mb-4 p-3 border-2 rounded-lg ${
                                          extractedData.metadata.matchRate ===
                                          100
                                            ? "bg-green-50 border-green-300"
                                            : "bg-yellow-50 border-yellow-300"
                                        }`}
                                      >
                                        <div className="flex items-start gap-2">
                                          <span className="text-xl">
                                            {extractedData.metadata
                                              .matchRate === 100
                                              ? "✅"
                                              : "⚠️"}
                                          </span>
                                          <div className="flex-1">
                                            <p
                                              className={`text-sm font-bold mb-1 ${
                                                extractedData.metadata
                                                  .matchRate === 100
                                                  ? "text-green-900"
                                                  : "text-yellow-900"
                                              }`}
                                            >
                                              2차 검증 완료 (Flash vs Pro 비교)
                                            </p>
                                            {/* OCR 워크플로우 헤더 (레거시 거래처 등록 시 숨김) */}
                                            {activeTab !==
                                              "legacy-registration" && (
                                              <div className="flex items-center gap-2 mb-4">
                                                <span
                                                  className={`text-sm font-bold ${fileList.length > 0 ? "text-gray-400" : "text-blue-600"}`}
                                                >
                                                  1. 파일 업로드
                                                </span>
                                                <ChevronRight
                                                  size={14}
                                                  className="text-gray-300"
                                                />
                                                <span
                                                  className={`text-sm font-bold ${fileList.length > 0 && !extractedData ? "text-blue-600" : "text-gray-400"}`}
                                                >
                                                  2. 확인 선택
                                                </span>
                                                <ChevronRight
                                                  size={14}
                                                  className="text-gray-300"
                                                />
                                                <span
                                                  className={`text-sm font-bold ${extractedData ? "text-blue-600" : "text-gray-400"}`}
                                                >
                                                  3. 추출 시작
                                                </span>
                                              </div>
                                            )}
                                            <div className="bg-white p-2 rounded border text-xs space-y-1">
                                              <div className="flex items-center justify-between">
                                                <span className="font-semibold text-gray-700">
                                                  일치율:
                                                </span>
                                                <span
                                                  className={`font-bold text-lg ${
                                                    extractedData.metadata
                                                      .matchRate === 100
                                                      ? "text-green-700"
                                                      : extractedData.metadata
                                                            .matchRate >= 90
                                                        ? "text-yellow-700"
                                                        : "text-red-700"
                                                  }`}
                                                >
                                                  {
                                                    extractedData.metadata
                                                      .comparisonResult
                                                      .agreementRate
                                                  }
                                                </span>
                                              </div>
                                              <div className="flex items-center justify-between">
                                                <span className="text-gray-600">
                                                  일치 필드:
                                                </span>
                                                <span className="text-gray-900 font-medium">
                                                  {
                                                    extractedData.metadata
                                                      .matchDetails.matchCount
                                                  }
                                                  개
                                                </span>
                                              </div>
                                              <div className="flex items-center justify-between">
                                                <span className="text-gray-600">
                                                  차이 필드:
                                                </span>
                                                <span
                                                  className={`font-medium ${
                                                    extractedData.metadata
                                                      .comparisonResult
                                                      .differentFields === 0
                                                      ? "text-green-700"
                                                      : "text-yellow-700"
                                                  }`}
                                                >
                                                  {
                                                    extractedData.metadata
                                                      .comparisonResult
                                                      .differentFields
                                                  }
                                                  개
                                                </span>
                                              </div>
                                            </div>

                                            {/* 하네스 결과 표시 */}
                                            {extractedData.metadata?.harness
                                              ?.enabled ? (
                                              <>
                                                {extractedData.metadata.harness
                                                  .qualityLabel === "gold" && (
                                                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                                                    <div className="flex items-center justify-between">
                                                      <p className="font-semibold">
                                                        🥇 Gold — 자동 검증 완료
                                                      </p>
                                                      <HarnessWorkflowGuide />
                                                    </div>
                                                    <p>
                                                      교차검증 100% 일치. 증류
                                                      데이터로 자동 저장됨.
                                                    </p>
                                                  </div>
                                                )}
                                                {extractedData.metadata.harness
                                                  .qualityLabel ===
                                                  "silver" && (
                                                  <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700">
                                                    <div className="flex items-center justify-between">
                                                      <p className="font-semibold">
                                                        🥈 Silver — AI 검증 완료
                                                      </p>
                                                      <HarnessWorkflowGuide />
                                                    </div>
                                                    <p>
                                                      {extractedData.metadata
                                                        .harness
                                                        .verificationMethod ===
                                                      "rule_based"
                                                        ? `규칙 기반으로 ${extractedData.metadata.harness.resolvedCount}개 필드 자동 해결.`
                                                        : `AI Judge가 ${extractedData.metadata.harness.resolvedCount}개 필드 합의 도출.`}{" "}
                                                      신뢰도:{" "}
                                                      {(
                                                        extractedData.metadata
                                                          .harness
                                                          .confidenceScore * 100
                                                      ).toFixed(1)}
                                                      %
                                                    </p>
                                                  </div>
                                                )}
                                                {extractedData.metadata.harness
                                                  .qualityLabel ===
                                                  "bronze" && (
                                                  <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-700">
                                                    <div className="flex items-center justify-between">
                                                      <p className="font-semibold">
                                                        🥉 Bronze — 부분 검증
                                                      </p>
                                                      <HarnessWorkflowGuide />
                                                    </div>
                                                    <p>
                                                      {
                                                        extractedData.metadata
                                                          .harness.resolvedCount
                                                      }
                                                      개 해결,{" "}
                                                      {
                                                        extractedData.metadata
                                                          .harness
                                                          .unresolvedCount
                                                      }
                                                      개 미해결. 확인 권장.
                                                    </p>
                                                  </div>
                                                )}
                                                {extractedData.metadata.harness
                                                  .qualityLabel ===
                                                  "needs_human" && (
                                                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                                                    <div className="flex items-center justify-between">
                                                      <p className="font-semibold">
                                                        👤 사용자 확인 필요
                                                      </p>
                                                      <HarnessWorkflowGuide />
                                                    </div>
                                                    <p>
                                                      {
                                                        extractedData.metadata
                                                          .harness
                                                          .unresolvedCount
                                                      }
                                                      개 필드를 확인해주세요.
                                                      확인 후 Gold 데이터로
                                                      저장됩니다.
                                                    </p>
                                                  </div>
                                                )}
                                              </>
                                            ) : // 하네스 미사용 시 기존 로직
                                            extractedData.metadata.matchRate ===
                                              100 ? (
                                              <div className="mt-2 p-2 bg-green-100 rounded text-xs text-green-800">
                                                <p className="font-semibold">
                                                  자동 통과 가능
                                                </p>
                                                <p>
                                                  Flash와 Pro가 모든 필드에서
                                                  일치합니다. 안심하고
                                                  사용하세요.
                                                </p>
                                              </div>
                                            ) : (
                                              <div className="mt-2 p-2 bg-yellow-100 rounded text-xs text-yellow-800">
                                                <p className="font-semibold">
                                                  ⚠️ 사용자 확인 필요
                                                  (휴먼인더루프)
                                                </p>
                                                <p>
                                                  일부 필드가 일치하지 않습니다.
                                                  아래 노란색 필드를 확인하고
                                                  올바른 값을 선택하세요.
                                                </p>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                  {extractedData.metadata?.validated &&
                                    extractedData.metadata.needsReview &&
                                    !extractedData.metadata
                                      ?.comparisonResult && (
                                      <div className="mb-4 p-3 bg-gray-50 border border-gray-300 rounded-lg">
                                        <p className="text-gray-800 text-sm font-medium">
                                          ⚠️ 일부 필드의 신뢰도가 낮습니다.
                                          확인해주세요.
                                        </p>
                                      </div>
                                    )}

                                  {extractedData.metadata?.validated &&
                                    extractedData.metadata?.claudeValidation
                                      ?.issues?.length > 0 && (
                                      <div className="mb-4 p-3 bg-gray-50 border border-gray-300 rounded-lg">
                                        <p className="text-gray-900 font-medium text-sm mb-2">
                                          검증 결과:
                                        </p>
                                        <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                                          {extractedData.metadata.claudeValidation.issues.map(
                                            (issue, i) => (
                                              <li key={i}>{issue}</li>
                                            ),
                                          )}
                                        </ul>
                                      </div>
                                    )}

                                  <div className="space-y-3">
                                    {/* 문서 타입 */}
                                    {extractedData.documentType && (
                                      <div className="mb-4">
                                        <label className="block text-xs font-medium text-gray-700 mb-2">
                                          📋 문서 타입
                                        </label>
                                        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg">
                                          <p className="text-lg font-bold text-blue-900 text-center">
                                            {extractedData.documentType}
                                          </p>
                                        </div>
                                      </div>
                                    )}

                                    {/* 동적 필드 렌더링 (그리드 레이아웃 지원) */}
                                    {(() => {
                                      const docType =
                                        extractedData.documentType;
                                      const fieldsMap =
                                        DOCUMENT_FIELDS_MAP[docType];

                                      if (!fieldsMap) {
                                        // 알 수 없는 타입: 기본 필드 모두 표시하거나, extractedData 키 기반 표시
                                        return (
                                          <div className="text-gray-500 text-center p-4">
                                            <p>
                                              정의되지 않은 문서 타입입니다:{" "}
                                              {docType}
                                            </p>
                                          </div>
                                        );
                                      }

                                      return (
                                        <div className="grid grid-cols-6 gap-3">
                                          {Object.entries(fieldsMap).map(
                                            ([fieldKey, config]) => {
                                              if (!config || config === false)
                                                return null;

                                              // 기본값 (Full width)
                                              let colSpan = "col-span-6";
                                              if (config.width === "half")
                                                colSpan = "col-span-3";
                                              if (config.width === "third")
                                                colSpan = "col-span-2";

                                              const label =
                                                config.label || fieldKey;

                                              return (
                                                <div
                                                  key={fieldKey}
                                                  className={colSpan}
                                                >
                                                  {renderField(label, fieldKey)}
                                                </div>
                                              );
                                            },
                                          )}
                                        </div>
                                      );
                                    })()}

                                    {extractedData.metadata?.validated &&
                                      extractedData.metadata?.claudeValidation
                                        ?.reasoning && (
                                        <div className="p-3 bg-gray-50 border border-gray-300 rounded-lg">
                                          <p className="text-xs font-medium text-gray-900 mb-1">
                                            검증 근거
                                          </p>
                                          <p className="text-xs text-gray-700">
                                            {
                                              extractedData.metadata
                                                .claudeValidation.reasoning
                                            }
                                          </p>
                                        </div>
                                      )}
                                  </div>
                                </div>
                                <div className="p-4 border-t border-gray-200 bg-gray-50">
                                  <button
                                    onClick={handleConfirm}
                                    className="w-full px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition font-medium shadow-sm"
                                  >
                                    ✓ 확인 완료 - 시스템 입력
                                  </button>
                                </div>
                              </div>
                            ) : (
                              /* 파일 없을 때: RightPanelTabs 표시 (템플릿 설정 확인용) */
                              <RightPanelTabs
                                selectedFile={null}
                                templates={templates}
                                selectedTemplateId={
                                  Object.values(templates).find(
                                    (t) => t.name === selectedOcrTemplate,
                                  )?.id || ""
                                }
                                apiBaseUrl={API_BASE_URL}
                                initialPrompt={editablePrompt}
                                availableModels={selectedValidationModels}
                                onRun={handleReExtract}
                                isProcessing={playgroundProcessing}
                                onPromptChange={setEditablePrompt}
                                onTemplateUpdate={fetchTemplates}
                                showTemplateManagement={showTemplateManagement}
                                onToggleTemplateManagement={() => {
                                  setTemplateManagementActiveTab("fields");
                                  setShowTemplateManagement((prev) => !prev);
                                }}
                                onOpenPromptTab={() => {
                                  if (showTemplateManagement) {
                                    setShowTemplateManagement(false);
                                  } else {
                                    setTemplateManagementActiveTab("prompt");
                                    setShowTemplateManagement(true);
                                  }
                                }}
                                onReset={handleReset}
                                onTemplateChange={(templateId) => {
                                  const tmpl = Object.values(templates).find(
                                    (t) => t.id === templateId,
                                  );
                                  if (tmpl) setSelectedOcrTemplate(tmpl.name);
                                }}
                              >
                                {showTemplateManagement ? (
                                  <TemplateManagement
                                    onClose={() =>
                                      setShowTemplateManagement(false)
                                    }
                                    isModal={false}
                                    apiBaseUrl={API_BASE_URL}
                                    selectedFile={null}
                                    onUpdateViewer={handleExternalFileSelect}
                                    onUpdate={fetchTemplates}
                                    onTemplateSelect={(templateName) => {
                                      setSelectedOcrTemplate(templateName);
                                    }}
                                    initialSelectedId={
                                      templateManagementTargetId ||
                                      Object.values(templates).find(
                                        (t) => t.name === selectedOcrTemplate,
                                      )?.id ||
                                      ""
                                    }
                                    initialMode={templateManagementMode}
                                    initialActiveTab={
                                      templateManagementActiveTab
                                    }
                                  />
                                ) : (
                                  <div className="h-full flex items-center justify-center text-gray-500">
                                    <div className="text-center px-8 max-w-md">
                                      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                        <span className="text-2xl">📄</span>
                                      </div>
                                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                                        문서를 업로드하세요
                                      </h3>
                                      <p className="text-sm text-gray-600">
                                        왼쪽에서 파일을 업로드하면
                                        <br />
                                        추출 결과가 여기에 표시됩니다
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </RightPanelTabs>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {/* ✏️ PDF 뷰어 + 오른쪽 패널 Wrapper 끝 */}
              </div>

              {/* 🤖 AI 채팅 패널 - PageBasedGridPanel 인라인 사이드바로 대체됨 */}
            </div>
            {/* ✏️ 메인 콘텐츠 + AI 패널 Wrapper 끝 */}
          </div>
        )}

        {/* 템플릿 관리 모달 (제거됨 - 사이드 드로어로 이동) */}
        {/* {showTemplateManagement && (
        <TemplateManagement onClose={() => setShowTemplateManagement(false)} />
      )} */}

        {/* 프롬프트 관리 모달 */}
        <PromptManagementModal
          isOpen={showPromptManager}
          onClose={() => setShowPromptManager(false)}
          apiUrl={API_BASE_URL}
        />

        {/* 기초설정 모달 */}
        {showMasterSettingsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-lg shadow-xl w-[52vw] h-[80vh] flex flex-col">
              <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <h3
                    className="text-lg font-semibold"
                    style={{ color: "#0A1037" }}
                  >
                    기초설정
                  </h3>
                  <span className="text-sm text-gray-600">
                    차량 기초자료관리(그룹코드:{" "}
                    {localStorage.getItem("groupCode") || "0001"})
                  </span>
                  <button
                    onClick={() => {
                      // VehicleMasterManagement 컴포넌트의 갱신 기능을 호출
                      window.dispatchEvent(
                        new CustomEvent("refreshVehicleMaster"),
                      );
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors text-sm"
                    title="새로고침"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>갱신</span>
                  </button>
                </div>
                <button
                  onClick={() => setShowMasterSettingsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden px-0 py-0 min-h-0">
                <VehicleMasterManagement API_BASE_URL={API_BASE_URL} />
              </div>
            </div>
          </div>
        )}

        {/* 🧙 문서 처리 마법사 */}
        <DocumentWizard
          isOpen={showDocumentWizard}
          onClose={() => setShowDocumentWizard(false)}
          firstPagePreview={wizardFirstPage}
          templates={templates}
          API_BASE_URL={API_BASE_URL}
          onSelectExisting={(templateName) => {
            setSelectedOcrTemplate(templateName);
            console.log("📋 [Wizard] 기존 템플릿 선택:", templateName);
          }}
          onCreateNew={(fields, templateName) => {
            console.log("🆕 [Wizard] 신규 필드:", fields);
            if (templateName) {
              console.log("💾 [Wizard] 템플릿 저장 요청:", templateName);
              // TODO: 템플릿 저장 API 호출
            }
          }}
          onExtract={() => {
            console.log("🚀 [Wizard] 딥러닝 시작");
            // 딥러닝 버튼 자동 클릭
            const extractBtn = document.getElementById("extract-btn");
            if (extractBtn) extractBtn.click();
          }}
        />

        {/* 📊 마이그레이션 도구 모달 */}
        {showMigrationModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-xl shadow-2xl w-[900px] max-h-[85vh] flex flex-col overflow-hidden">
              <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                <h3
                  className="text-lg font-semibold"
                  style={{ color: "#0A1037" }}
                >
                  📊 마이그레이션 도구
                </h3>
                <button
                  onClick={() => setShowMigrationModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-auto bg-white">
                <ExcelImageExtractor />
              </div>
            </div>
          </div>
        )}
        {/* 🟦 전역 드래그 앤 드롭 오버레이 (Google Drive 스타일) */}
        {isDragging && (
          <div className="fixed inset-0 z-[9999] bg-blue-50/90 backdrop-blur-sm flex items-center justify-center transition-all duration-300 pointer-events-none">
            {/* pointer-events-none: 오버레이가 이벤트를 가로채지 않게 하여 window 리스너가 계속 작동하게 함 */}
            <div className="border-4 border-blue-400 border-dashed rounded-3xl p-12 flex flex-col items-center justify-center animate-pulse bg-white/50">
              <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mb-6 text-blue-600 shadow-lg">
                <Upload size={48} strokeWidth={2.5} />
              </div>
              <h2 className="text-3xl font-bold text-blue-900 mb-2">
                파일을 여기에 놓으세요
              </h2>
              <p className="text-lg text-blue-600">
                PDF 또는 이미지 파일을 업로드합니다
              </p>
            </div>
          </div>
        )}
      </div>
      {/* 오른쪽 컬럼 wrapper 닫기 */}
      <VllmSettingsModal
        open={vllmSettingsOpen}
        onClose={() => setVllmSettingsOpen(false)}
      />
      <EvalLabelingModal
        open={evalLabelingOpen}
        onClose={() => setEvalLabelingOpen(false)}
        currentFile={selectedFile?.file || null}
        currentFileName={selectedFile?.name || selectedFile?.fileName || ""}
        paddleResult={paddleOcrResults?.[selectedFile?.name]?.fields || null}
        donutResult={donutResults?.[selectedFile?.name]?.fields || null}
        deepResult={selectedFile?.extractedData || null}
      />
      {/* 🤖 AI 어시스턴트 — 전역 렌더(모든 화면에서 헤더 버튼으로 호출 가능).
          AIChatPanel 은 relative+h-full 설계라 '높이 있는 flex 부모' 가 필요 →
          fixed 전체높이 래퍼로 감싸 우측 패널로 띄움. */}
      {showAIPanel && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            zIndex: 1200,
            display: "flex",
          }}
        >
          {activeTab === "fedex-verify" ? (
            /* FedEx 추출 검증 화면 — 검수 업무 전용 어시스턴트 (현재 파일 컨텍스트 인지) */
            <FedexAssistantPanel
              isOpen={showAIPanel}
              onClose={() => {
                setShowAIPanel(false);
                window.dispatchEvent(
                  new CustomEvent("aiPanelStateChange", {
                    detail: { open: false },
                  }),
                );
              }}
              apiBaseUrl={API_BASE_URL}
            />
          ) : IS_LOGINHS_PRODUCT ? (
            /* loginHS — 세번 분류 코파일럿 (분류·세율·선례). 그리드 변환 패널은 DocuAI 전용 */
            <HsCopilotPanel
              isOpen={showAIPanel}
              onClose={() => {
                setShowAIPanel(false);
                window.dispatchEvent(
                  new CustomEvent("aiPanelStateChange", {
                    detail: { open: false },
                  }),
                );
              }}
            />
          ) : (
            <AIChatPanel
              isOpen={showAIPanel}
              onClose={() => {
                setShowAIPanel(false);
                window.dispatchEvent(
                  new CustomEvent("aiPanelStateChange", {
                    detail: { open: false },
                  }),
                );
              }}
              apiBaseUrl={API_BASE_URL}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default App;
